// src/App.jsx — Mapa Financeiro 5G v3.0
// Novidades: Metas, Orçamento por categoria, PDF, Score saúde, Perfil WhatsApp, Excel, Modo claro/escuro

import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./supabaseClient";
import OneSignal from "@onesignal/capacitor-plugin";
import Login from "./Login";
import PublicOnboarding from "./PublicOnboarding";
import BoasVindasModal from "./BoasVindasModal";
import PremiumBonusModal from "./PremiumBonusModal";
import { initAnalytics, logFunnelEvent } from "./analytics";
import porquinho from "./assets/porquinho.png";
import porquinhoCarro from "./porquinho-carro.png";

const PAYMENT_METHODS = ["Débito/Pix","Cartão de Crédito","Boleto","Pix parcelado","Débito automático/Banco","Loja/Financeira","Consignado/Desconto em folha","Corretora/Financiadora","Pessoa física","Empréstimo","Dinheiro"];
const EXPENSE_CATEGORIES = ["Moradia","Alimentação","Supermercado","Restaurantes e Delivery","Limpeza e Higiene","Transporte","Acessórios do Veículo","Saúde","Farmácia","Educação","Vestuário & Moda","Beleza & Estética","Casa & Construção","Eletrodomésticos & Móveis","Serviços","Entretenimento","Lazer","Viagens","Comunicação","Assinaturas & Streaming","Apps Produtividade","Presentes","Generosidade","Família","Pets","Investimentos","Dívidas/Negociações","Empréstimos/Financiamentos","Seguros","Impostos","Reserva de Emergência","Fatura Cartão","Outros"];
const ENTRY_CATEGORIES = ["Salário","Rendimentos","Dividendos","Serviços Prestados","Empréstimo Recebido","Doação","Presente","Renda extra","Cashback, voucher, bônus","Premiação","Resgate de Investimentos","Outros"];
const CATEGORY_ICONS = {Moradia:"🏠",Alimentação:"🍽️",Supermercado:"🛒","Restaurantes e Delivery":"🍔","Limpeza e Higiene":"🧹",Transporte:"🚗","Acessórios do Veículo":"🔧",Saúde:"💊",Farmácia:"💊",Educação:"📚","Vestuário & Moda":"👗","Beleza & Estética":"💅","Casa & Construção":"🔨","Eletrodomésticos & Móveis":"🛋️",Serviços:"🛠️",Entretenimento:"🎬",Lazer:"🎉",Viagens:"✈️",Comunicação:"📱","Assinaturas & Streaming":"📺","Apps Produtividade":"🧠",Presentes:"🎁",Generosidade:"🤝",Família:"👨‍👩‍👧",Pets:"🐾",Investimentos:"📈","Dívidas/Negociações":"📌","Empréstimos/Financiamentos":"🏦",Seguros:"🛡️",Impostos:"🏛️","Reserva de Emergência":"🆘","Fatura Cartão":"💳",Outros:"📦",Salário:"💼",Rendimentos:"💹",Dividendos:"💸","Serviços Prestados":"🧾","Empréstimo Recebido":"🏦",Doação:"🎁",Presente:"🎀","Renda extra":"✨","Cashback, voucher, bônus":"🎟️",Premiação:"🏆","Resgate de Investimentos":"🏦"};
const MONTHS_PT = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const CHART_COLORS = ["#7C83FF","#22C55E","#F59E0B","#EF4444","#14B8A6","#A855F7","#EAB308","#3B82F6","#F97316","#10B981","#EC4899","#84CC16","#06B6D4","#8B5CF6","#DC2626","#65A30D","#0891B2","#F43F5E","#6366F1","#A3A3A3"];

const INITIAL_DATA = {bills:[],entries:[],goals:[],budgets:[],profile:{}};

// ─── Método Finanças 5G ───────────────────────────────────
const G5_PILARES = [
  {key:"ganhar", label:"Ganhar", emoji:"💼", color:"#22C55E",
   desc:"Desenvolver renda e oportunidades",
   categorias:["Salário","Rendimentos","Dividendos","Serviços Prestados","Renda extra","Empréstimo Recebido","Doação","Presente","Cashback, voucher, bônus","Premiação","Outros"]},
  {key:"gratidao", label:"Gratidão", emoji:"🙏", color:"#7C83FF",
   desc:"Consciência e valorização do que possui",
   categorias:["Família","Saúde","Educação","Pets","Seguros","Moradia","Transporte","Acessórios do Veículo","Alimentação","Supermercado","Restaurantes e Delivery","Limpeza e Higiene","Serviços","Entretenimento","Comunicação","Apps Produtividade","Consumo Pessoal","Lazer","Outros","Generosidade","Investimentos","Reserva de Emergência","Dívidas/Negociações","Empréstimos/Financiamentos","Seguros"]},
  {key:"generosidade", label:"Generosidade", emoji:"🤝", color:"#F59E0B",
   desc:"Repartir para multiplicar",
   categorias:["Generosidade"]},
  {key:"gastar", label:"Gastar", emoji:"🏠", color:"#EF4444",
   desc:"Controle e inteligência financeira",
   categorias:["Moradia","Transporte","Acessórios do Veículo","Alimentação","Supermercado","Restaurantes e Delivery","Limpeza e Higiene","Serviços","Entretenimento","Comunicação","Apps Produtividade","Consumo Pessoal","Lazer","Outros","Impostos","Dívidas/Negociações","Empréstimos/Financiamentos"]},
  {key:"guardar", label:"Guardar", emoji:"📈", color:"#14B8A6",
   desc:"Construir patrimônio com propósito",
   categorias:["Investimentos","Reserva de Emergência"]},
];



// ─── Utilitários ──────────────────────────────────────────
function uid(){return`${Date.now()}-${Math.random().toString(36).slice(2,10)}`;}
function round2(v){return Math.round((Number(v)+Number.EPSILON)*100)/100;}
function round8(v){return Math.round((Number(v)+Number.EPSILON)*100000000)/100000000;}
function currency(v){return new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(Number(v||0));}
function percent(v){return`${round2(v)}%`;}
function numberFromInput(value){
  const raw=String(value??"").trim().replace(/\s/g,"");if(!raw)return 0;
  if(raw.includes(",")&&raw.includes(".")){const n=Number(raw.replace(/\./g,"").replace(",","."));return Number.isFinite(n)?round2(n):0;}
  if(raw.includes(",")){const n=Number(raw.replace(",","."));return Number.isFinite(n)?round2(n):0;}
  const n=Number(raw);return Number.isFinite(n)?round2(n):0;
}
function clamp(v,min,max){return Math.min(max,Math.max(min,v));}
function pad(n){return String(n).padStart(2,"0");}
function monthKey(year,month){return`${year}-${pad(month+1)}`;}
function parseMonthKey(key){const[year,mm]=String(key||"").split("-").map(Number);return{year,month:mm-1};}
function compareMonthKey(a,b){return a.localeCompare(b);}
function isBeforeMonth(a,b){return compareMonthKey(a,b)<0;}
function addMonth(year,month,offset){const index=year*12+month+offset;return{year:Math.floor(index/12),month:index%12};}
function todayParts(){const now=new Date();return{day:now.getDate(),month:now.getMonth(),year:now.getFullYear()};}
function clone(obj){return JSON.parse(JSON.stringify(obj));}
function getMonthLabel(year,month){return`${MONTHS_PT[month]} ${year}`;}
function formatMonthKeyLabel(key){const{year,month}=parseMonthKey(key);return getMonthLabel(year,month);}
function getInstallmentCountLabel(bill){
  if(bill.frequency==="unico") return "Único";
  if(bill.frequency==="anual") return "Anual";
  if(bill.frequency==="semestral") return "Semestral";
  if(bill.installments===0) return "Mensal";
  return `${bill.installments}x`;
}
function getInstallmentNumberForMonth(bill,tY,tM){if(bill.installments===0)return null;return(tY*12+tM)-(bill.startYear*12+bill.startMonth)+1;}
// Rótulo de período usado na lista "Todas as despesas cadastradas", já que ali
// aparecem despesas de vários meses juntas e é preciso deixar claro a qual
// mês/ano cada uma se refere (principalmente Único e Parcelado).
function getBillPeriodoLabel(bill){
  const inicio=`${MONTHS_PT[bill.startMonth]}/${bill.startYear}`;
  if(bill.frequency==="unico")return inicio;
  if(bill.frequency==="parcelado"&&bill.installments>0){
    const fimIdx=bill.startYear*12+bill.startMonth+bill.installments-1;
    const fim=`${MONTHS_PT[fimIdx%12]}/${Math.floor(fimIdx/12)}`;
    return`${inicio} a ${fim}`;
  }
  if(bill.frequency==="mensal")return`Mensal, desde ${inicio}`;
  if(bill.frequency==="semestral")return`Semestral, desde ${inicio}`;
  if(bill.frequency==="anual")return`Anual, desde ${inicio}`;
  return inicio;
}

function getUpcomingBills(bills,daysAhead){
  // Retorna todas as contas que vencem entre amanhã e os próximos daysAhead dias
  const today=new Date();
  const result=[];
  for(let d=1;d<=daysAhead;d++){
    const target=new Date(today);target.setDate(today.getDate()+d);
    const tD=target.getDate(),tM=target.getMonth(),tY=target.getFullYear();
    const refKey=monthKey(tY,tM);
    bills.forEach(bill=>{
      if(!shouldGenerateForMonth(bill,tY,tM))return;
      if(bill.dueDay!==tD)return;
      if(getRecordStatus(bill,refKey)==="paid")return;
      // Evitar duplicatas
      if(!result.some(b=>b.id===bill.id))result.push(bill);
    });
  }
  return result;
}

function shouldGenerateForMonth(bill,tY,tM){
  const si=bill.startYear*12+bill.startMonth,ci=tY*12+tM;
  if(ci<si)return false;
  // Despesa encerrada manualmente: continua valendo até o mês final escolhido,
  // mas some dos meses seguintes. Os meses anteriores (já pagos) não são afetados.
  if(bill.endYear!=null&&bill.endMonth!=null){
    const ei=bill.endYear*12+bill.endMonth;
    if(ci>ei)return false;
  }
  // Único — só no mês de início
  if(bill.frequency==="unico"){
    return tY===bill.startYear&&tM===bill.startMonth;
  }
  // Anual — só no mês de início, todo ano
  if(bill.frequency==="anual"){
    return tM===bill.startMonth;
  }
  // Semestral — no mês de início e 6 meses depois, todo ano
  if(bill.frequency==="semestral"){
    const diff=(tM-bill.startMonth+12)%12;
    return diff===0||diff===6;
  }
  // Parcelado
  if(bill.installments>0) return ci-si<bill.installments;
  // Mensal recorrente
  return true;
}
function getOccurrenceId(billId,refMonthKey){return`${billId}::${refMonthKey}`;}
function sortBills(a,b){return a.dueDay-b.dueDay||a.name.localeCompare(b.name);}

function ensurePaymentsShape(data){
  const next=clone(data||INITIAL_DATA);
  next.bills=Array.isArray(next.bills)?next.bills:[];
  next.entries=Array.isArray(next.entries)?next.entries:[];
  next.goals=Array.isArray(next.goals)?next.goals:[];
  next.budgets=Array.isArray(next.budgets)?next.budgets:[];
  next.profile=next.profile&&typeof next.profile==="object"?next.profile:{};
  next.bills=next.bills.map((bill)=>({
    id:bill.id||uid(),name:String(bill.name||"").trim(),
    amount:round2(Math.max(0,Number(bill.amount||0))),
    dueDay:clamp(Number(bill.dueDay||1),1,31),
    installments:Math.max(0,Number.isFinite(Number(bill.installments))?Number(bill.installments):0),
    frequency:["mensal","semestral","anual","unico","parcelado"].includes(bill.frequency)?bill.frequency:"mensal",
    startMonth:clamp(Number.isFinite(Number(bill.startMonth))?Number(bill.startMonth):new Date().getMonth(),0,11),
    startYear:Math.max(2000,Number.isFinite(Number(bill.startYear))?Number(bill.startYear):new Date().getFullYear()),
    paymentMethod:PAYMENT_METHODS.includes(bill.paymentMethod)?bill.paymentMethod:PAYMENT_METHODS[0],
    category:EXPENSE_CATEGORIES.includes(bill.category)?bill.category:"Outros",
    notes:String(bill.notes||""),
    cartaoId:bill.cartaoId||"",
    taxaJuros:Number.isFinite(Number(bill.taxaJuros))?Number(bill.taxaJuros):0,
    endMonth:bill.endMonth==null?null:(Number.isFinite(Number(bill.endMonth))?Number(bill.endMonth):null),
    endYear:bill.endYear==null?null:(Number.isFinite(Number(bill.endYear))?Number(bill.endYear):null),
    desafioId:bill.desafioId||undefined,
    payments:bill.payments&&typeof bill.payments==="object"?bill.payments:{},
  }));
  next.bills=next.bills.map((bill)=>{
    const sp={};
    for(const[refKey,record]of Object.entries(bill.payments||{})){
      const entries=Array.isArray(record?.entries)?record.entries:[];
      sp[refKey]={entries:entries.map((e)=>({id:e.id||uid(),amount:round2(Math.max(0,Number(e.amount||0))),note:String(e.note||""),createdAt:e.createdAt||new Date().toISOString(),paidMonthKey:e.paidMonthKey||refKey,paymentMethod:PAYMENT_METHODS.includes(e.paymentMethod)?e.paymentMethod:bill.paymentMethod})).filter((e)=>e.amount>0),...(record?.valorAjustado?{valorAjustado:round2(Number(record.valorAjustado))}:{})};
    }
    return{...bill,payments:sp};
  });
  next.entries=next.entries.map((entry)=>({
    id:entry.id||uid(),name:String(entry.name||"").trim(),
    amount:round2(Math.max(0,Number(entry.amount||0))),
    category:ENTRY_CATEGORIES.includes(entry.category)?entry.category:"Outros",
    paymentMethod:PAYMENT_METHODS.includes(entry.paymentMethod)?entry.paymentMethod:PAYMENT_METHODS[0],
    month:clamp(Number.isFinite(Number(entry.month))?Number(entry.month):new Date().getMonth(),0,11),
    year:Math.max(2000,Number.isFinite(Number(entry.year))?Number(entry.year):new Date().getFullYear()),
    ...(entry.day&&Number(entry.day)>0?{day:clamp(Number(entry.day),1,31)}:{}),
    notes:String(entry.notes||""),createdAt:entry.createdAt||new Date().toISOString(),
  }));
  next.goals=next.goals.map((g)=>({
    id:g.id||uid(),name:String(g.name||"").trim(),
    targetAmount:round2(Math.max(0,Number(g.targetAmount||0))),
    currentAmount:round2(Math.max(0,Number(g.currentAmount||0))),
    deadline:g.deadline||"",
    color:g.color||CHART_COLORS[0],
    createdAt:g.createdAt||new Date().toISOString(),
    registros:Array.isArray(g.registros)?g.registros:[],
  }));
  next.budgets=next.budgets.map((b)=>({
    id:b.id||uid(),
    category:EXPENSE_CATEGORIES.includes(b.category)?b.category:"Outros",
    limitAmount:round2(Math.max(0,Number(b.limitAmount||0))),
  }));
  return next;
}

function getBillMonthRecord(bill,refMonthKey){return bill.payments?.[refMonthKey]||{entries:[]};}
function getBillAmountForMonth(bill,refMonthKey){
  // Retorna o valor ajustado para o mês se existir, senão o valor padrão
  return bill.payments?.[refMonthKey]?.valorAjustado || bill.amount || 0;
}
function getEntriesTotal(entries){return round2((entries||[]).reduce((sum,e)=>sum+Number(e.amount||0),0));}
function getRecordStatus(bill,refMonthKey){
  const valorMes=getBillAmountForMonth(bill,refMonthKey);
  if(bill.paymentMethod==="Cartão de Crédito"&&!bill.isFaturaGerada){
    const totalPaid=getEntriesTotal(getBillMonthRecord(bill,refMonthKey).entries);
    const entries=getBillMonthRecord(bill,refMonthKey).entries||[];
    const temPagManual=entries.some(e=>e.paymentMethod&&e.paymentMethod!=="Cartão de Crédito");
    if(temPagManual){
      if(totalPaid<=0)return"pending";
      if(round2(totalPaid)>=round2(valorMes))return"paid";
      return"partial";
    }
    return"paid";
  }
  const totalPaid=getEntriesTotal(getBillMonthRecord(bill,refMonthKey).entries);
  if(totalPaid<=0)return"pending";
  if(round2(totalPaid)>=round2(valorMes))return"paid";
  return"partial";
}
function getPercentPaid(bill,refMonthKey){
  const total=getBillAmountForMonth(bill,refMonthKey);if(!total)return 0;
  return round2((getEntriesTotal(getBillMonthRecord(bill,refMonthKey).entries)/total)*100);
}
function getAmountPaidForMonthReference(bill,refMonthKey){return getEntriesTotal(getBillMonthRecord(bill,refMonthKey).entries);}
// Valor que conta como GASTO REAL na categoria (gráficos Total pago e Evolução).
// Compra no cartão sem pagamento manual fica "paid" automaticamente, mas não tem entries:
// nesse caso o gasto é o valor previsto do mês. Demais casos: soma dos pagamentos.
function getGastoCategoriaMes(bill,refMonthKey){
  const ehCartao=bill.paymentMethod==="Cartão de Crédito"&&!bill.isFaturaGerada;
  if(ehCartao){
    const entries=getBillMonthRecord(bill,refMonthKey).entries||[];
    const temPagManual=entries.some(e=>e.paymentMethod&&e.paymentMethod!=="Cartão de Crédito");
    if(!temPagManual)return round2(getBillAmountForMonth(bill,refMonthKey));
  }
  return getAmountPaidForMonthReference(bill,refMonthKey);
}
function isCreatedInMonth(entry,year,month){
  if(!entry?.createdAt)return false;
  const d=new Date(entry.createdAt);
  return d.getFullYear()===year&&d.getMonth()===month;
}
function getLastPaymentDate(bill,refMonthKey){
  const entries=getBillMonthRecord(bill,refMonthKey).entries||[];
  if(entries.length===0)return null;
  const last=entries.reduce((a,b)=>new Date(a.createdAt||0)>new Date(b.createdAt||0)?a:b);
  return last.createdAt?new Date(last.createdAt).toLocaleDateString("pt-BR"):null;
}
function isPaymentOutsideMonth(rawDateStr,year,month){
  if(!rawDateStr)return false;
  const d=new Date(rawDateStr);
  return d.getFullYear()!==year||d.getMonth()!==month;
}
// Espelha exatamente o filtro de caixa do cron (server/index.js, fecharMesAnterior):
// dinheiro só "sai da carteira" quando a fatura é paga — compras no cartão sem fatura
// gerada ainda não contam no caixa do mês, mesmo já aparecendo como "pagas" na conta.
function getCashCountedForBillMonth(bill,refKey,year,month){
  const entries=bill?.payments?.[refKey]?.entries||[];
  let total=0;
  for(const entry of entries){
    if(!isCreatedInMonth(entry,year,month))continue;
    const ehCartao=bill.paymentMethod==="Cartão de Crédito"&&!bill.isFaturaGerada;
    const trocouForma=entry.paymentMethod&&entry.paymentMethod!=="Cartão de Crédito";
    if(!(bill.isFaturaGerada||!ehCartao||trocouForma))continue;
    total+=Number(entry.amount||0);
  }
  return round2(total);
}
function getRemainingForMonthReference(bill,refMonthKey){return round2(Math.max(0,getBillAmountForMonth(bill,refMonthKey)-getAmountPaidForMonthReference(bill,refMonthKey)));}
function getOccurrenceVisualState(occ,currentKey,isViewingCurrentMonth,todayDay){
  const status=getRecordStatus(occ.bill,occ.refMonthKey);
  const overdueCurrentMonth=status!=="paid"&&isViewingCurrentMonth&&occ.refMonthKey===currentKey&&occ.bill.dueDay<todayDay;
  return{status,isLate:overdueCurrentMonth||occ.isOverdueSection,percent:getPercentPaid(occ.bill,occ.refMonthKey),paid:getAmountPaidForMonthReference(occ.bill,occ.refMonthKey),remaining:getRemainingForMonthReference(occ.bill,occ.refMonthKey)};
}
// Total da fatura de um cartão para o mês das compras (ano/mês).
// Usa shouldGenerateForMonth (respeita Único, Parcelado, Anual, encerradas)
// e o valor ajustado do mês. Compras pagas por fora (Pix/débito) ficam de fora.
function calcularTotalFaturaCartao(bills,cartaoId,ano,mes){
  const key=monthKey(ano,mes);
  let total=0;
  for(const bill of bills||[]){
    if(bill.paymentMethod!=="Cartão de Crédito") continue;
    if(bill.cartaoId!==cartaoId) continue;
    if(bill.isFaturaGerada||String(bill.id).startsWith("fatura-auto-")) continue;
    if(!shouldGenerateForMonth(bill,ano,mes)) continue;
    const entries=bill.payments?.[key]?.entries||[];
    if(entries.some(e=>e.paymentMethod&&e.paymentMethod!=="Cartão de Crédito")) continue;
    total+=Number(getBillAmountForMonth(bill,key)||0);
  }
  return round2(total);
}
function isFutureCategoryExpense(c){return c==="Investimentos"||c==="Reserva de Emergência";}
function isFutureCategoryEntry(c){return c==="Rendimentos"||c==="Dividendos";}

// ─── Busca cotações para o PDF ────────────────────────────
async function buscarCotacoesParaPDF(investimentos){
  const idMap={"btc":"bitcoin","eth":"ethereum","bnb":"binancecoin","sol":"solana","usdt":"tether","usdc":"usd-coin","usual":"usual","monad":"monad-2"};
  const novasCot={};
  try{
    // Cripto
    const cryptoInvs=investimentos.filter(i=>i.tipo==="cripto");
    if(cryptoInvs.length>0){
      const cgIds=cryptoInvs.map(i=>idMap[i.simbolo.toLowerCase()]||i.simbolo.toLowerCase()).filter(Boolean);
      const r=await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${[...new Set(cgIds)].join(",")}&vs_currencies=brl`);
      const d=await r.json();
      for(const inv of cryptoInvs){const cgId=idMap[inv.simbolo.toLowerCase()]||inv.simbolo.toLowerCase();if(d[cgId]?.brl)novasCot[inv.id]=d[cgId].brl;}
    }
    // Moedas
    const moedas=investimentos.filter(i=>i.tipo==="moeda").map(i=>i.simbolo.toUpperCase().replace(/-BRL$/i,"").replace(/BRL$/i,"")+"-BRL").filter(m=>m.length>4);
    const temInt=investimentos.some(i=>i.tipo==="acao_int"||i.tipo==="etf_int");
    const moedasBusca=[...new Set((moedas.includes("USD-BRL")||!temInt)?moedas:[...moedas,"USD-BRL"])];
    if(moedasBusca.length>0){
      const r=await fetch(`https://economia.awesomeapi.com.br/json/last/${moedasBusca.join(",")}`);
      const d=await r.json();
      if(d["USDBRL"]?.bid)novasCot["__usd_brl__"]=parseFloat(d["USDBRL"].bid);
      for(const inv of investimentos.filter(i=>i.tipo==="moeda")){const k=inv.simbolo+"BRL";if(d[k]?.bid)novasCot[inv.id]=parseFloat(d[k].bid);}
    }
    // FIIs, ações, ETFs via Railway proxy
    const yahooInvs=investimentos.filter(i=>["fii","acao","etf_br","acao_int","etf_int"].includes(i.tipo));
    if(yahooInvs.length>0){
      const buildSym=inv=>inv.simbolo.toUpperCase().replace(".SA","");
      const syms=yahooInvs.map(buildSym).filter(s=>s&&s.length>=2);
      const res=await fetch(`https://authentic-trust-production-0edc.up.railway.app/cotacoes?symbols=${syms.join(",")}`);
      const cotData=await res.json();
      for(const inv of yahooInvs){const sym=buildSym(inv);const price=cotData[sym];if(price){novasCot[inv.id]=(inv.tipo==="acao_int"||inv.tipo==="etf_int")?price*(novasCot["__usd_brl__"]||5.0):price;}}
    }
  }catch(e){console.warn("Cotações parciais para PDF:",e);}
  window.__mf5g_cotacoes=novasCot;
  return novasCot;
}

// ─── Export Excel ─────────────────────────────────────────
function exportToExcel(data,currentYear,currentMonth){
  const rows=[["MAPA FINANCEIRO 5G — Relatório"],[""],["=== DESPESAS DO MÊS ==="],["Nome","Categoria","Valor","Vencimento","Data Pagamento","Forma Pagamento","Status"]];
  const key=monthKey(currentYear,currentMonth);
  for(const bill of data.bills){
    if(!shouldGenerateForMonth(bill,currentYear,currentMonth))continue;
    const status=getRecordStatus(bill,key);
    const valorMes=getBillAmountForMonth(bill,key);
    const valorExibido=status==="paid"?getAmountPaidForMonthReference(bill,key):valorMes;
    const dataPagamento=getLastPaymentDate(bill,key)||"—";
    rows.push([bill.name,bill.category,valorExibido,`Dia ${bill.dueDay}`,dataPagamento,bill.paymentMethod,status==="paid"?"Pago":status==="partial"?"Parcial":"Pendente"]);
  }
  rows.push([""],["=== ENTRADAS DO MÊS ==="],["Nome","Categoria","Valor","Dia","Forma Recebimento"]);
  for(const entry of data.entries){
    if(entry.month===currentMonth&&entry.year===currentYear){
      rows.push([entry.name,entry.category,entry.amount,entry.day?`Dia ${entry.day}`:"—",entry.paymentMethod]);
    }
  }
  rows.push([""],["=== METAS ==="],["Nome","Meta","Acumulado","Progresso","Prazo"]);
  for(const g of data.goals){
    const prog=g.targetAmount>0?round2((g.currentAmount/g.targetAmount)*100):0;
    rows.push([g.name,g.targetAmount,g.currentAmount,`${prog}%`,g.deadline||"-"]);
  }
  const csv=rows.map((r)=>r.map((c)=>`"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
  const bom="\uFEFF";
  const blob=new Blob([bom+csv],{type:"text/csv;charset=utf-8;"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");
  a.href=url;a.download=`financas-5g-${MONTHS_PT[currentMonth].toLowerCase()}-${currentYear}.csv`;
  document.body.appendChild(a);a.click();a.remove();
  URL.revokeObjectURL(url);
}

// ─── Export PDF ───────────────────────────────────────────
function exportToPDF(data,currentYear,currentMonth,monthMetrics,saldoAcumulado,investimentos=[],rendaFixa=[],desafios=[]){
  const mesAno=`${MONTHS_PT[currentMonth]} ${currentYear}`;
  const key=monthKey(currentYear,currentMonth);
  const agora=new Date();
  const hoje=agora.toLocaleDateString("pt-BR");
  const horaEmissao=agora.toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit",second:"2-digit"});
  const dataHoraEmissao=`${hoje} às ${horaEmissao}`;

  // ── Fechamento mensal: se o mês pedido já foi fechado (snapshot gravado pelo
  // cron do Railway na virada do mês), o relatório usa a foto congelada em vez
  // dos dados ao vivo — assim contas pagas depois da virada, aportes feitos
  // após o fechamento e reajustes de valor não "vazam" para meses passados.
  const fechamento=data.profile?.fechamentos?.[key]||null;

  const despesasMes=fechamento
    ? fechamento.despesas.map(d=>{
        const billLive=(data.bills||[]).find(b=>b.id===d.id);
        let foraDoMes=false;
        if(d.status==="paid"){
          if(billLive){
            const caixaContado=getCashCountedForBillMonth(billLive,key,currentYear,currentMonth);
            foraDoMes=round2(d.pago-caixaContado)>0.01;
          }else{
            // conta excluída depois do fechamento: melhor aproximação disponível é a data do pagamento
            foraDoMes=isPaymentOutsideMonth(d.dataPagamento,currentYear,currentMonth);
          }
        }
        const ehCompraCartao=!!billLive&&billLive.paymentMethod==="Cartão de Crédito"&&!billLive.isFaturaGerada;
        const isCartaoAuto=ehCompraCartao&&d.status==="paid"&&round2(d.pago||0)===0;
        return{
          name:d.name,category:d.category,dueDay:d.dueDay,
          valorMes:d.valorMes,status:d.status,pago:d.pago,
          dataPagamento:d.dataPagamento?new Date(d.dataPagamento).toLocaleDateString("pt-BR"):null,
          foraDoMes:isCartaoAuto?false:foraDoMes,isCartaoAuto,cartaoId:ehCompraCartao?billLive.cartaoId:"",
        };
      })
    : data.bills.filter((b)=>shouldGenerateForMonth(b,currentYear,currentMonth)).map((b)=>{
        const status=getRecordStatus(b,key);
        const pago=getAmountPaidForMonthReference(b,key);
        const valorMes=getBillAmountForMonth(b,key);
        const isCartaoAuto=b.paymentMethod==="Cartão de Crédito"&&!b.isFaturaGerada&&status==="paid"&&!(getBillMonthRecord(b,key).entries||[]).some(e=>e.paymentMethod&&e.paymentMethod!=="Cartão de Crédito");
        const caixaContado=getCashCountedForBillMonth(b,key,currentYear,currentMonth);
        const foraDoMes=status==="paid"&&round2(pago-caixaContado)>0.01;
        return{name:b.name,category:b.category,dueDay:b.dueDay,valorMes,status,pago,dataPagamento:getLastPaymentDate(b,key),foraDoMes,isCartaoAuto,cartaoId:b.paymentMethod==="Cartão de Crédito"&&!b.isFaturaGerada?b.cartaoId:""};
      });

  const entradasMes=fechamento
    ? fechamento.entradas.map(e=>({name:e.name,category:e.category,amount:e.amount,day:e.day,paymentMethod:null}))
    : data.entries.filter((e)=>e.month===currentMonth&&e.year===currentYear).map(e=>({name:e.name,category:e.category,amount:e.amount,day:e.day,paymentMethod:e.paymentMethod}));

  const investimentosMes=fechamento?fechamento.investimentos:investimentos;
  const rendaFixaMes=fechamento?fechamento.rendaFixa:rendaFixa;
  const desafiosMes=fechamento?fechamento.desafios:desafios;

  const paidCountMes=despesasMes.filter(d=>d.status==="paid").length;
  const metricsMes=fechamento?{
    previsto:fechamento.metrics.previsto,pago:fechamento.metrics.pago,pendente:fechamento.metrics.pendente,
    entradas:fechamento.metrics.entradas,saldoMes:fechamento.metrics.saldoMes,
    aportesDesteMes:fechamento.metrics.aportesDesteMes||0,atrasados:fechamento.metrics.atrasados||0,
    pagoDesteMes:fechamento.metrics.pagoDesteMes!=null?fechamento.metrics.pagoDesteMes:round2(fechamento.metrics.pago-(fechamento.metrics.atrasados||0)),
    atrasadosDetalhe:fechamento.metrics.atrasadosDetalhe||[],
    paidCount:paidCountMes,totalCount:despesasMes.length,
    progress:despesasMes.length?round2((paidCountMes/despesasMes.length)*100):0,
  }:monthMetrics;

  const despesaRows=despesasMes.map((d)=>{
    const statusLabel=d.status==="paid"?(d.isCartaoAuto?"💳 No cartão":"✅ Pago"):d.status==="partial"?`⚡ Parcial (${currency(d.pago)})`: "⏳ Pendente";
    // Compra no cartão: mostra o valor da compra (o dinheiro sai do caixa quando a fatura é paga)
    const valorExibido=d.isCartaoAuto?d.valorMes:(d.status==="paid"?d.pago:d.valorMes); // conta paga via "Quitar" pode ter saído por valor diferente do previsto
    const avisoForaDoMes=d.foraDoMes?` <span style="color:#d97706;font-size:10px;white-space:nowrap;" title="Valor pago aqui mas ainda não contabilizado no caixa deste mês — geralmente compra no cartão cuja fatura ainda não foi gerada/paga">⚠️ fora do caixa deste mês</span>`:"";
    return`<tr><td>${d.name}</td><td>${d.category}</td><td>${currency(valorExibido)}</td><td>Dia ${d.dueDay}</td><td>${d.dataPagamento||"—"}${avisoForaDoMes}</td><td>${statusLabel}</td></tr>`;
  }).join("");
  const qtdForaDoMes=despesasMes.filter(d=>d.foraDoMes).length;

  const entradaRows=entradasMes.map((e)=>`<tr><td>${e.name}</td><td>${e.category}</td><td>${currency(e.amount)}</td><td>${e.day?`Dia ${e.day}`:"—"}</td><td>${e.paymentMethod||"—"}</td></tr>`).join("");

  const goalRows=data.goals.map((g)=>{
    const prog=g.targetAmount>0?round2((g.currentAmount/g.targetAmount)*100):0;
    return`<tr><td>${g.name}</td><td>${currency(g.targetAmount)}</td><td>${currency(g.currentAmount)}</td><td>${prog}%</td><td>${g.deadline||"-"}</td></tr>`;
  }).join("");

  const html=`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>Relatório Financeiro — ${mesAno}</title>
  <style>
    body{font-family:Arial,sans-serif;margin:30px;color:#222;font-size:13px;}
    h1{color:#6f6ff7;font-size:22px;margin-bottom:4px;}
    h2{color:#444;font-size:15px;margin:20px 0 8px;border-bottom:2px solid #6f6ff7;padding-bottom:4px;}
    .info{color:#666;margin-bottom:20px;font-size:12px;}
    .cards{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px;}
    .card{background:#f8f8ff;border:1px solid #ddd;border-radius:8px;padding:12px;text-align:center;}
    .card-title{font-size:11px;color:#888;margin-bottom:4px;}
    .card-value{font-size:16px;font-weight:bold;color:#333;}
    .card.green{border-color:#22C55E;}.card.green .card-value{color:#16a34a;}
    .card.red{border-color:#EF4444;}.card.red .card-value{color:#dc2626;}
    .card.yellow{border-color:#F59E0B;}.card.yellow .card-value{color:#d97706;}
    table{width:100%;border-collapse:collapse;margin-bottom:16px;}
    th{background:#6f6ff7;color:#fff;padding:8px;text-align:left;font-size:12px;}
    td{padding:7px 8px;border-bottom:1px solid #eee;font-size:12px;}
    tr:nth-child(even){background:#f9f9f9;}
    .footer{margin-top:30px;color:#999;font-size:11px;text-align:center;}
    @media print{body{margin:15px;}}
  </style></head><body>
  <h1>💳 Mapa Financeiro 5G</h1>
  <div class="info">Relatório de ${mesAno} • Gerado em ${dataHoraEmissao}${fechamento?` • 🔒 Mês fechado em ${new Date(fechamento.fechadoEm).toLocaleDateString("pt-BR")} — dados congelados`:""}</div>
  <div class="cards">
    <div class="card"><div class="card-title">Previsto</div><div class="card-value">${currency(metricsMes.previsto)}</div></div>
    <div class="card green"><div class="card-title">Pago</div><div class="card-value">${currency(metricsMes.pago)}</div></div>
    <div class="card red"><div class="card-title">Pendente</div><div class="card-value">${currency(metricsMes.pendente)}</div></div>
    <div class="card"><div class="card-title">Entradas</div><div class="card-value">${currency(metricsMes.entradas)}</div></div>
    <div class="card ${metricsMes.saldoMes>=0?"green":"red"}"><div class="card-title">Saldo do mês</div><div class="card-value">${currency(metricsMes.saldoMes)}</div></div>
    <div class="card ${saldoAcumulado>=0?"green":"red"}"><div class="card-title">Saldo acumulado</div><div class="card-value">${currency(saldoAcumulado)}</div></div>
    <div class="card"><div class="card-title">Progresso</div><div class="card-value">${metricsMes.paidCount} de ${metricsMes.totalCount}</div></div>
    <div class="card yellow"><div class="card-title">Quitadas</div><div class="card-value">${percent(metricsMes.progress)}</div></div>
  </div>
  ${(()=>{
    const totalAportes=metricsMes.aportesDesteMes||0;
    const pagoSemAportes=round2(metricsMes.pagoDesteMes!=null?metricsMes.pagoDesteMes-totalAportes:metricsMes.pago-totalAportes);
    const saldoOperacional=round2(metricsMes.entradas-pagoSemAportes-totalAportes-(metricsMes.atrasados||0));
    if(totalAportes<=0&&!(metricsMes.atrasados>0)) return "";
    const cur2=v=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);
    return `<div style="background:#f0fdf4;border:1px solid #86efac;border-radius:8px;padding:14px 18px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:center;gap:16px;">
      <div>
        <div style="font-weight:700;color:#166534;font-size:13px;margin-bottom:3px;">💰 Saldo operacional</div>
        <div style="color:#555;font-size:11px;">O que sobrou das entradas após pagar todas as contas e investir</div>
        <div style="color:#555;font-size:11px;margin-top:2px;">Entradas ${cur2(metricsMes.entradas)} − Contas ${cur2(pagoSemAportes)} − Aportes ${cur2(totalAportes)}${metricsMes.atrasados>0?` − Atrasados ${cur2(metricsMes.atrasados)}`:""}</div>
      </div>
      <div style="font-weight:900;font-size:22px;color:${saldoOperacional>=0?"#166534":"#991b1b"};white-space:nowrap;">${cur2(saldoOperacional)}</div>
    </div>`;
  })()}
  ${(()=>{
    // Contas de meses anteriores pagas dentro deste mês — dinheiro que realmente
    // saiu do caixa agora, mesmo pertencendo a uma competência passada.
    const detalhe=metricsMes.atrasadosDetalhe||[];
    if(detalhe.length===0)return "";
    const cur2=v=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);
    const rows=detalhe.map(a=>`<tr><td>${a.nome}</td><td>${a.categoria}</td><td>${formatMonthKeyLabel(a.mesOrigem)}</td><td>${a.data?new Date(a.data).toLocaleDateString("pt-BR"):"—"}</td><td style="text-align:right">${cur2(a.valor)}</td></tr>`).join("");
    return `<h2 style="color:#d97706">💤 Atrasados de meses anteriores</h2>
    <div class="info" style="margin-bottom:8px">Contas de outros meses pagas agora — o dinheiro saiu do caixa neste mês, não no mês de origem da conta.</div>
    <table><tr><th>Nome</th><th>Categoria</th><th>Mês de origem</th><th>Pago em</th><th style="text-align:right">Valor</th></tr>${rows}</table>`;
  })()}
  ${(()=>{
    // Contas Pendentes — separadas das pagas para facilitar a visualização do que falta quitar.
    const pendentes=despesasMes.filter(d=>d.status==="pending"||d.status==="partial");
    if(pendentes.length===0)return "";
    const cur2=v=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);
    const rows=pendentes.map(d=>{
      const valorPendente=d.status==="partial"?round2(d.valorMes-(d.pago||0)):d.valorMes;
      const statusLabel=d.status==="partial"?`⚡ Parcial (falta ${cur2(valorPendente)})`:"⏳ Pendente";
      return`<tr><td>${d.name}</td><td>${d.category}</td><td>Dia ${d.dueDay}</td><td style="text-align:right">${cur2(valorPendente)}</td><td>${statusLabel}</td></tr>`;
    }).join("");
    const totalPendente=round2(pendentes.reduce((s,d)=>s+(d.status==="partial"?d.valorMes-(d.pago||0):d.valorMes),0));
    return `<h2 style="color:#dc2626">📌 Contas Pendentes</h2>
    <div class="info" style="margin-bottom:8px">Contas deste mês que ainda não foram quitadas até a geração/fechamento do relatório.</div>
    <table><tr><th>Nome</th><th>Categoria</th><th>Vencimento</th><th style="text-align:right">Valor</th><th>Status</th></tr>${rows}<tr style="font-weight:700;background:#fef2f2"><td colspan="3">Total pendente</td><td style="text-align:right">${cur2(totalPendente)}</td><td></td></tr></table>`;
  })()}
  <div class="cards">
  </div>
  <h2>📋 Despesas do Mês</h2>
  ${qtdForaDoMes>0?`<div class="info" style="margin-top:-14px;color:#d97706;">⚠️ ${qtdForaDoMes} conta${qtdForaDoMes>1?"s":""} ${qtdForaDoMes>1?"aparecem":"aparece"} como paga aqui, mas ${qtdForaDoMes>1?"ainda não contam":"ainda não conta"} no card "Pago" nem no Saldo Operacional deste mês — normalmente são compras no cartão de crédito cuja fatura ainda não foi gerada/paga (o valor vai entrar no caixa quando a fatura for quitada, em um mês futuro).</div>`:""}
  <table><tr><th>Nome</th><th>Categoria</th><th>Valor</th><th>Vencimento</th><th>Data Pagamento</th><th>Status</th></tr>${despesaRows||"<tr><td colspan='6'>Nenhuma despesa</td></tr>"}</table>
  ${(()=>{
    // Compras no cartão do mês, agrupadas por cartão — é o que compõe a fatura do mês seguinte
    const compras=despesasMes.filter(d=>d.isCartaoAuto);
    if(compras.length===0)return "";
    const cartoes=data.profile?.cartoes||[];
    const grupos={};
    compras.forEach(d=>{const k=d.cartaoId||"sem";(grupos[k]=grupos[k]||[]).push(d);});
    const cur2=v=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);
    let rows="";let totalGeral=0;
    Object.entries(grupos).forEach(([cid,itens])=>{
      const nome=cartoes.find(c=>c.id===cid)?.nome||"Cartão";
      const tot=round2(itens.reduce((s,d)=>s+Number(d.valorMes||0),0));
      totalGeral+=tot;
      rows+=`<tr style="background:#f0f4ff;font-weight:700"><td colspan="3">💳 ${nome} (${itens.length} compra${itens.length>1?"s":""})</td><td style="text-align:right">${cur2(tot)}</td></tr>`;
      itens.slice().sort((a,b)=>a.dueDay-b.dueDay).forEach(d=>{rows+=`<tr><td style="padding-left:16px">${d.name}</td><td>${d.category}</td><td>Dia ${d.dueDay}</td><td style="text-align:right">${cur2(d.valorMes)}</td></tr>`;});
    });
    return `<h2>💳 Compras no Cartão</h2>
    <div class="info" style="margin-bottom:8px">Compras feitas no cartão neste mês. Elas formam a fatura que vence no mês seguinte — por isso não descontam do caixa agora.</div>
    <table><tr><th>Compra</th><th>Categoria</th><th>Dia</th><th style="text-align:right">Valor</th></tr>${rows}<tr style="font-weight:700;background:#eef2ff"><td colspan="3">Total no cartão</td><td style="text-align:right">${cur2(round2(totalGeral))}</td></tr></table>`;
  })()}
  <h2>💰 Entradas do Mês</h2>
  <table><tr><th>Nome</th><th>Categoria</th><th>Valor</th><th>Dia</th><th>Recebimento</th></tr>${entradaRows||"<tr><td colspan='5'>Nenhuma entrada</td></tr>"}</table>
  ${data.goals.length>0?`<h2>🎯 Metas Financeiras</h2><table><tr><th>Meta</th><th>Objetivo</th><th>Acumulado</th><th>Progresso</th><th>Prazo</th></tr>${goalRows}</table>`:""}
  ${(()=>{
    const cur2=v=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);
    // Ativos variáveis — agrupados por tipo (igual ao app)
    const ORDEM_PDF={"fii":1,"acao":2,"etf_br":3,"cripto":4,"acao_int":5,"etf_int":6,"moeda":7,"cofrinho":8,"outro":9};
    const LABEL_PDF={"fii":"🏢 FIIs","acao":"📊 Ações BR","etf_br":"📦 ETFs BR","cripto":"₿ Criptomoedas","acao_int":"🌎 Ações Internacionais","etf_int":"🌐 ETFs Internacionais","moeda":"💱 Moedas","cofrinho":"🐷 Cofrinhos","outro":"💰 Outros"};
    const EMOJI_PDF={"cripto":"₿","moeda":"💱","acao":"📊","fii":"🏢","etf_br":"📦","acao_int":"🌎","etf_int":"🌐","cofrinho":"🐷"};
    // Agrupar por tipo
    const gruposPDF={};
    investimentosMes.forEach(i=>{
      const t=i.tipo||"outro";
      if(!gruposPDF[t])gruposPDF[t]=[];
      gruposPDF[t].push(i);
    });
    const tiposOrdenadosPDF=Object.keys(gruposPDF).sort((a,b)=>(ORDEM_PDF[a]||9)-(ORDEM_PDF[b]||9));
    let ativosRows="";
    tiposOrdenadosPDF.forEach(tipo=>{
      const itens=gruposPDF[tipo];
      const totalGrupoInv=itens.reduce((s,i)=>s+i.quantidade*i.precoMedio,0);
      const totalGrupoAtual=itens.reduce((s,i)=>s+(i.precoAtual?i.quantidade*i.precoAtual:i.quantidade*i.precoMedio),0);
      const lucroGrupo=totalGrupoAtual-totalGrupoInv;
      const pctGrupo=totalGrupoInv>0?((lucroGrupo/totalGrupoInv)*100).toFixed(1):0;
      const lucroGrupoStr=lucroGrupo>=0?`<span style="color:#16a34a">+${cur2(lucroGrupo)} (+${pctGrupo}%)</span>`:`<span style="color:#dc2626">${cur2(lucroGrupo)} (${pctGrupo}%)</span>`;
      ativosRows+=`<tr style="background:#f0f4ff"><td colspan="5" style="font-weight:700;font-size:12px;padding:6px 8px;">${LABEL_PDF[tipo]||tipo} <span style="font-weight:400;color:#666">(${itens.length} ativo${itens.length>1?"s":""})</span></td><td style="text-align:right;font-weight:700">${cur2(totalGrupoAtual||totalGrupoInv)}</td><td>${lucroGrupoStr}</td></tr>`;
      itens.forEach(i=>{
        const vlInv=i.quantidade*i.precoMedio;
        const vlAtual=i.precoAtual?i.quantidade*i.precoAtual:null;
        const lucro=vlAtual!=null?vlAtual-vlInv:null;
        const lucroPct=vlAtual!=null&&vlInv>0?((lucro/vlInv)*100):null;
        const lucroStr=lucro!=null?(lucro>=0?`<span style="color:#16a34a">+${cur2(lucro)} (+${lucroPct.toFixed(1)}%)</span>`:`<span style="color:#dc2626">${cur2(lucro)} (${lucroPct.toFixed(1)}%)</span>`):`<span style="color:#888">—</span>`;
        const emoji=i.emoji&&i.emoji!=="💰"?i.emoji:(EMOJI_PDF[i.tipo]||"💰");
        ativosRows+=`<tr><td style="padding-left:16px">${emoji} ${i.nome}</td><td style="color:#666;font-size:11px">${i.quantidade} ${i.simbolo}</td><td style="color:#666;font-size:11px">${cur2(i.precoMedio)}/un.</td><td>${cur2(vlInv)}</td><td>${cur2(vlInv)}</td><td>${vlAtual!=null?cur2(vlAtual):"—"}</td><td>${lucroStr}</td></tr>`;
      });
    });
    // Renda fixa
    const rfRows=rendaFixaMes.map(r=>{
      const total=r.valorInvestido+(r.rendimentoAcumulado||0);
      return `<tr><td>${r.nome}</td><td>${r.tipo}</td><td>${cur2(r.valorInvestido)}</td><td style="color:#16a34a">+${cur2(r.rendimentoAcumulado||0)}</td><td>${cur2(total)}</td><td>${r.dataVencimento||"-"}</td></tr>`;
    }).join("");
    // Desafios/Cofrinhos
    const cofRows=desafiosMes.filter(d=>d.ativo&&(d.economiaTotal||0)>0).map(d=>{
      return `<tr><td>${d.emoji||"🏆"} ${d.nome}</td><td>${cur2(d.economiaTotal||0)}</td></tr>`;
    }).join("");
    // Totais
    const totalAtivos=investimentosMes.reduce((s,i)=>s+i.quantidade*i.precoMedio,0);
    const totalRF=rendaFixaMes.reduce((s,r)=>s+r.valorInvestido+(r.rendimentoAcumulado||0),0);
    // Cofrinhos já estão incluídos nos investimentos (tipo cofrinho) — NÃO somar novamente
    const totalPatrimonio=totalAtivos+totalRF;
    return `
    <h2>📊 Patrimônio — Pilar Guardar 5G</h2>
    <div class="cards" style="grid-template-columns:repeat(3,1fr)">
      <div class="card green"><div class="card-title">Total do Patrimônio</div><div class="card-value">${cur2(totalPatrimonio)}</div></div>
      <div class="card"><div class="card-title">Ativos Variáveis</div><div class="card-value">${cur2(totalAtivos)}</div></div>
      <div class="card"><div class="card-title">Renda Fixa</div><div class="card-value">${cur2(totalRF)}</div></div>
    </div>
    ${investimentosMes.length>0?`<h2>📈 Ativos Variáveis (Cripto, Ações, FIIs)</h2><table><tr><th>Nome</th><th>Qtd / Símbolo</th><th>Preço médio</th><th>Total investido</th><th></th><th>Valor atual*</th><th>Resultado</th></tr>${ativosRows}<tr style="font-size:10px;color:#888;background:#f9f9f9"><td colspan="7">${fechamento?"* Valores congelados no fechamento do mês (sem cotação de mercado em tempo real).":"* Valor atual baseado na cotação salva no momento da geração do relatório. Para cotações em tempo real, acesse a aba Investimentos no app."}</td></tr></table>`:""}
    ${rendaFixaMes.length>0?`<h2>🏦 Renda Fixa</h2><table><tr><th>Nome</th><th>Tipo</th><th>Investido</th><th>Rendimento</th><th>Total</th><th>Vencimento</th></tr>${rfRows}</table>`:""}
    ${cofRows?`<h2>🐷 Reservas e Cofrinhos</h2><table><tr><th>Desafio</th><th>Acumulado</th></tr>${cofRows}</table>`:""}
    `;
  })()}
  ${(()=>{
    // Resumo por categoria — mesma lógica do monthMetrics para evitar dupla contagem de cartão
    const catMap={};
    despesasMes.forEach(d=>{
      if(!catMap[d.category])catMap[d.category]={previsto:0,pago:0,pendente:0,count:0};
      // Conta paga ACIMA do previsto (ex.: fatura gerada R$ 400, paga R$ 1.400): vale o que foi pago
      const valorRef=d.status==="paid"&&d.pago>d.valorMes?d.pago:d.valorMes;
      catMap[d.category].previsto+=valorRef;
      catMap[d.category].count+=1;
      const pagoCaixaLimitado=Math.min(d.pago,valorRef);
      if(d.status==="paid"||d.status==="partial"){catMap[d.category].pago+=pagoCaixaLimitado;if(d.status==="partial")catMap[d.category].pendente+=Math.max(0,valorRef-pagoCaixaLimitado);}
      else catMap[d.category].pendente+=valorRef;
    });
    const cur2=v=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);
    const totalPrevisto=metricsMes.previsto||1;
    const catRows=Object.entries(catMap).sort((a,b)=>b[1].previsto-a[1].previsto).map(([cat,v])=>`<tr><td>${CATEGORY_ICONS[cat]||"📦"} ${cat}</td><td style="text-align:right">${cur2(v.previsto)}</td><td style="text-align:right;color:#16a34a">${cur2(v.pago)}</td><td style="text-align:right;color:#dc2626">${cur2(v.pendente)}</td><td style="text-align:right;color:#555">${Math.round((v.previsto/totalPrevisto)*100)}%</td><td style="text-align:center">${v.count}</td></tr>`).join("");
    // Resumo por pilar 5G (apenas despesas pagas)
    const pilarMap={};
    G5_PILARES.forEach(p=>{pilarMap[p.key]={label:p.label,emoji:p.emoji,color:p.color,total:0};});
    despesasMes.forEach(d=>{
      if(d.pago<=0)return;
      G5_PILARES.forEach(p=>{if(p.categorias.includes(d.category))pilarMap[p.key].total+=d.pago;});
    });
    // Entradas no Ganhar
    const totalEntradas=metricsMes.entradas||0;
    pilarMap["ganhar"].total=totalEntradas;
    const pilarRows=Object.values(pilarMap).map(p=>`<tr><td>${p.emoji} ${p.label}</td><td style="text-align:right;font-weight:bold;color:${p.color}">${cur2(p.total)}</td></tr>`).join("");
    // Alertas de vencimento nos próximos 3 dias — só faz sentido para o mês corrente (aberto)
    const todayD=new Date();
    const em3dias=fechamento?[]:despesasMes.filter(d=>{
      if(d.status==="paid")return false;
      const diff=d.dueDay-todayD.getDate();
      return diff>=0&&diff<=3;
    }).sort((a,b)=>a.dueDay-b.dueDay);
    const alertaRows=em3dias.map(d=>`<tr style="background:#fff3cd"><td>⚠️ ${d.name}</td><td>${d.category}</td><td style="text-align:right">${cur2(d.valorMes)}</td><td style="text-align:center">Dia ${d.dueDay} (${d.dueDay-todayD.getDate()===0?"hoje":`em ${d.dueDay-todayD.getDate()} dia${d.dueDay-todayD.getDate()>1?"s":""}`})</td></tr>`).join("");
    return `
    ${em3dias.length>0?`<h2 style="color:#d97706">⚠️ Atenção — Vencimentos Próximos</h2><table><tr><th>Nome</th><th>Categoria</th><th>Valor</th><th>Vencimento</th></tr>${alertaRows}</table>`:""}
    <h2>📊 Resumo por Categoria</h2>
    <table><tr><th>Categoria</th><th style="text-align:right">Previsto</th><th style="text-align:right">Pago</th><th style="text-align:right">Pendente</th><th style="text-align:right">% do total</th><th style="text-align:center">Qtd</th></tr>${catRows}</table>
    <h2>🌟 Resumo por Pilar 5G</h2>
    <table><tr><th>Pilar</th><th style="text-align:right">Valor</th></tr>${pilarRows}</table>
    `;
  })()}
  <div class="footer">Mapa Financeiro 5G • mapafinanceiro5g.com.br</div>
  </body></html>`;

  const nomeArquivo=`relatorio-${MONTHS_PT[currentMonth].toLowerCase()}-${currentYear}.pdf`;
  gerarPDFRelatorio(html,nomeArquivo);
}

// ─── Gera PDF de verdade (funciona no app Android e no navegador) ─────────
// Carrega o html2pdf sob demanda (não precisa npm install). No celular salva
// no cache e abre o menu "Compartilhar" do Android (Salvar no Drive, WhatsApp, Arquivos...).
// Se algo falhar, volta para o modo antigo (abrir e imprimir).
function carregarHtml2Pdf(){
  if(window.html2pdf)return Promise.resolve(window.html2pdf);
  return new Promise((resolve,reject)=>{
    const sc=document.createElement("script");
    sc.src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
    sc.onload=()=>window.html2pdf?resolve(window.html2pdf):reject(new Error("html2pdf indisponível"));
    sc.onerror=()=>reject(new Error("Falha ao carregar html2pdf"));
    document.head.appendChild(sc);
  });
}
function abrirRelatorioParaImprimir(html){
  const w=window.open("","_blank");
  if(w){w.document.write(html);w.document.close();setTimeout(()=>w.print(),500);}
  else window.alert("Permita pop-ups para gerar o relatório.");
}
async function gerarPDFRelatorio(html,nomeArquivo){
  const ehNativo=!!window.Capacitor?.isNativePlatform?.();
  // No computador/navegador: abre o relatório e a janela de impressão
  // ("Salvar como PDF") — fica com o layout perfeito, em tamanho A4.
  // O gerador de PDF por imagem é usado só no app Android, onde imprimir não funciona.
  if(!ehNativo){abrirRelatorioParaImprimir(html);return;}
  let container=null;
  try{
    const html2pdf=await carregarHtml2Pdf();
    // Monta o relatório fora da tela, em largura de folha A4
    const estilo=(html.match(/<style>([\s\S]*?)<\/style>/)||[])[1]||"";
    const corpo=(html.match(/<body>([\s\S]*)<\/body>/)||[])[1]||html;
    // O "esconderijo" fica num wrapper; o html2pdf recebe só o conteúdo interno
    // (se o próprio elemento tiver left:-10000px, a cópia que ele fotografa sai em branco).
    container=document.createElement("div");
    container.style.cssText="position:fixed;left:-10000px;top:0;";
    const conteudo=document.createElement("div");
    conteudo.className="pdf-root";
    conteudo.style.cssText="width:740px;box-sizing:border-box;margin:0;padding:0;background:#fff;color:#222;font-family:Arial,sans-serif;font-size:13px;";
    conteudo.innerHTML=`<style>${estilo.replace(/body\{/g,".pdf-root-ignorado{")}.pdf-root table{width:100%;}.pdf-root td,.pdf-root th{word-wrap:break-word;}</style>${corpo}`;
    container.appendChild(conteudo);
    document.body.appendChild(container);
    await new Promise(r=>setTimeout(r,150)); // deixa o navegador desenhar antes da "foto"
    // O WebView do Android tem limite de tamanho de imagem (canvas): relatório longo
    // em escala 2 passa do limite e a imagem sai em branco. Ajusta a escala ao tamanho.
    const largura=conteudo.scrollWidth||780, altura=conteudo.scrollHeight||1100;
    const limitePixels=ehNativo?10000000:30000000;
    const escala=Math.max(0.8,Math.min(2,Math.sqrt(limitePixels/(largura*altura))));
    const worker=html2pdf().set({
      margin:[8,8,8,8],
      filename:nomeArquivo,
      image:{type:"jpeg",quality:0.92},
      html2canvas:{scale:escala,useCORS:true,backgroundColor:"#ffffff",width:740,windowWidth:740,scrollX:0,scrollY:0},
      jsPDF:{unit:"mm",format:"a4",orientation:"portrait"},
      pagebreak:{mode:["css","legacy"],avoid:["tr",".card"]},
    }).from(conteudo);
    const dataUri=await worker.outputPdf("datauristring");
    const base64=dataUri.split(",")[1];
    const {Filesystem,Share}=window.Capacitor.Plugins||{};
    if(!Filesystem||!Share) throw new Error("Plugins Filesystem/Share indisponíveis");
    const res=await Filesystem.writeFile({path:nomeArquivo,data:base64,directory:"CACHE"});
    await Share.share({title:nomeArquivo,url:res.uri,dialogTitle:"Salvar ou enviar relatório"});
  }catch(err){
    console.warn("PDF direto falhou:",err);
    window.alert("Não foi possível gerar o PDF. Verifique a conexão e tente de novo.");
  }finally{
    if(container)container.remove();
  }
}

// ─── Componentes UI ───────────────────────────────────────
function AppCard({title,value,sub,tone="default",dark=true}){
  return(
    <div className={`stat-card ${tone}`}>
      <div className="stat-title">{title}</div>
      <div className="stat-value-wrap"><div className="stat-value">{value}</div></div>
      {sub?<div className="stat-sub">{sub}</div>:null}
    </div>
  );
}

function Modal({open,onClose,title,children,width=760}){
  useEffect(()=>{
    if(!open)return undefined;
    function onKeyDown(e){if(e.key==="Escape")onClose();}
    window.addEventListener("keydown",onKeyDown);
    return()=>window.removeEventListener("keydown",onKeyDown);
  },[open,onClose]);
  if(!open)return null;
  return(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-shell" style={{maxWidth:width}} onClick={(e)=>e.stopPropagation()}>
        <div className="modal-header"><h3>{title}</h3><button className="icon-btn" onClick={onClose} aria-label="Fechar">✕</button></div>
        <div className="modal-body scrollable-modal">{children}</div>
      </div>
    </div>
  );
}

function AlertaBanner({bills, currentYear, currentMonth}){
  const em1dia=getUpcomingBills(bills,1);
  const em7dias=getUpcomingBills(bills,7).filter(b=>!em1dia.some(x=>x.id===b.id));
  const[expandir7,setExpandir7]=React.useState(false);
  // Usar valor ajustado do mês corrente
  const getValor=(b)=>{
    const target=new Date();target.setDate(target.getDate()+1);
    const mk=monthKey(target.getFullYear(),target.getMonth());
    return getBillAmountForMonth(b,mk);
  };
  const getValor7=(b)=>{
    const mk=monthKey(currentYear,currentMonth);
    return getBillAmountForMonth(b,mk);
  };
  const totalEm7=em7dias.reduce((s,b)=>s+getValor7(b),0);
  if(em1dia.length===0&&em7dias.length===0)return null;
  return(
    <div className="alerta-banner">
      {em1dia.map((b)=>(<div key={b.id} className="alerta-item alerta-urgente"><span>🔴</span><span><strong>{b.name}</strong> vence <strong>amanhã</strong> (dia {b.dueDay}) — {currency(getValor(b))}</span></div>))}
      {em7dias.length>0&&(
        <div>
          <div className="alerta-item alerta-aviso" style={{cursor:"pointer",userSelect:"none"}} onClick={()=>setExpandir7(e=>!e)}>
            <span>🟡</span>
            <span><strong>{em7dias.length} conta{em7dias.length>1?"s":""}</strong> vencem nos próximos 7 dias — {currency(totalEm7)}</span>
            <span style={{marginLeft:"auto",fontSize:12}}>{expandir7?"▲ ocultar":"▼ ver"}</span>
          </div>
          {expandir7&&em7dias.map((b)=>(<div key={b.id} className="alerta-item alerta-aviso" style={{paddingLeft:32,opacity:0.85}}><span>📅</span><span>{b.name} <span style={{color:"var(--text-muted)"}}>dia {b.dueDay}</span> — {currency(getValor7(b))}</span></div>))}
        </div>
      )}
    </div>
  );
}

// ─── Gráficos ─────────────────────────────────────────────
function polarToCartesian(cx,cy,r,a){const rad=((a-90)*Math.PI)/180;return{x:cx+r*Math.cos(rad),y:cy+r*Math.sin(rad)};}
function describeArc(cx,cy,r,s,e){const start=polarToCartesian(cx,cy,r,e),end=polarToCartesian(cx,cy,r,s),laf=e-s<=180?"0":"1";return["M",start.x,start.y,"A",r,r,0,laf,0,end.x,end.y].join(" ");}

function DonutChart({data}){
  const width=360,height=360,cx=180,cy=180,r=98;
  const total=Math.max(1,data.reduce((sum,d)=>sum+d.value,0));
  let angle=0;
  return(
    <div className="donut-wrap">
      <svg viewBox={`0 0 ${width} ${height}`} className="chart-svg">
        <circle cx={cx} cy={cy} r={r} className="donut-track"/>
        {data.map((item,i)=>{const p=(item.value/total)*360,s=angle,e=angle+p;angle=e;return<path key={item.label} d={describeArc(cx,cy,r,s,e)} stroke={CHART_COLORS[i%CHART_COLORS.length]} strokeWidth="34" fill="none" strokeLinecap="butt"/>;  })}
        <circle cx={cx} cy={cy} r="58" className="donut-hole"/>
        <text x={cx} y={cy-6} textAnchor="middle" className="donut-center-title">Total</text>
        <text x={cx} y={cy+20} textAnchor="middle" className="donut-center-value">{currency(data.reduce((s,d)=>s+d.value,0))}</text>
      </svg>
      <div className="legend">{data.map((item,i)=>{const share=total>0?round2((item.value/total)*100):0;return(<div className="legend-row" key={item.label}><span className="legend-swatch" style={{background:CHART_COLORS[i%CHART_COLORS.length]}}/><span className="legend-name">{item.label}</span><span className="legend-value">{currency(item.value)} • {percent(share)}</span></div>);})}</div>
    </div>
  );
}

function LineChart({points,color="#7C83FF",label}){
  const width=620,height=240,padding=40;
  if(!points.length)return<div className="empty">Sem dados para este período.</div>;
  const max=Math.max(1,...points.map((p)=>p.value));
  const uW=width-padding*2,uH=height-padding*2;
  const coords=points.map((p,i)=>({...p,x:padding+(points.length===1?uW/2:(i/(points.length-1))*uW),y:height-padding-(p.value/max)*uH}));
  const d=coords.map((p,i)=>`${i===0?"M":"L"} ${p.x} ${p.y}`).join(" ");
  return(
    <svg viewBox={`0 0 ${width} ${height}`} className="chart-svg">
      <line x1={padding} y1={padding} x2={padding} y2={height-padding} className="axis"/>
      <line x1={padding} y1={height-padding} x2={width-padding} y2={height-padding} className="axis"/>
      <path d={d} fill="none" stroke={color} strokeWidth="3"/>
      {coords.map((p)=>(<g key={p.label}><circle cx={p.x} cy={p.y} r="5" fill={color}/><text x={p.x} y={p.y-10} textAnchor="middle" className="bar-value">{currency(p.value)}</text><text x={p.x} y={height-10} textAnchor="middle" className="bar-label">{p.label}</text></g>))}
    </svg>
  );
}

// Gráfico de barras horizontais: média mensal x mês selecionado, por categoria (top 10)
function MediaChart({itens,labelMes}){
  const lista=itens.slice(0,10);
  const width=700,labelW=190,rightPad=95,rowH=46,top=8;
  const height=top+lista.length*rowH+34;
  const barMax=width-labelW-rightPad;
  const max=Math.max(...lista.map((i)=>Math.max(i.media,i.atual)),1);
  const corta=(t)=>t.length>24?t.slice(0,23)+"…":t;
  return(
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className="chart-svg">
        {lista.map((i,idx)=>{
          const y=top+idx*rowH;
          const wMedia=Math.max(2,(i.media/max)*barMax);
          const wAtual=i.atual>0?Math.max(2,(i.atual/max)*barMax):0;
          const acima=i.atual>i.media;
          return(
            <g key={i.category}>
              <text x={labelW-10} y={y+22} textAnchor="end" className="bar-label" style={{fontSize:13}}>{corta(i.category)}</text>
              <rect x={labelW} y={y+6} width={wMedia} height={14} rx="4" fill="#6366F1"/>
              <text x={labelW+wMedia+6} y={y+17} className="bar-value" style={{fontSize:12}}>{currency(i.media)}</text>
              <rect x={labelW} y={y+23} width={wAtual} height={14} rx="4" fill={acima?"#F59E0B":"#22C55E"}/>
              <text x={labelW+wAtual+6} y={y+34} className="bar-value" style={{fontSize:12}}>{currency(i.atual)}</text>
            </g>
          );
        })}
        <g>
          <rect x={labelW} y={height-22} width="12" height="12" rx="3" fill="#6366F1"/>
          <text x={labelW+18} y={height-12} className="bar-label" style={{fontSize:12}}>Média mensal</text>
          <rect x={labelW+130} y={height-22} width="12" height="12" rx="3" fill="#22C55E"/>
          <text x={labelW+148} y={height-12} className="bar-label" style={{fontSize:12}}>{labelMes} (verde: abaixo da média · laranja: acima)</text>
        </g>
      </svg>
      {itens.length>10&&<div className="muted" style={{fontSize:12,marginTop:6}}>Mostrando as 10 maiores categorias. Veja todas na lista.</div>}
    </div>
  );
}

// ─── Aba Evolução ─────────────────────────────────────────
function EvolucaoTab({evolutionData}){
  const[cat1,setCat1]=useState("");
  const[cat2,setCat2]=useState("");
  const[modo,setModo]=useState("unico");
  const[zoom,setZoom]=useState(null); // {title,points,color}
  const cats=evolutionData.map((s)=>s.category);
  useEffect(()=>{if(evolutionData.length>0&&!cat1)setCat1(evolutionData[0].category);},[evolutionData,cat1]);
  const s1=evolutionData.find((s)=>s.category===cat1);
  const s2=evolutionData.find((s)=>s.category===cat2);
  const i1=evolutionData.findIndex((s)=>s.category===cat1);
  const i2=evolutionData.findIndex((s)=>s.category===cat2);
  if(evolutionData.length===0)return<div className="empty">Ainda não há histórico suficiente.</div>;
  return(
    <section className="section-card">
      <h2 className="section-title">Evolução das despesas por categoria</h2>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
        <button className={`nav-btn ${modo==="unico"?"active":""}`} onClick={()=>setModo("unico")}>📊 Ver categoria</button>
        <button className={`nav-btn ${modo==="comparar"?"active":""}`} onClick={()=>setModo("comparar")}>⚖️ Comparar duas</button>
      </div>
      {modo==="unico"&&(<>
        <div style={{marginBottom:14}}><select className="select" value={cat1} onChange={(e)=>setCat1(e.target.value)} style={{maxWidth:280}}>{cats.map((c)=><option key={c} value={c}>{CATEGORY_ICONS[c]||"📦"} {c}</option>)}</select></div>
        {s1?<div className="chart-card" onClick={()=>setZoom({title:cat1,points:s1.points,color:CHART_COLORS[i1%CHART_COLORS.length]})} style={{cursor:"zoom-in"}}><div className="chart-title">{CATEGORY_ICONS[cat1]||"📦"} {cat1} <span style={{fontSize:11,fontWeight:400,color:"var(--text-muted)"}}>🔍 toque para ampliar</span></div><LineChart points={s1.points} color={CHART_COLORS[i1%CHART_COLORS.length]} label={cat1}/></div>:<div className="empty">Sem dados.</div>}
      </>)}
      {modo==="comparar"&&(<>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div><label className="label" style={{marginBottom:6,display:"block"}}>Categoria 1</label><select className="select" value={cat1} onChange={(e)=>setCat1(e.target.value)}>{cats.map((c)=><option key={c} value={c}>{CATEGORY_ICONS[c]||"📦"} {c}</option>)}</select></div>
          <div><label className="label" style={{marginBottom:6,display:"block"}}>Categoria 2</label><select className="select" value={cat2} onChange={(e)=>setCat2(e.target.value)}><option value="">-- Selecione --</option>{cats.filter((c)=>c!==cat1).map((c)=><option key={c} value={c}>{CATEGORY_ICONS[c]||"📦"} {c}</option>)}</select></div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:cat2?"1fr 1fr":"1fr",gap:14}}>
          {s1&&<div className="chart-card" onClick={()=>setZoom({title:cat1,points:s1.points,color:CHART_COLORS[i1%CHART_COLORS.length]})} style={{cursor:"zoom-in"}}><div className="chart-title" style={{color:CHART_COLORS[i1%CHART_COLORS.length]}}>{CATEGORY_ICONS[cat1]||"📦"} {cat1} <span style={{fontSize:11,fontWeight:400,color:"var(--text-muted)"}}>🔍</span></div><LineChart points={s1.points} color={CHART_COLORS[i1%CHART_COLORS.length]} label={cat1}/></div>}
          {cat2&&s2&&<div className="chart-card" onClick={()=>setZoom({title:cat2,points:s2.points,color:CHART_COLORS[i2%CHART_COLORS.length]})} style={{cursor:"zoom-in"}}><div className="chart-title" style={{color:CHART_COLORS[i2%CHART_COLORS.length]}}>{CATEGORY_ICONS[cat2]||"📦"} {cat2} <span style={{fontSize:11,fontWeight:400,color:"var(--text-muted)"}}>🔍</span></div><LineChart points={s2.points} color={CHART_COLORS[i2%CHART_COLORS.length]} label={cat2}/></div>}
          {!cat2&&<div className="empty">Selecione a segunda categoria para comparar.</div>}
        </div>
      </>)}
      <Modal open={!!zoom} onClose={()=>setZoom(null)} title={zoom?`${CATEGORY_ICONS[zoom.title]||"📦"} ${zoom.title}`:""} width={920}>
        {zoom&&<LineChart points={zoom.points} color={zoom.color} label={zoom.title}/>}
      </Modal>
    </section>
  );
}

// ─── Aba Metas ────────────────────────────────────────────
function MetasTab({goals,onSave,onDelete,onAddValue,onResgate}){
  const emptyGoalForm={name:"",targetAmount:"",currentAmount:"",deadline:"",color:CHART_COLORS[0]};
  const[form,setForm]=useState(emptyGoalForm);
  const[editId,setEditId]=useState(null);
  const[open,setOpen]=useState(false);
  const[addValueModal,setAddValueModal]=useState(null);
  const[addValue,setAddValue]=useState("");
  const[resgateMetaModal,setResgateMetaModal]=useState(null);
  const[resgateMetaValor,setResgateMetaValor]=useState("");

  function handleResgateMeta(e){
    e.preventDefault();
    const v=numberFromInput(resgateMetaValor);
    if(v<=0||!resgateMetaModal)return;
    const novoAcumulado=round2(Math.max(0,resgateMetaModal.currentAmount-v));
    const now=new Date();
    const mesKey=`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    const registros=[...(resgateMetaModal.registros||[]),{data:now.toISOString(),mes:mesKey,valor:round2(-v)}];
    onSave({...resgateMetaModal,currentAmount:novoAcumulado,registros});
    setResgateMetaModal(null);
    setResgateMetaValor("");
  }

  function openNew(){try{const saved=localStorage.getItem("mf5g_goal_draft");if(saved){const p=JSON.parse(saved);if(p.name||p.targetAmount)setForm({...emptyGoalForm,...p});else setForm(emptyGoalForm);}else setForm(emptyGoalForm);}catch{setForm(emptyGoalForm);}setEditId(null);setOpen(true);}
  function openEdit(g){setForm({name:g.name,targetAmount:String(g.targetAmount).replace(".",","),currentAmount:String(g.currentAmount).replace(".",","),deadline:g.deadline||"",color:g.color||CHART_COLORS[0]});setEditId(g.id);setOpen(true);}
  function handleSave(e){
    e.preventDefault();
    const target=numberFromInput(form.targetAmount);
    const current=numberFromInput(form.currentAmount);
    if(!form.name.trim()||target<=0)return;
    try{localStorage.removeItem("mf5g_goal_draft");}catch{}
    onSave({id:editId||uid(),name:form.name.trim(),targetAmount:target,currentAmount:current,deadline:form.deadline,color:form.color,createdAt:editId?goals.find((g)=>g.id===editId)?.createdAt||new Date().toISOString():new Date().toISOString()});
    setOpen(false);
  }
  function handleAddValue(e){
    e.preventDefault();
    const v=numberFromInput(addValue);
    if(v>0&&addValueModal){onAddValue(addValueModal.id,v);setAddValueModal(null);setAddValue("");}
  }

  function updateGoalField(k,v){setForm((p)=>{const next={...p,[k]:v};if(!editId){try{localStorage.setItem("mf5g_goal_draft",JSON.stringify(next));}catch{}}return next;});}
  const[buscaMeta,setBuscaMeta]=useState("");
  const goalsFiltradas=goals.filter(g=>g.name.toLowerCase().includes(buscaMeta.toLowerCase()));
  return(
    <section className="section-card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",marginBottom:14}}>
        <h2 className="section-title" style={{margin:0}}>🎯 Metas Financeiras</h2>
        <button className="primary-btn" onClick={openNew}>+ Nova meta</button>
      </div>
      {goals.length>0&&<input className="input" placeholder="🔍 Buscar meta..." value={buscaMeta} onChange={e=>setBuscaMeta(e.target.value)} style={{marginBottom:12}}/>}
      {goals.length===0?<div className="empty">Nenhuma meta cadastrada ainda.<br/>Defina um objetivo financeiro para começar!</div>:(
        <div className="goals-list">
          {goalsFiltradas.length===0?<div className="empty">Nenhuma meta encontrada para "{buscaMeta}".</div>:goalsFiltradas.map((g)=>{
            const prog=g.targetAmount>0?Math.min(100,round2((g.currentAmount/g.targetAmount)*100)):0;
            const restante=round2(Math.max(0,g.targetAmount-g.currentAmount));
            const concluida=g.currentAmount>=g.targetAmount;
            return(
              <div key={g.id} className={`goal-card ${concluida?"goal-done":""}`}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontSize:17,fontWeight:700,color:"var(--text-primary)",display:"flex",alignItems:"center",gap:8}}>
                      {concluida?"🏆":"🎯"} {g.name}
                      {concluida&&<span className="badge green">Concluída!</span>}
                    </div>
                    {g.deadline&&<div style={{fontSize:12,color:"var(--text-muted)",marginTop:4}}>Prazo: {new Date(g.deadline+"T00:00:00").toLocaleDateString("pt-BR")}</div>}
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:18,fontWeight:800,color:g.color}}>{currency(g.currentAmount)}</div>
                    <div style={{fontSize:12,color:"var(--text-muted)"}}>de {currency(g.targetAmount)}</div>
                  </div>
                </div>
                <div style={{margin:"10px 0 4px"}}>
                  <div style={{height:10,borderRadius:999,background:"var(--bg-input)",overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${prog}%`,background:g.color,borderRadius:999,transition:"width .4s"}}/>
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"var(--text-muted)",marginBottom:10}}>
                  <span>{percent(prog)} concluído</span>
                  {!concluida&&<span>Faltam {currency(restante)}</span>}
                </div>
                {(g.registros||[]).length>0&&(
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--text-muted)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em"}}>Histórico de aportes</div>
                    <div style={{display:"flex",flexDirection:"column",gap:3,maxHeight:96,overflowY:"auto"}}>
                      {[...(g.registros||[])].reverse().map((r,i)=>(
                        <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"var(--text-muted)",background:"var(--bg-input)",borderRadius:6,padding:"4px 8px"}}>
                          <span>{new Date(r.data).toLocaleDateString("pt-BR",{month:"short",year:"numeric"})}</span>
                          <span style={{fontWeight:700,color:r.valor>=0?"#22c55e":"#ef4444"}}>{r.valor>=0?"+":""}{currency(Math.abs(r.valor))}{r.valor<0?" (resgate)":""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {!concluida&&<button className="action-btn" onClick={()=>{setAddValueModal(g);setAddValue("");}}>+ Adicionar valor</button>}
                  {g.currentAmount>0&&<button className="action-btn" style={{background:"#EF444420",border:"1px solid #EF444444",color:"#EF4444"}} onClick={()=>{setResgateMetaModal(g);setResgateMetaValor("");}}>💸 Resgatar</button>}
                  <button className="secondary-btn" onClick={()=>openEdit(g)}>✏️ Editar</button>
                  <button className="secondary-btn" onClick={()=>onDelete(g.id)}>🗑️ Excluir</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={()=>{if(!editId){try{localStorage.removeItem("mf5g_goal_draft");}catch{}}setOpen(false);}} title={editId?"Editar meta":"Nova meta"} width={500}>
        <form onSubmit={handleSave}>
          <div className="form-grid">
            <div className="field full"><label className="label">Nome da meta</label><input className="input" value={form.name} onChange={(e)=>updateGoalField("name",e.target.value)} placeholder="Ex.: Viagem, Carro, Reserva..."/></div>
            <div className="field"><label className="label">Valor objetivo (R$)</label><input className="input" type="text" inputMode="decimal" value={form.targetAmount} onChange={(e)=>updateGoalField("targetAmount",e.target.value)} placeholder="Ex.: 5.000,00"/></div>
            <div className="field"><label className="label">Já tenho (R$)</label><input className="input" type="text" inputMode="decimal" value={form.currentAmount} onChange={(e)=>updateGoalField("currentAmount",e.target.value)} placeholder="Ex.: 500,00"/></div>
            <div className="field"><label className="label">Prazo (opcional)</label><input className="input" type="date" value={form.deadline} onChange={(e)=>updateGoalField("deadline",e.target.value)}/></div>
            <div className="field"><label className="label">Cor</label><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{CHART_COLORS.slice(0,10).map((c)=><button key={c} type="button" onClick={()=>updateGoalField("color",c)} style={{width:28,height:28,borderRadius:"50%",background:c,border:form.color===c?"3px solid #fff":"2px solid transparent",cursor:"pointer"}}/> )}</div></div>
          </div>
          <div className="actions"><button type="button" className="ghost-btn" onClick={()=>{try{localStorage.removeItem("mf5g_goal_draft");}catch{}setOpen(false);}}>Cancelar</button><button type="submit" className="primary-btn">Salvar meta</button></div>
        </form>
      </Modal>

      <Modal open={!!addValueModal} onClose={()=>setAddValueModal(null)} title={`Adicionar valor — ${addValueModal?.name}`} width={400}>
        <form onSubmit={handleAddValue}>
          <div className="field"><label className="label">Valor a adicionar (R$)</label><input className="input" type="text" inputMode="decimal" value={addValue} onChange={(e)=>setAddValue(e.target.value)} placeholder="Ex.: 200,00" autoFocus/></div>
          <div style={{fontSize:12,color:"var(--text-muted)",background:"#14B8A618",border:"1px solid #14B8A630",borderRadius:8,padding:"8px 12px",marginBottom:14,lineHeight:1.6}}>
            📊 Informe quanto você guardou este mês. O valor será adicionado ao progresso da meta — você guarda onde quiser (poupança, cofrinho, qualquer lugar).
          </div>
          <div className="actions"><button type="button" className="ghost-btn" onClick={()=>setAddValueModal(null)}>Cancelar</button><button type="submit" className="primary-btn">Adicionar</button></div>
        </form>
      </Modal>

      <Modal open={!!resgateMetaModal} onClose={()=>setResgateMetaModal(null)} title={`💸 Resgatar — ${resgateMetaModal?.name}`} width={400}>
        <form onSubmit={handleResgateMeta}>
          <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:8,padding:"10px 12px",marginBottom:14,fontSize:13}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
              <span style={{color:"var(--text-muted)"}}>Acumulado na meta</span>
              <span style={{fontWeight:700,color:"#14B8A6"}}>{currency(resgateMetaModal?.currentAmount||0)}</span>
            </div>
          </div>
          <div className="field">
            <label className="label">Valor a resgatar (R$)</label>
            <input className="input" type="text" inputMode="decimal" value={resgateMetaValor} onChange={(e)=>setResgateMetaValor(e.target.value)} placeholder="Ex.: 200,00" autoFocus/>
            {numberFromInput(resgateMetaValor)>0&&(
              <div style={{fontSize:12,color:"var(--text-muted)",marginTop:4}}>
                Meta ficará com: <strong style={{color:"#14B8A6"}}>{currency(Math.max(0,(resgateMetaModal?.currentAmount||0)-numberFromInput(resgateMetaValor)))}</strong>
              </div>
            )}
          </div>
          <div style={{fontSize:12,color:"var(--text-muted)",background:"#14B8A618",border:"1px solid #14B8A630",borderRadius:8,padding:"8px 12px",marginBottom:14,lineHeight:1.6}}>
            📊 O valor será deduzido do progresso da meta. O resgate aparece no histórico.
          </div>
          <div className="actions">
            <button type="button" className="ghost-btn" onClick={()=>setResgateMetaModal(null)}>Cancelar</button>
            <button type="submit" className="primary-btn">Confirmar resgate</button>
          </div>
        </form>
      </Modal>
    </section>
  );
}

// ─── Aba Orçamento ────────────────────────────────────────
function OrcamentoTab({budgets,onSave,onDelete,spentByCategory,mediaCategorias}){
  const[form,setForm]=useState({category:EXPENSE_CATEGORIES[0],limitAmount:""});
  const[editId,setEditId]=useState(null);
  const[open,setOpen]=useState(false);

  function openNew(){setForm({category:EXPENSE_CATEGORIES[0],limitAmount:""});setEditId(null);setOpen(true);}
  function openEdit(b){setForm({category:b.category,limitAmount:String(b.limitAmount).replace(".",",")});setEditId(b.id);setOpen(true);}
  function handleSave(e){
    e.preventDefault();
    const limit=numberFromInput(form.limitAmount);
    if(limit<=0)return;
    onSave({id:editId||uid(),category:form.category,limitAmount:limit});
    setOpen(false);
  }

  const categoriasComLimite=new Set(budgets.map((b)=>b.category));
  const[buscaOrcamento,setBuscaOrcamento]=useState("");
  const[modoMedia,setModoMedia]=useState("grafico");
  const budgetsFiltrados=budgets.filter(b=>b.category.toLowerCase().includes(buscaOrcamento.toLowerCase()));

  return(
    <section className="section-card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",marginBottom:14}}>
        <h2 className="section-title" style={{margin:0}}>💰 Orçamento por Categoria</h2>
        <button className="primary-btn" onClick={openNew}>+ Definir limite</button>
      </div>
      {budgets.length>0&&<input className="input" placeholder="🔍 Buscar categoria..." value={buscaOrcamento} onChange={e=>setBuscaOrcamento(e.target.value)} style={{marginBottom:12}}/>}
      {budgets.length===0?<div className="empty">Nenhum orçamento definido.<br/>Defina limites de gastos por categoria para controlar melhor suas despesas!</div>:(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {budgetsFiltrados.length===0?<div className="empty">Nenhuma categoria encontrada para "{buscaOrcamento}".</div>:budgetsFiltrados.map((b)=>{
            const gasto=spentByCategory[b.category]||0;
            const prog=b.limitAmount>0?Math.min(100,round2((gasto/b.limitAmount)*100)):0;
            const estourado=gasto>b.limitAmount;
            const cor=estourado?"#EF4444":prog>80?"#F59E0B":"#22C55E";
            return(
              <div key={b.id} className="budget-card">
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:8}}>
                  <div style={{fontWeight:700,fontSize:16,color:"var(--text-primary)"}}>{CATEGORY_ICONS[b.category]||"📦"} {b.category}</div>
                  <div style={{textAlign:"right"}}>
                    <span style={{fontWeight:800,fontSize:16,color:cor}}>{currency(gasto)}</span>
                    <span style={{color:"var(--text-muted)",fontSize:13}}> / {currency(b.limitAmount)}</span>
                    {estourado&&<span className="badge red" style={{marginLeft:8}}>Estourado!</span>}
                    {!estourado&&prog>80&&<span className="badge yellow" style={{marginLeft:8}}>Atenção</span>}
                  </div>
                </div>
                <div style={{height:8,borderRadius:999,background:"var(--bg-input)",overflow:"hidden",marginBottom:6}}>
                  <div style={{height:"100%",width:`${prog}%`,background:cor,borderRadius:999,transition:"width .4s"}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,color:"var(--text-muted)"}}>
                  <span>{percent(prog)} usado</span>
                  <div style={{display:"flex",gap:6}}>
                    <button className="icon-btn" style={{width:28,height:28,fontSize:13}} onClick={()=>openEdit(b)}>✏️</button>
                    <button className="icon-btn" style={{width:28,height:28,fontSize:13}} onClick={()=>onDelete(b.id)}>🗑️</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {mediaCategorias&&mediaCategorias.itens.length>0&&(()=>{
        const maxMedia=Math.max(...mediaCategorias.itens.map((i)=>Math.max(i.media,i.atual)),1);
        const temLimite=new Set(budgets.map((b)=>b.category));
        return(
          <div style={{marginTop:24}}>
            <h3 className="section-title" style={{margin:0,fontSize:17}}>📊 Média mensal por categoria</h3>
            <div className="muted" style={{fontSize:12,margin:"4px 0 14px"}}>
              Média dos últimos {mediaCategorias.meses} {mediaCategorias.meses===1?"mês fechado":"meses fechados"} com movimentação, comparada ao mês selecionado.
            </div>
            {(()=>{
              const totalMedia=round2(mediaCategorias.itens.reduce((sum,i)=>sum+i.media,0));
              const totalAtual=round2(mediaCategorias.itens.reduce((sum,i)=>sum+i.atual,0));
              const difTotal=totalMedia>0?round2(((totalAtual-totalMedia)/totalMedia)*100):null;
              const acimaTotal=totalAtual>totalMedia;
              return(
                <div className="budget-card" style={{marginBottom:14}}>
                  <div className="muted" style={{fontSize:12,marginBottom:4}}>Média mensal total das despesas</div>
                  <div style={{fontWeight:800,fontSize:26,color:"var(--text-primary)"}}>{currency(totalMedia)}</div>
                  <div style={{fontSize:12,color:"var(--text-muted)",marginTop:6}}>
                    Este mês: {currency(totalAtual)}
                    {difTotal!==null&&<span style={{marginLeft:6,color:acimaTotal?"#F59E0B":"#22C55E",fontWeight:700}}>{difTotal>0?"+":""}{String(difTotal).replace(".",",")}% vs média</span>}
                  </div>
                </div>
              );
            })()}
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              <button className={`nav-btn ${modoMedia==="grafico"?"active":""}`} onClick={()=>setModoMedia("grafico")}>📊 Gráfico</button>
              <button className={`nav-btn ${modoMedia==="lista"?"active":""}`} onClick={()=>setModoMedia("lista")}>📋 Lista</button>
            </div>
            {modoMedia==="grafico"&&<div className="chart-card"><MediaChart itens={mediaCategorias.itens} labelMes="Este mês"/></div>}
            {modoMedia==="lista"&&<div style={{display:"flex",flexDirection:"column",gap:10}}>
              {mediaCategorias.itens.map((i)=>{
                const dif=i.media>0?round2(((i.atual-i.media)/i.media)*100):null;
                const acima=i.atual>i.media;
                return(
                  <div key={i.category} className="budget-card">
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:8}}>
                      <div style={{fontWeight:700,color:"var(--text-primary)"}}>{CATEGORY_ICONS[i.category]||"📦"} {i.category}</div>
                      <div style={{textAlign:"right",fontSize:13}}>
                        <span style={{fontWeight:800,color:"var(--text-primary)"}}>{currency(i.media)}</span>
                        <span style={{color:"var(--text-muted)"}}> /mês em média</span>
                      </div>
                    </div>
                    <div style={{height:8,borderRadius:999,background:"var(--bg-input)",overflow:"hidden",marginBottom:4}}>
                      <div style={{height:"100%",width:`${(i.media/maxMedia)*100}%`,background:"#6366F1",borderRadius:999}}/>
                    </div>
                    <div style={{height:8,borderRadius:999,background:"var(--bg-input)",overflow:"hidden",marginBottom:6}}>
                      <div style={{height:"100%",width:`${(i.atual/maxMedia)*100}%`,background:acima?"#F59E0B":"#22C55E",borderRadius:999}}/>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,fontSize:12,color:"var(--text-muted)"}}>
                      <span>
                        Este mês: {currency(i.atual)}
                        {dif!==null&&<span style={{marginLeft:6,color:acima?"#F59E0B":"#22C55E",fontWeight:700}}>{dif>0?"+":""}{String(dif).replace(".",",")}% vs média</span>}
                      </span>
                      {!temLimite.has(i.category)&&i.media>0&&(
                        <button className="ghost-btn" style={{fontSize:12,padding:"4px 10px"}} onClick={()=>onSave({id:uid(),category:i.category,limitAmount:i.media})}>Usar média como limite</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>}
          </div>
        );
      })()}
      <Modal open={open} onClose={()=>setOpen(false)} title={editId?"Editar orçamento":"Novo orçamento"} width={440}>
        <form onSubmit={handleSave}>
          <div className="form-grid">
            <div className="field full"><label className="label">Categoria</label>
              <select className="select" value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})}>
                {EXPENSE_CATEGORIES.map((c)=><option key={c} value={c} disabled={!editId&&categoriasComLimite.has(c)}>{CATEGORY_ICONS[c]||"📦"} {c}{!editId&&categoriasComLimite.has(c)?" (já tem limite)":""}</option>)}
              </select>
            </div>
            <div className="field full"><label className="label">Limite mensal (R$)</label><input className="input" type="text" inputMode="decimal" value={form.limitAmount} onChange={(e)=>setForm({...form,limitAmount:e.target.value})} placeholder="Ex.: 500,00"/></div>
          </div>
          <div className="actions"><button type="button" className="ghost-btn" onClick={()=>setOpen(false)}>Cancelar</button><button type="submit" className="primary-btn">Salvar</button></div>
        </form>
      </Modal>
    </section>
  );
}

// ─── Aba Perfil ───────────────────────────────────────────
// ─── Componente trocar senha ─────────────────────────────────────────────────
function TrocarSenha(){
  const[open,setOpen]=React.useState(false);
  const[novaSenha,setNovaSenha]=React.useState("");
  const[confirmar,setConfirmar]=React.useState("");
  const[loading,setLoading]=React.useState(false);
  const[msg,setMsg]=React.useState("");
  const[erro,setErro]=React.useState("");

  async function handleTrocar(e){
    e.preventDefault();
    if(novaSenha.length<6){setErro("A nova senha deve ter pelo menos 6 caracteres.");return;}
    if(novaSenha!==confirmar){setErro("As senhas não coincidem.");return;}
    setLoading(true);setErro("");setMsg("");
    const{error}=await supabase.auth.updateUser({password:novaSenha});
    setLoading(false);
    if(error){setErro("Erro: "+error.message);}
    else{
      setMsg("✅ Senha alterada com sucesso!");
      setNovaSenha("");setConfirmar("");
      setTimeout(()=>{setMsg("");setOpen(false);},3000);
    }
  }

  return(
    <div style={{marginBottom:16}}>
      <button type="button" onClick={()=>{setOpen(!open);setErro("");setMsg("");}}
        style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 16px",color:"var(--text-primary)",fontWeight:600,fontSize:13,cursor:"pointer",width:"100%",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span>🔐 Alterar senha</span>
        <span style={{color:"var(--text-muted)",fontSize:12}}>{open?"▲":"▼"}</span>
      </button>
      {open&&(
        <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:"0 0 10px 10px",padding:"14px 16px",borderTop:"none"}}>
          <form onSubmit={handleTrocar}>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>Nova senha</label>
              <input type="password" value={novaSenha} onChange={e=>setNovaSenha(e.target.value)}
                placeholder="Mínimo 6 caracteres" className="input" style={{width:"100%"}}/>
            </div>
            <div style={{marginBottom:12}}>
              <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>Confirmar nova senha</label>
              <input type="password" value={confirmar} onChange={e=>setConfirmar(e.target.value)}
                placeholder="Digite novamente" className="input" style={{width:"100%"}}/>
            </div>
            {erro&&<div style={{background:"#EF444418",border:"1px solid #EF444440",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#EF4444",marginBottom:10}}>{erro}</div>}
            {msg&&<div style={{background:"#22c55e18",border:"1px solid #22c55e44",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#22c55e",marginBottom:10}}>{msg}</div>}
            <button type="submit" disabled={loading} className="primary-btn" style={{width:"100%"}}>
              {loading?"Alterando...":"✅ Confirmar nova senha"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function PerfilTab({session,profile,onSave,onDeleteAccount}){
  const rawSaved = profile?.whatsapp||"";
  const initialLocal = rawSaved.startsWith("+55") ? rawSaved.slice(3) : rawSaved;
  const rawSaved2 = profile?.whatsapp2||"";
  const initialLocal2 = rawSaved2.startsWith("+55") ? rawSaved2.slice(3) : rawSaved2;

  const[localPhone,setLocalPhone]=useState(initialLocal);
  const[localPhone2,setLocalPhone2]=useState(initialLocal2);
  const[form,setForm]=useState({nome:profile?.nome||""});
  const[saved,setSaved]=useState(false);
  const[phoneError,setPhoneError]=useState("");
  const[phoneError2,setPhoneError2]=useState("");
  const[showDeleteModal,setShowDeleteModal]=useState(false);
  const[deleteConfirm,setDeleteConfirm]=useState("");
  const[deleting,setDeleting]=useState(false);

  function handlePhoneChange(v){ setLocalPhone(v.replace(/\D/g,"")); setPhoneError(""); }
  function handlePhone2Change(v){ setLocalPhone2(v.replace(/\D/g,"")); setPhoneError2(""); }

  function handleSave(e){
    e.preventDefault();
    const digits = localPhone.replace(/\D/g,"");
    if(digits.length > 0 && (digits.length < 10 || digits.length > 11)){
      setPhoneError("Número inválido. Ex.: 43999999999");
      return;
    }
    const digits2 = localPhone2.replace(/\D/g,"");
    if(digits2.length > 0 && (digits2.length < 10 || digits2.length > 11)){
      setPhoneError2("Número inválido. Ex.: 41999999999");
      return;
    }
    const fullPhone = digits ? `+55${digits}` : "";
    const fullPhone2 = digits2 ? `+55${digits2}` : "";
    onSave({...profile, whatsapp:fullPhone, whatsapp2:fullPhone2, nome:form.nome.trim()});
    setSaved(true);setTimeout(()=>setSaved(false),2000);
  }

  return(
    <section className="section-card">
      <h2 className="section-title">👤 Meu Perfil</h2>
      <form onSubmit={handleSave}>
        <div className="form-grid">
          <div className="field full">
            <label className="label">E-mail</label>
            <input className="input" value={session.user.email} disabled style={{opacity:.6}}/>
          </div>
          <div className="field full">
            <label className="label">Nome (opcional)</label>
            <input className="input" value={form.nome} onChange={(e)=>setForm({...form,nome:e.target.value})} placeholder="Seu nome"/>
          </div>

          {/* WhatsApp 1 */}
          <div className="field full">
            <label className="label">📱 WhatsApp — Titular</label>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"10px 14px",fontSize:15,color:"var(--text-muted)",flexShrink:0,fontWeight:600}}>+55</div>
              <input className="input" style={{flex:1}} value={localPhone} onChange={(e)=>handlePhoneChange(e.target.value)} placeholder="43999999999" maxLength={11} inputMode="numeric"/>
            </div>
            {phoneError && <div style={{fontSize:12,color:"#ef4444",marginTop:4}}>{phoneError}</div>}
            <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4,lineHeight:1.5}}>
              💡 Para <strong style={{color:"var(--text-primary)"}}>parar os alertas</strong>, apague o número e salve. Para <strong style={{color:"var(--text-primary)"}}>trocar</strong>, basta digitar o novo número e salvar.
            </div>
          </div>

          {/* WhatsApp 2 */}
          <div className="field full">
            <label className="label">📱 WhatsApp — Familiar/Cônjuge <span style={{fontSize:11,color:"var(--text-muted)",fontWeight:400}}>(opcional)</span></label>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"10px 14px",fontSize:15,color:"var(--text-muted)",flexShrink:0,fontWeight:600}}>+55</div>
              <input className="input" style={{flex:1}} value={localPhone2} onChange={(e)=>handlePhone2Change(e.target.value)} placeholder="41999999999" maxLength={11} inputMode="numeric"/>
            </div>
            {phoneError2 && <div style={{fontSize:12,color:"#ef4444",marginTop:4}}>{phoneError2}</div>}
            <div style={{fontSize:12,color:"var(--text-muted)",marginTop:4}}>
              Ambos receberão alertas de contas a vencer 7 dias e 1 dia antes.<br/>
              <span style={{color:"#f59e0b",fontWeight:600}}>⭐ Recurso exclusivo do plano Premium.</span>
            </div>
          </div>

          {/* Botão ativar alertas WhatsApp */}
          <div className="field full">
            <div style={{background:"#22c55e18",border:"1px solid #22c55e44",borderRadius:12,padding:"14px 16px"}}>
              <div style={{fontWeight:700,fontSize:13,color:"#22c55e",marginBottom:6}}>📲 Ativar alertas WhatsApp</div>
              <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:10,lineHeight:1.6}}>
                Para receber alertas, envie <strong style={{color:"var(--text-primary)"}}>uma mensagem</strong> para o número do Mapa Financeiro 5G. É só fazer isso <strong style={{color:"var(--text-primary)"}}>uma única vez</strong>!
              </div>
              <a href={`https://wa.me/554187834941?text=${encodeURIComponent("Olá! Quero ativar meus alertas financeiros do Mapa Financeiro 5G!")}`}
                target="_blank" rel="noreferrer"
                style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:"#22c55e",color:"#fff",textDecoration:"none",padding:"10px 16px",borderRadius:10,fontWeight:700,fontSize:13}}>
                💬 Ativar meus alertas agora
              </a>
            </div>
          </div>
        </div>
        {/* Trocar senha */}
        <TrocarSenha/>

        {/* Canal de feedback */}
        <div style={{background:"#22c55e12",border:"1px solid #22c55e33",borderRadius:14,padding:"14px 16px",margin:"16px 0"}}>
          <div style={{fontWeight:700,fontSize:14,color:"#22c55e",marginBottom:6}}>💬 Sugestões e Feedback</div>
          <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:12,lineHeight:1.6}}>
            Encontrou um bug? Tem uma sugestão? Quer elogiar ou criticar?<br/>
            Fale direto com a equipe do Mapa Financeiro 5G!
          </div>
          <a href="https://wa.me/5541995964572?text=Olá!%20Tenho%20um%20feedback%20sobre%20o%20Mapa%20Financeiro%205G:%20"
            target="_blank" rel="noreferrer"
            style={{display:"inline-flex",alignItems:"center",gap:8,background:"#22c55e",color:"#fff",textDecoration:"none",padding:"10px 20px",borderRadius:10,fontWeight:700,fontSize:13}}>
            <span>📱</span> Enviar feedback pelo WhatsApp
          </a>
        </div>

        <div className="actions">
          <button type="submit" className="primary-btn">{saved?"✓ Salvo!":"Salvar perfil"}</button>
        </div>
      </form>

      {/* Botão excluir conta */}
      <div style={{marginTop:24,borderTop:"1px solid var(--border)",paddingTop:20}}>
        <button
          onClick={()=>setShowDeleteModal(true)}
          style={{width:"100%",background:"none",border:"1px solid #ef444444",borderRadius:10,color:"#ef4444",padding:"10px 16px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
          🗑️ Excluir minha conta
        </button>
      </div>

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:"var(--bg-card)",borderRadius:20,padding:28,maxWidth:380,width:"100%",textAlign:"center",boxShadow:"0 8px 40px rgba(0,0,0,0.4)"}}>
            <img src="/pipo-triste.png" alt="Pipo triste" style={{width:130,height:130,objectFit:"contain",marginBottom:8}}/>
            <h3 style={{margin:"0 0 8px",fontSize:18,color:"var(--text-primary)"}}>Não vai embora... 😢</h3>
            <p style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6,margin:"0 0 16px"}}>
              Ao excluir sua conta, <strong style={{color:"var(--text-primary)"}}>todos os seus dados serão apagados permanentemente</strong> — contas, despesas, entradas, investimentos e histórico.
            </p>
            <p style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6,margin:"0 0 16px"}}>
              Essa ação <strong style={{color:"#ef4444"}}>não pode ser desfeita</strong>. Se quiser usar o app novamente, precisará se cadastrar do zero.
            </p>
            <p style={{fontSize:13,color:"var(--text-muted)",marginBottom:8}}>
              Para confirmar, digite <strong style={{color:"var(--text-primary)"}}>EXCLUIR</strong> abaixo:
            </p>
            <input
              className="input"
              value={deleteConfirm}
              onChange={e=>setDeleteConfirm(e.target.value)}
              placeholder="Digite EXCLUIR"
              style={{marginBottom:16,textAlign:"center",letterSpacing:2}}
            />
            <div style={{display:"flex",gap:10}}>
              <button
                onClick={()=>{setShowDeleteModal(false);setDeleteConfirm("");}}
                style={{flex:1,padding:"10px 0",borderRadius:10,border:"1px solid var(--border)",background:"var(--bg-main)",color:"var(--text-primary)",fontWeight:600,cursor:"pointer",fontSize:13}}>
                Cancelar
              </button>
              <button
                onClick={async()=>{
                  if(deleteConfirm!=="EXCLUIR")return;
                  setDeleting(true);
                  await onDeleteAccount();
                  setDeleting(false);
                }}
                disabled={deleteConfirm!=="EXCLUIR"||deleting}
                style={{flex:1,padding:"10px 0",borderRadius:10,border:"none",background:deleteConfirm==="EXCLUIR"?"#ef4444":"#ef444444",color:"#fff",fontWeight:700,cursor:deleteConfirm==="EXCLUIR"?"pointer":"not-allowed",fontSize:13}}>
                {deleting?"Excluindo...":"Excluir conta"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Formulários ──────────────────────────────────────────
function ExpenseFormModal({open,onClose,onSave,editingBill,cartoes=[],onGastoRapido,currentKey}){
  const today=new Date();
  const emptyForm={name:"",amount:"",dueDay:"",installments:"0",frequency:"unico",startMonth:String(today.getMonth()),startYear:String(today.getFullYear()),paymentMethod:PAYMENT_METHODS[0],category:EXPENSE_CATEGORIES[0],notes:"",cartaoId:"",taxaJuros:""};
  const[form,setForm]=useState(emptyForm);
  const[error,setError]=useState("");
  const payAgoraRef=React.useRef(false);
  // Pergunta de escopo ao alterar o valor de uma despesa Mensal já existente:
  // "somente_mes" grava um ajuste pontual (não mexe no valor padrão);
  // "todos" atualiza o valor padrão dali pra frente e congela o valor antigo
  // nos meses passados que já têm registro, para não reescrever o histórico.
  const[pendingPayload,setPendingPayload]=useState(null);

  useEffect(()=>{
    if(editingBill){
      setForm({name:editingBill.name,amount:String(editingBill.amount).replace(".",","),dueDay:String(editingBill.dueDay),installments:String(editingBill.installments),frequency:editingBill.frequency||"mensal",startMonth:String(editingBill.startMonth),startYear:String(editingBill.startYear),paymentMethod:editingBill.paymentMethod,category:editingBill.category,notes:editingBill.notes||"",cartaoId:editingBill.cartaoId||"",taxaJuros:editingBill.taxaJuros?String(editingBill.taxaJuros):""});
    } else if(open) {
      try{
        const saved=localStorage.getItem("mf5g_expense_draft");
        if(saved){const p=JSON.parse(saved);if(p.name||p.amount)setForm({...emptyForm,...p});else setForm(emptyForm);}
        else setForm(emptyForm);
      }catch{setForm(emptyForm);}
    }
    setError("");
    setPendingPayload(null);
  },[editingBill,open]); // eslint-disable-line react-hooks/exhaustive-deps
  function updateField(k,v){
    setForm((p)=>{
      const next={...p,[k]:v};
      if(!editingBill){try{localStorage.setItem("mf5g_expense_draft",JSON.stringify(next));}catch{}}
      return next;
    });
  }
  // Data de início só pode ser alterada em despesas "Único" (reagendar uma despesa
  // que ainda não aconteceu). Para Mensal/Semestral/Anual/Parcelado já cadastradas,
  // mudar o início faz a despesa "sumir" dos meses anteriores — trava por segurança.
  const startLocked=!!editingBill&&form.frequency!=="unico";
  function handleSubmit(e){
    e.preventDefault();
    const amount=numberFromInput(form.amount);
    if(!form.name.trim()){setError("Informe o nome.");payAgoraRef.current=false;return;}
    if(amount<=0){setError("Informe um valor maior que zero.");payAgoraRef.current=false;return;}
    if(form.paymentMethod==="Cartão de Crédito"&&cartoes.length>0&&!form.cartaoId){setError("Selecione o cartão.");payAgoraRef.current=false;return;}
    const pagarAgora=payAgoraRef.current;
    payAgoraRef.current=false;
    const payload={...form,amount,dueDay:clamp(Number(form.dueDay||1),1,31),installments:Math.max(0,Number(form.installments||0)),frequency:form.frequency||"unico",startMonth:startLocked?editingBill.startMonth:clamp(Number(form.startMonth||0),0,11),startYear:startLocked?editingBill.startYear:Math.max(2000,Number(form.startYear||today.getFullYear())),_pagarAgora:pagarAgora};
    // Só pergunta o escopo se for edição de despesa Mensal com o valor alterado
    if(editingBill&&editingBill.frequency==="mensal"&&amount!==editingBill.amount){
      setPendingPayload(payload);
      return;
    }
    try{localStorage.removeItem("mf5g_expense_draft");}catch{}
    onSave(payload);
  }
  function confirmScope(escopo){
    if(!pendingPayload)return;
    try{localStorage.removeItem("mf5g_expense_draft");}catch{}
    onSave({...pendingPayload,_escopoValor:escopo,_mesReferenciaEdicao:currentKey});
    setPendingPayload(null);
  }
  if(pendingPayload){
    return(
      <Modal open={open} onClose={()=>setPendingPayload(null)} title="Alterar valor da despesa">
        <div style={{fontSize:14,color:"var(--text-primary)",lineHeight:1.6,marginBottom:16}}>
          Você mudou o valor de <strong>{editingBill?.name}</strong> de <strong>{currency(editingBill?.amount)}</strong> para <strong>{currency(pendingPayload.amount)}</strong>.
          <br/>Essa mudança vale só para {currentKey?formatMonthKeyLabel(currentKey):"este mês"} ou também para os próximos?
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <button type="button" onClick={()=>confirmScope("somente_mes")}
            style={{padding:"14px 16px",borderRadius:12,border:"2px solid var(--border)",background:"var(--bg-card)",cursor:"pointer",textAlign:"left"}}>
            <div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)"}}>📅 Somente este mês</div>
            <div style={{fontSize:12,color:"var(--text-muted)",marginTop:2}}>Os demais meses continuam com o valor de {currency(editingBill?.amount)}.</div>
          </button>
          <button type="button" onClick={()=>confirmScope("todos")}
            style={{padding:"14px 16px",borderRadius:12,border:"2px solid #7C83FF",background:"#7C83FF18",cursor:"pointer",textAlign:"left"}}>
            <div style={{fontWeight:700,fontSize:14,color:"#7C83FF"}}>🔁 Este mês e os próximos</div>
            <div style={{fontSize:12,color:"var(--text-muted)",marginTop:2}}>Os meses passados já registrados mantêm o valor antigo — o histórico não muda.</div>
          </button>
        </div>
        <div className="actions" style={{marginTop:16}}><button type="button" className="ghost-btn" onClick={()=>setPendingPayload(null)}>Voltar</button></div>
      </Modal>
    );
  }
  return(
    <Modal open={open} onClose={onClose} title={editingBill?"Editar despesa":"Nova despesa"}>
      {!editingBill&&onGastoRapido&&(
        <div style={{marginBottom:16}}>
          <button type="button" onClick={()=>{onClose();onGastoRapido();}}
            style={{width:"100%",padding:"12px",background:"linear-gradient(135deg,#F59E0B22,#D9770622)",border:"2px solid #F59E0B66",borderRadius:12,color:"#F59E0B",fontWeight:800,fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            <span style={{fontSize:20}}>⚡</span>
            <div style={{textAlign:"left"}}>
              <div>Gasto rápido</div>
              <div style={{fontSize:11,fontWeight:400,color:"#D97706",marginTop:1}}>Só nome, valor, categoria e pagamento — cadastrado como pago agora</div>
            </div>
          </button>
          <div style={{display:"flex",alignItems:"center",gap:8,margin:"12px 0"}}>
            <div style={{flex:1,height:1,background:"var(--border)"}}/>
            <span style={{fontSize:11,color:"var(--text-muted)"}}>ou cadastre com mais detalhes abaixo</span>
            <div style={{flex:1,height:1,background:"var(--border)"}}/>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit}>

        <div className="form-grid">
          <div className="field full"><label className="label">Nome</label><input className="input" value={form.name} onChange={(e)=>updateField("name",e.target.value)} placeholder="Ex.: Aluguel, Internet..."/></div>
          <div className="field"><label className="label">Valor (R$)</label><input className="input" type="text" inputMode="decimal" value={form.amount} onChange={(e)=>updateField("amount",e.target.value)} placeholder="Ex.: 20,90"/>{form.frequency==="parcelado"&&<div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>💡 Informe o valor de <strong>cada parcela</strong>, não o total.</div>}</div>
          <div className="field"><label className="label">Dia vencimento</label><input className="input" type="number" min="1" max="31" value={form.dueDay} onChange={(e)=>updateField("dueDay",e.target.value)}/></div>
          {/* Frequência */}
          <div className="field full">
            <label className="label">Frequência</label>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[
                {v:"unico",label:"1️⃣ Único",desc:"Uma só vez"},
                {v:"mensal",label:"📅 Mensal",desc:"Todo mês"},
                {v:"semestral",label:"🗓️ Semestral",desc:"A cada 6 meses"},
                {v:"anual",label:"📆 Anual",desc:"1x por ano"},
                {v:"parcelado",label:"🔢 Parcelado",desc:"N vezes"},
              ].map(opt=>(
                <button key={opt.v} type="button" onClick={()=>{updateField("frequency",opt.v);if(opt.v!=="parcelado")updateField("installments","0");}}
                  style={{flex:1,minWidth:90,padding:"8px 6px",borderRadius:8,border:`2px solid ${form.frequency===opt.v?"#7C83FF":"var(--border)"}`,background:form.frequency===opt.v?"#7C83FF22":"var(--bg-card)",cursor:"pointer",textAlign:"center"}}>
                  <div style={{fontSize:13,fontWeight:700,color:form.frequency===opt.v?"#7C83FF":"var(--text-primary)"}}>{opt.label}</div>
                  <div style={{fontSize:10,color:"var(--text-muted)",marginTop:2}}>{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Parcelas — só para parcelado */}
          {form.frequency==="parcelado"&&(
            <div className="field">
              <label className="label">Número de parcelas</label>
              <input className="input" type="number" min="1" placeholder="Ex.: 12" value={form.installments==="0"?"":form.installments} onChange={(e)=>updateField("installments",e.target.value)}/>
              {Number(form.installments)>0&&Number(form.amount)>0&&(
                <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>
                  Total: <strong>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(numberFromInput(form.amount)*Number(form.installments))}</strong>
                  {" · "}Término: <strong>{(()=>{const d=new Date(Number(form.startYear),Number(form.startMonth)+Number(form.installments)-1);return `${MONTHS_PT[d.getMonth()]}/${d.getFullYear()}`;})()}</strong>
                </div>
              )}
            </div>
          )}

          {/* Taxa de juros — só para parcelado */}
          {form.frequency==="parcelado"&&(
            <div className="field">
              <label className="label">Taxa de juros mensal <span style={{fontSize:11,color:"var(--text-muted)",fontWeight:400}}>(opcional)</span></label>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",flex:1,minWidth:120,background:"var(--bg-input)",border:"1px solid var(--border-input)",borderRadius:10,overflow:"hidden"}}>
                  <input className="input" style={{border:"none",background:"none",flex:1}} type="text" inputMode="decimal"
                    placeholder="Ex: 0,99 ou 1,5"
                    value={form.taxaJuros}
                    onChange={e=>updateField("taxaJuros",e.target.value)}
                    disabled={form.taxaJuros==="0"}
                  />
                  <span style={{paddingRight:12,color:"var(--text-muted)",fontSize:13,fontWeight:600}}>% a.m.</span>
                </div>
                <button type="button"
                  onClick={()=>updateField("taxaJuros", form.taxaJuros==="0"?"":"0")}
                  style={{padding:"10px 14px",borderRadius:10,border:`2px solid ${form.taxaJuros==="0"?"#22c55e44":"var(--border)"}`,background:form.taxaJuros==="0"?"#22c55e18":"var(--bg-card)",cursor:"pointer",fontSize:12,fontWeight:700,color:form.taxaJuros==="0"?"#22c55e":"var(--text-muted)",whiteSpace:"nowrap"}}>
                  ✅ Sem juros
                </button>
              </div>
              {form.taxaJuros&&form.taxaJuros!=="0"&&Number(form.installments)>0&&numberFromInput(form.amount)>0&&(()=>{
                const taxa=parseFloat(form.taxaJuros.replace(",","."))/100;
                const pmt=numberFromInput(form.amount);
                const n=Number(form.installments);
                const vp=round2(pmt*(1-Math.pow(1+taxa,-n))/taxa);
                const totalJuros=round2(pmt*n-vp);
                return(
                  <div style={{fontSize:11,color:"#F59E0B",marginTop:6,background:"#F59E0B0a",border:"1px solid #F59E0B33",borderRadius:8,padding:"6px 10px",lineHeight:1.6}}>
                    💸 Valor original (sem juros): <strong>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(vp)}</strong>
                    {" · "}Juros total estimado: <strong style={{color:"#ef4444"}}>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(totalJuros)}</strong>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Mês e Ano de início */}
          <div className="field">
            <label className="label">{form.frequency==="anual"?"Mês de cobrança":form.frequency==="semestral"?"Primeiro mês":"Mês início"}</label>
            <select className="select" value={form.startMonth} disabled={startLocked} onChange={(e)=>updateField("startMonth",e.target.value)}>
              {MONTHS_PT.map((l,i)=><option key={l} value={i}>{l}</option>)}
            </select>
            {startLocked&&<div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>🔒 Não é possível alterar o início de uma despesa já cadastrada — isso faria ela sumir dos meses anteriores. Para corrigir, exclua e recadastre.</div>}
            {form.frequency==="semestral"&&(
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>
                Cobrado em: <strong>{MONTHS_PT[Number(form.startMonth)]}</strong> e <strong>{MONTHS_PT[(Number(form.startMonth)+6)%12]}</strong>
              </div>
            )}
            {form.frequency==="anual"&&(
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>
                Cobrado todo ano em <strong>{MONTHS_PT[Number(form.startMonth)]}</strong>
              </div>
            )}
          </div>
          <div className="field"><label className="label">Ano início</label><input className="input" type="number" min="2000" max="2100" value={form.startYear} disabled={startLocked} onChange={(e)=>updateField("startYear",e.target.value)}/>
            {form.frequency==="anual"&&Number(form.startYear)>new Date().getFullYear()&&(
              <div style={{fontSize:11,color:"#22c55e",marginTop:4}}>✅ IPVA, IPTU e outras contas anuais de anos futuros serão planejadas corretamente.</div>
            )}
            {form.frequency==="anual"&&Number(form.startYear)===new Date().getFullYear()&&(
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>💡 Para planejar em 2026, deixe o ano atual. Para 2027 em diante, altere o ano aqui.</div>
            )}
          </div>
          {form.frequency==="parcelado"?(
            <div className="field">
              <label className="label">Como vai pagar?</label>
              <select className="select" value={form.paymentMethod} onChange={(e)=>{updateField("paymentMethod",e.target.value);if(e.target.value!=="Cartão de Crédito")updateField("cartaoId","");}}>
                <option value="Cartão de Crédito">💳 Cartão de Crédito</option>
                <option value="Débito automático/Banco">🏦 Débito automático/Banco</option>
                <option value="Boleto">🧾 Boleto</option>
                <option value="Loja/Financeira">🏪 Loja/Financeira</option>
                <option value="Pix parcelado">⚡ Pix parcelado</option>
                <option value="Consignado/Desconto em folha">📋 Consignado/Desconto em folha</option>
                <option value="Corretora/Financiadora">📊 Corretora/Financiadora</option>
                <option value="Pessoa física">🤝 Pessoa física</option>
                <option value="Débito/Pix">💸 Débito/Pix</option>
                <option value="Dinheiro">💵 Dinheiro</option>
              </select>
            </div>
          ):(
            <div className="field"><label className="label">Forma pagamento</label><select className="select" value={form.paymentMethod} onChange={(e)=>{updateField("paymentMethod",e.target.value);if(e.target.value!=="Cartão de Crédito")updateField("cartaoId","");}}>{PAYMENT_METHODS.map((l)=><option key={l} value={l}>{l}</option>)}</select></div>
          )}
          {form.paymentMethod==="Cartão de Crédito"&&cartoes.length>0&&(
            <div className="field"><label className="label">Cartão</label><select className="select" value={form.cartaoId} onChange={(e)=>updateField("cartaoId",e.target.value)}><option value="">Selecione o cartão...</option>{cartoes.map(c=><option key={c.id} value={c.id}>{c.bandeira} {c.nome}</option>)}</select></div>
          )}
          <div className="field"><label className="label">Categoria</label><select className="select" value={form.category} onChange={(e)=>updateField("category",e.target.value)}>{EXPENSE_CATEGORIES.map((l)=><option key={l} value={l}>{l}</option>)}</select></div>
          <div className="field full"><label className="label">Observações</label><textarea className="textarea" value={form.notes} onChange={(e)=>updateField("notes",e.target.value)} placeholder="Opcional"/></div>
        </div>
        {error?<div className="helper-error">{error}</div>:null}
        <div className="actions"><button type="button" className="ghost-btn" onClick={onClose}>Cancelar</button><button type="button" className="secondary-btn" onClick={()=>{payAgoraRef.current=true;setTimeout(()=>document.getElementById("expense-form-submit")?.click(),10);}}>Salvar e pagar hoje</button><button type="submit" id="expense-form-submit" className="primary-btn">Salvar despesa</button></div>
      </form>
    </Modal>
  );
}

function EntryFormModal({open,onClose,onSave,editingEntry}){
  const today=new Date();
  const emptyForm={name:"",amount:"",day:String(today.getDate()),month:String(today.getMonth()),year:String(today.getFullYear()),paymentMethod:PAYMENT_METHODS[0],category:ENTRY_CATEGORIES[0],notes:""};
  const[form,setForm]=useState(emptyForm);
  const[error,setError]=useState("");
  useEffect(()=>{
    if(editingEntry){setForm({name:editingEntry.name,amount:String(editingEntry.amount).replace(".",","),day:String(editingEntry.day||""),month:String(editingEntry.month),year:String(editingEntry.year),paymentMethod:editingEntry.paymentMethod,category:editingEntry.category,notes:editingEntry.notes||""});}
    else if(open){
      try{const saved=localStorage.getItem("mf5g_entry_draft");if(saved){const p=JSON.parse(saved);if(p.name||p.amount)setForm({...emptyForm,...p});else setForm(emptyForm);}else setForm(emptyForm);}catch{setForm(emptyForm);}
    }
    setError("");
  },[editingEntry,open]); // eslint-disable-line react-hooks/exhaustive-deps
  function updateField(k,v){setForm((p)=>{const next={...p,[k]:v};if(!editingEntry){try{localStorage.setItem("mf5g_entry_draft",JSON.stringify(next));}catch{}}return next;});}
  function handleSubmit(e){
    e.preventDefault();
    const amount=numberFromInput(form.amount);
    if(!form.name.trim()){setError("Informe o nome.");return;}
    if(amount<=0){setError("Informe um valor maior que zero.");return;}
    try{localStorage.removeItem("mf5g_entry_draft");}catch{}
    onSave({...form,amount,day:clamp(Number(form.day||today.getDate()),1,31),month:clamp(Number(form.month||0),0,11),year:Math.max(2000,Number(form.year||today.getFullYear()))});
  }
  return(
    <Modal open={open} onClose={()=>{if(!editingEntry){try{localStorage.removeItem("mf5g_entry_draft");}catch{}}onClose();}} title={editingEntry?"Editar entrada":"Nova entrada"}>
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="field full"><label className="label">Nome</label><input className="input" value={form.name} onChange={(e)=>updateField("name",e.target.value)} placeholder="Ex.: Salário, serviço..."/></div>
          <div className="field"><label className="label">Valor (R$)</label><input className="input" type="text" inputMode="decimal" value={form.amount} onChange={(e)=>updateField("amount",e.target.value)} placeholder="Ex.: 300,00"/></div>
          <div className="field"><label className="label">Tipo</label>
            <select className="select" value={form.category} onChange={(e)=>updateField("category",e.target.value)}>
              {ENTRY_CATEGORIES.filter(c=>c!=="Resgate de Investimentos").map((l)=><option key={l} value={l}>{l}</option>)}
              <option disabled>──────────────</option>
              <option value="Resgate de Investimentos">🏦 Resgate de Investimentos</option>
            </select>
          </div>
          {form.category==="Resgate de Investimentos"&&(
            <div className="field full">
              <div style={{background:"#14B8A618",border:"1px solid #14B8A644",borderRadius:8,padding:"10px 12px"}}>
                <div style={{fontWeight:700,fontSize:12,color:"#14B8A6",marginBottom:6}}>📋 Lembre-se de atualizar o Guardar</div>
                <div style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.6}}>
                  Após salvar, atualize a fonte do resgate:<br/>
                  <strong>Investimentos</strong> → edite a quantidade do ativo vendido<br/>
                  <strong>Meta</strong> → reduza o valor acumulado em Metas<br/>
                  <strong>Reserva/Aporte</strong> → ajuste o lançamento em Despesas<br/>
                  <strong>Desafio</strong> → considere encerrar o desafio em Desafios
                </div>
              </div>
            </div>
          )}
          <div className="field"><label className="label">Dia</label><input className="input" type="number" min="1" max="31" value={form.day} onChange={(e)=>updateField("day",e.target.value)} placeholder="Ex.: 5"/></div>
          <div className="field"><label className="label">Mês</label><select className="select" value={form.month} onChange={(e)=>updateField("month",e.target.value)}>{MONTHS_PT.map((l,i)=><option key={l} value={i}>{l}</option>)}</select></div>
          <div className="field"><label className="label">Ano</label><input className="input" type="number" min="2000" max="2100" value={form.year} onChange={(e)=>updateField("year",e.target.value)}/></div>
          <div className="field full"><label className="label">Forma recebimento</label><select className="select" value={form.paymentMethod} onChange={(e)=>updateField("paymentMethod",e.target.value)}>{PAYMENT_METHODS.map((l)=><option key={l} value={l}>{l}</option>)}</select></div>
          <div className="field full"><label className="label">Observações</label><textarea className="textarea" value={form.notes} onChange={(e)=>updateField("notes",e.target.value)} placeholder="Opcional"/></div>
        </div>
        {error?<div className="helper-error">{error}</div>:null}
        <div className="actions"><button type="button" className="ghost-btn" onClick={()=>{try{localStorage.removeItem("mf5g_entry_draft");}catch{}onClose();}}>Cancelar</button><button type="submit" className="primary-btn">Salvar entrada</button></div>
      </form>
    </Modal>
  );
}

// ─── Tela de redefinição de senha ────────────────────────────────────────────
function ResetPasswordScreen({onDone}){
  const[senha,setSenha]=React.useState("");
  const[confirmar,setConfirmar]=React.useState("");
  const[loading,setLoading]=React.useState(false);
  const[msg,setMsg]=React.useState("");
  const[erro,setErro]=React.useState("");
  async function handleSubmit(e){
    e.preventDefault();
    if(senha.length<6){setErro("A senha deve ter pelo menos 6 caracteres.");return;}
    if(senha!==confirmar){setErro("As senhas não coincidem.");return;}
    setLoading(true);setErro("");
    const{error}=await supabase.auth.updateUser({password:senha});
    setLoading(false);
    if(error){setErro("Erro ao atualizar senha: "+error.message);}
    else{setMsg("✅ Senha atualizada com sucesso!");setTimeout(()=>onDone(),2000);}
  }
  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#0a0a0f",padding:16}}>
      <div style={{background:"#1a1a2e",border:"1px solid #2a2a4a",borderRadius:18,padding:32,width:"100%",maxWidth:400,boxShadow:"0 8px 32px rgba(0,0,0,0.4)"}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:40,marginBottom:8}}>🔐</div>
          <h2 style={{margin:0,color:"#fff",fontSize:20,fontWeight:800}}>Criar nova senha</h2>
          <p style={{color:"#94a3b8",fontSize:13,marginTop:6}}>Mapa Financeiro 5G</p>
        </div>
        {msg?(
          <div style={{background:"#22c55e18",border:"1px solid #22c55e44",borderRadius:10,padding:16,textAlign:"center",color:"#22c55e",fontWeight:700,fontSize:15}}>
            {msg}<br/><span style={{fontSize:12,fontWeight:400,color:"#94a3b8"}}>Redirecionando...</span>
          </div>
        ):(
          <form onSubmit={handleSubmit}>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,color:"#94a3b8",display:"block",marginBottom:4}}>Nova senha</label>
              <input type="password" value={senha} onChange={e=>setSenha(e.target.value)} placeholder="Mínimo 6 caracteres"
                style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1px solid #2a2a4a",background:"#0f0f1a",color:"#fff",fontSize:14,boxSizing:"border-box"}}/>
            </div>
            <div style={{marginBottom:20}}>
              <label style={{fontSize:12,color:"#94a3b8",display:"block",marginBottom:4}}>Confirmar nova senha</label>
              <input type="password" value={confirmar} onChange={e=>setConfirmar(e.target.value)} placeholder="Digite novamente"
                style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1px solid #2a2a4a",background:"#0f0f1a",color:"#fff",fontSize:14,boxSizing:"border-box"}}/>
            </div>
            {erro&&<div style={{background:"#EF444418",border:"1px solid #EF444440",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#EF4444",marginBottom:12}}>{erro}</div>}
            <button type="submit" disabled={loading}
              style={{width:"100%",padding:"12px",background:"linear-gradient(135deg,#7C83FF,#a855f7)",color:"#fff",border:"none",borderRadius:12,fontSize:15,fontWeight:800,cursor:"pointer",opacity:loading?0.7:1}}>
              {loading?"Atualizando...":"✅ Salvar nova senha"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── App Principal ────────────────────────────────────────
// ─── Aba Método 5G — Mapa do Tesouro ─────────────────────
function MetodoG5Tab({bills, entries, goals, monthMetrics, currentYear, currentMonth, desafios, investimentos, rendaFixa=[], onResgate}) {
  const refKey = `${currentYear}-${String(currentMonth+1).padStart(2,"0")}`;

  const contasPagas = bills.filter(b => {
    const si = b.startYear*12+b.startMonth, ci = currentYear*12+currentMonth;
    if(ci<si) return false;
    if(b.installments!==0 && ci-si>=b.installments) return false;
    const record = b.payments?.[refKey] || {entries:[]};
    const totalPago = (record.entries||[]).reduce((s,e) => s+Number(e.amount||0), 0);
    return totalPago >= Number(b.amount||0) * 0.99;
  });

  const gastosPorPilar = G5_PILARES.map(pilar => {
    if(pilar.key === "ganhar") {
      // Apenas entradas do mês atual (exceto resgates — não é renda gerada)
      const entradasGanhar = entries.filter(e => e.year === currentYear && e.month === currentMonth && e.category !== "Resgate de Investimentos");
      const total = entradasGanhar.reduce((s,e) => s + Number(e.amount||0), 0);
      return {...pilar, total: Math.round(total*100)/100, count: entradasGanhar.length};
    }
    if(pilar.key === "gratidao") {
      // Contas efetivamente pagas no mês atual
      const total = contasPagas.reduce((s,b) => s+Number(b.amount||0), 0);
      return {...pilar, total: Math.round(total*100)/100, count: contasPagas.length};
    }
    if(pilar.key === "guardar") {
      // Apenas o que foi efetivamente guardado/pago no mês atual
      // 1. Despesas das categorias Guardar pagas no mês atual
      const billsGuardar = bills.filter(b => pilar.categorias.includes(b.category));
      const totalBillsMes = billsGuardar.reduce((s, b) => {
        const rec = b.payments?.[refKey] || {entries:[]};
        const pago = (rec.entries||[]).reduce((ps,e) => ps+Number(e.amount||0), 0);
        return s + pago;
      }, 0);
      const countBills = billsGuardar.filter(b => {
        const rec = b.payments?.[refKey]||{entries:[]};
        return (rec.entries||[]).reduce((s,e)=>s+Number(e.amount||0),0) > 0;
      }).length;

      // 2. Entradas de "Resgate de Investimentos" NÃO contam como guardar
      // 3. Aportes em investimentos registrados como saída no mês (despesas categoria Investimentos pagas)
      const total = Math.round(totalBillsMes*100)/100;
      return {...pilar, total, count: countBills};
    }

    // Gastar, Generosidade — apenas o efetivamente pago no mês atual
    const billsDoMes = bills.filter(b => {
      const si = b.startYear*12+b.startMonth, ci = currentYear*12+currentMonth;
      if(ci<si) return false;
      if(b.installments!==0 && ci-si>=b.installments) return false;
      return true;
    }).filter(b => pilar.categorias.includes(b.category));

    const totalPago = billsDoMes.reduce((s,b) => {
      const rec = b.payments?.[refKey]||{entries:[]};
      const pago = (rec.entries||[]).reduce((ps,e)=>ps+Number(e.amount||0),0);
      return s + pago; // só o pago — sem incluir previsto
    }, 0);
    const countPago = billsDoMes.filter(b=>{
      const rec = b.payments?.[refKey]||{entries:[]};
      return (rec.entries||[]).reduce((s,e)=>s+Number(e.amount||0),0)>0;
    }).length;
    return {...pilar, total: Math.round(totalPago*100)/100, count: countPago};
  });

  const cur = v => new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);
  const totalGeral = gastosPorPilar.reduce((s,p) => s+(p.key==="ganhar"||p.key==="gratidao"?0:p.total),0);

  // Determina se cada pilar está "completo" para iluminar o mapa
  const pilarAtivo = {
    ganhar: gastosPorPilar.find(p=>p.key==="ganhar")?.total > 0,
    gratidao: gastosPorPilar.find(p=>p.key==="gratidao")?.count > 0,
    generosidade: gastosPorPilar.find(p=>p.key==="generosidade")?.total > 0,
    gastar: gastosPorPilar.find(p=>p.key==="gastar")?.total > 0,
    guardar: gastosPorPilar.find(p=>p.key==="guardar")?.total > 0,
  };
  const pilaresConcluidos = Object.values(pilarAtivo).filter(Boolean).length;

  // Modal de detalhes por pilar
  const [pilarModal, setPilarModal] = React.useState(null);

  function getDetalhesPilar(key) {
    const DESCRICOES = {
      ganhar: "Tudo que entra no seu bolso — salário, renda extra, rendimentos e qualquer outra fonte de receita do mês.",
      gratidao: "Contas que você já quitou neste mês. Cada pagamento é um ato de gratidão pelo que você tem e pelo compromisso que honrou.",
      generosidade: "Valores destinados a ajudar outras pessoas. Quando você doa, você ativa o princípio da multiplicação.",
      gastar: "Seus gastos com moradia, alimentação, transporte e lazer. O controle aqui é o que libera recursos para os outros pilares.",
      guardar: "Tudo que está construindo seu patrimônio: investimentos, reservas, metas e desafios financeiros.",
    };
    if(key === "ganhar") {
      const itens = entries.filter(e => e.year===currentYear && e.month===currentMonth && e.category !== "Resgate de Investimentos");
      const resgates = entries.filter(e => e.year===currentYear && e.month===currentMonth && e.category === "Resgate de Investimentos");
      const secoesGanhar = [{ titulo: "Entradas do mês", itens: itens.map(e => ({ nome: e.name, sub: e.category, valor: e.amount, cor: "#22C55E" })) }];
      if(resgates.length>0) secoesGanhar.push({ titulo: "💸 Resgates (não contabilizados no Ganhar)", itens: resgates.map(e => ({ nome: e.name, sub: "Resgate de Investimentos", valor: e.amount, cor: "#14B8A6" })) });
      return { descricao: DESCRICOES.ganhar, secoes: secoesGanhar };
    }
    if(key === "gratidao") {
      return { descricao: DESCRICOES.gratidao, secoes: [{ titulo: "Contas quitadas", itens: contasPagas.map(b => ({ nome: b.name, sub: b.category, valor: b.amount, cor: "#7C83FF" })) }] };
    }
    if(key === "generosidade") {
      const billsDoMes = bills.filter(b => {
        const si=b.startYear*12+b.startMonth, ci=currentYear*12+currentMonth;
        if(ci<si) return false;
        return b.installments===0 || ci-si<b.installments;
      }).filter(b => b.category==="Generosidade");
      return { descricao: DESCRICOES.generosidade, secoes: [{ titulo: "Doações e generosidade", itens: billsDoMes.map(b => ({ nome: b.name, sub: b.category, valor: b.amount, cor: "#F59E0B" })) }] };
    }
    if(key === "gastar") {
      const categorias = ["Moradia","Transporte","Acessórios do Veículo","Alimentação","Supermercado","Restaurantes e Delivery","Limpeza e Higiene","Serviços","Entretenimento","Comunicação","Apps Produtividade","Consumo Pessoal","Lazer","Outros","Dívidas/Negociações","Empréstimos/Financiamentos"];
      const billsDoMes = bills.filter(b => {
        const si=b.startYear*12+b.startMonth, ci=currentYear*12+currentMonth;
        if(ci<si) return false;
        return b.installments===0 || ci-si<b.installments;
      }).filter(b => categorias.includes(b.category));
      return { descricao: DESCRICOES.gastar, secoes: [{ titulo: "Despesas do mês", itens: billsDoMes.map(b => ({ nome: b.name, sub: b.category, valor: b.amount, cor: "#EF4444" })) }] };
    }
    if(key === "guardar") {
      // Detalhe: apenas aportes pagos no mês atual
      const billsGuardar2 = bills.filter(b => b.category==="Investimentos"||b.category==="Reserva de Emergência");
      const itensAportesMes = billsGuardar2
        .map(b => {
          const rec = b.payments?.[refKey]||{entries:[]};
          const pago = (rec.entries||[]).reduce((s,e)=>s+Number(e.amount||0),0);
          if(pago<=0) return null;
          return { nome: b.name, sub: b.category, valor: pago, cor: "#14B8A6" };
        })
        .filter(Boolean);
      return {
        descricao: DESCRICOES.guardar,
        secoes: [
          { titulo: "💰 Guardado este mês", itens: itensAportesMes.length>0 ? itensAportesMes : [{nome:"Nenhum aporte registrado neste mês",sub:"",valor:0,cor:"#888"}] },
        ]
      };
    }
    return { descricao: "", secoes: [] };
  }

  const pilarSelecionado = pilarModal ? gastosPorPilar.find(p=>p.key===pilarModal) : null;
  const detalhes = pilarModal ? getDetalhesPilar(pilarModal) : null;

  return (
    <section className="section-card">
      {/* Modal de detalhes do pilar */}
      {pilarModal && pilarSelecionado && (
        <div onClick={()=>setPilarModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg-main)",border:"1px solid var(--border)",borderRadius:18,width:"100%",maxWidth:480,maxHeight:"80vh",overflowY:"auto",boxShadow:"0 8px 40px rgba(0,0,0,0.4)"}}>
            {/* Header do modal */}
            <div style={{padding:"20px 20px 0",position:"sticky",top:0,background:"var(--bg-main)",borderBottom:"1px solid var(--border)",paddingBottom:14,zIndex:1}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:28}}>{pilarSelecionado.emoji}</span>
                  <div>
                    <div style={{fontWeight:800,fontSize:16,color:pilarSelecionado.color}}>{pilarSelecionado.label}</div>
                    <div style={{fontSize:11,color:"var(--text-muted)"}}>{pilarSelecionado.desc}</div>
                  </div>
                </div>
                <button onClick={()=>setPilarModal(null)} style={{background:"none",border:"none",fontSize:20,color:"var(--text-muted)",cursor:"pointer",padding:4,lineHeight:1}}>✕</button>
              </div>
              <div style={{marginTop:12,display:"flex",alignItems:"baseline",gap:8}}>
                <span style={{fontSize:24,fontWeight:900,color:"var(--text-primary)"}}>{cur(pilarSelecionado.total)}</span>
                <span style={{fontSize:12,color:"var(--text-muted)"}}>total neste pilar</span>
              </div>
            </div>
            {/* Corpo do modal */}
            <div style={{padding:20}}>
              <p style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6,margin:"0 0 16px"}}>{detalhes.descricao}</p>
              {detalhes.secoes.map((sec,si) => {
                if(sec.itens.length===0) return null;
                return (
                  <div key={si} style={{marginBottom:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:"var(--text-muted)",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.5px"}}>{sec.titulo}</div>
                    {sec.itens.map((item,ii) => (
                      <div key={ii} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:"var(--bg-card)",borderRadius:8,marginBottom:6,border:"1px solid var(--border)"}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--text-primary)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.nome}</div>
                          {item.sub && <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>{item.sub}</div>}
                        </div>
                        <div style={{fontSize:13,fontWeight:700,color:item.cor,flexShrink:0,marginLeft:12}}>{cur(item.valor)}</div>
                      </div>
                    ))}
                    {sec.itens.length===0 && <div style={{fontSize:12,color:"var(--text-muted)",fontStyle:"italic"}}>Nenhum item ainda</div>}
                  </div>
                );
              })}
              {detalhes.secoes.every(s=>s.itens.length===0) && (
                <div style={{textAlign:"center",padding:"24px 0",color:"var(--text-muted)",fontSize:13}}>
                  <div style={{fontSize:32,marginBottom:8}}>💡</div>
                  Nenhum lançamento neste pilar ainda.<br/>Que tal começar agora?
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cabeçalho */}
      <div style={{textAlign:"center", marginBottom:28}}>
        <div style={{fontSize:36, marginBottom:6}}>🗺️</div>
        <h2 style={{fontSize:22, fontWeight:900, color:"var(--text-primary)", margin:0}}>Mapa do Tesouro 5G</h2>
        <p style={{fontSize:13, color:"var(--text-muted)", marginTop:6, lineHeight:1.6}}>
          Siga os 5 passos e chegue à liberdade financeira 🏆
        </p>
        <div style={{display:"inline-flex", alignItems:"center", gap:8, background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:20, padding:"6px 16px", marginTop:8}}>
          <span style={{fontSize:13, color:"var(--text-muted)"}}>Progresso do mês:</span>
          <span style={{fontSize:14, fontWeight:800, color:"#F59E0B"}}>{pilaresConcluidos}/5 pilares ativos 🔥</span>
        </div>
      </div>

      {/* Cards detalhados dos pilares — clicáveis */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px,1fr))", gap:10, marginBottom:24}}>
        {gastosPorPilar.map(pilar => {
          const ativo = pilarAtivo[pilar.key];
          const pct = pilar.key==="ganhar" ? 100 : (totalGeral>0 ? Math.round((pilar.total/totalGeral)*100) : 0);
          return (
            <div key={pilar.key} onClick={()=>setPilarModal(pilar.key)} style={{background:"var(--bg-card)", border:`2px solid ${ativo ? pilar.color+"66" : "var(--border)"}`, borderRadius:14, padding:"14px 12px", textAlign:"center", position:"relative", overflow:"hidden", opacity: ativo ? 1 : 0.6, transition:"all .3s", cursor:"pointer"}} title="Clique para ver detalhes">
              <div style={{position:"absolute", top:0, left:0, right:0, height:3, background: ativo ? pilar.color : "var(--border)", borderRadius:"14px 14px 0 0"}}/>
              <div style={{fontSize:24, marginBottom:4}}>{pilar.emoji}</div>
              <div style={{fontWeight:800, fontSize:13, color: ativo ? pilar.color : "var(--text-muted)", marginBottom:2}}>{pilar.label}</div>
              <div style={{fontWeight:700, fontSize:15, color:"var(--text-primary)", margin:"6px 0"}}>{cur(pilar.total)}</div>
              <div style={{fontSize:10, color:"var(--text-muted)"}}>
                {pilar.key==="ganhar" ? `${pilar.count} entrada(s)` : pilar.key==="gratidao" ? `${pilar.count} quitada(s)` : `${pilar.count} conta(s)`}
              </div>
              {pilar.key!=="ganhar" && pilar.key!=="gratidao" && (
                <div style={{marginTop:8, height:3, background:"var(--border)", borderRadius:4, overflow:"hidden"}}>
                  <div style={{height:"100%", width:`${pct}%`, background:pilar.color, borderRadius:4, transition:"width .5s"}}/>
                </div>
              )}
              <div style={{fontSize:9, color:"var(--text-muted)", marginTop:6, opacity:0.7}}>toque para detalhes</div>
            </div>
          );
        })}
      </div>

      {/* Orientação de resgate */}
      <div style={{background:"#14B8A610",border:"1px solid #14B8A630",borderRadius:10,padding:"10px 16px",marginBottom:16,fontSize:12,color:"var(--text-muted)",lineHeight:1.6}}>
        💸 <strong style={{color:"#14B8A6"}}>Para registrar um resgate:</strong> vá em <strong>Investimentos</strong>, clique em ✏️ no ativo e use a opção "Registrar venda". O valor entra automaticamente como receita no mês.
      </div>

      {/* Metas como Guardar */}
      {goals.length > 0 && (
        <div style={{background:`#14B8A622`, border:"1px solid #14B8A644", borderRadius:12, padding:"12px 16px"}}>
          <div style={{fontWeight:700, fontSize:13, color:"#14B8A6", marginBottom:8}}>📈 Guardar — suas metas ativas</div>
          {goals.map(g => {
            const pct = g.targetAmount > 0 ? Math.min(100, Math.round((g.currentAmount/g.targetAmount)*100)) : 0;
            return (
              <div key={g.id} style={{marginBottom:8}}>
                <div style={{display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:3}}>
                  <span style={{color:"var(--text-primary)", fontWeight:600}}>{g.name}</span>
                  <span style={{color:"var(--text-muted)"}}>{pct}%</span>
                </div>
                <div style={{height:6, background:"var(--border)", borderRadius:4, overflow:"hidden"}}>
                  <div style={{height:"100%", width:`${pct}%`, background:"#14B8A6", borderRadius:4}}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─── Aba Carteira de Investimentos ──────────────────────
// Lista de ativos conhecidos para busca assistida no formulário
const ATIVOS_CONHECIDOS = [
  // ─── FIIs populares (busca dinâmica Brapi cobre todos os demais) ───
  {simbolo:"MXRF11",nome:"Maxi Renda",tipo:"fii"},{simbolo:"KNRI11",nome:"Kinea Renda Imobiliária",tipo:"fii"},
  {simbolo:"HGLG11",nome:"CSHG Logística",tipo:"fii"},{simbolo:"XPML11",nome:"XP Malls",tipo:"fii"},
  {simbolo:"BTLG11",nome:"BTG Pactual Logística",tipo:"fii"},{simbolo:"VILG11",nome:"Vinci Logística",tipo:"fii"},
  {simbolo:"VGIA11",nome:"Valora CRA Fiagro",tipo:"fii"},{simbolo:"GGRC11",nome:"GGR Covepi Renda",tipo:"fii"},
  {simbolo:"RBRR11",nome:"RBR Rendimento High Grade",tipo:"fii"},{simbolo:"HSAF11",nome:"HSI Ativos Financeiros",tipo:"fii"},
  {simbolo:"ALZR11",nome:"Alianza Trust",tipo:"fii"},{simbolo:"HGBS11",nome:"CSHG Brasil Shopping",tipo:"fii"},
  {simbolo:"GARE11",nome:"Guardian Real Estate",tipo:"fii"},{simbolo:"KNCR11",nome:"Kinea Crédito Imobiliário",tipo:"fii"},
  {simbolo:"RECR11",nome:"REC Recebíveis Imobiliários",tipo:"fii"},{simbolo:"IRDM11",nome:"Iridium Recebíveis",tipo:"fii"},
  {simbolo:"CPTS11",nome:"Capitânia Securities II",tipo:"fii"},{simbolo:"XPLG11",nome:"XP Log",tipo:"fii"},
  {simbolo:"BRCO11",nome:"Bresco Logística",tipo:"fii"},{simbolo:"PVBI11",nome:"VBI Prime Properties",tipo:"fii"},
  {simbolo:"TRXF11",nome:"TRX Real Estate",tipo:"fii"},{simbolo:"VCJR11",nome:"Vinci Crédito Imobiliário",tipo:"fii"},
  {simbolo:"RZAG11",nome:"Riza Akin",tipo:"fii"},{simbolo:"TEPP11",nome:"Tellus Properties",tipo:"fii"},
  {simbolo:"HCTR11",nome:"Hectare CE",tipo:"fii"},{simbolo:"BCFF11",nome:"BTG Fundo de Fundos",tipo:"fii"},
  {simbolo:"RBRF11",nome:"RBR Alpha Fundo de Fundos",tipo:"fii"},{simbolo:"VRTA11",nome:"Votorantim Recebíveis",tipo:"fii"},
  {simbolo:"NCHB11",nome:"NCH Crédito e Valor",tipo:"fii"},{simbolo:"VGHF11",nome:"Valora Hedge Fund",tipo:"fii"},
  {simbolo:"HFOF11",nome:"Hedge Top FOFII 3",tipo:"fii"},{simbolo:"BRCR11",nome:"BTG Corporate Office",tipo:"fii"},
  {simbolo:"VISC11",nome:"Vinci Shopping Centers",tipo:"fii"},{simbolo:"JSRE11",nome:"JS Real Estate",tipo:"fii"},
  {simbolo:"RVBI11",nome:"RBR Valor",tipo:"fii"},{simbolo:"MGFF11",nome:"Mogno Fundo de Fundos",tipo:"fii"},
  // ─── Ações BR populares ───
  {simbolo:"PETR4",nome:"Petrobras PN",tipo:"acao"},{simbolo:"PETR3",nome:"Petrobras ON",tipo:"acao"},
  {simbolo:"VALE3",nome:"Vale ON",tipo:"acao"},{simbolo:"ITUB4",nome:"Itaú Unibanco PN",tipo:"acao"},
  {simbolo:"BBDC4",nome:"Bradesco PN",tipo:"acao"},{simbolo:"BBDC3",nome:"Bradesco ON",tipo:"acao"},
  {simbolo:"BBAS3",nome:"Banco do Brasil ON",tipo:"acao"},{simbolo:"WEGE3",nome:"WEG ON",tipo:"acao"},
  {simbolo:"RENT3",nome:"Localiza ON",tipo:"acao"},{simbolo:"SUZB3",nome:"Suzano ON",tipo:"acao"},
  {simbolo:"RADL3",nome:"RaiaDrogasil ON",tipo:"acao"},{simbolo:"ELET3",nome:"Eletrobras ON",tipo:"acao"},
  {simbolo:"ELET6",nome:"Eletrobras PNB",tipo:"acao"},{simbolo:"ABEV3",nome:"Ambev ON",tipo:"acao"},
  {simbolo:"HAPV3",nome:"Hapvida ON",tipo:"acao"},{simbolo:"RDOR3",nome:"Rede D'Or ON",tipo:"acao"},
  {simbolo:"EGIE3",nome:"Engie Brasil ON",tipo:"acao"},{simbolo:"TAEE11",nome:"Taesa Units",tipo:"acao"},
  {simbolo:"CPLE6",nome:"Copel PNB",tipo:"acao"},{simbolo:"VIVT3",nome:"Telefônica Vivo ON",tipo:"acao"},
  {simbolo:"TOTS3",nome:"TOTVS ON",tipo:"acao"},{simbolo:"PRIO3",nome:"PetroRio ON",tipo:"acao"},
  {simbolo:"CSNA3",nome:"CSN ON",tipo:"acao"},{simbolo:"UGPA3",nome:"Ultrapar ON",tipo:"acao"},
  {simbolo:"CSAN3",nome:"Cosan ON",tipo:"acao"},{simbolo:"LREN3",nome:"Lojas Renner ON",tipo:"acao"},
  {simbolo:"MGLU3",nome:"Magazine Luiza ON",tipo:"acao"},{simbolo:"AMZO34",nome:"Amazon BDR",tipo:"acao"},
  {simbolo:"AAPL34",nome:"Apple BDR",tipo:"acao"},{simbolo:"MSFT34",nome:"Microsoft BDR",tipo:"acao"},
  {simbolo:"GOGL34",nome:"Alphabet BDR",tipo:"acao"},{simbolo:"NVDC34",nome:"NVIDIA BDR",tipo:"acao"},
  {simbolo:"TSLA34",nome:"Tesla BDR",tipo:"acao"},{simbolo:"META34",nome:"Meta BDR",tipo:"acao"},
  {simbolo:"NFLX34",nome:"Netflix BDR",tipo:"acao"},{simbolo:"BPAC11",nome:"BTG Pactual Units",tipo:"acao"},
  {simbolo:"SANB11",nome:"Santander Units",tipo:"acao"},{simbolo:"ITSA4",nome:"Itaúsa PN",tipo:"acao"},
  {simbolo:"KLBN11",nome:"Klabin Units",tipo:"acao"},{simbolo:"EMBR3",nome:"Embraer ON",tipo:"acao"},
  {simbolo:"ENEV3",nome:"Eneva ON",tipo:"acao"},{simbolo:"JBSS3",nome:"JBS ON",tipo:"acao"},
  {simbolo:"MRFG3",nome:"Marfrig ON",tipo:"acao"},{simbolo:"BEEF3",nome:"Minerva ON",tipo:"acao"},
  {simbolo:"SMTO3",nome:"São Martinho ON",tipo:"acao"},{simbolo:"RAIZ4",nome:"Raízen PN",tipo:"acao"},
  {simbolo:"POMO4",nome:"Marcopolo PN",tipo:"acao"},{simbolo:"RAIL3",nome:"Rumo ON",tipo:"acao"},
  {simbolo:"CCRO3",nome:"CCR ON",tipo:"acao"},{simbolo:"ECOR3",nome:"EcoRodovias ON",tipo:"acao"},
  // ─── ETFs BR ───
  {simbolo:"BOVA11",nome:"iShares Ibovespa",tipo:"etf_br"},{simbolo:"SMAL11",nome:"iShares Small Cap",tipo:"etf_br"},
  {simbolo:"IVVB11",nome:"iShares S&P 500 BRL",tipo:"etf_br"},{simbolo:"HASH11",nome:"Hashdex Nasdaq Crypto",tipo:"etf_br"},
  {simbolo:"GOLD11",nome:"Trend Ouro",tipo:"etf_br"},{simbolo:"DIVO11",nome:"iShares Dividendos",tipo:"etf_br"},
  {simbolo:"FIND11",nome:"iShares Financeiro",tipo:"etf_br"},{simbolo:"SPXI11",nome:"iShares S&P 500 Hedge",tipo:"etf_br"},
  {simbolo:"FIXA11",nome:"Trend ETF IRFM",tipo:"etf_br"},{simbolo:"NDIV11",nome:"Trend ETF IDIV",tipo:"etf_br"},
  {simbolo:"XINA11",nome:"iShares China",tipo:"etf_br"},{simbolo:"EURP11",nome:"iShares Europa",tipo:"etf_br"},
  {simbolo:"NASD11",nome:"Trend ETF Nasdaq",tipo:"etf_br"},{simbolo:"ACWI11",nome:"iShares Global",tipo:"etf_br"},
  // ─── Criptomoedas (top 100+) ───
  {simbolo:"BTC",nome:"Bitcoin",tipo:"cripto"},{simbolo:"ETH",nome:"Ethereum",tipo:"cripto"},
  {simbolo:"BNB",nome:"Binance BNB",tipo:"cripto"},{simbolo:"SOL",nome:"Solana",tipo:"cripto"},
  {simbolo:"XRP",nome:"XRP (Ripple)",tipo:"cripto"},{simbolo:"ADA",nome:"Cardano",tipo:"cripto"},
  {simbolo:"DOGE",nome:"Dogecoin",tipo:"cripto"},{simbolo:"AVAX",nome:"Avalanche",tipo:"cripto"},
  {simbolo:"DOT",nome:"Polkadot",tipo:"cripto"},{simbolo:"MATIC",nome:"Polygon",tipo:"cripto"},
  {simbolo:"LINK",nome:"Chainlink",tipo:"cripto"},{simbolo:"LTC",nome:"Litecoin",tipo:"cripto"},
  {simbolo:"ATOM",nome:"Cosmos",tipo:"cripto"},{simbolo:"UNI",nome:"Uniswap",tipo:"cripto"},
  {simbolo:"XLM",nome:"Stellar",tipo:"cripto"},{simbolo:"ALGO",nome:"Algorand",tipo:"cripto"},
  {simbolo:"VET",nome:"VeChain",tipo:"cripto"},{simbolo:"ICP",nome:"Internet Computer",tipo:"cripto"},
  {simbolo:"FIL",nome:"Filecoin",tipo:"cripto"},{simbolo:"SAND",nome:"The Sandbox",tipo:"cripto"},
  {simbolo:"MANA",nome:"Decentraland",tipo:"cripto"},{simbolo:"AXS",nome:"Axie Infinity",tipo:"cripto"},
  {simbolo:"THETA",nome:"Theta Network",tipo:"cripto"},{simbolo:"EGLD",nome:"MultiversX",tipo:"cripto"},
  {simbolo:"NEAR",nome:"NEAR Protocol",tipo:"cripto"},{simbolo:"FTM",nome:"Fantom",tipo:"cripto"},
  {simbolo:"HBAR",nome:"Hedera",tipo:"cripto"},{simbolo:"EOS",nome:"EOS",tipo:"cripto"},
  {simbolo:"XTZ",nome:"Tezos",tipo:"cripto"},{simbolo:"FLOW",nome:"Flow",tipo:"cripto"},
  {simbolo:"CAKE",nome:"PancakeSwap",tipo:"cripto"},{simbolo:"GRT",nome:"The Graph",tipo:"cripto"},
  {simbolo:"AAVE",nome:"Aave",tipo:"cripto"},{simbolo:"MKR",nome:"Maker",tipo:"cripto"},
  {simbolo:"COMP",nome:"Compound",tipo:"cripto"},{simbolo:"SNX",nome:"Synthetix",tipo:"cripto"},
  {simbolo:"CRV",nome:"Curve DAO",tipo:"cripto"},{simbolo:"1INCH",nome:"1inch",tipo:"cripto"},
  {simbolo:"SUSHI",nome:"SushiSwap",tipo:"cripto"},{simbolo:"BAT",nome:"Basic Attention Token",tipo:"cripto"},
  {simbolo:"ZRX",nome:"0x Protocol",tipo:"cripto"},{simbolo:"ENJ",nome:"Enjin Coin",tipo:"cripto"},
  {simbolo:"CHZ",nome:"Chiliz",tipo:"cripto"},{simbolo:"GALA",nome:"Gala",tipo:"cripto"},
  {simbolo:"APE",nome:"ApeCoin",tipo:"cripto"},{simbolo:"LRC",nome:"Loopring",tipo:"cripto"},
  {simbolo:"ROSE",nome:"Oasis Network",tipo:"cripto"},{simbolo:"ONE",nome:"Harmony",tipo:"cripto"},
  {simbolo:"ZIL",nome:"Zilliqa",tipo:"cripto"},{simbolo:"IOTA",nome:"IOTA",tipo:"cripto"},
  {simbolo:"DASH",nome:"Dash",tipo:"cripto"},{simbolo:"ZEC",nome:"Zcash",tipo:"cripto"},
  {simbolo:"XMR",nome:"Monero",tipo:"cripto"},{simbolo:"BCH",nome:"Bitcoin Cash",tipo:"cripto"},
  {simbolo:"BSV",nome:"Bitcoin SV",tipo:"cripto"},{simbolo:"ETC",nome:"Ethereum Classic",tipo:"cripto"},
  {simbolo:"TRX",nome:"TRON",tipo:"cripto"},{simbolo:"NEO",nome:"NEO",tipo:"cripto"},
  {simbolo:"WAVES",nome:"Waves",tipo:"cripto"},{simbolo:"QTUM",nome:"Qtum",tipo:"cripto"},
  {simbolo:"OMG",nome:"OMG Network",tipo:"cripto"},{simbolo:"BAL",nome:"Balancer",tipo:"cripto"},
  {simbolo:"YFI",nome:"Yearn Finance",tipo:"cripto"},{simbolo:"RUNE",nome:"THORChain",tipo:"cripto"},
  {simbolo:"KAVA",nome:"Kava",tipo:"cripto"},{simbolo:"KSM",nome:"Kusama",tipo:"cripto"},
  {simbolo:"AR",nome:"Arweave",tipo:"cripto"},{simbolo:"INJ",nome:"Injective",tipo:"cripto"},
  {simbolo:"SUI",nome:"Sui",tipo:"cripto"},{simbolo:"APT",nome:"Aptos",tipo:"cripto"},
  {simbolo:"OP",nome:"Optimism",tipo:"cripto"},{simbolo:"ARB",nome:"Arbitrum",tipo:"cripto"},
  {simbolo:"BLUR",nome:"Blur",tipo:"cripto"},{simbolo:"PEPE",nome:"Pepe",tipo:"cripto"},
  {simbolo:"SHIB",nome:"Shiba Inu",tipo:"cripto"},{simbolo:"FLOKI",nome:"Floki",tipo:"cripto"},
  {simbolo:"BONK",nome:"Bonk",tipo:"cripto"},{simbolo:"WIF",nome:"dogwifhat",tipo:"cripto"},
  {simbolo:"USUAL",nome:"Usual",tipo:"cripto"},{simbolo:"MONAD",nome:"Monad",tipo:"cripto"},
  {simbolo:"TON",nome:"Toncoin",tipo:"cripto"},{simbolo:"SТХ",nome:"Stacks",tipo:"cripto"},
  {simbolo:"USDT",nome:"USDT Dólar",tipo:"cripto"},{simbolo:"USDC",nome:"USD Coin",tipo:"cripto"},
  {simbolo:"DAI",nome:"DAI",tipo:"cripto"},{simbolo:"BUSD",nome:"Binance USD",tipo:"cripto"},
  // ─── Ações Internacionais (S&P 500 + outras) ───
  {simbolo:"AAPL",nome:"Apple",tipo:"acao_int"},{simbolo:"MSFT",nome:"Microsoft",tipo:"acao_int"},
  {simbolo:"GOOGL",nome:"Alphabet (Google)",tipo:"acao_int"},{simbolo:"AMZN",nome:"Amazon",tipo:"acao_int"},
  {simbolo:"TSLA",nome:"Tesla",tipo:"acao_int"},{simbolo:"NVDA",nome:"NVIDIA",tipo:"acao_int"},
  {simbolo:"META",nome:"Meta (Facebook)",tipo:"acao_int"},{simbolo:"NFLX",nome:"Netflix",tipo:"acao_int"},
  {simbolo:"AMD",nome:"AMD",tipo:"acao_int"},{simbolo:"INTC",nome:"Intel",tipo:"acao_int"},
  {simbolo:"AVGO",nome:"Broadcom",tipo:"acao_int"},{simbolo:"QCOM",nome:"Qualcomm",tipo:"acao_int"},
  {simbolo:"TXN",nome:"Texas Instruments",tipo:"acao_int"},{simbolo:"MU",nome:"Micron",tipo:"acao_int"},
  {simbolo:"ORCL",nome:"Oracle",tipo:"acao_int"},{simbolo:"CRM",nome:"Salesforce",tipo:"acao_int"},
  {simbolo:"ADBE",nome:"Adobe",tipo:"acao_int"},{simbolo:"PYPL",nome:"PayPal",tipo:"acao_int"},
  {simbolo:"SQ",nome:"Block (Square)",tipo:"acao_int"},{simbolo:"SHOP",nome:"Shopify",tipo:"acao_int"},
  {simbolo:"SNAP",nome:"Snap",tipo:"acao_int"},{simbolo:"UBER",nome:"Uber",tipo:"acao_int"},
  {simbolo:"LYFT",nome:"Lyft",tipo:"acao_int"},{simbolo:"ABNB",nome:"Airbnb",tipo:"acao_int"},
  {simbolo:"COIN",nome:"Coinbase",tipo:"acao_int"},{simbolo:"RBLX",nome:"Roblox",tipo:"acao_int"},
  {simbolo:"HOOD",nome:"Robinhood",tipo:"acao_int"},{simbolo:"PLTR",nome:"Palantir",tipo:"acao_int"},
  {simbolo:"RIVN",nome:"Rivian",tipo:"acao_int"},{simbolo:"LCID",nome:"Lucid Motors",tipo:"acao_int"},
  {simbolo:"NIO",nome:"NIO (EV China)",tipo:"acao_int"},{simbolo:"BIDU",nome:"Baidu",tipo:"acao_int"},
  {simbolo:"BABA",nome:"Alibaba",tipo:"acao_int"},{simbolo:"JD",nome:"JD.com",tipo:"acao_int"},
  {simbolo:"PDD",nome:"PDD Holdings",tipo:"acao_int"},{simbolo:"TCEHY",nome:"Tencent",tipo:"acao_int"},
  {simbolo:"JPM",nome:"JPMorgan Chase",tipo:"acao_int"},{simbolo:"BAC",nome:"Bank of America",tipo:"acao_int"},
  {simbolo:"GS",nome:"Goldman Sachs",tipo:"acao_int"},{simbolo:"MS",nome:"Morgan Stanley",tipo:"acao_int"},
  {simbolo:"WFC",nome:"Wells Fargo",tipo:"acao_int"},{simbolo:"C",nome:"Citigroup",tipo:"acao_int"},
  {simbolo:"V",nome:"Visa",tipo:"acao_int"},{simbolo:"MA",nome:"Mastercard",tipo:"acao_int"},
  {simbolo:"BRK.B",nome:"Berkshire Hathaway B",tipo:"acao_int"},{simbolo:"JNJ",nome:"Johnson & Johnson",tipo:"acao_int"},
  {simbolo:"PFE",nome:"Pfizer",tipo:"acao_int"},{simbolo:"MRNA",nome:"Moderna",tipo:"acao_int"},
  {simbolo:"ABBV",nome:"AbbVie",tipo:"acao_int"},{simbolo:"LLY",nome:"Eli Lilly",tipo:"acao_int"},
  {simbolo:"UNH",nome:"UnitedHealth",tipo:"acao_int"},{simbolo:"CVS",nome:"CVS Health",tipo:"acao_int"},
  {simbolo:"WMT",nome:"Walmart",tipo:"acao_int"},{simbolo:"TGT",nome:"Target",tipo:"acao_int"},
  {simbolo:"COST",nome:"Costco",tipo:"acao_int"},{simbolo:"HD",nome:"Home Depot",tipo:"acao_int"},
  {simbolo:"NKE",nome:"Nike",tipo:"acao_int"},{simbolo:"DIS",nome:"Disney",tipo:"acao_int"},
  {simbolo:"SBUX",nome:"Starbucks",tipo:"acao_int"},{simbolo:"MCD",nome:"McDonald's",tipo:"acao_int"},
  {simbolo:"KO",nome:"Coca-Cola",tipo:"acao_int"},{simbolo:"PEP",nome:"PepsiCo",tipo:"acao_int"},
  {simbolo:"XOM",nome:"ExxonMobil",tipo:"acao_int"},{simbolo:"CVX",nome:"Chevron",tipo:"acao_int"},
  {simbolo:"T",nome:"AT&T",tipo:"acao_int"},{simbolo:"VZ",nome:"Verizon",tipo:"acao_int"},
  {simbolo:"SPOT",nome:"Spotify",tipo:"acao_int"},{simbolo:"TWTR",nome:"Twitter/X",tipo:"acao_int"},
  {simbolo:"ZOOM",nome:"Zoom",tipo:"acao_int"},{simbolo:"ZM",nome:"Zoom Video",tipo:"acao_int"},
  {simbolo:"DOCU",nome:"DocuSign",tipo:"acao_int"},{simbolo:"SNOW",nome:"Snowflake",tipo:"acao_int"},
  {simbolo:"DDOG",nome:"Datadog",tipo:"acao_int"},{simbolo:"NET",nome:"Cloudflare",tipo:"acao_int"},
  {simbolo:"CRWD",nome:"CrowdStrike",tipo:"acao_int"},{simbolo:"OKTA",nome:"Okta",tipo:"acao_int"},
  {simbolo:"PANW",nome:"Palo Alto Networks",tipo:"acao_int"},{simbolo:"FTNT",nome:"Fortinet",tipo:"acao_int"},
  {simbolo:"TSMC",nome:"TSMC",tipo:"acao_int"},{simbolo:"ASML",nome:"ASML",tipo:"acao_int"},
  {simbolo:"SAP",nome:"SAP",tipo:"acao_int"},{simbolo:"SONY",nome:"Sony",tipo:"acao_int"},
  {simbolo:"TM",nome:"Toyota",tipo:"acao_int"},{simbolo:"HMC",nome:"Honda",tipo:"acao_int"},
  // ─── ETFs Internacionais ───
  {simbolo:"VTI",nome:"Vanguard Total Market",tipo:"etf_int"},{simbolo:"VOO",nome:"Vanguard S&P 500",tipo:"etf_int"},
  {simbolo:"QQQ",nome:"Invesco Nasdaq 100",tipo:"etf_int"},{simbolo:"SPY",nome:"SPDR S&P 500",tipo:"etf_int"},
  {simbolo:"IWM",nome:"iShares Russell 2000",tipo:"etf_int"},{simbolo:"VNQ",nome:"Vanguard Real Estate",tipo:"etf_int"},
  {simbolo:"GLD",nome:"SPDR Gold",tipo:"etf_int"},{simbolo:"SLV",nome:"iShares Silver",tipo:"etf_int"},
  {simbolo:"TLT",nome:"iShares 20+ Year Treasury",tipo:"etf_int"},{simbolo:"HYG",nome:"iShares High Yield Bond",tipo:"etf_int"},
  {simbolo:"VEA",nome:"Vanguard FTSE Developed",tipo:"etf_int"},{simbolo:"VWO",nome:"Vanguard FTSE Emerging",tipo:"etf_int"},
  {simbolo:"EEM",nome:"iShares MSCI Emerging",tipo:"etf_int"},{simbolo:"EFA",nome:"iShares MSCI EAFE",tipo:"etf_int"},
  {simbolo:"ARKK",nome:"ARK Innovation",tipo:"etf_int"},{simbolo:"XLK",nome:"Technology Select SPDR",tipo:"etf_int"},
  {simbolo:"XLF",nome:"Financial Select SPDR",tipo:"etf_int"},{simbolo:"XLE",nome:"Energy Select SPDR",tipo:"etf_int"},
  {simbolo:"XLV",nome:"Health Care Select SPDR",tipo:"etf_int"},{simbolo:"IBIT",nome:"iShares Bitcoin ETF",tipo:"etf_int"},
  {simbolo:"FBTC",nome:"Fidelity Bitcoin ETF",tipo:"etf_int"},{simbolo:"GBTC",nome:"Grayscale Bitcoin Trust",tipo:"etf_int"},
  // ─── Moedas ───
  {simbolo:"USD",nome:"Dólar Americano",tipo:"moeda"},{simbolo:"EUR",nome:"Euro",tipo:"moeda"},
  {simbolo:"GBP",nome:"Libra Esterlina",tipo:"moeda"},{simbolo:"ARS",nome:"Peso Argentino",tipo:"moeda"},
  {simbolo:"JPY",nome:"Iene Japonês",tipo:"moeda"},{simbolo:"CAD",nome:"Dólar Canadense",tipo:"moeda"},
  {simbolo:"AUD",nome:"Dólar Australiano",tipo:"moeda"},{simbolo:"CHF",nome:"Franco Suíço",tipo:"moeda"},
  {simbolo:"CNY",nome:"Yuan Chinês",tipo:"moeda"},{simbolo:"MXN",nome:"Peso Mexicano",tipo:"moeda"},
];

// Ícone padrão por tipo de ativo (usado quando inv.emoji está vazio ou é 💰 genérico)
const EMOJI_POR_TIPO = {
  cripto:    "₿",
  moeda:     "💱",
  acao:      "📊",
  fii:       "🏢",
  etf_br:    "📦",
  acao_int:  "🌎",
  etf_int:   "🌐",
  cofrinho:  "🐷",
};
function getEmojiAtivo(inv){
  // Se tem emoji customizado diferente do padrão, usa ele
  if(inv.emoji && inv.emoji !== "💰") return inv.emoji;
  return EMOJI_POR_TIPO[inv.tipo] || "💰";
}

const ATIVOS_SUGERIDOS = [
  {id:"bitcoin",nome:"Bitcoin",simbolo:"BTC",tipo:"cripto",emoji:"₿"},
  {id:"ethereum",nome:"Ethereum",simbolo:"ETH",tipo:"cripto",emoji:"⟠"},
  {id:"usd",nome:"Dólar",simbolo:"USD",tipo:"moeda",emoji:"🇺🇸"},
  {id:"eur",nome:"Euro",simbolo:"EUR",tipo:"moeda",emoji:"🇪🇺"},
  {id:"ard",nome:"Ações BR",simbolo:"PETR4",tipo:"acao",emoji:"📊"},
  {id:"hglg",nome:"HGLG11",simbolo:"HGLG11",tipo:"fii",emoji:"🏢"},
  {id:"mxrf",nome:"MXRF11",simbolo:"MXRF11",tipo:"fii",emoji:"🏬"},
  {id:"knri",nome:"KNRI11",simbolo:"KNRI11",tipo:"fii",emoji:"🏗️"},
  {id:"bova11",nome:"BOVA11",simbolo:"BOVA11",tipo:"etf_br",emoji:"📦"},
  {id:"ivvb11",nome:"IVVB11",simbolo:"IVVB11",tipo:"etf_br",emoji:"🌐"},
  {id:"aapl",nome:"Apple",simbolo:"AAPL",tipo:"acao_int",emoji:"🍎"},
  {id:"vti",nome:"VTI",simbolo:"VTI",tipo:"etf_int",emoji:"🌎"},
];

function CarteiraInvestTab({investimentos, onSave, onResgate, onRegistrarSaida, onRegistrarDividendo, desafios=[], rendaFixa=[], onSaveRendaFixa, futureData=[], entries=[], bills=[], plano="gratis"}) {
  const[cotacoes,setCotacoes]=React.useState({});
  const[loadingCot,setLoadingCot]=React.useState(false);
  const[ultimaAtualizacao,setUltimaAtualizacao]=React.useState(null);
  const[buscaAtivo,setBuscaAtivo]=React.useState("");
  const[modalSaida,setModalSaida]=React.useState(null); // {valor, descricao}
  const[aporteRVModal,setAporteRVModal]=React.useState(null); // {inv, valor, precoPago}
  // eslint-disable-next-line no-unused-vars
  const[venderRVModal,setVenderRVModal]=React.useState(null); // {inv, qtd, valorRecebido}
  const[buscaFormAtivo,setBuscaFormAtivo]=React.useState("");
  const[filtroCatAtivo,setFiltroCatAtivo]=React.useState("todos");
  const[resultadosBusca,setResultadosBusca]=React.useState([]);
  const[buscandoAtivo,setBuscandoAtivo]=React.useState(false);
  const buscaTimerRef=React.useRef(null);
  const[form,setForm]=React.useState({nome:"",simbolo:"",tipo:"cripto",quantidade:"",precoMedio:"",emoji:"💰"});
  const[showForm,setShowForm]=React.useState(false);
  const[editId,setEditId]=React.useState(null);
  const editFormRef=React.useRef(null);
  const[qtdVendida,setQtdVendida]=React.useState("");
  const[dividendoModal,setDividendoModal]=React.useState(null); // {inv, valor:""}

  async function buscarCotacoes(){
    setLoadingCot(true);
    try{
      const novasCot={};
      // Cripto via CoinGecko
      const cryptoIds=investimentos.filter(i=>i.tipo==="cripto").map(i=>i.simbolo.toLowerCase());
      const idMap={"btc":"bitcoin","eth":"ethereum","bnb":"binancecoin","sol":"solana","ada":"cardano","dot":"polkadot","xrp":"ripple","wld":"worldcoin","matic":"polygon","avax":"avalanche-2","link":"chainlink","ltc":"litecoin","doge":"dogecoin","atom":"cosmos","uni":"uniswap","near":"near","algo":"algorand","vet":"vechain","grt":"the-graph","usdt":"tether","usdc":"usd-coin","dai":"dai","busd":"binance-usd","shib":"shiba-inu","ftm":"fantom","one":"harmony","zil":"zilliqa","iota":"iota","dash":"dash","zec":"zcash","xmr":"monero","bch":"bitcoin-cash","bsv":"bitcoin-sv","etc":"ethereum-classic","trx":"tron","neo":"neo","waves":"waves","qtum":"qtum","omg":"omg-network","bal":"balancer","yfi":"yearn-finance","rune":"thorchain","kava":"kava","ksm":"kusama","ar":"arweave","inj":"injective-protocol","sui":"sui","apt":"aptos","op":"optimism","arb":"arbitrum","blur":"blur","pepe":"pepe","floki":"floki","bonk":"bonk","wif":"dogwifcoin","usual":"usual","ton":"the-open-network","stx":"blockstack","sand":"the-sandbox","mana":"decentraland","axs":"axie-infinity","theta":"theta-token","egld":"elrond-erd-2","hbar":"hedera-hashgraph","eos":"eos","xtz":"tezos","flow":"flow","cake":"pancakeswap-token","aave":"aave","mkr":"maker","comp":"compound-governance-token","snx":"havven","crv":"curve-dao-token","sushi":"sushi","bat":"basic-attention-token","zrx":"0x","enj":"enjincoin","chz":"chiliz","gala":"gala","ape":"apecoin","lrc":"loopring","rose":"oasis-network","fil":"filecoin","icp":"internet-computer","xlm":"stellar"};
      const cgIds=cryptoIds.map(s=>idMap[s]||s).filter(Boolean);
      if(cgIds.length>0){
        const r=await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${cgIds.join(",")}&vs_currencies=brl`);
        const d=await r.json();
        for(const inv of investimentos.filter(i=>i.tipo==="cripto")){
          const cgId=idMap[inv.simbolo.toLowerCase()]||inv.simbolo.toLowerCase();
          if(d[cgId]?.brl) novasCot[inv.id]=d[cgId].brl;
        }
      }
      // Moedas via AwesomeAPI
      // Garante símbolo limpo: remove -BRL se já veio junto, e ignora símbolos inválidos
      const moedas=investimentos
        .filter(i=>i.tipo==="moeda")
        .map(i=>i.simbolo.toUpperCase().replace(/-BRL$/i,"").replace(/BRL$/i,"")+"-BRL")
        .filter(m=>m.length>4); // Evita "-BRL" sozinho
      // Sempre incluir USD-BRL para conversão de ativos internacionais (acao_int, etf_int)
      const temUSD=moedas.includes("USD-BRL");
      const temInternacional=investimentos.some(i=>i.tipo==="acao_int"||i.tipo==="etf_int");
      const moedasBusca=(temUSD||!temInternacional)?moedas:[...moedas,"USD-BRL"];
      const moedasUnicas=[...new Set(moedasBusca)]; // Remove duplicatas
      if(moedasUnicas.length>0){
        const r=await fetch(`https://economia.awesomeapi.com.br/json/last/${moedasUnicas.join(",")}`);
        const d=await r.json();
        // Salvar USD/BRL para conversão de ações internacionais
        if(d["USDBRL"]?.bid) novasCot["__usd_brl__"]=parseFloat(d["USDBRL"].bid);
        for(const inv of investimentos.filter(i=>i.tipo==="moeda")){
          const key=inv.simbolo+"BRL";
          if(d[key]?.bid) novasCot[inv.id]=parseFloat(d[key].bid);
        }
      }
      // FIIs, Ações BR, ETFs BR e internacionais via proxy Railway (evita CORS)
      const yahooInvs=investimentos.filter(i=>i.tipo==="fii"||i.tipo==="acao"||i.tipo==="etf_br"||i.tipo==="acao_int"||i.tipo==="etf_int");
      if(yahooInvs.length>0){
        // Brapi espera símbolo sem .SA (ex: VGIA11, PETR4, AAPL)
        const buildBrapiSym=(inv)=>inv.simbolo.toUpperCase().replace(".SA","");
        const brapiSyms=yahooInvs.map(buildBrapiSym).filter(s=>s&&s.length>=2);
        try{
          const proxyUrl=`https://authentic-trust-production-0edc.up.railway.app/cotacoes?symbols=${brapiSyms.join(",")}`;
          const res=await fetch(proxyUrl);
          const cotData=await res.json();
          for(const inv of yahooInvs){
            const sym=buildBrapiSym(inv);
            const price=cotData[sym];
            if(price){
              if(inv.tipo==="acao_int"||inv.tipo==="etf_int"){
                const usdBrl=novasCot["__usd_brl__"]||5.0;
                novasCot[inv.id]=price*usdBrl;
              } else {
                novasCot[inv.id]=price;
              }
            }
          }
        }catch(e){console.warn("Proxy cotações indisponível:",e);}
      }
      setCotacoes(novasCot);
      window.__mf5g_cotacoes=novasCot;
      setUltimaAtualizacao(new Date());
    }catch(e){console.error("Erro cotações:",e);}
    finally{setLoadingCot(false);}
  }

  React.useEffect(()=>{if(investimentos.length>0)buscarCotacoes();},[ investimentos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Atualização automática a cada 30 minutos
  React.useEffect(()=>{
    if(investimentos.length===0) return;
    const timer=setInterval(()=>buscarCotacoes(), 30*60*1000);
    return ()=>clearInterval(timer);
  },[investimentos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  function salvar(){
    // form.precoMedio = total investido em R$
    // form.quantidade = preço pago por unidade em R$
    const totalInvestido=parseFloat((form.precoMedio||"").replace(",","."))||0;
    const precoPorUnidade=parseFloat((form.quantidade||"").replace(",","."))||0;
    const qtd=precoPorUnidade>0?round8(totalInvestido/precoPorUnidade):0;
    const pm=precoPorUnidade; // preço médio = preço pago por unidade
    if(!form.nome.trim()||!form.simbolo.trim()||precoPorUnidade<=0||totalInvestido<=0){alert("Preencha nome, símbolo, total investido e preço por unidade.");return;}
    const totalInvestidoItem=round2(totalInvestido);
    const simboloNorm=form.simbolo.trim().toUpperCase();
    // Verificar se já existe ativo com mesmo símbolo — bloquear e redirecionar para + Aporte
    const existente=!editId&&investimentos.find(i=>i.simbolo.toUpperCase()===simboloNorm&&i.tipo===form.tipo);
    if(existente){
      alert(`⚠️ Você já tem ${existente.nome} (${simboloNorm}) cadastrado!\n\nPara registrar uma nova compra, use o botão "+ Aporte" no card do ativo. Assim o preço médio é calculado corretamente sem sobrescrever seus dados.`);
      setShowForm(false);
      setForm({nome:"",simbolo:"",tipo:"cripto",quantidade:"",precoMedio:"",emoji:""});
      setBuscaFormAtivo("");setResultadosBusca([]);
      // Abrir automaticamente o modal de aporte do ativo existente
      setAporteRVModal({inv:existente,valor:"",precoPago:""});
      return;
    }
    // Criar novo ativo
    const novoItem={
      id:editId||`inv-${Date.now()}`,
      nome:form.nome.trim(),
      simbolo:simboloNorm,
      tipo:form.tipo,
      quantidade:qtd,
      precoMedio:pm,
      emoji:form.emoji,
    };
    const novo=editId?investimentos.map(i=>i.id===editId?novoItem:i):[...investimentos,novoItem];
    onSave(novo);
    // Ao cadastrar novo ativo/aporte, perguntar se deseja registrar saída do saldo
    if(!editId && onRegistrarSaida && totalInvestidoItem>0){
      setModalSaida({
        valor: totalInvestidoItem,
        descricao: existente
          ? `Aporte: ${existente.nome} +${qtd} ${simboloNorm} (PM atualizado)`
          : `Investimento: ${form.nome.trim()} (${qtd} ${simboloNorm||"un."})`,
      });
    }
    setForm({nome:"",simbolo:"",tipo:"cripto",quantidade:"",precoMedio:"",emoji:""});
    setShowForm(false);setEditId(null);setQtdVendida("");setBuscaFormAtivo("");setResultadosBusca([]);
  }

  function excluir(id){if(window.confirm("Remover este ativo?"))onSave(investimentos.filter(i=>i.id!==id));}

  function editar(inv){
    // form.precoMedio = campo "Total investido (R$)" | form.quantidade = campo "Preço médio pago por unidade (R$)"
    // (nomes internos mantidos por compatibilidade com salvar(), mas os valores abaixo são recalculados
    // a partir do ativo real para não corromper quantidade/preço médio ao editar)
    const totalInvestidoAtual=round2(inv.quantidade*inv.precoMedio);
    setForm({nome:inv.nome,simbolo:inv.simbolo,tipo:inv.tipo,quantidade:String(inv.precoMedio),precoMedio:String(totalInvestidoAtual),emoji:(inv.emoji&&inv.emoji!=="💰"&&!Object.values(EMOJI_POR_TIPO).includes(inv.emoji))?inv.emoji:""});
    setBuscaFormAtivo(inv.nome+" ("+inv.simbolo+")");
    setEditId(inv.id);
    setShowForm(false);
    setTimeout(()=>{
      if(editFormRef.current){
        editFormRef.current.scrollIntoView({behavior:"smooth",block:"center"});
      }
    },80);
  }

  const cur=(v)=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);

  const totalInvestido=investimentos.reduce((s,i)=>s+i.quantidade*i.precoMedio,0);
  const totalAtual=investimentos.reduce((s,i)=>s+(cotacoes[i.id]?i.quantidade*cotacoes[i.id]:i.quantidade*i.precoMedio),0);
  const totalRendaFixaVal=(rendaFixa||[]).reduce((s,r)=>s+Number(r.valorInvestido||0)+Number(r.rendimentoAcumulado||0),0);
  const totalCofrinhos=(desafios||[]).filter(d=>d.ativo).reduce((s,d)=>s+(d.economiaTotal||0),0);
  const totalPatrimonio=round2(totalAtual+totalRendaFixaVal+totalCofrinhos);
  const lucro=totalAtual-totalInvestido;
  const lucroPct=totalInvestido>0?((lucro/totalInvestido)*100).toFixed(2):0;

  // Conteúdo do formulário — reutilizado no topo (novo ativo) e inline (editar)
  const formContent=(
    <>
      <div className="form-grid">
        <div className="field">
          <label className="label">Tipo</label>
          <select className="select" value={form.tipo} onChange={e=>{
            const novoTipo=e.target.value;
            const emojiAtual=form.emoji;
            const eraCustom=emojiAtual&&emojiAtual!=="💰"&&!Object.values(EMOJI_POR_TIPO).includes(emojiAtual);
            setForm({...form,tipo:novoTipo,emoji:eraCustom?emojiAtual:""});
          }}>
            <option value="cripto">🪙 Criptomoeda</option>
            <option value="moeda">💱 Moeda estrangeira</option>
            <option value="acao">📊 Ação BR (B3)</option>
            <option value="fii">🏢 FII (B3)</option>
            <option value="etf_br">📦 ETF BR (B3)</option>
            <option value="acao_int">🌎 Ação Internacional (NYSE/NASDAQ)</option>
            <option value="etf_int">🌐 ETF Internacional (NYSE/NASDAQ)</option>
            <option value="outro">💰 Outro</option>
          </select>
        </div>
        <div className="field" style={{gridColumn:"1/-1"}}>
          <label className="label">Buscar ativo</label>
          {/* Filtros por categoria */}
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
            {[
              {id:"todos",label:"Todos"},{id:"fii",label:"🏢 FIIs"},
              {id:"acao",label:"📊 Ações BR"},{id:"etf_br",label:"📦 ETFs BR"},
              {id:"cripto",label:"₿ Cripto"},{id:"acao_int",label:"🌎 Internacional"},
              {id:"etf_int",label:"🌐 ETF Int."},{id:"moeda",label:"💱 Moeda"},
            ].map(cat=>(
              <button key={cat.id} onClick={()=>setFiltroCatAtivo(cat.id)} style={{
                fontSize:11,padding:"3px 10px",borderRadius:20,border:"1px solid",cursor:"pointer",
                background:filtroCatAtivo===cat.id?"var(--accent)":"transparent",
                color:filtroCatAtivo===cat.id?"#fff":"var(--text-muted)",
                borderColor:filtroCatAtivo===cat.id?"var(--accent)":"var(--border)",
                fontWeight:filtroCatAtivo===cat.id?700:400,
              }}>{cat.label}</button>
            ))}
          </div>
          <div style={{position:"relative"}}>
            <input className="input"
              placeholder={filtroCatAtivo==="cripto"?"🔍 Ex: Bitcoin, BTC, ETH..."
                :filtroCatAtivo==="moeda"?"🔍 Ex: Dólar, USD, EUR..."
                :filtroCatAtivo==="acao_int"||filtroCatAtivo==="etf_int"?"🔍 Ex: Apple, AAPL, VOO..."
                :"🔍 Ex: VGIA11, Petrobras, BOVA11..."}
              value={buscaFormAtivo}
              onChange={e=>{
                const v=e.target.value;
                setBuscaFormAtivo(v);
                if(buscaTimerRef.current) clearTimeout(buscaTimerRef.current);
                if(v.length<2){setResultadosBusca([]);return;}
                // Busca local imediata nos ativos conhecidos
                const termo=v.toLowerCase();
                const locais=ATIVOS_CONHECIDOS.filter(a=>
                  (filtroCatAtivo==="todos"||a.tipo===filtroCatAtivo)&&
                  (a.simbolo.toLowerCase().includes(termo)||a.nome.toLowerCase().includes(termo))
                ).slice(0,8);
                setResultadosBusca(locais);
                // Busca remota na Brapi com debounce (300ms) — apenas para FIIs e ações BR
                if(filtroCatAtivo==="todos"||filtroCatAtivo==="fii"||filtroCatAtivo==="acao"||filtroCatAtivo==="etf_br"){
                  buscaTimerRef.current=setTimeout(async()=>{
                    try{
                      setBuscandoAtivo(true);
                      const r=await fetch(`https://authentic-trust-production-0edc.up.railway.app/buscar-ativos?q=${encodeURIComponent(v.toUpperCase())}`);
                      const data=await r.json();
                      if(data.length>0){
                        // Mesclar resultados remotos com locais, sem duplicatas
                        const todos=[...locais,...data.filter(d=>!locais.some(l=>l.simbolo===d.simbolo))].slice(0,12);
                        setResultadosBusca(todos);
                      }
                    }catch(e){}
                    finally{setBuscandoAtivo(false);}
                  },300);
                }
              }}
              autoComplete="off"
            />
            {buscandoAtivo&&<span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",fontSize:12,color:"var(--text-muted)"}}>⏳</span>}
          </div>
          {/* Lista de resultados */}
          {buscaFormAtivo.length>=2&&!form.simbolo&&(
            <div style={{marginTop:6}}>
              {resultadosBusca.length>0?(
                <div style={{display:"flex",flexDirection:"column",gap:2,maxHeight:240,overflowY:"auto",background:"var(--bg-input)",borderRadius:10,padding:6,border:"1px solid var(--border)"}}>
                  {resultadosBusca.map(a=>(
                    <button key={a.simbolo+a.tipo}
                      onClick={()=>{
                        const simboloNorm=a.simbolo.toUpperCase();
                        const jaExiste=investimentos.find(i=>i.simbolo.toUpperCase()===simboloNorm&&i.tipo===a.tipo);
                        if(jaExiste){
                          setBuscaFormAtivo("");setResultadosBusca([]);
                          setShowForm(false);
                          setAporteRVModal({inv:jaExiste,valor:"",precoPago:""});
                          alert(`✅ Você já tem ${a.nome} cadastrado!\n\nAbrindo o + Aporte para registrar sua nova compra.`);
                          return;
                        }
                        setForm({...form,nome:a.nome,simbolo:a.simbolo,tipo:a.tipo,emoji:""});
                        setBuscaFormAtivo("");
                        setResultadosBusca([]);
                      }}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,border:"none",background:"transparent",cursor:"pointer",textAlign:"left",width:"100%"}}
                      onMouseEnter={e=>e.currentTarget.style.background="var(--bg-card)"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                    >
                      <span style={{fontSize:20,width:28,textAlign:"center"}}>{EMOJI_POR_TIPO[a.tipo]||"💰"}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,color:"var(--text-primary)"}}>{a.simbolo}</div>
                        <div style={{fontSize:11,color:"var(--text-muted)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.nome}</div>
                      </div>
                      <span style={{fontSize:10,color:"var(--text-muted)",background:"var(--bg-card)",borderRadius:6,padding:"2px 6px",border:"1px solid var(--border)",flexShrink:0}}>
                        {a.tipo==="fii"?"FII":a.tipo==="acao"?"Ação BR":a.tipo==="etf_br"?"ETF BR":a.tipo==="cripto"?"Cripto":a.tipo==="acao_int"?"Int.":a.tipo==="etf_int"?"ETF Int.":a.tipo==="moeda"?"Moeda":"Outro"}
                      </span>
                    </button>
                  ))}
                </div>
              ):(
                <div style={{background:"#F59E0B11",border:"1px solid #F59E0B44",borderRadius:8,padding:"10px 12px",fontSize:12,color:"#F59E0B"}}>
                  ⚠️ Não encontrado na lista — preencha manualmente:
                  <div style={{display:"flex",gap:8,marginTop:8}}>
                    <div style={{flex:2}}>
                      <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:3}}>Nome</div>
                      <input className="input" placeholder="Ex: Monad" value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})}/>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:3}}>Símbolo</div>
                      <input className="input" placeholder="Ex: MON" value={form.simbolo} onChange={e=>setForm({...form,simbolo:e.target.value.toUpperCase()})}/>
                    </div>
                  </div>
                  {form.nome&&form.simbolo&&(
                    <div style={{marginTop:8,fontSize:11,color:"#22c55e"}}>✅ Ativo preenchido! Complete quantidade e preço médio abaixo.</div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Ativo selecionado */}
          {form.nome&&form.simbolo&&(
            <div style={{marginTop:6,padding:"8px 12px",background:"#14B8A611",border:"1px solid #14B8A644",borderRadius:8,fontSize:13,display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:22}}>{getEmojiAtivo(form)}</span>
              <div>
                <div style={{fontWeight:700,color:"var(--text-primary)"}}>{form.simbolo} <span style={{fontWeight:400,color:"var(--text-muted)"}}>· {form.nome}</span></div>
                <div style={{fontSize:11,color:"var(--text-muted)"}}>{form.tipo==="fii"?"FII":form.tipo==="acao"?"Ação BR":form.tipo==="etf_br"?"ETF BR":form.tipo==="cripto"?"Criptomoeda":form.tipo==="acao_int"?"Ação Internacional":form.tipo==="etf_int"?"ETF Internacional":form.tipo==="moeda"?"Moeda":"Outro"}</div>
              </div>
              <button onClick={()=>{setForm({...form,nome:"",simbolo:"",tipo:"cripto",emoji:""});setBuscaFormAtivo("");setResultadosBusca([]);}}
                style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"var(--text-muted)",fontSize:16,padding:4}}>✕</button>
            </div>
          )}
        </div>
        <div className="field">
          <label className="label">
            Total investido (R$)
            <span style={{fontSize:10,fontWeight:400,color:"var(--text-muted)",marginLeft:6}}>quanto você gastou no total</span>
          </label>
          <input className="input" type="number" step="any"
            placeholder={
              form.tipo==="cripto"?`Ex: 950,00 (total gasto em ${form.simbolo||"cripto"})`:
              form.tipo==="moeda"?`Ex: 500,00 (total em ${form.simbolo||"moeda"} comprado)`:
              `Ex: 250,00 (total gasto em ${form.simbolo||"cotas/ações"})`
            }
            value={form.precoMedio}
            onChange={e=>setForm({...form,precoMedio:e.target.value})}/>
        </div>
        <div className="field">
          <label className="label">
            Preço médio pago por unidade (R$)
            <span style={{fontSize:10,fontWeight:400,color:"var(--text-muted)",marginLeft:6}}>quanto pagou por cada unidade</span>
          </label>
          <input className="input" type="number" step="any"
            placeholder={
              form.tipo==="cripto"?`Ex: 580000 (preço por ${form.simbolo||"BTC"})`:
              form.tipo==="moeda"?`Ex: 5.80 (preço do dólar)`:
              `Ex: 25.50 (preço por cota/ação)`
            }
            value={form.quantidade}
            onChange={e=>setForm({...form,quantidade:e.target.value})}/>
          {form.quantidade&&form.precoMedio&&parseFloat(form.quantidade)>0&&parseFloat(form.precoMedio)>0&&(
            <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>
              📊 Quantidade calculada: <strong style={{color:"#14B8A6"}}>
                {((parseFloat(form.precoMedio.replace(",","."))||0)/(parseFloat(form.quantidade)||1)).toFixed(8).replace(/\.?0+$/,"")}
              </strong> {form.simbolo||"unidades"}
            </div>
          )}
          <div style={{fontSize:11,color:"#7C83FF",marginTop:4}}>
            💡 A cotação atual é buscada automaticamente para calcular lucro/prejuízo.
          </div>
        </div>
      </div>
      {!editId&&(
        <div style={{marginTop:8,marginBottom:12}}>
          <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:6}}>Sugestões rápidas:</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {ATIVOS_SUGERIDOS.map(a=>(
              <button key={a.id} className="secondary-btn" style={{fontSize:11,padding:"4px 10px"}}
                onClick={()=>{
                  const simboloNorm=a.simbolo.toUpperCase();
                  const jaExiste=investimentos.find(i=>i.simbolo.toUpperCase()===simboloNorm&&i.tipo===a.tipo);
                  if(jaExiste){setShowForm(false);setAporteRVModal({inv:jaExiste,valor:"",precoPago:""});alert(`✅ Você já tem ${a.nome} cadastrado!\n\nAbrindo o + Aporte para registrar sua nova compra.`);return;}
                  setForm({...form,nome:a.nome,simbolo:a.simbolo,tipo:a.tipo,emoji:a.emoji});
                }}>
                {a.emoji} {a.nome}
              </button>
            ))}
          </div>
        </div>
      )}
      {editId&&(()=>{
        const invAtual=investimentos.find(i=>i.id===editId);
        const qtdV=parseFloat(qtdVendida)||0;
        const cotAtual=cotacoes[editId]||invAtual?.precoMedio||0;
        const valorVenda=round2(qtdV*cotAtual);
        return(
          <div style={{marginTop:12,padding:14,borderRadius:12,background:"#EF444408",border:"1px solid #EF444433"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#EF4444",marginBottom:4}}>💸 Vendeu cotas/unidades?</div>
            <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:10}}>
              Preencha aqui apenas se quiser registrar uma venda. O <strong>Salvar</strong> abaixo só atualiza os dados do ativo.
            </div>
            <div className="form-grid">
              <div className="field">
                <label className="label">Qtd. vendida</label>
                <input className="input" type="number" step="any" placeholder="0" value={qtdVendida} onChange={e=>setQtdVendida(e.target.value)}/>
              </div>
              <div className="field">
                <label className="label">Valor da venda</label>
                <input className="input" value={qtdV>0?currency(valorVenda):""} readOnly style={{background:"var(--bg-input)",color:"#22c55e",fontWeight:700}} placeholder="—"/>
              </div>
            </div>
            {qtdV>0&&qtdV<=(invAtual?.quantidade||0)&&(
              <button style={{width:"100%",padding:"10px",borderRadius:10,background:"#EF4444",color:"#fff",fontWeight:700,fontSize:13,border:"none",cursor:"pointer",marginTop:4}} onClick={()=>{
                const novaQtd=Math.round(((invAtual.quantidade||0)-qtdV)*1e8)/1e8;
                const novos=novaQtd>0.000000001?investimentos.map(i=>i.id===editId?{...i,quantidade:novaQtd}:i):investimentos.filter(i=>i.id!==editId);
                onSave(novos);
                onResgate(valorVenda,`Venda: ${invAtual.nome} (${qtdV} ${invAtual.simbolo})`,"Ativos (cripto/ações/FIIs)");
                setEditId(null);setQtdVendida("");
              }}>
                🔴 Registrar venda de {qtdV} {invAtual?.simbolo} · {currency(valorVenda)}
              </button>
            )}
            {qtdV>(invAtual?.quantidade||0)&&(
              <div style={{fontSize:11,color:"#EF4444",marginTop:6}}>⚠️ Quantidade maior que o disponível ({invAtual?.quantidade} {invAtual?.simbolo})</div>
            )}
          </div>
        );
      })()}
      <div className="actions" style={{marginTop:16,paddingTop:12,borderTop:"1px solid var(--border)"}}>
        <button className="ghost-btn" onClick={()=>{setShowForm(false);setEditId(null);setQtdVendida("");}}>Cancelar</button>
        <button className="primary-btn" onClick={salvar}>💾 {editId?"Salvar alterações":"Salvar ativo"}</button>
      </div>
    </>
  );

  return(
    <section className="section-card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8}}>
        <h2 className="section-title" style={{margin:0}}>📊 Meus Investimentos</h2>
        <div style={{display:"flex",gap:8}}>
          <button className="secondary-btn" onClick={buscarCotacoes} disabled={loadingCot} style={{fontSize:13}}>
            {loadingCot?"⏳ Atualizando...":"🔄 Atualizar cotações"}
          </button>
          <button className="primary-btn" onClick={()=>{setShowForm(!showForm);setEditId(null);setForm({nome:"",simbolo:"",tipo:"cripto",quantidade:"",precoMedio:"",emoji:""});setBuscaFormAtivo("");setFiltroCatAtivo("todos");}}>
            + Ativo
          </button>
        </div>
      </div>

      {/* Cards de patrimônio — empilhados no mobile para não cortar valores */}
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
        <div style={{background:"linear-gradient(135deg,#14B8A622,#14B8A610)",border:"2px solid #14B8A666",borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
          <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:4}}>💰 Total Patrimônio</div>
          <div style={{fontSize:20,fontWeight:900,color:"#14B8A6",wordBreak:"break-word",lineHeight:1.2}}>{cur(totalPatrimonio)}</div>
          <div style={{fontSize:10,color:"var(--text-muted)",marginTop:4}}>Pilar Guardar 5G</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <div style={{background:"var(--bg-card)",border:"1px solid #22c55e44",borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
            <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:4}}>🏦 Renda Fixa + Cofrinhos</div>
            <div style={{fontSize:15,fontWeight:800,color:"#22c55e",wordBreak:"break-word",lineHeight:1.2}}>{cur(totalRendaFixaVal+totalCofrinhos)}</div>
            <div style={{fontSize:9,color:"var(--text-muted)",marginTop:4}}>CDB, LCI, LCA + Desafios</div>
          </div>
          <div style={{background:"var(--bg-card)",border:"1px solid #7C83FF44",borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
            <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:4}}>📈 Renda Variável</div>
            <div style={{fontSize:15,fontWeight:800,color:"#7C83FF",wordBreak:"break-word",lineHeight:1.2}}>{cur(totalAtual)}</div>
            <div style={{fontSize:9,color:"var(--text-muted)",marginTop:4}}>Cripto, ações, FIIs, ETFs</div>
          </div>
        </div>
      </div>

      {/* Formulário novo ativo — só aparece no topo quando showForm=true (novo ativo) */}
      {showForm&&!editId&&(
        <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:16,marginBottom:16}}>
          <h3 style={{margin:"0 0 12px",fontSize:14,color:"var(--text-primary)"}}>➕ Novo ativo</h3>
          {formContent}
        </div>
      )}

      {/* Lista de ativos */}
      {investimentos.length>0&&(
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:13,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em"}}>📈 Renda Variável</div>
          <div style={{display:"flex",alignItems:"center",gap:6,background:lucro>=0?"#14532d22":"#450a0a22",border:`1px solid ${lucro>=0?"#22c55e44":"#ef444444"}`,borderRadius:10,padding:"6px 12px",fontSize:12}}>
            <span style={{color:"var(--text-muted)"}}>Resultado:</span>
            <strong style={{color:lucro>=0?"#22c55e":"#ef4444"}}>{lucro>=0?"+":""}{cur(lucro)} ({lucro>=0?"+":""}{lucroPct}%)</strong>
          </div>
        </div>
      )}
      {investimentos.length>1&&<input className="input" placeholder="🔍 Buscar ativo..." value={buscaAtivo} onChange={e=>setBuscaAtivo(e.target.value)} style={{marginBottom:10}}/>}
      {investimentos.length===0?(
        <div className="empty" style={{padding:"40px 0"}}>
          <div style={{fontSize:48,marginBottom:12}}>💼</div>
          <div>Nenhum ativo cadastrado ainda.</div>
          <div style={{fontSize:13,color:"var(--text-muted)",marginTop:4}}>Clique em <strong>+ Ativo</strong> para começar!</div>
        </div>
      ):(()=>{
        const ativosFiltrados=investimentos.filter(inv=>
          inv.nome.toLowerCase().includes(buscaAtivo.toLowerCase())||
          inv.simbolo.toLowerCase().includes(buscaAtivo.toLowerCase())
        );
        const ativosComValor=ativosFiltrados.map(inv=>{
          const cotAtual=cotacoes[inv.id];
          const vlAtual=cotAtual?inv.quantidade*cotAtual:inv.quantidade*inv.precoMedio;
          return{...inv,_vlAtual:vlAtual};
        });
        const ORDEM_TIPOS_ATIVO={"fii":1,"acao":2,"etf_br":3,"cripto":4,"acao_int":5,"etf_int":6,"moeda":7,"cofrinho":8,"outro":9};
        const LABEL_TIPO={"fii":"🏢 FIIs","acao":"📊 Ações BR","etf_br":"📦 ETFs BR","cripto":"₿ Criptomoedas","acao_int":"🌎 Ações Internacionais","etf_int":"🌐 ETFs Internacionais","moeda":"💱 Moedas","cofrinho":"🐷 Cofrinhos e Desafios","outro":"💰 Outros"};
        const grupos={};
        for(const inv of ativosComValor){
          const t=inv.tipo||"outro";
          if(!grupos[t])grupos[t]=[];
          grupos[t].push(inv);
        }
        for(const t of Object.keys(grupos)) grupos[t].sort((a,b)=>b._vlAtual-a._vlAtual);
        const tiposOrdenados=Object.keys(grupos).sort((a,b)=>(ORDEM_TIPOS_ATIVO[a]||9)-(ORDEM_TIPOS_ATIVO[b]||9));
        return(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {tiposOrdenados.map(tipo=>{
            // Calcular total do grupo
            const totalGrupo=grupos[tipo].reduce((s,inv)=>{
              const cot=cotacoes[inv.id];
              return s+(cot?inv.quantidade*cot:inv.quantidade*inv.precoMedio);
            },0);
            const lucroGrupo=grupos[tipo].reduce((s,inv)=>{
              const cot=cotacoes[inv.id];
              const vlAtual=cot?inv.quantidade*cot:inv.quantidade*inv.precoMedio;
              return s+(vlAtual-inv.quantidade*inv.precoMedio);
            },0);
            return(
            <div key={tipo}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em",margin:"8px 0 4px",paddingLeft:4,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span>{LABEL_TIPO[tipo]||tipo}</span>
                <span style={{fontWeight:400,fontSize:10}}>({grupos[tipo].length})</span>
                <span style={{marginLeft:"auto",fontWeight:800,fontSize:12,color:"var(--text-primary)"}}>{currency(totalGrupo)}</span>
                {lucroGrupo!==0&&(()=>{
                  const totalInvGrupo=grupos[tipo].reduce((s,inv)=>s+inv.quantidade*inv.precoMedio,0);
                  const pctGrupo=totalInvGrupo>0?((lucroGrupo/totalInvGrupo)*100).toFixed(1):0;
                  return <span style={{fontSize:10,fontWeight:700,color:lucroGrupo>=0?"#22c55e":"#ef4444"}}>{lucroGrupo>=0?"+":""}{currency(lucroGrupo)} ({lucroGrupo>=0?"+":""}{pctGrupo}%)</span>;
                })()}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {grupos[tipo].map(inv=>{
            const cotAtual=cotacoes[inv.id];
            const vlInvestido=inv.quantidade*inv.precoMedio;
            const vlAtual=cotAtual?inv.quantidade*cotAtual:vlInvestido;
            const lucroItem=vlAtual-vlInvestido;
            const pct=vlInvestido>0?((lucroItem/vlInvestido)*100).toFixed(2):0;
            // Se este ativo está em edição, renderiza o form inline no lugar do card
            if(editId===inv.id){
              return(
                <div key={inv.id} ref={editFormRef} style={{background:"var(--bg-card)",border:"2px solid #7C83FF66",borderRadius:14,padding:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                    <h3 style={{margin:0,fontSize:14,color:"var(--text-primary)"}}>✏️ Editando: {inv.nome}</h3>
                    <button className="icon-btn" onClick={()=>{setEditId(null);setQtdVendida("");}} title="Cancelar">✕</button>
                  </div>
                  {formContent}
                </div>
              );
            }
            return(
              <div key={inv.id} style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:"14px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{fontSize:32,width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center",background:"var(--bg-input)",borderRadius:12}}>{getEmojiAtivo(inv)}</div>
                  <div>
                    <div style={{fontWeight:700,fontSize:15,color:"var(--text-primary)"}}>{inv.nome}</div>
                    <div style={{fontSize:12,color:"var(--text-muted)"}}>{inv.quantidade} {inv.simbolo} · Preço de compra: {cur(inv.precoMedio)}/un.</div>
                    {cotAtual&&<div style={{fontSize:11,color:"var(--text-muted)"}}>Cotação: {cur(cotAtual)}</div>}
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontWeight:800,fontSize:16,color:"var(--text-primary)"}}>{cur(vlAtual)}</div>
                  <div style={{fontSize:12,color:lucroItem>=0?"#22c55e":"#ef4444",fontWeight:600}}>{lucroItem>=0?"+":""}{cur(lucroItem)} ({lucroItem>=0?"+":""}{pct}%)</div>
                  <div style={{display:"flex",gap:6,marginTop:6,justifyContent:"flex-end",flexWrap:"wrap"}}>
                    {onRegistrarDividendo&&<button className="secondary-btn" style={{fontSize:11,padding:"4px 8px",background:"#22c55e18",border:"1px solid #22c55e44",color:"#22c55e",fontWeight:700}} onClick={()=>setDividendoModal({inv,valor:""})}>+ Dividendo</button>}
                    <button className="secondary-btn" style={{fontSize:11,padding:"4px 8px",background:"#7C83FF18",border:"1px solid #7C83FF44",color:"#7C83FF",fontWeight:700}} onClick={()=>setAporteRVModal({inv,valor:"",precoPago:""})}>+ Aporte</button>
                    <button className="secondary-btn" style={{fontSize:11,padding:"4px 8px",background:"#ef444418",border:"1px solid #ef444444",color:"#ef4444",fontWeight:700}} onClick={()=>setVenderRVModal({inv,qtd:"",valorRecebido:""})}>- Vender</button>
                    <button className="icon-btn" onClick={()=>editar(inv)}>✏️</button>
                    <button className="icon-btn" onClick={()=>excluir(inv.id)}>🗑️</button>
                  </div>
                </div>
              </div>
            );
          })}
              </div>
            </div>
            );
          })}
        </div>
        );
      })()}
      {/* Modal: registrar saída do saldo ao cadastrar ativo */}
      {modalSaida&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setModalSaida(null)}>
          <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:380,textAlign:"center"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:36,marginBottom:8}}>💸</div>
            <h3 style={{margin:"0 0 8px",fontSize:16,color:"var(--text-primary)"}}>Deseja descontar do saldo?</h3>
            <p style={{fontSize:13,color:"var(--text-muted)",margin:"0 0 6px"}}>
              Você investiu <strong style={{color:"var(--text-primary)"}}>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(modalSaida.valor)}</strong>
            </p>
            <p style={{fontSize:12,color:"var(--text-muted)",margin:"0 0 20px"}}>
              Quer registrar como saída do saldo? Isso cria uma despesa em <strong>Investimentos</strong> para manter o controle correto.
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <button className="primary-btn" style={{width:"100%"}} onClick={()=>{
                onRegistrarSaida(modalSaida.valor,modalSaida.descricao);
                setModalSaida(null);
              }}>✅ Sim, descontar do saldo</button>
              <button className="ghost-btn" style={{width:"100%"}} onClick={()=>setModalSaida(null)}>
                ❌ Não, já saiu de outra conta
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal + Aporte Renda Variável */}
      {/* Modal - Vender ativo RV */}
      {venderRVModal&&(()=>{
        const inv=venderRVModal.inv;
        const cotAtual=cotacoes[inv.id]||inv.precoMedio;
        const qtdVendida=parseFloat((venderRVModal.qtd||"").replace(",","."))||0;
        const valorRecebido=qtdVendida>0?round2(qtdVendida*cotAtual):0;
        const custoVenda=round2(qtdVendida*inv.precoMedio);
        const lucro=round2(valorRecebido-custoVenda);
        return(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setVenderRVModal(null)}>
            <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:400}} onClick={e=>e.stopPropagation()}>
              <h3 style={{margin:"0 0 16px",fontSize:16,color:"var(--text-primary)"}}>💸 Vender — {inv.nome} ({inv.simbolo})</h3>
              <div style={{background:"var(--bg-card)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,display:"flex",justifyContent:"space-between"}}>
                <span style={{color:"var(--text-muted)"}}>Você possui:</span>
                <strong>{inv.quantidade} {inv.simbolo} · PM: {currency(inv.precoMedio)}</strong>
              </div>
              <div className="field" style={{marginBottom:12}}>
                <label className="label">Quantidade a vender</label>
                <input className="input" type="number" step="any" placeholder={`Ex: 0.0001 (max: ${inv.quantidade})`}
                  value={venderRVModal.qtd}
                  onChange={e=>setVenderRVModal(m=>({...m,qtd:e.target.value}))} autoFocus/>
              </div>
              {qtdVendida>0&&(
                <div style={{background:"#7C83FF11",border:"1px solid #7C83FF33",borderRadius:8,padding:"10px 12px",fontSize:12,marginBottom:14}}>
                  <div>💰 Valor estimado recebido: <strong style={{color:"#22c55e"}}>{currency(valorRecebido)}</strong></div>
                  <div style={{marginTop:4}}>📊 Custo médio: <strong>{currency(custoVenda)}</strong></div>
                  <div style={{marginTop:4,color:lucro>=0?"#22c55e":"#ef4444"}}>
                    {lucro>=0?"📈 Lucro":"📉 Prejuízo"}: <strong>{lucro>=0?"+":""}{currency(lucro)}</strong>
                  </div>
                  <div style={{marginTop:4,color:"var(--text-muted)"}}>✅ Valor entra automaticamente no caixa como Entrada</div>
                </div>
              )}
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <button className="ghost-btn" onClick={()=>setVenderRVModal(null)}>Cancelar</button>
                <button className="primary-btn" style={{background:"linear-gradient(135deg,#ef4444,#dc2626)"}}
                  disabled={qtdVendida<=0||qtdVendida>inv.quantidade}
                  onClick={()=>{
                    if(qtdVendida<=0){alert("Informe a quantidade a vender.");return;}
                    if(qtdVendida>inv.quantidade){alert(`Você só tem ${inv.quantidade} ${inv.simbolo}.`);return;}
                    // Atualizar quantidade do ativo
                    const novaQtd=round8(inv.quantidade-qtdVendida);
                    const novos=novaQtd<=0
                      ? investimentos.filter(i=>i.id!==inv.id)
                      : investimentos.map(i=>i.id===inv.id?{...i,quantidade:novaQtd}:i);
                    onSave(novos);
                    // Registrar entrada no caixa
                    if(onRegistrarDividendo) onRegistrarDividendo(valorRecebido,`Venda: ${inv.nome} (${qtdVendida} ${inv.simbolo})`,"Resgate de Investimentos");
                    setVenderRVModal(null);
                  }}>💸 Confirmar venda</button>
              </div>
            </div>
          </div>
        );
      })()}

      {aporteRVModal&&(()=>{
        const inv=aporteRVModal.inv;
        const valorAporte=parseFloat((aporteRVModal.valor||"").replace(",","."))||0;
        const precoPago=parseFloat((aporteRVModal.precoPago||"").replace(",","."))||0;
        // Usa o preço que a pessoa pagou — não a cotação atual
        const qtdNova=precoPago>0?round8(valorAporte/precoPago):0;
        const qtdTotal=round8(inv.quantidade+qtdNova);
        const pmNovo=qtdTotal>0?round2((inv.quantidade*inv.precoMedio+valorAporte)/qtdTotal):inv.precoMedio;
        const cotAtual=cotacoes[inv.id]||inv.precoMedio;
        return(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setAporteRVModal(null)}>
            <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:400}} onClick={e=>e.stopPropagation()}>
              <h3 style={{margin:"0 0 16px",fontSize:16,color:"var(--text-primary)"}}>📈 Novo aporte — {inv.nome}</h3>

              <div className="field" style={{marginBottom:12}}>
                <label className="label">Valor investido (R$)</label>
                <input className="input" type="number" step="any" placeholder="Ex: 1.500,00"
                  value={aporteRVModal.valor}
                  onChange={e=>setAporteRVModal(m=>({...m,valor:e.target.value}))} autoFocus/>
              </div>

              <div className="field" style={{marginBottom:12}}>
                <label className="label">Preço médio que você pagou por unidade (R$)</label>
                <input className="input" type="number" step="any" placeholder={`Ex: ${Math.round(cotAtual)} (só números)`}
                  value={aporteRVModal.precoPago}
                  onChange={e=>setAporteRVModal(m=>({...m,precoPago:e.target.value}))}/>
                <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>
                  Cotação atual: <strong>{currency(cotAtual)}</strong> por {inv.simbolo} · Digite só números, sem pontos ou vírgulas
                </div>
              </div>

              {valorAporte>0&&precoPago>0&&(
                <div style={{background:"#7C83FF11",border:"1px solid #7C83FF33",borderRadius:8,padding:"10px 12px",fontSize:12,marginBottom:16}}>
                  <div>📦 Quantidade adquirida: <strong>+{qtdNova} {inv.simbolo}</strong></div>
                  <div style={{marginTop:4}}>📊 Novo preço médio ponderado: <strong>{currency(pmNovo)}</strong></div>
                  <div style={{marginTop:4}}>📈 Quantidade total: <strong>{qtdTotal} {inv.simbolo}</strong></div>
                </div>
              )}

              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <button className="ghost-btn" onClick={()=>setAporteRVModal(null)}>Cancelar</button>
                <button className="primary-btn"
                  disabled={valorAporte<=0||precoPago<=0}
                  onClick={()=>{
                    if(valorAporte<=0||precoPago<=0){alert("Preencha o valor investido e o preço pago por unidade.");return;}
                    // Salvar histórico de aportes no próprio ativo
                    const novoAporte={
                      id:`aporte-${Date.now()}`,
                      data:new Date().toISOString().slice(0,10),
                      valor:valorAporte,
                      precoPago,
                      quantidade:round8(qtdNova),
                    };
                    const historico=[...(inv.historico_aportes||[]),novoAporte];
                    const novos=investimentos.map(i=>i.id===inv.id?{...i,quantidade:qtdTotal,precoMedio:pmNovo,historico_aportes:historico}:i);
                    onSave(novos);
                    setAporteRVModal(null);
                    setModalSaida({valor:valorAporte,descricao:`Aporte: ${inv.nome} +${qtdNova} ${inv.simbolo}`});
                  }}>💾 Salvar aporte</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal + Dividendo */}
      {dividendoModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setDividendoModal(null)}>
          <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:400}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:"0 0 16px",fontSize:16,color:"var(--text-primary)"}}>💸 Registrar dividendo — {dividendoModal.inv.nome}</h3>
            <div style={{marginBottom:12}}>
              <label style={{fontSize:13,fontWeight:600,color:"var(--text-muted)",display:"block",marginBottom:6}}>Valor recebido (R$)</label>
              <input className="input" type="text" inputMode="decimal" placeholder="Ex.: 150,00" value={dividendoModal.valor}
                onChange={e=>setDividendoModal(d=>({...d,valor:e.target.value}))} autoFocus/>
            </div>
            <div style={{fontSize:12,color:"var(--text-muted)",background:"#22c55e18",border:"1px solid #22c55e30",borderRadius:8,padding:"8px 12px",marginBottom:16,lineHeight:1.6}}>
              ✅ Será registrado como entrada na categoria <strong style={{color:"#22c55e"}}>Dividendos</strong> no mês atual.
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="ghost-btn" onClick={()=>setDividendoModal(null)}>Cancelar</button>
              <button className="primary-btn" onClick={()=>{
                const v=parseFloat((dividendoModal.valor||"").replace(",","."));
                if(v>0&&onRegistrarDividendo){
                  onRegistrarDividendo(v,`Dividendo: ${dividendoModal.inv.nome} (${dividendoModal.inv.simbolo})`);
                  setDividendoModal(null);
                }
              }}>Registrar</button>
            </div>
          </div>
        </div>
      )}

      {Object.keys(cotacoes).length>0&&<div style={{fontSize:11,color:"var(--text-muted)",textAlign:"center",marginTop:12}}>Cotações em tempo real: CoinGecko (cripto) · AwesomeAPI (moedas) · Yahoo Finance via Railway (FIIs, ações BR e internacionais){ultimaAtualizacao&&` · Atualizado às ${ultimaAtualizacao.toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})} · próxima em 30min`}</div>}

      {/* ─── Renda Fixa ─── */}
      <RendaFixaSection rendaFixa={rendaFixa} onSave={onSaveRendaFixa} onRegistrarSaida={onRegistrarSaida} onRegistrarDividendo={onRegistrarDividendo}/>

      {/* ─── Reservas e Cofrinhos ─── */}
      {desafios.filter(d=>d.ativo&&(d.economiaTotal||0)>0).length>0&&(
        <div style={{marginTop:24}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <h3 style={{margin:0,fontSize:15,fontWeight:800,color:"var(--text-primary)"}}>🐷 Reservas e Cofrinhos</h3>
            <div style={{fontSize:12,color:"var(--text-muted)"}}>Acumulado nos desafios ativos</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {desafios.filter(d=>d.ativo&&(d.economiaTotal||0)>0).map(d=>(
              <div key={d.id} style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:12,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontSize:28}}>{d.emoji||"🏆"}</span>
                  <div>
                    <div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)"}}>{d.nome}</div>
                    <div style={{fontSize:11,color:"var(--text-muted)"}}>Cofrinho ativo · {d.quantidade}x/mês</div>
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontWeight:800,fontSize:15,color:"#14B8A6"}}>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(d.economiaTotal||0)}</div>
                  <div style={{fontSize:11,color:"var(--text-muted)"}}>acumulado total</div>
                </div>
              </div>
            ))}
            <div style={{background:"var(--bg-card)",border:"1px solid #14B8A644",borderRadius:12,padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:13,fontWeight:700,color:"#14B8A6"}}>Total em cofrinhos</span>
              <span style={{fontSize:15,fontWeight:800,color:"#14B8A6"}}>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(desafios.filter(d=>d.ativo).reduce((s,d)=>s+(d.economiaTotal||0),0))}</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── Gráfico MEU FUTURO ─── */}
      {futureData.length>0&&(
        <div style={{marginTop:28}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <h3 style={{margin:0,fontSize:15,fontWeight:800,color:"var(--text-primary)"}}>📉 MEU FUTURO</h3>
            <div style={{fontSize:11,color:"var(--text-muted)"}}>Evolução patrimonial mensal</div>
          </div>
          <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:"16px 12px"}}>
            <LineChart points={futureData} color="#22C55E" label="Meu Futuro"/>
          </div>
          <div style={{marginTop:12,display:"flex",gap:8,flexWrap:"wrap"}}>
            <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 14px",flex:1,minWidth:140}}>
              <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>📈 O que alimenta este gráfico</div>
              <div style={{fontSize:12,lineHeight:1.7}}>
                <div>• <strong>Mês atual:</strong> Patrimônio total real (ativos + renda fixa + cofrinhos)</div>
                <div>• <strong>Meses anteriores:</strong> Aportes em Investimentos/Reserva + Rendimentos/Dividendos</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Componente Renda Fixa ───────────────────────────────
const TIPOS_RENDA_FIXA=["CDB","LCA","LCI","Tesouro Direto","LIG","Debênture","CRI","CRA","Outro"];
function RendaFixaSection({rendaFixa=[],onSave,onRegistrarSaida,onRegistrarDividendo}){
  const cur=v=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);
  const[showForm,setShowForm]=React.useState(false);
  const[editId,setEditId]=React.useState(null);
  const emptyRF={nome:"",tipo:"CDB",valorInvestido:"",rendimentoAcumulado:"",dataVencimento:"",liquidez:"No vencimento",observacoes:""};
  const[form,setForm]=React.useState(emptyRF);

  const[modalSaidaRF,setModalSaidaRF]=React.useState(null);
  const[aporteRFModal,setAporteRFModal]=React.useState(null);
  const[rendimentoRFModal,setRendimentoRFModal]=React.useState(null);
  // eslint-disable-next-line no-unused-vars
  const[resgateRFModal,setResgateRFModal]=React.useState(null); // {ativo, valor}
  function salvar(){
    const vi=parseFloat(form.valorInvestido.replace(",","."))||0;
    const ra=parseFloat(form.rendimentoAcumulado.replace(",","."))||0;
    if(!form.nome.trim()||vi<=0) return;
    const item={id:editId||("rf_"+Date.now()),nome:form.nome.trim(),tipo:form.tipo,valorInvestido:vi,rendimentoAcumulado:ra,dataVencimento:form.dataVencimento,liquidez:form.liquidez,observacoes:form.observacoes};
    const novo=editId?rendaFixa.map(r=>r.id===editId?item:r):[...rendaFixa,item];
    onSave(novo);
    setForm(emptyRF);setShowForm(false);setEditId(null);
    // Ao cadastrar novo, perguntar se desconta do saldo
    if(!editId&&onRegistrarSaida&&vi>0){
      setModalSaidaRF({valor:vi,descricao:`Investimento: ${item.nome} (${item.tipo})`});
    }
  }
  function excluir(id){onSave(rendaFixa.filter(r=>r.id!==id));}
  function editar(r){setForm({nome:r.nome,tipo:r.tipo,valorInvestido:String(r.valorInvestido),rendimentoAcumulado:String(r.rendimentoAcumulado),dataVencimento:r.dataVencimento||"",liquidez:r.liquidez||"No vencimento",observacoes:r.observacoes||""});setEditId(r.id);setShowForm(true);}

  const totalInvestido=rendaFixa.reduce((s,r)=>s+r.valorInvestido,0);
  const totalRendimento=rendaFixa.reduce((s,r)=>s+(r.rendimentoAcumulado||0),0);
  const totalAtual=totalInvestido+totalRendimento;

  return(
    <div style={{marginTop:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <h3 style={{margin:0,fontSize:15,fontWeight:800,color:"var(--text-primary)"}}>🏦 Renda Fixa</h3>
        <button className="primary-btn" style={{fontSize:12,padding:"6px 14px"}} onClick={()=>{setShowForm(!showForm);setEditId(null);setForm(emptyRF);}}>+ Aplicação</button>
      </div>

      {/* Aviso de atualização manual */}
      <div style={{display:"flex",alignItems:"flex-start",gap:8,background:"#14B8A611",border:"1px solid #14B8A633",borderRadius:10,padding:"8px 12px",marginBottom:12,fontSize:12,color:"var(--text-muted)",lineHeight:1.5}}>
        <span style={{fontSize:14,flexShrink:0}}>💡</span>
        <span>Uma vez por mês, abra o app do banco, veja quanto rendeu e use <strong style={{color:"var(--text-primary)"}}>+ Rendimento</strong> em cada ativo para registrar e somar ao acumulado automaticamente.</span>
      </div>

      {/* Totais */}
      {rendaFixa.length>0&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
          {[
            {label:"Investido",valor:totalInvestido,cor:"var(--text-primary)"},
            {label:"Rendimento",valor:totalRendimento,cor:"#22c55e"},
            {label:"Total atual",valor:totalAtual,cor:"#14B8A6"},
          ].map((c,i)=>(
            <div key={i} style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 8px",textAlign:"center"}}>
              <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:3}}>{c.label}</div>
              <div style={{fontSize:13,fontWeight:800,color:c.cor}}>{cur(c.valor)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Modal + Aporte Renda Fixa */}
      {aporteRFModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setAporteRFModal(null)}>
          <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:380}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:"0 0 16px",fontSize:16,color:"var(--text-primary)"}}>📈 Novo aporte — {aporteRFModal.ativo.nome}</h3>
            <div className="field">
              <label className="label">Valor do aporte (R$)</label>
              <input className="input" type="number" step="any" placeholder="Ex: 500,00" value={aporteRFModal.valor}
                onChange={e=>setAporteRFModal(m=>({...m,valor:e.target.value}))} autoFocus/>
            </div>
            {aporteRFModal.valor&&parseFloat(aporteRFModal.valor)>0&&(
              <div style={{background:"#7C83FF11",border:"1px solid #7C83FF33",borderRadius:8,padding:"8px 12px",fontSize:12,marginBottom:12}}>
                Novo total investido: <strong>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format((aporteRFModal.ativo.valorInvestido||0)+(parseFloat(aporteRFModal.valor)||0))}</strong>
              </div>
            )}
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="ghost-btn" onClick={()=>setAporteRFModal(null)}>Cancelar</button>
              <button className="primary-btn" onClick={()=>{
                const v=parseFloat(aporteRFModal.valor)||0;
                if(v<=0) return;
                const ativo=aporteRFModal.ativo;
                const novoValor=round2((ativo.valorInvestido||0)+v);
                const novos=rendaFixa.map(r=>r.id===ativo.id?{...r,valorInvestido:novoValor}:r);
                onSave(novos);
                setAporteRFModal(null);
                if(onRegistrarSaida) setModalSaidaRF({valor:v,descricao:`Aporte: ${ativo.nome} (${ativo.tipo})`});
              }}>💾 Salvar aporte</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal + Rendimento Renda Fixa */}
      {rendimentoRFModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setRendimentoRFModal(null)}>
          <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:380}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:"0 0 16px",fontSize:16,color:"var(--text-primary)"}}>💹 Registrar rendimento — {rendimentoRFModal.ativo.nome}</h3>
            <div className="field">
              <label className="label">Valor do rendimento (R$)</label>
              <input className="input" type="number" step="any" placeholder="Ex: 85,50" value={rendimentoRFModal.valor}
                onChange={e=>setRendimentoRFModal(m=>({...m,valor:e.target.value}))} autoFocus/>
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>Soma ao rendimento acumulado do investimento. Não entra no caixa — só vira entrada quando você resgatar.</div>
            </div>
            {rendimentoRFModal.valor&&parseFloat(rendimentoRFModal.valor)>0&&(
              <div style={{background:"#22c55e11",border:"1px solid #22c55e33",borderRadius:8,padding:"8px 12px",fontSize:12,marginBottom:12}}>
                Rendimento acumulado: <strong style={{color:"#22c55e"}}>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format((rendimentoRFModal.ativo.rendimentoAcumulado||0)+(parseFloat(rendimentoRFModal.valor)||0))}</strong>
              </div>
            )}
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="ghost-btn" onClick={()=>setRendimentoRFModal(null)}>Cancelar</button>
              <button className="primary-btn" style={{background:"#22c55e"}} onClick={()=>{
                const v=parseFloat(rendimentoRFModal.valor)||0;
                if(v<=0) return;
                const ativo=rendimentoRFModal.ativo;
                const novoRend=round2((ativo.rendimentoAcumulado||0)+v);
                const novos=rendaFixa.map(r=>r.id===ativo.id?{...r,rendimentoAcumulado:novoRend}:r);
                onSave(novos);
                // Rendimento é valorização do investimento: NÃO cria entrada no caixa (só o resgate cria)
                setRendimentoRFModal(null);
              }}>💾 Registrar rendimento</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Resgatar Renda Fixa */}
      {resgateRFModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setResgateRFModal(null)}>
          <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:380}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:"0 0 16px",fontSize:16,color:"var(--text-primary)"}}>💸 Resgatar — {resgateRFModal.ativo.nome}</h3>
            <div style={{background:"var(--bg-card)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13}}>
              <div style={{color:"var(--text-muted)"}}>Valor investido atual:</div>
              <div style={{fontWeight:800,color:"var(--text-primary)",fontSize:15}}>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(resgateRFModal.ativo.valorInvestido||0)}</div>
            </div>
            <div className="field">
              <label className="label">Valor a resgatar (R$)</label>
              <input className="input" type="number" step="any" placeholder="Ex: 500,00"
                value={resgateRFModal.valor}
                onChange={e=>setResgateRFModal(m=>({...m,valor:e.target.value}))} autoFocus/>
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>O valor será debitado do investimento e registrado como Entrada no caixa.</div>
            </div>
            {resgateRFModal.valor&&parseFloat(resgateRFModal.valor)>0&&(
              <div style={{background:"#22c55e11",border:"1px solid #22c55e33",borderRadius:8,padding:"8px 12px",fontSize:12,marginBottom:12}}>
                Novo saldo investido: <strong style={{color:"#22c55e"}}>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(Math.max(0,(resgateRFModal.ativo.valorInvestido||0)-(parseFloat(resgateRFModal.valor)||0)))}</strong>
              </div>
            )}
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="ghost-btn" onClick={()=>setResgateRFModal(null)}>Cancelar</button>
              <button className="primary-btn" style={{background:"linear-gradient(135deg,#ef4444,#dc2626)"}} onClick={()=>{
                const v=parseFloat(resgateRFModal.valor)||0;
                if(v<=0){alert("Informe um valor maior que zero.");return;}
                const ativo=resgateRFModal.ativo;
                const novoValor=round2(Math.max(0,(ativo.valorInvestido||0)-v));
                const novos=rendaFixa.map(r=>r.id===ativo.id?{...r,valorInvestido:novoValor}:r);
                onSave(novos);
                if(onRegistrarDividendo) onRegistrarDividendo(v, `Resgate: ${ativo.nome} (${ativo.tipo})`,"Resgate de Investimentos");
                setResgateRFModal(null);
              }}>💸 Confirmar resgate</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal saída de saldo ao cadastrar Renda Fixa */}
      {modalSaidaRF&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setModalSaidaRF(null)}>
          <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:380,textAlign:"center"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:36,marginBottom:8}}>💸</div>
            <h3 style={{margin:"0 0 8px",fontSize:16,color:"var(--text-primary)"}}>Deseja descontar do saldo?</h3>
            <p style={{fontSize:13,color:"var(--text-muted)",margin:"0 0 6px"}}>
              Você investiu <strong style={{color:"var(--text-primary)"}}>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(modalSaidaRF.valor)}</strong>
            </p>
            <p style={{fontSize:12,color:"var(--text-muted)",margin:"0 0 20px"}}>
              Quer registrar como saída do saldo? Isso cria uma despesa em <strong>Investimentos</strong>.
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <button className="primary-btn" style={{width:"100%"}} onClick={()=>{
                onRegistrarSaida(modalSaidaRF.valor,modalSaidaRF.descricao);
                setModalSaidaRF(null);
              }}>✅ Sim, descontar do saldo</button>
              <button className="ghost-btn" style={{width:"100%"}} onClick={()=>setModalSaidaRF(null)}>
                ❌ Não, já saiu de outra conta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulário */}
      {showForm&&(
        <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:12,padding:16,marginBottom:12}}>
          <div className="form-grid">
            <div className="field"><label className="label">Nome</label><input className="input" placeholder="Ex: CDB Banco XP" value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})}/></div>
            <div className="field"><label className="label">Tipo</label><select className="select" value={form.tipo} onChange={e=>setForm({...form,tipo:e.target.value})}>{TIPOS_RENDA_FIXA.map(t=><option key={t}>{t}</option>)}</select></div>
            <div className="field"><label className="label">Valor investido (R$)</label><input className="input" inputMode="decimal" placeholder="Ex: 1000,00" value={form.valorInvestido} onChange={e=>setForm({...form,valorInvestido:e.target.value})}/></div>
            <div className="field"><label className="label">Rendimento acumulado (R$)</label><input className="input" inputMode="decimal" placeholder="Ex: 85,50" value={form.rendimentoAcumulado} onChange={e=>setForm({...form,rendimentoAcumulado:e.target.value})}/><div style={{fontSize:10,color:"var(--text-muted)",marginTop:3}}>Quanto seu dinheiro já rendeu até hoje</div></div>
            <div className="field"><label className="label">Vencimento</label><input className="input" type="date" value={form.dataVencimento} onChange={e=>setForm({...form,dataVencimento:e.target.value})}/></div>
            <div className="field"><label className="label">Liquidez</label><select className="select" value={form.liquidez} onChange={e=>setForm({...form,liquidez:e.target.value})}><option>No vencimento</option><option>Diária</option><option>30 dias</option><option>90 dias</option></select></div>
            <div className="field full"><label className="label">Observações</label><input className="input" placeholder="Ex: 100% CDI, banco XP" value={form.observacoes} onChange={e=>setForm({...form,observacoes:e.target.value})}/></div>
          </div>
          <div className="actions">
            <button className="ghost-btn" onClick={()=>{setShowForm(false);setEditId(null);setForm(emptyRF);}}>Cancelar</button>
            <button className="primary-btn" onClick={salvar}>💾 Salvar</button>
          </div>
        </div>
      )}

      {/* Lista */}
      {rendaFixa.length===0&&!showForm&&(
        <div style={{textAlign:"center",padding:"16px 0",color:"var(--text-muted)",fontSize:13}}>
          <div style={{fontSize:28,marginBottom:6}}>🏦</div>
          Nenhuma aplicação cadastrada ainda.<br/>
          <span style={{fontSize:11}}>CDB, LCA, LCI, Tesouro Direto e mais</span>
        </div>
      )}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {rendaFixa.map(r=>{
          const total=r.valorInvestido+(r.rendimentoAcumulado||0);
          const pct=r.valorInvestido>0?((r.rendimentoAcumulado||0)/r.valorInvestido*100).toFixed(2):0;
          return(
            <div key={r.id} style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:12,padding:"14px 16px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)"}}>{r.nome}</div>
                  <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>
                    {r.tipo} · Liquidez: {r.liquidez}
                    {r.observacoes&&` · ${r.observacoes}`}
                  </div>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
                  <button className="secondary-btn" style={{fontSize:11,padding:"4px 8px",background:"#7C83FF18",border:"1px solid #7C83FF44",color:"#7C83FF",fontWeight:700}} onClick={()=>setAporteRFModal({ativo:r,valor:""})}>+ Aporte</button>
                  <button className="secondary-btn" style={{fontSize:11,padding:"4px 8px",background:"#22c55e18",border:"1px solid #22c55e44",color:"#22c55e",fontWeight:700}} onClick={()=>setRendimentoRFModal({ativo:r,valor:""})}>+ Rendimento</button>
                  <button className="secondary-btn" style={{fontSize:11,padding:"4px 8px",background:"#ef444418",border:"1px solid #ef444444",color:"#ef4444",fontWeight:700}} onClick={()=>setResgateRFModal({ativo:r,valor:""})}>- Resgatar</button>
                  <button className="icon-btn" onClick={()=>editar(r)}>✏️</button>
                  <button className="icon-btn" onClick={()=>excluir(r.id)}>🗑️</button>
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <div style={{background:"var(--bg-input)",borderRadius:8,padding:"6px 10px",fontSize:12}}>
                  <span style={{color:"var(--text-muted)"}}>Investido: </span>
                  <strong>{cur(r.valorInvestido)}</strong>
                </div>
                <div style={{background:"#22c55e18",borderRadius:8,padding:"6px 10px",fontSize:12}}>
                  <span style={{color:"var(--text-muted)"}}>Rendimento: </span>
                  <strong style={{color:"#22c55e"}}>+{cur(r.rendimentoAcumulado||0)} (+{pct}%)</strong>
                </div>
                <div style={{background:"#14B8A618",borderRadius:8,padding:"6px 10px",fontSize:12}}>
                  <span style={{color:"var(--text-muted)"}}>Total: </span>
                  <strong style={{color:"#14B8A6"}}>{cur(total)}</strong>
                </div>

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Aba Cartões ─────────────────────────────────────────
function CartoesTab({cartoes, bills, currentYear, currentMonth, plano, onSave, onGoToPlanos}) {
  const[showForm,setShowForm]=React.useState(false);
  const[form,setForm]=React.useState({nome:"",bandeira:"💳",limite:"",vencimento:"",fechamento:"",cor:"#7C83FF"});
  const[detalheCartao,setDetalheCartao]=React.useState(null);
  const[editId,setEditId]=React.useState(null);
  const isPremium = plano==="premium";
  const maxCartoes = isPremium ? 999 : plano==="basico" ? 1 : 0;
  const BANDEIRAS=[{e:"💳",n:"Genérico"},{e:"🟦",n:"Visa"},{e:"🟧",n:"Mastercard"},{e:"🟥",n:"Elo"},{e:"🟪",n:"Amex"},{e:"⬛",n:"Hipercard"}];
  const CORES=["#7C83FF","#22C55E","#F59E0B","#EF4444","#14B8A6","#8B5CF6","#EC4899"];

  const formCartaoContent=(
    <>
      <div className="form-grid">
        <div className="field full">
          <label className="label">Nome do cartão</label>
          <input className="input" placeholder="Ex: Nubank, Inter, C6..." value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})}/>
        </div>
        <div className="field">
          <label className="label">Bandeira</label>
          <select className="select" value={form.bandeira} onChange={e=>setForm({...form,bandeira:e.target.value})}>
            {BANDEIRAS.map(b=><option key={b.e} value={b.e}>{b.e} {b.n}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="label">Dia fechamento</label>
          <input className="input" type="number" min="1" max="31" placeholder="Ex: 10" value={form.fechamento} onChange={e=>setForm({...form,fechamento:e.target.value})}/>
          <div style={{fontSize:10,color:"var(--text-muted)",marginTop:2}}>Após este dia, gastos vão para a próxima fatura</div>
        </div>
        <div className="field">
          <label className="label">Dia vencimento</label>
          <input className="input" type="number" min="1" max="31" placeholder="Ex: 17" value={form.vencimento} onChange={e=>setForm({...form,vencimento:e.target.value})}/>
        </div>
        <div className="field">
          <label className="label">Limite (R$)</label>
          <input className="input" type="number" placeholder="Ex: 5000" value={form.limite} onChange={e=>setForm({...form,limite:e.target.value})}/>
        </div>
        <div className="field">
          <label className="label">Cor</label>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {CORES.map(c=><button key={c} type="button" onClick={()=>setForm({...form,cor:c})} style={{width:28,height:28,borderRadius:"50%",background:c,border:form.cor===c?"3px solid #fff":"2px solid transparent",cursor:"pointer"}}/>)}
          </div>
        </div>
      </div>
      <div className="actions">
        <button className="ghost-btn" onClick={()=>{setShowForm(false);setEditId(null);}}>Cancelar</button>
        <button className="primary-btn" onClick={salvar}>💾 Salvar</button>
      </div>
    </>
  );

  const cur=(v)=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);


  // Calcular fatura do cartão para o mês (despesas cadastradas)

  function salvar() {
    if(!form.nome.trim()) return;
    const novo = {id:editId||`card-${Date.now()}`,nome:form.nome.trim(),bandeira:form.bandeira,limite:parseFloat(form.limite)||0,vencimento:parseInt(form.vencimento)||10,fechamento:parseInt(form.fechamento)||0,cor:form.cor};
    const novos = editId ? cartoes.map(c=>c.id===editId?novo:c) : [...cartoes,novo];
    onSave(novos);
    setForm({nome:"",bandeira:"💳",limite:"",vencimento:"",fechamento:"",cor:"#7C83FF"});
    setShowForm(false);setEditId(null);
  }

  function excluir(id){if(window.confirm("Remover este cartão?"))onSave(cartoes.filter(c=>c.id!==id));}
  const editCartaoRef=React.useRef(null);
  function editar(c){setForm({nome:c.nome,bandeira:c.bandeira,limite:String(c.limite),vencimento:String(c.vencimento),fechamento:String(c.fechamento||""),cor:c.cor});setEditId(c.id);setShowForm(false);setTimeout(()=>{if(editCartaoRef.current)editCartaoRef.current.scrollIntoView({behavior:"smooth",block:"center"});},80);}

  const mesAtual = {year:currentYear, month:currentMonth};

  // Total gasto em TODOS os cartões no mês atual — recalcula sozinho sempre que
  // `bills` mudar (nova despesa, edição ou exclusão) ou a lista de cartões mudar.
  const totalGeralCartoes = React.useMemo(()=>{
    const hojeRef = new Date();
    return cartoes.reduce((somaGeral,c)=>{
      const despesasDoCartao = bills.filter(b=>{
        if(b.cartaoId!==c.id) return false;
        if(b.isFaturaGerada) return false;
        return shouldGenerateForMonth(b,hojeRef.getFullYear(),hojeRef.getMonth());
      });
      const totalCartao = despesasDoCartao.reduce((s,b)=>s+b.amount,0);
      return somaGeral+totalCartao;
    },0);
  },[bills,cartoes]);

  return(
    <section className="section-card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8}}>
        <h2 className="section-title" style={{margin:0}}>💳 Meus Cartões</h2>
        {maxCartoes===0?(
          <span style={{fontSize:12,color:"#ef4444",fontWeight:700}}>🔒 Disponível no plano Básico ou Premium</span>
        ):(
          cartoes.length < maxCartoes && (
            <button className="primary-btn" onClick={()=>{setShowForm(!showForm);setEditId(null);setForm({nome:"",bandeira:"💳",limite:"",vencimento:"",cor:"#7C83FF"});}}>
              + Cartão
            </button>
          )
        )}
      </div>

      {maxCartoes===0&&(
        <div className="empty" style={{padding:"40px 0"}}>
          <div style={{fontSize:48,marginBottom:12}}>💳</div>
          <div>Recurso disponível nos planos Básico e Premium.</div>
          <button className="primary-btn" style={{marginTop:16}} onClick={()=>onGoToPlanos?.()}>Ver planos</button>
        </div>
      )}

      {maxCartoes>0&&(
        <>
          {/* Formulário novo cartão — só no topo quando não está editando */}
          {showForm&&!editId&&(
            <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:16,marginBottom:16}}>
              <h3 style={{margin:"0 0 12px",fontSize:14}}>➕ Novo cartão</h3>
              {formCartaoContent}
            </div>
          )}

          {/* Total gasto em todos os cartões */}
          {cartoes.length>0&&(
            <div style={{background:"linear-gradient(135deg,#7C83FF22,#7C83FF08)",border:"1px solid #7C83FF44",borderRadius:14,padding:"16px 20px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:24}}>💰</span>
                <span style={{fontWeight:700,fontSize:14,color:"var(--text-primary)"}}>Total gasto em todos os cartões</span>
              </div>
              <span style={{fontWeight:900,fontSize:20,color:"#7C83FF"}}>{cur(totalGeralCartoes)}</span>
            </div>
          )}

          {/* Lista de cartões */}
          {cartoes.length===0?(
            <div className="empty" style={{padding:"40px 0"}}>
              <div style={{fontSize:48,marginBottom:12}}>💳</div>
              <div>Nenhum cartão cadastrado ainda.</div>
              <div style={{fontSize:13,color:"var(--text-muted)",marginTop:4}}>Clique em <strong>+ Cartão</strong> para começar!</div>
            </div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {cartoes.map(cartao=>{
                // Edição inline
                if(editId===cartao.id){
                  return(
                    <div key={cartao.id} ref={editCartaoRef} style={{background:"var(--bg-card)",border:"2px solid #7C83FF66",borderRadius:14,padding:16}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                        <h3 style={{margin:0,fontSize:14,color:"var(--text-primary)"}}>✏️ Editando: {cartao.nome}</h3>
                        <button className="icon-btn" onClick={()=>setEditId(null)}>✕</button>
                      </div>
                      {formCartaoContent}
                    </div>
                  );
                }
                // Lógica de fechamento
                const hoje = new Date();
                const diaHoje = hoje.getDate();
                const fechamento = cartao.fechamento||0;
                const hoje2=new Date();

                // Despesas do cartão no mês atual (a lista que o usuário vê)
                const despesasFixas = bills.filter(b=>{
                  if(b.cartaoId!==cartao.id) return false;
                  if(b.isFaturaGerada) return false;
                  return shouldGenerateForMonth(b,hoje2.getFullYear(),hoje2.getMonth());
                });
                // Fatura ABERTA = soma das despesas do mês atual (o que está acumulando)
                const totalFixo = despesasFixas.reduce((s,b)=>s+b.amount,0);
                const faturaAberta = totalFixo;
                const pctLimite = cartao.limite>0?Math.min(100,Math.round((faturaAberta/cartao.limite)*100)):0;

                // Fatura fechada — buscar a fatura-auto do mês que venceu
                const faturaAPagarRef = fechamento>0&&diaHoje>fechamento ? mesAtual : {year:currentYear,month:currentMonth===0?11:currentMonth-1};
                const faturaAutoFechada = bills.find(b=>b.isFaturaGerada&&(b.cartaoNome===cartao.nome||b.name===`Fatura ${cartao.nome}`)&&b.startYear===faturaAPagarRef.year&&b.startMonth===faturaAPagarRef.month)
                  || bills.find(b=>b.isFaturaGerada&&(b.cartaoNome===cartao.nome||b.name===`Fatura ${cartao.nome}`));
                const faturaFechadaKey = faturaAutoFechada ? monthKey(faturaAutoFechada.startYear, faturaAutoFechada.startMonth) : null;
                const faturaFechadaStatus = faturaAutoFechada && faturaFechadaKey ? getRecordStatus(faturaAutoFechada, faturaFechadaKey) : null;
                const faturaFechadaPaga = faturaFechadaStatus === "paid";
                const faturaFechadaParcial = faturaFechadaStatus === "partial";
                const faturaFechadaValor = faturaAutoFechada ? faturaAutoFechada.amount : 0;
                const faturaFechadaPagoValor = faturaAutoFechada && faturaFechadaKey ? getAmountPaidForMonthReference(faturaAutoFechada, faturaFechadaKey) : 0;
                const mostrarFaturaFechada = faturaAutoFechada && !faturaFechadaPaga;

                // Agrupar despesas por categoria
                const porCategoria = {};
                despesasFixas.forEach(b=>{
                  const cat=b.category||"Outros";
                  if(!porCategoria[cat]) porCategoria[cat]={total:0,itens:[]};
                  porCategoria[cat].total=Math.round((porCategoria[cat].total+b.amount)*100)/100;
                  porCategoria[cat].itens.push(b);
                });
                const CATEGORY_ICONS_LOCAL={"Alimentação":"🍽️","Supermercado":"🛒","Restaurantes e Delivery":"🍔","Transporte":"🚗","Acessórios do Veículo":"🔧","Saúde":"💊","Moradia":"🏠","Lazer":"🎭","Comunicação":"📱","Educação":"📚","Serviços":"⚙️","Consumo Pessoal":"🛍️","Entretenimento":"🎬","Apps Produtividade":"💻","Seguros":"🛡️","Investimentos":"📈","Outros":"📦","Fatura Cartão":"💳"};

                return(
                  <div key={cartao.id} style={{background:`linear-gradient(135deg,${cartao.cor}22,${cartao.cor}08)`,border:`1px solid ${cartao.cor}44`,borderRadius:16,overflow:"hidden"}}>
                    {/* Header */}
                    <div style={{background:`linear-gradient(135deg,${cartao.cor},${cartao.cor}99)`,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:28}}>{cartao.bandeira}</span>
                        <div>
                          <div style={{fontWeight:800,fontSize:16,color:"#fff"}}>{cartao.nome}</div>
                          <div style={{fontSize:12,color:"rgba(255,255,255,0.8)"}}>
                            {fechamento>0?`Fecha dia ${fechamento} · `:""}Vence dia {cartao.vencimento}{cartao.limite>0?` · Limite: ${cur(cartao.limite)}`:""}
                          </div>
                        </div>
                      </div>
                      <div style={{display:"flex",gap:6}}>
                        <button className="icon-btn" onClick={()=>editar(cartao)} style={{background:"rgba(255,255,255,0.2)",border:"none",color:"#fff"}}>✏️</button>
                        <button className="icon-btn" onClick={()=>excluir(cartao.id)} style={{background:"rgba(255,255,255,0.2)",border:"none",color:"#fff"}}>🗑️</button>
                      </div>
                    </div>

                    {/* Fatura fechada pendente — banner de alerta, some quando paga */}
                    {mostrarFaturaFechada&&(
                      <div style={{margin:"12px 20px 0",background:faturaFechadaParcial?"#f59e0b18":"#ef444418",border:`1.5px solid ${faturaFechadaParcial?"#f59e0b44":"#ef444444"}`,borderRadius:10,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:700,color:faturaFechadaParcial?"#f59e0b":"#ef4444",marginBottom:3}}>
                            {faturaFechadaParcial?"⚡ Fatura parcialmente paga":"🔴 Fatura vencida"}
                          </div>
                          <div style={{fontSize:11,color:"var(--text-muted)",lineHeight:1.5}}>
                            {faturaFechadaParcial
                              ?`Pago ${cur(faturaFechadaPagoValor)} de ${cur(faturaFechadaValor)} · Falta ${cur(Math.max(0,faturaFechadaValor-faturaFechadaPagoValor))}`
                              :`${cur(faturaFechadaValor)} · Venceu dia ${cartao.vencimento} · Registre nas Despesas`}
                          </div>
                        </div>
                        <div style={{fontWeight:800,fontSize:16,color:faturaFechadaParcial?"#f59e0b":"#ef4444",whiteSpace:"nowrap"}}>
                          {faturaFechadaParcial?cur(Math.max(0,faturaFechadaValor-faturaFechadaPagoValor)):cur(faturaFechadaValor)}
                        </div>
                      </div>
                    )}

                    {/* Fatura aberta — acumulando para próximo fechamento */}
                    <div style={{padding:"12px 20px 4px"}}>
                      <div style={{background:"var(--bg-card)",borderRadius:10,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:2}}>🔓 Fatura aberta</div>
                          <div style={{fontSize:11,color:"var(--text-muted)"}}>
                            {fechamento>0?`Fecha dia ${fechamento} · ${MONTHS_PT[hoje2.getMonth()]}`:"Mês atual"}
                          </div>
                        </div>
                        <div style={{fontWeight:800,fontSize:20,color:faturaAberta>0?"var(--text-primary)":"var(--text-muted)"}}>
                          {cur(faturaAberta)}
                        </div>
                      </div>
                    </div>

                    {/* Barra de limite */}
                    {cartao.limite>0&&(
                      <div style={{padding:"0 20px 12px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--text-muted)",marginBottom:4}}>
                          <span>Limite utilizado</span>
                          <span style={{color:pctLimite>80?"#ef4444":pctLimite>60?"#f59e0b":"#22c55e",fontWeight:700}}>{pctLimite}%</span>
                        </div>
                        <div style={{height:6,background:"var(--bg-input)",borderRadius:3,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${pctLimite}%`,background:pctLimite>80?"#ef4444":pctLimite>60?"#f59e0b":cartao.cor,borderRadius:3}}/>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--text-muted)",marginTop:4}}>
                          <span>{cur(totalFixo)} em aberto</span>
                          <span>{cur(Math.max(0,cartao.limite-totalFixo))} disponível</span>
                        </div>
                      </div>
                    )}

                    {/* Relação: O que já pago mensalmente neste cartão */}
                    {despesasFixas.length>0&&(
                      <div style={{padding:"0 20px 16px"}}>
                        <button onClick={()=>setDetalheCartao(detalheCartao===cartao.id?null:cartao.id)}
                          style={{width:"100%",background:"var(--bg-card)",border:`1px solid ${cartao.cor}33`,borderRadius:10,padding:"10px 14px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <span style={{fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>📋 Despesas deste mês no cartão</span>
                          <span style={{fontSize:12,color:cartao.cor,fontWeight:700}}>{cur(totalFixo)}/mês {detalheCartao===cartao.id?"▲":"▼"}</span>
                        </button>

                        {detalheCartao===cartao.id&&(
                          <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:6}}>
                            {Object.entries(porCategoria).sort((a,b)=>b[1].total-a[1].total).map(([cat,dados])=>(
                              <div key={cat} style={{background:"var(--bg-card)",borderRadius:10,padding:"10px 14px"}}>
                                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:dados.itens.length>1?8:0}}>
                                  <span style={{fontSize:13,fontWeight:700}}>{CATEGORY_ICONS_LOCAL[cat]||"📦"} {cat}</span>
                                  <span style={{fontSize:13,fontWeight:800,color:cartao.cor}}>{cur(dados.total)}</span>
                                </div>
                                {dados.itens.map(item=>(
                                  <div key={item.id} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"var(--text-muted)",paddingLeft:8,marginTop:4}}>
                                    <span>
                                      {item.name}
                                      {item.installments>1&&<span style={{background:cartao.cor+"22",color:cartao.cor,fontSize:10,fontWeight:700,padding:"1px 5px",borderRadius:4,marginLeft:6}}>{item.installments}x</span>}
                                      {item.installments===0&&item.frequency!=="unico"&&<span style={{background:"#22c55e22",color:"#22c55e",fontSize:10,fontWeight:700,padding:"1px 5px",borderRadius:4,marginLeft:6}}>Recorrente</span>}
                                      {item.installments===0&&item.frequency==="unico"&&<span style={{background:"#64748b22",color:"#94a3b8",fontSize:10,fontWeight:700,padding:"1px 5px",borderRadius:4,marginLeft:6}}>Único</span>}
                                      {item.installments===1&&<span style={{background:"#64748b22",color:"#94a3b8",fontSize:10,fontWeight:700,padding:"1px 5px",borderRadius:4,marginLeft:6}}>1x</span>}
                                    </span>
                                    <span>{cur(item.amount)}</span>
                                  </div>
                                ))}
                              </div>
                            ))}
                            <div style={{background:`${cartao.cor}18`,border:`1px solid ${cartao.cor}44`,borderRadius:10,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                              <span style={{fontSize:13,fontWeight:800,color:"var(--text-primary)"}}>Total no cartão este mês</span>
                              <span style={{fontSize:15,fontWeight:900,color:cartao.cor}}>{cur(totalFixo)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ─── Aba Planos ──────────────────────────────────────────
function PlanosTab({plano, totalDespesas, userId, userEmail, trialExpira}) {
  const [showCancelModal, setShowCancelModal] = React.useState(false);
  // eslint-disable-next-line no-unused-vars
  const [confirmarPlano, setConfirmarPlano] = React.useState(null);

  // Trial ativo = tem data de expiração futura e o plano atual é premium (aplicado automaticamente no 1º acesso)
  const hojeStr=new Date().toISOString().slice(0,10);
  const emTrial=plano==="premium"&&trialExpira&&trialExpira>=hojeStr;
  const diasRestantesTrial=emTrial?Math.max(0,Math.ceil((new Date(trialExpira)-new Date(hojeStr))/86400000)):0;

  // Links de assinatura Mercado Pago — external_reference = user_id do Supabase para ativação automática confiável
  const MP_LINKS = {
    basico: `https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=6b52126b6dda45f7b0c4070dc8c537b9&external_reference=${userId}`,
    premium: `https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=91baf257db8d46aca90ecd292df5cb21&external_reference=${userId}`,
  };

  const planos = [
    {
      id:"gratis", emoji:"🆓", nome:"Grátis",
      precoMes:"R$ 0", precoAnual:"R$ 0", precoOriginal:null,
      cor:"#52526a", corBg:"var(--bg-card)",
      recursos:["15 despesas cadastradas","Acesso a todas as abas","Visualização dos dados","Metas e orçamentos","Desafios gamificados","Gráficos e evolução","Relatório mensal PDF"],
      bloqueios:["Alertas WhatsApp","Notícias e motivacional","Carteira de investimentos","Cartões de crédito","Renda fixa"],
    },
    {
      id:"basico", emoji:"⭐", nome:"Básico",
      precoMes:"R$ 8,90", precoAnual:"R$ 6,90", precoOriginal:"R$ 29,90",
      cor:"#7c83ff", corBg:"rgba(124,131,255,0.1)",
      recursos:["50 despesas/mês","Notícias financeiras por e-mail","Mensagem motivacional por e-mail","1 cartão de crédito","Desafios gamificados","Relatório mensal PDF","Carteira de ativos (5 ativos)"],
      bloqueios:["Alertas WhatsApp","Carteira ilimitada de ativos","Renda fixa","2 números WhatsApp"],
      destaque:null,
    },
    {
      id:"premium", emoji:"💎", nome:"Premium",
      precoMes:"R$ 14,90", precoAnual:"R$ 14,90", precoOriginal:"R$ 69,90",
      cor:"#f59e0b", corBg:"rgba(245,158,11,0.1)",
      recursos:["Despesas ilimitadas","Alertas de contas WhatsApp + e-mail 👫","2 números WhatsApp (titular + familiar)","Notícias financeiras diárias","Mensagem motivacional diária","Cartões ilimitados","Carteira de ativos ilimitada","Renda fixa ilimitada","Desafios gamificados","Relatório mensal PDF","Suporte prioritário"],
      bloqueios:[],
      destaque:"Mais popular",
    },
  ];

  return(
    <section className="section-card">
      <h2 className="section-title" style={{marginBottom:4}}>💎 Planos e Preços</h2>
      <p style={{fontSize:13,color:"var(--text-muted)",marginBottom:16}}>
        Seu plano atual: <strong style={{color:"var(--text-primary)"}}>{planos.find(p=>p.id===plano)?.nome}</strong>
        {plano==="gratis"&&<span style={{color:totalDespesas>=11?"#ef4444":"#f59e0b"}}> · {totalDespesas}/15 despesas usadas</span>}
      </p>

      {emTrial&&(
        <div style={{
          background:"rgba(245,158,11,0.1)",border:"1.5px solid #f59e0b",
          borderRadius:14,padding:"14px 16px",marginBottom:20,
          display:"flex",gap:12,alignItems:"flex-start",
        }}>
          <span style={{fontSize:22,flexShrink:0}}>⏳</span>
          <div style={{fontSize:13,color:"var(--text-primary)",lineHeight:1.6}}>
            <strong>Você está no período de teste Premium</strong> — restam {diasRestantesTrial} {diasRestantesTrial===1?"dia":"dias"} (até {new Date(trialExpira).toLocaleDateString("pt-BR")}).
            <br/>
            <span style={{color:"var(--text-muted)"}}>Se você não assinar, seu plano volta automaticamente para o Grátis — sem nenhuma cobrança. Você pode continuar usando o app gratuitamente quando quiser.</span>
          </div>
        </div>
      )}


      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16,marginBottom:20}}>
        {planos.map(p=>(
          <div key={p.id} style={{background:p.corBg,border:`2px solid ${plano===p.id?p.cor:p.destaque?"var(--border)":"var(--border)"}`,borderRadius:16,padding:20,position:"relative",boxShadow:p.destaque?"0 4px 20px rgba(245,158,11,0.15)":"none"}}>
            {plano===p.id&&<div style={{position:"absolute",top:-10,left:"50%",transform:"translateX(-50%)",background:p.cor,color:"#fff",fontSize:10,fontWeight:700,padding:"3px 12px",borderRadius:20,whiteSpace:"nowrap"}}>PLANO ATUAL</div>}
            {p.destaque&&plano!==p.id&&<div style={{position:"absolute",top:-10,left:"50%",transform:"translateX(-50%)",background:"#f59e0b",color:"#fff",fontSize:10,fontWeight:700,padding:"3px 12px",borderRadius:20,whiteSpace:"nowrap"}}>{p.destaque}</div>}
            <div style={{textAlign:"center",marginBottom:16}}>
              <div style={{fontSize:32,marginBottom:6}}>{p.emoji}</div>
              <div style={{fontWeight:800,fontSize:18,color:"var(--text-primary)"}}>{p.nome}</div>
              {p.precoOriginal&&(
                <div style={{fontSize:12,color:"var(--text-muted)",textDecoration:"line-through",marginTop:4}}>{p.precoOriginal}/mês</div>
              )}
              <div style={{fontWeight:900,fontSize:26,color:p.cor,marginTop:2}}>
                {p.precoMes}
                <span style={{fontSize:12,fontWeight:400,color:"var(--text-muted)"}}>/mês</span>
              </div>

            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
              {p.recursos.map((r,i)=>(
                <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",fontSize:12,color:"var(--text-primary)"}}>
                  <span style={{color:"#22c55e",flexShrink:0}}>✅</span>{r}
                </div>
              ))}
              {p.bloqueios.map((r,i)=>(
                <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",fontSize:12,color:"var(--text-muted)"}}>
                  <span style={{flexShrink:0}}>❌</span>{r}
                </div>
              ))}
            </div>

            {/* Botão de assinatura */}
            {p.id==="gratis"&&plano==="gratis"&&(
              <div style={{textAlign:"center",fontSize:12,color:"var(--text-muted)",padding:"10px 0"}}>Plano atual gratuito</div>
            )}
            {p.id!=="gratis"&&plano===p.id&&(
              <div style={{textAlign:"center",padding:"10px 0"}}>
                <div style={{fontSize:12,color:"#22c55e",fontWeight:700}}>✅ Assinatura ativa</div>
                <button
                  onClick={()=>setShowCancelModal(true)}
                  style={{fontSize:11,color:"#ef4444",textDecoration:"underline",background:"none",border:"none",cursor:"pointer",marginTop:6,padding:0}}>
                  Cancelar assinatura
                </button>
              </div>
            )}
            {p.id!=="gratis"&&plano!==p.id&&(
              <div>
                <button
                  onClick={()=>setConfirmarPlano({id:p.id,link:MP_LINKS[p.id],nome:p.nome,cor:p.cor,preco:p.precoMes})}
                  style={{display:"block",width:"100%",textAlign:"center",background:`linear-gradient(135deg,${p.cor},${p.cor}cc)`,color:"#fff",border:"none",padding:"12px 16px",borderRadius:12,fontWeight:800,fontSize:14,boxShadow:`0 4px 14px ${p.cor}44`,cursor:"pointer",fontFamily:"inherit"}}>
                  {p.id==="premium"?"💎 Assinar Premium":"⭐ Assinar Básico"} — {p.precoMes}/mês
                </button>
                <button
                  onClick={()=>{
                    const msg=encodeURIComponent(`Olá! Assinei o plano mas não recebi o acesso. Meu e-mail: ${userEmail||""} | ID: ${userId||""}`);
                    window.open(`https://wa.me/5541995964572?text=${msg}`,"_blank");
                  }}
                  style={{width:"100%",marginTop:6,background:"none",border:"1px solid var(--border)",borderRadius:10,padding:"8px",fontSize:11,color:"var(--text-muted)",cursor:"pointer"}}>
                  ❓ Não recebi meu acesso
                </button>
              </div>
            )}
            {p.id!=="gratis"&&(
              <div style={{fontSize:10,color:"var(--text-muted)",textAlign:"center",marginTop:6}}>
                🔒 Pagamento seguro via Mercado Pago · Cancele quando quiser
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Frase motivacional */}
      <div style={{background:"linear-gradient(135deg,rgba(124,131,255,0.1),rgba(245,158,11,0.08))",border:"1px solid var(--border)",borderRadius:14,padding:"14px 16px",marginBottom:16,textAlign:"center"}}>
        <div style={{fontSize:16,marginBottom:6}}>💡</div>
        <p style={{fontSize:13,color:"var(--text-primary)",margin:0,lineHeight:1.6,fontStyle:"italic"}}>
          "Investir no controle das suas finanças é o menor gasto com o maior retorno da sua vida."
        </p>
      </div>

      {/* Pix mensal */}
      <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:20,marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:15,color:"var(--text-primary)",marginBottom:6}}>📲 Prefere pagar via Pix?</div>
        <p style={{fontSize:13,color:"var(--text-muted)",margin:"0 0 14px",lineHeight:1.6}}>
          Acesso liberado por <strong style={{color:"var(--text-primary)"}}>30 dias</strong> após confirmação do pagamento. Para renovar, é só entrar em contato antes do vencimento.
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <a href={`https://wa.me/5541995964572?text=${encodeURIComponent("Olá! Quero assinar o plano Básico via Pix. Meu e-mail de cadastro é: ")}`}
            target="_blank" rel="noreferrer"
            style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:"#22c55e",color:"#fff",textDecoration:"none",padding:"12px 16px",borderRadius:12,fontWeight:700,fontSize:14}}>
            💸 Assinar Básico via Pix — R$ 8,90/mês
          </a>
          <a href={`https://wa.me/5541995964572?text=${encodeURIComponent("Olá! Quero assinar o plano Premium via Pix. Meu e-mail de cadastro é: ")}`}
            target="_blank" rel="noreferrer"
            style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:"#f59e0b",color:"#fff",textDecoration:"none",padding:"12px 16px",borderRadius:12,fontWeight:700,fontSize:14}}>
            💎 Assinar Premium via Pix — R$ 14,90/mês
          </a>
        </div>
      </div>

      {/* Política de cancelamento */}
      <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:20,marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:15,color:"var(--text-primary)",marginBottom:8}}>📋 Política de Cancelamento</div>
        <p style={{fontSize:13,color:"var(--text-muted)",margin:"0 0 8px",lineHeight:1.6}}>
          Você pode cancelar sua assinatura a qualquer momento, sem multa ou taxa. Após o cancelamento:
        </p>
        <ul style={{fontSize:13,color:"var(--text-muted)",paddingLeft:18,margin:"0 0 8px",lineHeight:1.8}}>
          <li>Seus dados ficam <strong style={{color:"var(--text-primary)"}}>100% preservados</strong></li>
          <li>O acesso volta automaticamente para o plano Grátis</li>
          <li>Você pode reativar a qualquer momento</li>
        </ul>
        <p style={{fontSize:12,color:"var(--text-muted)",margin:0,lineHeight:1.5}}>
          Para cancelar assinaturas via Mercado Pago, acesse sua conta no Mercado Pago ou clique em "Cancelar assinatura" acima.
          Para cancelamentos de planos pagos via Pix, entre em contato pelo WhatsApp.
        </p>
      </div>

      {/* Suporte */}
      <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:20,textAlign:"center"}}>
        <div style={{fontWeight:700,fontSize:15,color:"var(--text-primary)",marginBottom:8}}>🎧 Suporte e Atendimento</div>
        <p style={{fontSize:13,color:"var(--text-muted)",margin:"0 0 16px",lineHeight:1.6}}>
          Dúvidas sobre planos ou precisa de ajuda? Fale com nossa equipe!
        </p>
        <a href="https://wa.me/5541995964572?text=Olá! Preciso de suporte com o Mapa Financeiro 5G!" target="_blank" rel="noreferrer" style={{display:"inline-block",background:"#22c55e",color:"#fff",textDecoration:"none",padding:"12px 24px",borderRadius:12,fontWeight:700,fontSize:14}}>
          💬 Falar com Suporte
        </a>
      </div>

      {/* Modal de cancelamento */}
      {/* Modal confirmação pagamento */}
      {confirmarPlano&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:2000,display:"flex",alignItems:"flex-end",justifyContent:"center",fontFamily:"inherit"}}
          onClick={()=>setConfirmarPlano(null)}>
          <div style={{width:"100%",maxWidth:480,background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:"20px 20px 0 0",padding:"28px 24px 40px"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{width:36,height:4,borderRadius:2,background:"var(--border)",margin:"0 auto 20px"}}/>
            <h3 style={{fontSize:17,fontWeight:800,color:"var(--text-primary)",margin:"0 0 16px",textAlign:"center"}}>⚠️ Antes de continuar</h3>
            <div style={{background:"rgba(245,158,11,0.12)",border:"1.5px solid rgba(245,158,11,0.4)",borderRadius:14,padding:"14px 16px",marginBottom:16}}>
              <div style={{fontSize:12,color:"#f59e0b",fontWeight:700,marginBottom:6}}>📧 Seu e-mail de cadastro no app:</div>
              <div style={{fontSize:15,fontWeight:800,color:"var(--text-primary)",wordBreak:"break-all"}}>{userEmail}</div>
            </div>
            <p style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.7,margin:"0 0 20px",textAlign:"center"}}>
              Ao abrir o Mercado Pago, <strong style={{color:"var(--text-primary)"}}>faça login com este mesmo e-mail</strong> para que seu plano seja ativado automaticamente.
              <br/><br/>Se usar um e-mail diferente, o acesso pode não ser liberado.
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <a href={confirmarPlano.link} target="_blank" rel="noreferrer"
                onClick={()=>setConfirmarPlano(null)}
                style={{display:"block",textAlign:"center",background:`linear-gradient(135deg,${confirmarPlano.cor},${confirmarPlano.cor}cc)`,color:"#fff",textDecoration:"none",padding:"14px 16px",borderRadius:14,fontWeight:800,fontSize:15}}>
                ✅ Entendido — Ir para o pagamento
              </a>
              <button onClick={()=>setConfirmarPlano(null)}
                style={{width:"100%",padding:"12px",borderRadius:14,background:"none",border:"1px solid var(--border)",color:"var(--text-muted)",fontWeight:600,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:"var(--bg-card)",borderRadius:20,padding:28,maxWidth:380,width:"100%",boxShadow:"0 8px 40px rgba(0,0,0,0.3)"}}>
            <div style={{fontSize:32,textAlign:"center",marginBottom:8}}>😢</div>
            <h3 style={{textAlign:"center",color:"var(--text-primary)",marginBottom:8,fontSize:18}}>Cancelar assinatura?</h3>
            <p style={{fontSize:13,color:"var(--text-muted)",textAlign:"center",lineHeight:1.6,marginBottom:16}}>
              Seus dados ficam <strong style={{color:"var(--text-primary)"}}>100% preservados</strong>. Você voltará para o plano Grátis e pode reativar quando quiser.
            </p>
            <div style={{background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:10,padding:"10px 14px",marginBottom:20}}>
              <p style={{fontSize:12,color:"#f59e0b",margin:0,lineHeight:1.5,fontWeight:600}}>
                💡 Para cancelar via Mercado Pago, você será redirecionado para sua conta no Mercado Pago.
              </p>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <a
                href="https://www.mercadopago.com.br/subscriptions"
                target="_blank"
                rel="noreferrer"
                onClick={()=>setShowCancelModal(false)}
                style={{display:"block",textAlign:"center",background:"#ef4444",color:"#fff",textDecoration:"none",padding:"12px 16px",borderRadius:12,fontWeight:700,fontSize:14}}>
                Continuar e cancelar no Mercado Pago
              </a>
              <a
                href={`https://wa.me/5541995964572?text=${encodeURIComponent("Olá! Quero cancelar minha assinatura do Mapa Financeiro 5G.")}`}
                target="_blank"
                rel="noreferrer"
                onClick={()=>setShowCancelModal(false)}
                style={{display:"block",textAlign:"center",background:"#22c55e",color:"#fff",textDecoration:"none",padding:"12px 16px",borderRadius:12,fontWeight:700,fontSize:14}}>
                💬 Cancelar via WhatsApp (Pix)
              </a>
              <button
                onClick={()=>setShowCancelModal(false)}
                style={{background:"var(--bg-secondary)",border:"1px solid var(--border)",borderRadius:12,padding:"12px 16px",fontWeight:600,fontSize:14,cursor:"pointer",color:"var(--text-primary)"}}>
                Voltar — manter assinatura
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
const DESAFIOS_CATALOGO = [
  {id:"xsalada",emoji:"🥗",nome:"X-Salada",desc:"Troque um x-salada por economia!",valorUnitario:30,unidade:"x-salada"},
  {id:"cafe",emoji:"☕",nome:"Café fora",desc:"Abra mão dos cafezinhos na rua",valorUnitario:15,unidade:"café"},
  {id:"pizza",emoji:"🍕",nome:"Pizza",desc:"Uma pizza a menos por mês",valorUnitario:60,unidade:"pizza"},
  {id:"combustivel",emoji:"⛽",nome:"Combustível",desc:"Evite viagens desnecessárias",valorUnitario:80,unidade:"saída de carro"},
  {id:"shopping",emoji:"🛍️",nome:"Shopping",desc:"Idas ao shopping sem necessidade",valorUnitario:150,unidade:"ida ao shopping"},
  {id:"happyhour",emoji:"🍻",nome:"Happy Hour",desc:"Saídas após o trabalho",valorUnitario:80,unidade:"happy hour"},
  {id:"online",emoji:"📦",nome:"Comprinhas online",desc:"Shein, Shopee, Amazon...",valorUnitario:100,unidade:"comprinha"},
  {id:"mercado",emoji:"🛒",nome:"Mercado",desc:"Itens fora da lista",valorUnitario:50,unidade:"item extra"},
  {id:"cinema",emoji:"🎬",nome:"Cinema/Streaming",desc:"Menos plataformas assinadas",valorUnitario:45,unidade:"assinatura"},
  {id:"delivery",emoji:"🍔",nome:"Delivery",desc:"iFood, Uber Eats...",valorUnitario:70,unidade:"pedido"},
];

function DesafiosTab({desafios, onSave, bills=[], onSaveBills, investimentos=[], onSaveInvestimentos, onRegistrarDividendo, currentYear, currentMonth}) {
  const[showCatalogo,setShowCatalogo]=React.useState(false);
  const cur=(v)=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);

  function ativarDesafio(d, qtd, mesesStr) {
    const valor=d.valorUnitario*qtd;
    const meses=parseInt(mesesStr)||0; // 0 = indeterminado
    const novo={...d,quantidade:qtd,ativo:true,inicio:new Date().toISOString(),economiaTotal:0,registros:[],meses};
    onSave([...desafios.filter(x=>x.id!==d.id), novo]);
    setShowCatalogo(false);
    // Criar despesa recorrente mensal vinculada ao desafio
    if(onSaveBills){
      const hoje=new Date();
      const novaDespesa={
        id:"desafio_"+d.id,
        name:"💰 Desafio: "+d.nome,
        amount:valor,
        dueDay:5,
        frequency:"mensal",
        startMonth:hoje.getMonth(),
        startYear:hoje.getFullYear(),
        installments:meses>0?meses:0, // 0 = sem limite
        paymentMethod:"Débito/Pix",
        category:"Investimentos",
        notes:"Cofrinho do desafio "+d.nome,
        payments:{},
        desafioId:d.id,
      };
      onSaveBills([...(bills||[]), novaDespesa]);
    }
  }



  function getEconomiaMes(d) {
    const now = new Date();
    const anoMes = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    return (d.registros||[])
      .filter(r => r.data && r.data.slice(0,7) === anoMes)
      .reduce((s,r) => s+r.valor, 0);
  }

  const[cancelModal,setCancelModal]=React.useState(null);
  const[rendimentoDesafioModal,setRendimentoDesafioModal]=React.useState(null);
  function confirmarCancelamento(id){
    const desafio=desafios.find(d=>d.id===id);
    onSave(desafios.filter(d=>d.id!==id));
    if(onSaveBills) onSaveBills((bills||[]).filter(b=>b.desafioId!==id));
    // Renomear o cofrinho para indicar que o desafio foi encerrado, mantendo o saldo acumulado
    if(onSaveInvestimentos&&desafio){
      const cofrinhoId="cofrinho_"+id;
      const novoNome="🐷 "+desafio.nome+" (encerrado)";
      onSaveInvestimentos(
        (investimentos||[]).map(i=>i.id===cofrinhoId?{...i,nome:novoNome}:i)
      );
    }
    setCancelModal(null);
  }

  const totalEconomizado = desafios.reduce((s,d)=>s+(d.economiaTotal||0),0);

  return(
    <section className="section-card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8}}>
        <h2 className="section-title" style={{margin:0}}>🏆 Meus Desafios</h2>
        <button className="primary-btn" onClick={()=>setShowCatalogo(!showCatalogo)}>+ Novo desafio</button>
      </div>

      {totalEconomizado>0&&(
        <div style={{background:"linear-gradient(135deg,#14532d22,#166534aa)",border:"1px solid #22c55e44",borderRadius:14,padding:"16px",marginBottom:16,textAlign:"center"}}>
          <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:4}}>💰 Total economizado com desafios</div>
          <div style={{fontSize:28,fontWeight:900,color:"#22c55e"}}>{cur(totalEconomizado)}</div>
        </div>
      )}

      {/* Catálogo de desafios */}
      {showCatalogo&&(
        <div style={{marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:700,color:"var(--text-muted)",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.06em"}}>Escolha um desafio</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
            {DESAFIOS_CATALOGO.filter(d=>!desafios.find(x=>x.id===d.id)).map(d=>(
              <DesafioCard key={d.id} desafio={d} onAtivar={(qtd,meses)=>ativarDesafio(d,qtd,meses)}/>
            ))}
          </div>
        </div>
      )}

      {/* Desafios ativos */}
      {desafios.length===0?(
        <div className="empty" style={{padding:"40px 0"}}>
          <div style={{fontSize:48,marginBottom:12}}>🏆</div>
          <div>Nenhum desafio ativo ainda.</div>
          <div style={{fontSize:13,color:"var(--text-muted)",marginTop:4}}>Clique em <strong>+ Novo desafio</strong> para começar!</div>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {desafios.map(d=>{
            const metaMensal=d.valorUnitario*d.quantidade;
            const metaAnual=metaMensal*12;
            const meta5Anos=metaAnual*5;
            const economiaMes=getEconomiaMes(d);
            const mesesEscolhidos=d.meses||0;
            const mesesPagos=(d.registros||[]).length;
            const pct=mesesEscolhidos>0
              ? Math.min(100,Math.round((mesesPagos/mesesEscolhidos)*100))
              : (metaMensal>0?Math.min(100,Math.round((economiaMes/metaMensal)*100)):0);
            const metaAtingida=mesesEscolhidos>0?mesesPagos>=mesesEscolhidos:economiaMes>=metaMensal;
            return(
              <div key={d.id} style={{background:"var(--bg-card)",border:`1px solid ${metaAtingida?"#22c55e44":"var(--border)"}`,borderRadius:14,padding:16}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <div style={{fontSize:32}}>{d.emoji}</div>
                    <div>
                      <div style={{fontWeight:700,fontSize:15,color:"var(--text-primary)"}}>{d.nome}</div>
                      <div style={{fontSize:12,color:"var(--text-muted)"}}>{d.quantidade}x por mês · {cur(metaMensal)}/mês · {cur(metaAnual)}/ano · <strong style={{color:"#22c55e"}}>{cur(meta5Anos)} em 5 anos! 🚀</strong></div>
                    </div>
                  </div>
                  <button className="ghost-btn" style={{fontSize:11,padding:"4px 10px",color:"#ef4444",border:"1px solid #ef444433"}} onClick={()=>setCancelModal(d.id)}>Cancelar</button>
                </div>
                <div style={{height:8,background:"var(--bg-input)",borderRadius:4,overflow:"hidden",marginBottom:6}}>
                  <div style={{height:"100%",width:`${pct}%`,background:"linear-gradient(90deg,#22c55e,#16a34a)",borderRadius:4,transition:"width .4s"}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <div style={{fontSize:12,color:"var(--text-muted)"}}>
                    {mesesEscolhidos>0?(
                      <>
                        <span style={{color:metaAtingida?"#22c55e":"var(--text-primary)",fontWeight:600}}>{mesesPagos} mês{mesesPagos!==1?"es":""} pagos</span>
                        {" de "}{mesesEscolhidos}{" ({pct}%)".replace("{pct}",pct)}
                        {metaAtingida&&<span style={{marginLeft:6,color:"#22c55e",fontWeight:700}}>✅ Desafio concluído!</span>}
                      </>
                    ):(
                      <>
                        <span style={{color:"var(--text-primary)",fontWeight:600}}>{cur(economiaMes)}</span>
                        {" de "}{cur(metaMensal)}{" este mês"}
                        {economiaMes>=metaMensal&&<span style={{marginLeft:6,color:"#22c55e",fontWeight:700}}>✅ Meta do mês atingida!</span>}
                      </>
                    )}
                  </div>
                  <button className="secondary-btn" style={{fontSize:11,padding:"4px 10px",background:"#22c55e18",border:"1px solid #22c55e44",color:"#22c55e",fontWeight:700}} onClick={()=>setRendimentoDesafioModal({desafio:d,valor:""})}>+ Rendimento</button>
                </div>
                {(d.economiaTotal||0)>0&&(
                  <div style={{fontSize:11,color:"var(--text-muted)",textAlign:"right"}}>
                    💰 Total acumulado: <strong style={{color:"#22c55e"}}>{cur(d.economiaTotal||0)}</strong>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {cancelModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setCancelModal(null)}>
          <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:380,textAlign:"center"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:40,marginBottom:8}}>😢</div>
            <h3 style={{margin:"0 0 8px",fontSize:16,color:"var(--text-primary)"}}>Que pena!</h3>
            <p style={{fontSize:13,color:"var(--text-muted)",margin:"0 0 8px",lineHeight:1.6}}>
              Você precisa mesmo desistir do desafio?<br/>
              <strong style={{color:"var(--text-primary)"}}>Tente reduzir uma despesa antes e só depois pare de investir!</strong><br/>
              Cada real guardado hoje é sua liberdade financeira amanhã. 💪
            </p>
            {cancelModal&&(desafios.find(d=>d.id===cancelModal)?.economiaTotal||0)>0&&(
              <div style={{background:"#22c55e11",border:"1px solid #22c55e33",borderRadius:10,padding:"10px 12px",marginBottom:12,fontSize:12,color:"var(--text-muted)",lineHeight:1.6,textAlign:"left"}}>
                🐷 <strong style={{color:"#22c55e"}}>Seu cofrinho fica!</strong> O valor de <strong style={{color:"var(--text-primary)"}}>{cur(desafios.find(d=>d.id===cancelModal)?.economiaTotal||0)}</strong> acumulado permanece em Investimentos — nada se perde.
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <button className="ghost-btn" style={{width:"100%"}} onClick={()=>setCancelModal(null)}>💪 Vou continuar o desafio!</button>
              <button style={{width:"100%",padding:"10px",borderRadius:10,background:"transparent",border:"1px solid #ef444444",color:"#ef4444",fontSize:13,cursor:"pointer"}} onClick={()=>confirmarCancelamento(cancelModal)}>Cancelar mesmo assim</button>
            </div>
          </div>
        </div>
      )}
      {rendimentoDesafioModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setRendimentoDesafioModal(null)}>
          <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:380}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:"0 0 16px",fontSize:16,color:"var(--text-primary)"}}>💹 Rendimento — {rendimentoDesafioModal.desafio.nome}</h3>
            <div className="field" style={{marginBottom:12}}>
              <label className="label">Valor do rendimento (R$)</label>
              <input className="input" type="number" step="any" placeholder="Ex: 2,50"
                value={rendimentoDesafioModal.valor}
                onChange={e=>setRendimentoDesafioModal(m=>({...m,valor:e.target.value}))} autoFocus/>
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>Soma ao total acumulado do desafio. Não entra no caixa.</div>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="ghost-btn" onClick={()=>setRendimentoDesafioModal(null)}>Cancelar</button>
              <button className="primary-btn" style={{background:"#22c55e"}} onClick={()=>{
                const v=parseFloat(rendimentoDesafioModal.valor)||0;
                if(v<=0) return;
                const d=rendimentoDesafioModal.desafio;
                onSave(desafios.map(x=>x.id===d.id?{...x,economiaTotal:Math.round(((x.economiaTotal||0)+v)*100)/100}:x));
                // Rendimento do cofrinho fica no cofrinho: NÃO cria entrada no caixa
                setRendimentoDesafioModal(null);
              }}>💾 Registrar</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function DesafioCard({desafio, onAtivar}) {
  const[qtd,setQtd]=React.useState(1);
  const[meses,setMeses]=React.useState("12");
  const cur=(v)=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);
  const m=parseInt(meses)||0;
  return(
    <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:12,padding:14,textAlign:"center"}}>
      <div style={{fontSize:28,marginBottom:6}}>{desafio.emoji}</div>
      <div style={{fontWeight:700,fontSize:13,color:"var(--text-primary)",marginBottom:4}}>{desafio.nome}</div>
      <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:10,lineHeight:1.4}}>{desafio.desc}</div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:10}}>
        <button onClick={()=>setQtd(Math.max(1,qtd-1))} style={{width:24,height:24,borderRadius:"50%",border:"1px solid var(--border)",background:"var(--bg-input)",cursor:"pointer",color:"var(--text-primary)",fontSize:14}}>-</button>
        <span style={{fontWeight:700,fontSize:16,color:"var(--text-primary)",minWidth:20}}>{qtd}</span>
        <button onClick={()=>setQtd(qtd+1)} style={{width:24,height:24,borderRadius:"50%",border:"1px solid var(--border)",background:"var(--bg-input)",cursor:"pointer",color:"var(--text-primary)",fontSize:14}}>+</button>
      </div>
      <div style={{fontSize:11,color:"#22c55e",fontWeight:700,marginBottom:8}}>{cur(desafio.valorUnitario*qtd)}/mês · {cur(desafio.valorUnitario*qtd*12)}/ano</div>
      <div style={{marginBottom:10}}>
        <label style={{fontSize:11,color:"var(--text-muted)",display:"block",marginBottom:4}}>Por quantos meses?</label>
        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:4}}>
          {["3","6","12","24","0"].map(op=>(
            <button key={op} onClick={()=>setMeses(op)}
              style={{fontSize:10,padding:"5px 2px",borderRadius:8,border:"1px solid",cursor:"pointer",textAlign:"center",
                background:meses===op?"var(--accent)":"transparent",
                color:meses===op?"#fff":"var(--text-muted)",
                borderColor:meses===op?"var(--accent)":"var(--border)"}}>
              {op==="0"?"∞":op+"x"}
            </button>
          ))}
        </div>
        {m>0&&<div style={{fontSize:10,color:"var(--text-muted)",marginTop:4}}>Total: {cur(desafio.valorUnitario*qtd*m)} em {m} meses</div>}
        {meses==="0"&&<div style={{fontSize:10,color:"var(--text-muted)",marginTop:4}}>Sem prazo definido</div>}
      </div>
      <button className="primary-btn" style={{fontSize:12,padding:"8px 12px",width:"100%"}} onClick={()=>onAtivar(qtd,meses)}>Aceitar! 🚀</button>
    </div>
  );
}

// ─── Aba Notícias ────────────────────────────────────────
function NoticiasTab() {
  const[noticias,setNoticias]=React.useState([]);
  const[loading,setLoading]=React.useState(true);
  const[erro,setErro]=React.useState(false);

  async function buscarNoticias() {
    setLoading(true);setErro(false);
    try {
      // Buscar via Railway (sem CORS, sempre funciona no mobile)
      const r = await fetch("https://authentic-trust-production-0edc.up.railway.app/noticias");
      const d = await r.json();
      if(d.ok && d.items?.length > 0){
        setNoticias(d.items);
      } else {
        setErro(true);
      }
    }catch(e){
      setErro(true);
    }finally{
      setLoading(false);
    }
  }

  React.useEffect(()=>{buscarNoticias();},[]);

  return(
    <section className="section-card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <h2 className="section-title" style={{margin:0}}>📰 Notícias de Economia</h2>
        <button className="secondary-btn" onClick={buscarNoticias} disabled={loading} style={{fontSize:13}}>
          {loading?"⏳ Carregando...":"🔄 Atualizar"}
        </button>
      </div>

      {loading&&(
        <div style={{textAlign:"center",padding:"40px 0",color:"var(--text-muted)"}}>
          <div className="spinner" style={{margin:"0 auto 12px"}}/>
          Buscando últimas notícias...
        </div>
      )}

      {erro&&!loading&&(
        <div className="empty" style={{padding:"40px 0"}}>
          <div style={{fontSize:48,marginBottom:12}}>📡</div>
          <div>Não foi possível carregar as notícias.</div>
          <div style={{fontSize:13,color:"var(--text-muted)",marginTop:4}}>Verifique sua conexão e tente novamente.</div>
          <button className="primary-btn" style={{marginTop:16}} onClick={buscarNoticias}>Tentar novamente</button>
        </div>
      )}

      {!loading&&!erro&&(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {noticias.map((n,i)=>(
            <a key={i} href={n.link} target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
              <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:16,transition:"border-color .15s",cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:6}}>
                  <div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)",lineHeight:1.4,flex:1}}>{n.titulo}</div>
                  <div style={{fontSize:11,color:"var(--text-muted)",flexShrink:0,marginTop:2}}>🔗</div>
                </div>
                {n.descricao&&<div style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.5,marginBottom:6}}>{n.descricao}</div>}
                <div style={{display:"flex",gap:12,fontSize:11,color:"var(--text-muted)"}}>
                  <span>📰 {n.fonte}</span>
                  <span>📅 {n.data}</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
      <div style={{fontSize:11,color:"var(--text-muted)",textAlign:"center",marginTop:16}}>Fonte: InfoMoney · Atualizado automaticamente</div>
    </section>
  );
}


// ─── Gráfico Saldo Devedor ───────────────────────────────
// ─── Aba Dívidas ─────────────────────────────────────────
function DividasTab({bills, currentYear, currentMonth}) {
  const cur = v => new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v||0);
  const hoje = new Date();
  const [mostrarQuitadas, setMostrarQuitadas] = React.useState(false);
  const [buscaDivida, setBuscaDivida] = React.useState("");

  // Filtrar apenas dívidas com parcelas (financiamentos, empréstimos, compras parceladas)
  const dividas = bills.filter(b => b.installments > 0 && b.frequency !== "unico").map(b => {
    const totalParcelas = b.installments;
    const mesInicio = b.startYear*12 + b.startMonth;
    const mesAtual = hoje.getFullYear()*12 + hoje.getMonth();
    const pagas = Math.min(totalParcelas, Math.max(0, mesAtual - mesInicio + 1));
    const restantes = Math.max(0, totalParcelas - pagas);
    const totalDivida = round2(b.amount * totalParcelas);
    const totalPago = round2(b.amount * pagas);
    const totalRestante = round2(b.amount * restantes);
    const pct = totalParcelas > 0 ? Math.round((pagas/totalParcelas)*100) : 0;
    const mesTermino = new Date(b.startYear, b.startMonth + totalParcelas - 1);
    // Cálculo de quitação antecipada com taxa cadastrada
    const taxa = Number(b.taxaJuros||0)/100;
    let valorQuitacaoAuto = totalRestante;
    if(taxa>0 && restantes>0){
      valorQuitacaoAuto = round2(b.amount*(1-Math.pow(1+taxa,-restantes))/taxa);
    }
    const economiaAuto = round2(totalRestante - valorQuitacaoAuto);
    return {...b, pagas, restantes, totalDivida, totalPago, totalRestante, pct, mesTermino, valorQuitacaoAuto, economiaAuto, taxaPerc: Number(b.taxaJuros||0)};
  }).sort((a,b) => b.totalRestante - a.totalRestante);

  const dividasAtivas = dividas.filter(d => d.pct < 100 && d.name.toLowerCase().includes(buscaDivida.toLowerCase()));
  const dividasQuitadas = dividas.filter(d => d.pct >= 100 && d.name.toLowerCase().includes(buscaDivida.toLowerCase()));

  // Totais gerais
  const totalGeralDivida = dividas.reduce((s,d) => s+d.totalDivida, 0);
  const totalGeralPago = dividas.reduce((s,d) => s+d.totalPago, 0);
  const totalGeralRestante = dividas.reduce((s,d) => s+d.totalRestante, 0);
  const pctGeral = totalGeralDivida > 0 ? Math.round((totalGeralPago/totalGeralDivida)*100) : 0;

  // Totais de quitação antecipada (só dívidas com taxa cadastrada)
  const dividasComTaxa = dividas.filter(d=>d.taxaPerc>0 && d.restantes>0);
  const totalQuitacaoAntecipada = round2(dividasComTaxa.reduce((s,d)=>s+d.valorQuitacaoAuto,0));
  const totalEconomiaJuros = round2(dividasComTaxa.reduce((s,d)=>s+d.economiaAuto,0));

  // Agrupar por categoria
  const porCategoria = {};
  for(const d of dividas) {
    if(!porCategoria[d.category]) porCategoria[d.category] = {total:0, count:0};
    porCategoria[d.category].total += d.totalRestante;
    porCategoria[d.category].count++;
  }

  if(dividas.length === 0) {
    return(
      <section className="section-card">
        <h2 className="section-title">💳 Painel de Dívidas</h2>
        <div className="empty" style={{padding:"60px 0"}}>
          <div style={{fontSize:48,marginBottom:12}}>🎉</div>
          <div style={{fontWeight:700,fontSize:16,marginBottom:8}}>Nenhuma dívida parcelada encontrada!</div>
          <div style={{fontSize:13,color:"var(--text-muted)"}}>Dívidas com parcelas cadastradas aparecerão aqui automaticamente.</div>
        </div>
      </section>
    );
  }

  return(
    <section className="section-card">
      <h2 className="section-title">💳 Painel de Dívidas</h2>

      {/* Cards de resumo — empilhados no mobile para não cortar valores */}
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <div style={{background:"#EF444418",border:"2px solid #EF444466",borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
            <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:4}}>💸 Total das dívidas</div>
            <div style={{fontSize:16,fontWeight:900,color:"#EF4444",wordBreak:"break-word",lineHeight:1.2}}>{cur(totalGeralDivida)}</div>
            <div style={{fontSize:10,color:"var(--text-muted)",marginTop:4}}>{dividas.length} dívidas cadastradas</div>
          </div>
          <div style={{background:"#22c55e18",border:"2px solid #22c55e66",borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
            <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:4}}>✅ Já pago</div>
            <div style={{fontSize:16,fontWeight:900,color:"#22c55e",wordBreak:"break-word",lineHeight:1.2}}>{cur(totalGeralPago)}</div>
            <div style={{fontSize:10,color:"#22c55e",marginTop:4}}>{pctGeral < 1 && pctGeral > 0 ? pctGeral.toFixed(1) : pctGeral}% quitado</div>
          </div>
        </div>
        <div style={{background:"var(--bg-card)",border:"2px solid #F59E0B66",borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
          <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:4}}>⏳ Ainda devo</div>
          <div style={{fontSize:18,fontWeight:900,color:"#F59E0B"}}>{cur(totalGeralRestante)}</div>
          <div style={{fontSize:10,color:"var(--text-muted)",marginTop:4}}>{pctGeral >= 99 ? "quase lá! 🎉" : `${Math.max(0,100-pctGeral).toFixed(pctGeral < 1 ? 1 : 0)}% restante`}</div>
        </div>
      </div>

      {/* Card motivacional — sempre visível */}
      <div style={{background:"linear-gradient(135deg,#22c55e0a,#14b8a60a)",border:"2px solid #22c55e44",borderRadius:14,padding:"16px",marginBottom:20}}>
        <div style={{fontWeight:800,fontSize:14,color:"#22c55e",marginBottom:12}}>🎯 Quitando antecipadamente você economiza:</div>
        {dividasComTaxa.length>0 ? (
          <>
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:10}}>
              <div style={{background:"var(--bg-card)",borderRadius:10,padding:"12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontSize:12,color:"var(--text-muted)"}}>💰 Para quitar tudo agora</div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:16,fontWeight:900,color:"#F59E0B"}}>{cur(totalQuitacaoAntecipada)}</div>
                  <div style={{fontSize:9,color:"var(--text-muted)",marginTop:2}}>vs {cur(totalGeralRestante)} em parcelas</div>
                </div>
              </div>
              <div style={{background:"var(--bg-card)",borderRadius:10,padding:"12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontSize:12,color:"var(--text-muted)"}}>🎉 Economia em juros</div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:16,fontWeight:900,color:"#22c55e"}}>{cur(totalEconomiaJuros)}</div>
                  <div style={{fontSize:9,color:"#22c55e",marginTop:2}}>{dividasComTaxa.length} dívida(s) com juros</div>
                </div>
              </div>
            </div>
            <div style={{fontSize:11,color:"var(--text-muted)",lineHeight:1.5}}>
              📌 Baseado nas taxas cadastradas. Use o botão 💡 em cada dívida para simular individualmente.
            </div>
          </>
        ) : (
          <div style={{background:"var(--bg-card)",borderRadius:10,padding:"14px",textAlign:"center"}}>
            <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6,marginBottom:8}}>
              Cadastre a <strong style={{color:"var(--text-primary)"}}>taxa de juros mensal</strong> nas suas dívidas para ver quanto você economizaria quitando antecipadamente.
            </div>
            <div style={{fontSize:12,color:"#F59E0B",fontWeight:600}}>
              ✏️ Edite uma dívida → campo "Taxa de juros (%)" → salve
            </div>
          </div>
        )}
      </div>

      {/* Por categoria */}
      {Object.keys(porCategoria).length > 1 && (
        <div style={{marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Por categoria</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {Object.entries(porCategoria).sort((a,b)=>b[1].total-a[1].total).map(([cat,dados])=>(
              <div key={cat} style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 14px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>{CATEGORY_ICONS[cat]||"📦"}</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>{cat}</div>
                    <div style={{fontSize:11,color:"var(--text-muted)"}}>{dados.count} dívida(s)</div>
                  </div>
                </div>
                <div style={{fontWeight:800,fontSize:14,color:"#EF4444"}}>{cur(dados.total)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista detalhada de cada dívida */}
      {dividas.length>0&&<input className="input" placeholder="🔍 Buscar dívida..." value={buscaDivida} onChange={e=>setBuscaDivida(e.target.value)} style={{marginBottom:10}}/>}
      <div style={{fontSize:12,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Dívidas em andamento</div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {dividasAtivas.length===0?(
          <div className="empty">🎉 Nenhuma dívida ativa! Todas quitadas.</div>
        ):(
          dividasAtivas.map(d=><DividaCard key={d.id} d={d} cur={cur}/>)
        )}
      </div>

      {/* Quitadas — colapsável */}
      {dividasQuitadas.length>0&&(
        <div style={{marginTop:16}}>
          <button onClick={()=>setMostrarQuitadas(v=>!v)}
            style={{background:"none",border:"1px solid #22c55e33",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,color:"#22c55e",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            ✅ {mostrarQuitadas?"Ocultar":"Ver"} quitadas ({dividasQuitadas.length})
          </button>
          {mostrarQuitadas&&(
            <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:10}}>
              {dividasQuitadas.map(d=><DividaCard key={d.id} d={d} cur={cur}/>)}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function DividaCard({d, cur}) {
  const [simAberto, setSimAberto] = React.useState(false);
  const [taxa, setTaxa] = React.useState(d.taxaPerc>0?String(d.taxaPerc):"");

  const taxaDecimal = (parseFloat(taxa.replace(",","."))||0)/100;
  let valorQuitacao = d.totalRestante;
  let economiaJuros = 0;
  if(taxaDecimal>0 && d.restantes>0){
    valorQuitacao = round2(d.amount * (1-Math.pow(1+taxaDecimal,-d.restantes)) / taxaDecimal);
    economiaJuros = round2(d.totalRestante - valorQuitacao);
  }

  return(
    <div style={{background:"var(--bg-card)",border:`1px solid ${d.pct>=100?"#22c55e44":"var(--border)"}`,borderRadius:14,padding:"14px 16px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
        <div>
          <div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)",display:"flex",alignItems:"center",gap:8}}>
            {CATEGORY_ICONS[d.category]||"📦"} {d.name}
            {d.pct>=100&&<span style={{background:"#22c55e22",color:"#22c55e",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10}}>✅ Quitada!</span>}
          </div>
          <div style={{fontSize:11,color:"var(--text-muted)",marginTop:3}}>
            {d.pagas}/{d.installments} parcelas · {cur(d.amount)}/mês · Termina {MONTHS_PT[d.mesTermino.getMonth()]}/{d.mesTermino.getFullYear()}
            {d.taxaPerc>0&&<span style={{marginLeft:6,color:"#F59E0B",fontWeight:600}}>· {Number(d.taxaPerc) % 1 === 0 ? d.taxaPerc : parseFloat(d.taxaPerc.toFixed(3))}% a.m.</span>}
            {!d.taxaPerc&&String(d.taxaJuros)==="0"&&<span style={{marginLeft:6,color:"#22c55e",fontWeight:600}}>· Sem juros</span>}
          </div>
          {/* Quitação antecipada — aparece na simulação individual abaixo */}
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{fontSize:15,fontWeight:800,color:"#EF4444"}}>{cur(d.totalRestante)}</div>
          <div style={{fontSize:11,color:"var(--text-muted)"}}>restante</div>
        </div>
      </div>
      <div style={{height:8,background:"var(--bg-input)",borderRadius:4,overflow:"hidden",marginBottom:6}}>
        <div style={{height:"100%",width:`${d.pct}%`,background:d.pct>=100?"#22c55e":"linear-gradient(90deg,#7C83FF,#6366f1)",borderRadius:4,transition:"width .5s"}}/>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--text-muted)",marginBottom:8}}>
        <span style={{color:"#22c55e",fontWeight:600}}>✅ Pago: {cur(d.totalPago)}</span>
        <span style={{fontWeight:700,color:"#7C83FF"}}>{d.pct}% quitado</span>
        <span>Total: {cur(d.totalDivida)}</span>
      </div>
      {d.restantes>0&&(
        <div>
          <button onClick={()=>setSimAberto(s=>!s)}
            style={{background:"none",border:"1px solid #F59E0B44",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,color:"#F59E0B",cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
            💡 {simAberto?"Fechar simulação":"Simular quitação antecipada"}
          </button>
          {simAberto&&(
            <div style={{marginTop:10,background:"#F59E0B0a",border:"1px solid #F59E0B33",borderRadius:10,padding:"12px 14px"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#F59E0B",marginBottom:10}}>💡 Simulação de quitação antecipada</div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                <label style={{fontSize:12,color:"var(--text-muted)",whiteSpace:"nowrap"}}>Taxa de juros mensal (%):</label>
                <input
                  style={{background:"var(--bg-input)",border:"1px solid var(--border-input)",borderRadius:8,padding:"6px 10px",fontSize:13,color:"var(--text-primary)",width:90,outline:"none"}}
                  placeholder="Ex: 1,99"
                  value={taxa}
                  onChange={e=>setTaxa(e.target.value)}
                  inputMode="decimal"
                />
                {!taxa&&<span style={{fontSize:11,color:"var(--text-muted)"}}>← informe a taxa do contrato</span>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <div style={{background:"var(--bg-card)",borderRadius:10,padding:"10px 12px",textAlign:"center"}}>
                  <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:3}}>
                    {taxaDecimal>0?"💰 Para quitar hoje":"📋 Saldo restante"}
                  </div>
                  <div style={{fontSize:17,fontWeight:900,color:"#F59E0B"}}>{cur(valorQuitacao)}</div>
                  {taxaDecimal>0&&<div style={{fontSize:9,color:"var(--text-muted)",marginTop:2}}>Valor presente das {d.restantes} parcelas</div>}
                </div>
                <div style={{background:economiaJuros>0?"#22c55e11":"var(--bg-card)",borderRadius:10,padding:"10px 12px",textAlign:"center",border:economiaJuros>0?"1px solid #22c55e33":"none"}}>
                  <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:3}}>
                    {economiaJuros>0?"🎉 Economia em juros":taxaDecimal>0?"⚠️ Sem economia":"ℹ️ Com juros"}
                  </div>
                  <div style={{fontSize:17,fontWeight:900,color:economiaJuros>0?"#22c55e":"var(--text-muted)"}}>
                    {taxaDecimal>0?cur(economiaJuros):"Informe a taxa"}
                  </div>
                  {economiaJuros>0&&<div style={{fontSize:9,color:"#22c55e",marginTop:2}}>{d.restantes} parcelas antecipadas</div>}
                </div>
              </div>
              {taxaDecimal>0&&economiaJuros>0&&(
                <div style={{marginTop:8,fontSize:11,color:"var(--text-muted)",background:"#22c55e0a",borderRadius:8,padding:"6px 10px"}}>
                  📌 Pagando <strong style={{color:"#F59E0B"}}>{cur(valorQuitacao)}</strong> hoje você economiza <strong style={{color:"#22c55e"}}>{cur(economiaJuros)}</strong> em juros.
                </div>
              )}
              <div style={{fontSize:10,color:"var(--text-muted)",marginTop:8,lineHeight:1.5}}>
                📌 Simulação pelo sistema Price. Confirme o valor exato com a instituição financeira antes de quitar.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



// ─── Tela Jornada — primeiro acesso pós-login ─────────────
function JornadaScreen({ nomeUsuario, onStart, onSkip }) {
  const nome = (nomeUsuario||"").split(" ")[0];
  const [carAnim, setCarAnim] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setCarAnim(true), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{
      minHeight:"100vh",
      display:"flex",flexDirection:"column",
      alignItems:"center",justifyContent:"center",
      background:"radial-gradient(circle at 30% 20%,rgba(124,131,255,0.18),transparent 40%),radial-gradient(circle at 70% 80%,rgba(245,158,11,0.12),transparent 40%),#09090f",
      fontFamily:"Inter,ui-sans-serif,system-ui,sans-serif",
      padding:"32px 24px",
      textAlign:"center",
      position:"relative",
      overflow:"hidden",
    }}>
      {/* Estrada animada */}
      <div style={{
        position:"absolute",bottom:0,left:0,right:0,height:6,
        background:"linear-gradient(90deg,transparent,#f59e0b44,#f59e0b,#f59e0b44,transparent)",
        animation:"roadLine 2s linear infinite",
      }}/>

      {/* Porquinho no carro */}
      <div style={{
        marginBottom:32,
        opacity: carAnim ? 1 : 0,
        transform: carAnim ? "translateX(0) scale(1)" : "translateX(-80px) scale(0.8)",
        transition:"all 0.7s cubic-bezier(0.34,1.2,0.64,1)",
        animation: carAnim ? "carBob 2s ease-in-out infinite" : "none",
        filter:"drop-shadow(0 16px 40px rgba(245,158,11,0.4))",
      }}>
        <img
          src={porquinhoCarro}
          alt="Porquinho no carro"
          style={{width:220,height:220,objectFit:"contain"}}
        />
      </div>

      {/* Texto */}
      <div style={{
        opacity: carAnim ? 1 : 0,
        transform: carAnim ? "translateY(0)" : "translateY(20px)",
        transition:"all 0.6s ease 0.3s",
        maxWidth:320,
      }}>
        <h1 style={{
          fontSize:26,fontWeight:900,
          color:"#f8fafc",
          lineHeight:1.25,margin:"0 0 12px",
          letterSpacing:"-0.02em",
        }}>
          {nome ? `${nome}, sua jornada` : "Sua jornada"}<br/>
          <span style={{
            background:"linear-gradient(135deg,#f59e0b,#fbbf24)",
            WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",
          }}>
            rumo à liberdade
          </span><br/>
          financeira começa agora! 🚀
        </h1>
        <p style={{
          fontSize:14,color:"#8f8fa4",lineHeight:1.7,margin:"0 0 32px",
        }}>
          O <strong style={{color:"#f8fafc"}}>Mapa Financeiro 5G</strong> vai te guiar pelos 5 pilares — Ganhar, Gratidão, Generosidade, Gastar e Guardar — até você chegar lá.
        </p>
      </div>

      {/* Botões */}
      <div style={{
        width:"100%",maxWidth:320,display:"flex",flexDirection:"column",gap:12,
        opacity: carAnim ? 1 : 0,
        transform: carAnim ? "translateY(0)" : "translateY(20px)",
        transition:"all 0.6s ease 0.5s",
      }}>
        <button onClick={onSkip} style={{
          width:"100%",padding:"16px",borderRadius:16,
          background:"linear-gradient(135deg,#f59e0b,#d97706)",
          color:"#fff",fontWeight:800,fontSize:16,
          border:"none",cursor:"pointer",fontFamily:"inherit",
          boxShadow:"0 8px 32px rgba(245,158,11,0.45)",
          letterSpacing:0.3,
        }}>
          🚀 Começar a usar agora
        </button>
        <button onClick={onStart} style={{
          width:"100%",padding:"13px",borderRadius:16,
          background:"transparent",
          color:"#52526a",fontWeight:600,fontSize:14,
          border:"1.5px solid #2a2a3e",cursor:"pointer",fontFamily:"inherit",
        }}>
          🗺️ Ver o guia completo antes
        </button>
      </div>

      <style>{`
        @keyframes carBob {
          0%,100%{transform:translateY(0) rotate(-0.5deg)}
          50%{transform:translateY(-6px) rotate(0.5deg)}
        }
        @keyframes roadLine {
          from{background-position:0 0}
          to{background-position:200px 0}
        }
      `}</style>
    </div>
  );
}
// ──────────────────────────────────────────────────────────

// ─── Banner Jornada com Porquinho animado ────────────────────
function BannerJornada({ onOpen }) {
  const [hovered, setHovered] = React.useState(false);
  const jaViu = localStorage.getItem("mf5g_jornada_viu");

  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        borderRadius: 18,
        marginBottom: 16,
        padding: "18px 20px 18px 16px",
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        border: hovered ? "1.5px solid #7C83FFaa" : "1.5px solid #7C83FF44",
        boxShadow: hovered ? "0 8px 32px rgba(124,131,255,0.2)" : "0 4px 16px rgba(0,0,0,0.3)",
        transition: "all 0.25s ease",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      {/* Estrelas de fundo */}
      {["10%","30%","55%","75%","90%"].map((left, i) => (
        <div key={i} style={{
          position: "absolute",
          left, top: `${15 + i * 12}%`,
          width: i % 2 === 0 ? 3 : 2,
          height: i % 2 === 0 ? 3 : 2,
          borderRadius: "50%",
          background: "#ffffff",
          opacity: 0.4,
          animation: `twinkle ${1.5 + i * 0.4}s ease-in-out ${i * 0.3}s infinite`,
        }}/>
      ))}

      {/* Porquinho no carro animado */}
      <div style={{
        flexShrink: 0,
        width: 72,
        height: 72,
        position: "relative",
      }}>
        <img
          src={porquinhoCarro}
          alt="Porquinho no carro"
          style={{
            width: 72,
            height: 72,
            objectFit: "contain",
            filter: "drop-shadow(0 4px 16px rgba(245,158,11,0.5))",
            transform: hovered ? "scale(1.08)" : "scale(1)",
            transition: "transform 0.2s ease",
            animation: "carDriveBanner 1.8s ease-in-out infinite",
          }}
        />
        {/* Rodas girando */}
        <div style={{position:"absolute",bottom:8,left:10,width:12,height:12,borderRadius:"50%",border:"2px solid #f59e0b",animation:"wheelSpin 0.5s linear infinite"}}/>
        <div style={{position:"absolute",bottom:8,right:8,width:12,height:12,borderRadius:"50%",border:"2px solid #f59e0b",animation:"wheelSpin 0.5s linear infinite"}}/>
      </div>

      {/* Texto */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!jaViu ? (
          <>
            <div style={{
              fontWeight: 800,
              fontSize: 14,
              color: "#f8fafc",
              lineHeight: 1.3,
              marginBottom: 4,
            }}>
              🗺️ Vamos começar sua jornada rumo à liberdade financeira?
            </div>
            <div style={{
              fontSize: 12,
              color: "#94a3b8",
              lineHeight: 1.4,
              marginBottom: 6,
            }}>
              O Mapa Financeiro 5G vai te guiar até lá!
            </div>
          </>
        ) : (
          <>
            <div style={{
              fontWeight: 700,
              fontSize: 13,
              color: "#f8fafc",
              marginBottom: 3,
            }}>
              📖 Como usar o Mapa Financeiro 5G
            </div>
            <div style={{
              fontSize: 12,
              color: "#94a3b8",
            }}>
              Guia completo de todas as funcionalidades
            </div>
          </>
        )}
        <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "linear-gradient(135deg, #7C83FF, #a855f7)",
          borderRadius: 20,
          padding: "5px 14px",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          boxShadow: "0 4px 12px rgba(124,131,255,0.4)",
          animation: !jaViu ? "ctaPulse 2s ease-in-out infinite" : "none",
        }}>
          {!jaViu ? "Começar minha jornada →" : "Ver guia →"}
        </div>
      </div>

      <style>{`
        @keyframes carDriveBanner {
          0%, 100% { transform: translateY(0) rotate(-0.5deg); }
          25% { transform: translateY(-3px) rotate(0.5deg); }
          75% { transform: translateY(-1px) rotate(-0.3deg); }
        }
        @keyframes wheelSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.5); }
        }
        @keyframes ctaPulse {
          0%, 100% { box-shadow: 0 4px 12px rgba(124,131,255,0.4); }
          50% { box-shadow: 0 4px 24px rgba(124,131,255,0.7); }
        }
      `}</style>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────

export default function App(){
  const now=todayParts();
  const[session,setSession]=useState(null);
  const[showResetPassword,setShowResetPassword]=useState(false);
  const[authLoading,setAuthLoading]=useState(true);
  // Onboarding público — aparece só até a pessoa decidir uma vez (persistido no dispositivo)
  const[showPublicOnboarding,setShowPublicOnboarding]=useState(()=>{
    try{return localStorage.getItem("mf5g_onboarding_visto")!=="1";}catch{return true;}
  });

  const[showBoasVindas,setShowBoasVindas]=useState(false);
  const[boasVindasNome,setBoasVindasNome]=useState("");
  const[showPremiumBonus,setShowPremiumBonus]=useState(false); // aparece só após o 1º registro (despesa/entrada)
  // Tela de boas-vindas pós-login (primeiro acesso logado)
  const[showJornadaScreen,setShowJornadaScreen]=useState(false);
  const[data,setData]=useState(INITIAL_DATA);
  const[loading,setLoading]=useState(true);
  const[erroCarregamento,setErroCarregamento]=useState(false);
  const[saveStatus,setSaveStatus]=useState("saved");
  const[activeTab,setActiveTab]=useState("painel");
  const[currentMonth,setCurrentMonth]=useState(now.month);
  const[currentYear,setCurrentYear]=useState(now.year);
  const[darkMode,setDarkMode]=useState(true);
  const[valoresVisiveis,setValoresVisiveis]=useState(true);
  const[expenseFormOpen,setExpenseFormOpen]=useState(false);
  const[editingBillId,setEditingBillId]=useState(null);
  const[entryFormOpen,setEntryFormOpen]=useState(false);
  const[editingEntryId,setEditingEntryId]=useState(null);
  const[paymentModal,setPaymentModal]=useState({open:false,billId:null,refMonthKey:null});
  const[undoModal,setUndoModal]=useState({open:false,billId:null,refMonthKey:null});
  const[tutorialOpen,setTutorialOpen]=useState(false);
  const[toolsOpen,setToolsOpen]=useState(false);
  const[paymentTab,setPaymentTab]=useState("total");
  const[totalPayAmount,setTotalPayAmount]=useState("");
  const[totalPayNote,setTotalPayNote]=useState("");
  const[totalPayMethod,setTotalPayMethod]=useState(PAYMENT_METHODS[0]);
  const[partialAmount,setPartialAmount]=useState("");
  const[partialNote,setPartialNote]=useState("");
  const[partialMethod,setPartialMethod]=useState(PAYMENT_METHODS[0]);
  const[resgateModal,setResgateModal]=useState(false);
  const[resgateValor,setResgateValor]=useState("");
  const[resgateDesc,setResgateDesc]=useState("");
  const[resgateOrigem,setResgateOrigem]=useState("");
  const[resgateConfirmacao,setResgateConfirmacao]=useState(null);
  const[voiceOpen,setVoiceOpen]=useState(false);
  const[voiceEscutando,setVoiceEscutando]=useState(false);
  const[voiceTexto,setVoiceTexto]=useState("");
  const[voiceResultado,setVoiceResultado]=useState(null);
  const[voiceLoading,setVoiceLoading]=useState(false);
  const[voiceErro,setVoiceErro]=useState("");
  const[voiceMultiplas,setVoiceMultiplas]=useState(null);
  const[menuOpen,setMenuOpen]=useState(false);
  const[gastoRapidoOpen,setGastoRapidoOpen]=useState(false);
  const[buscaContas,setBuscaContas]=useState("");
  const[categoriaFiltroContas,setCategoriaFiltroContas]=useState("");
  const[verTodasCadastradas,setVerTodasCadastradas]=useState(false);
  const[modoCadastro,setModoCadastro]=useState("mes"); // "mes" (mês em navegação) | "todos"
  const[categoriaFiltroTodas,setCategoriaFiltroTodas]=useState("");
  const[buscaTodasCadastradas,setBuscaTodasCadastradas]=useState("");
  const[buscaEntradas,setBuscaEntradas]=useState("");
  const[buscaPainel,setBuscaPainel]=useState("");
  const[gruposAbertos,setGruposAbertos]=useState({});
  const toggleGrupo=(lbl)=>setGruposAbertos(prev=>({...prev,[lbl]:!prev[lbl]}));
  const[ajusteModal,setAjusteModal]=useState(null); // {billId, refMonthKey, nome, valorAtual}
  const[gastoRapidoForm,setGastoRapidoForm]=useState({nome:"",valor:"",categoria:"Supermercado",pagamento:"Débito/Pix"});
  const[ultimoBackup,setUltimoBackup]=useState(()=>localStorage.getItem("ultimoBackup")||null);
  const[debtModal,setDebtModal]=useState(null);
  const[encerrarModal,setEncerrarModal]=useState(null); // {bill, mes, ano}
  function abrirEncerrarBill(bill){
    const now=new Date();
    setEncerrarModal({bill,mes:String(now.getMonth()),ano:String(now.getFullYear())});
  }
  function confirmarEncerrarBill(){
    if(!encerrarModal)return;
    const{bill,mes,ano}=encerrarModal;
    setData(p=>({...p,bills:p.bills.map(b=>b.id===bill.id?{...b,endMonth:parseInt(mes),endYear:parseInt(ano)}:b)}));
    setEncerrarModal(null);
  }
  function reativarBill(id){
    if(!window.confirm("Reativar esta despesa? Ela volta a valer para os próximos meses."))return;
    setData(p=>({...p,bills:p.bills.map(b=>b.id===id?{...b,endMonth:null,endYear:null}:b)}));
  }
  const[extraMensal,setExtraMensal]=useState("");
  const[taxaJuros,setTaxaJuros]=useState("");
  const saveTimer=useRef(null);
  const firstLoadDone=useRef(false);
  const lastSavedSnapshot=useRef("");
  const oneSignalReady=useRef(false); // true depois que OneSignal.initialize() rodar
  const latestDataRef=useRef(INITIAL_DATA);
  const currentKey=monthKey(currentYear,currentMonth);
  const today=todayParts();
  const isViewingCurrentMonth=currentMonth===today.month&&currentYear===today.year;

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{setSession(session);setAuthLoading(false);});
    const{data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
      setSession(session);
      // Detectar quando usuário chegou pelo link de recuperação de senha
      if(event==="PASSWORD_RECOVERY"){
        setShowResetPassword(true);
      }
    });
    // Inicializar OneSignal para notificações push
    if(window.Capacitor && window.Capacitor.isNativePlatform()){
      setTimeout(()=>{
        try{
          OneSignal.initialize("d46e2941-0ee4-4fc4-ae00-e3bc1c280a0b");
          oneSignalReady.current=true;
          setTimeout(()=>{
            try{OneSignal.Notifications.requestPermission(true);}catch(e){console.log("OneSignal permission error:",e);}
          },2000);
        }catch(e){console.log("OneSignal init error:",e);}
      },3000);
      initAnalytics();
    }
    return()=>subscription.unsubscribe();
  },[]);

  // Tag de segmentação do OneSignal: marca se o usuário já se cadastrou ou não.
  // Roda no carregamento inicial e sempre que a sessão mudar (login/logout).
  useEffect(()=>{
    if(!(window.Capacitor && window.Capacitor.isNativePlatform()))return;
    if(authLoading)return; // aguarda a checagem inicial de sessão terminar
    const tagStatus=()=>{
      try{
        OneSignal.User.addTag("status",session?"cadastrado":"nao_cadastrado");
      }catch(e){console.log("OneSignal tag error:",e);}
    };
    if(oneSignalReady.current){
      tagStatus();
    }else{
      // OneSignal ainda não terminou de inicializar (delay de 3s) — espera antes de marcar
      const t=setTimeout(tagStatus,3500);
      return()=>clearTimeout(t);
    }
  },[session,authLoading]);

  useEffect(()=>{
    if(session){loadFromSupabase();}else if(!authLoading){setLoading(false);} // eslint-disable-line react-hooks/exhaustive-deps
  },[session,authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{latestDataRef.current=data;},[data]); // Sempre sincronizado com o estado mais recente

  // Salva imediatamente ao trocar de aba do navegador ou minimizar
  useEffect(()=>{
    async function saveNow(){
      if(!firstLoadDone.current||!session)return;
      const snapshot=JSON.stringify(latestDataRef.current);
      if(snapshot===lastSavedSnapshot.current)return;
      if(saveTimer.current)window.clearTimeout(saveTimer.current);
      try{
        const{error}=await supabase.from("financial_data").upsert({user_id:session.user.id,data:latestDataRef.current},{onConflict:"user_id"});
        if(!error){lastSavedSnapshot.current=snapshot;setSaveStatus("saved");}
      }catch(err){console.error("Erro ao salvar ao sair da aba:",err);}
    }
    function onVisibilityChange(){if(document.visibilityState==="hidden")saveNow();}
    function onPageHide(){saveNow();}
    document.addEventListener("visibilitychange",onVisibilityChange);
    window.addEventListener("pagehide",onPageHide);
    return()=>{
      document.removeEventListener("visibilitychange",onVisibilityChange);
      window.removeEventListener("pagehide",onPageHide);
    };
  },[session]);

  useEffect(()=>{
    if(!firstLoadDone.current||!session)return undefined;
    const snapshot=JSON.stringify(data);
    if(snapshot===lastSavedSnapshot.current){setSaveStatus("saved");return undefined;}
    if(saveTimer.current)window.clearTimeout(saveTimer.current);
    setSaveStatus("saving");
    saveTimer.current=window.setTimeout(async()=>{
      try{
        // Usar latestDataRef para garantir dados mais recentes
        const currentData=latestDataRef.current;
        const{error}=await supabase.from("financial_data").upsert({user_id:session.user.id,data:currentData},{onConflict:"user_id"});
        if(error)throw error;
        lastSavedSnapshot.current=JSON.stringify(currentData);setSaveStatus("saved");
      }catch(err){console.error("Erro ao salvar:",err);setSaveStatus("error");}
    },800);
    return()=>{if(saveTimer.current)window.clearTimeout(saveTimer.current);};
  },[data,session]);

  async function loadFromSupabase(){
    setLoading(true);setErroCarregamento(false);
    try{
      let{data:row,error}=await supabase.from("financial_data").select("data").eq("user_id",session.user.id).single();
      if(error&&error.code!=="PGRST116")throw error;
      if(!row){
        // "Nenhuma linha encontrada" pode ser um usuário genuinamente novo — mas também pode
        // ser uma falha passageira logo após o login (comum em app recém-instalado/reinstalado).
        // Antes de tratar como conta nova — o que sobrescreveria dados reais na hora — tentamos
        // de novo uma vez, dando tempo da sessão/autenticação se estabilizar.
        await new Promise(r=>setTimeout(r,1200));
        const retry=await supabase.from("financial_data").select("data").eq("user_id",session.user.id).single();
        if(retry.error&&retry.error.code!=="PGRST116")throw retry.error;
        row=retry.data;
      }
      const parsed=row?ensurePaymentsShape(row.data):ensurePaymentsShape(INITIAL_DATA);
      lastSavedSnapshot.current=JSON.stringify(parsed);
      setData(parsed);latestDataRef.current=parsed;setSaveStatus("saved");
      // Primeiro acesso: aplicar trial, mostrar boas-vindas e tela jornada
      if(!parsed?.profile?.onboarded){
        logFunnelEvent("cadastro_concluido");
        const hoje=new Date();
        const expira=new Date(hoje);expira.setDate(hoje.getDate()+30);
        const mesAtual=hoje.getMonth()+1;
        const anoAtual=hoje.getFullYear();
        const trialAtivo=anoAtual===2026&&mesAtual<=12;
        const trialFields=trialAtivo?{plano:"premium",trial_inicio:hoje.toISOString().slice(0,10),trial_expira:expira.toISOString().slice(0,10)}:{};
        const perfilAtualizado={...parsed.profile,...trialFields,onboarded:true};
        const novoData={...parsed,profile:perfilAtualizado};
        setData(novoData);latestDataRef.current=novoData;lastSavedSnapshot.current=JSON.stringify(novoData);
        // Salvar imediatamente
        supabase.from("financial_data").upsert({user_id:session.user.id,data:novoData},{onConflict:"user_id"}).then(()=>{});
        // Mostrar modal de boas-vindas (celebração — o bônus Premium é revelado depois, no 1º registro)
        // "nome" vem do cadastro por e-mail; "name"/"full_name"/"given_name" vêm do Google
        const metaUser=session.user.user_metadata||{};
        const nomeDoUsuario=perfilAtualizado?.nome||metaUser.nome||metaUser.name||metaUser.full_name||metaUser.given_name||"";
        setBoasVindasNome(nomeDoUsuario);
        setShowBoasVindas(true);
        // Após fechar o modal, mostrar tela da jornada
        // (o onClose do BoasVindasModal vai chamar setShowJornadaScreen(true))
      }
      setLoading(false);firstLoadDone.current=true;
    }catch(err){
      // Falha ao buscar os dados (ex: sem internet logo após atualizar o app).
      // NUNCA marcar firstLoadDone aqui — isso liberaria o salvamento automático
      // com os dados ainda vazios do estado inicial, sobrescrevendo os dados reais
      // já salvos na nuvem. Em vez disso, mostramos uma tela de erro com "tentar
      // novamente" e mantemos o autosave travado até os dados reais chegarem.
      console.error(err);
      setLoading(false);setErroCarregamento(true);
    }
  }

  // Revela o bônus de 30 dias Premium só depois do 1º registro real (despesa ou
  // entrada) — não mais junto com o cadastro. Dispara uma única vez por conta,
  // usando profile.premiumBonusMostrado como trava.
  useEffect(()=>{
    if(!firstLoadDone.current||!session)return;
    const totalRegistros=(data?.bills?.length||0)+(data?.entries?.length||0);
    if(totalRegistros>0 && !data?.profile?.premiumBonusMostrado){
      logFunnelEvent("primeiro_registro");
      setShowPremiumBonus(true);
      setData(prev=>({...prev,profile:{...prev.profile,premiumBonusMostrado:true}}));
    }
  },[data?.bills?.length,data?.entries?.length,session,data?.profile?.premiumBonusMostrado]);

  async function handleLogout(){
    if(!window.confirm("Deseja sair?"))return;
    await supabase.auth.signOut();setData(INITIAL_DATA);firstLoadDone.current=false;
  }

  async function handleDeleteAccount(){
    try{
      const userId = session.user.id;
      // Chama o Railway para apagar dados + usuário auth com permissão de admin
      const resp = await fetch("https://authentic-trust-production-0edc.up.railway.app/excluir-conta", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId })
      });
      const result = await resp.json();
      if (!result.ok) throw new Error(result.erro || "Erro desconhecido");
      await supabase.auth.signOut();
      setData(INITIAL_DATA);
      firstLoadDone.current=false;
    }catch(err){
      console.error("Erro ao excluir conta:", err);
      alert("Ocorreu um erro ao excluir sua conta. Tente novamente ou entre em contato com o suporte.");
    }
  }

  async function exportBackup(){
    const nomeArquivo=`backup-financas5g-${new Date().toISOString().slice(0,10)}.json`;
    const conteudo=JSON.stringify(latestDataRef.current,null,2);
    const agora=new Date().toISOString();
    // Android nativo — acessa via window.Capacitor.Plugins (sem import, não quebra o build web)
    if(typeof window!=="undefined"&&window.Capacitor?.isNativePlatform()){
      const plugins=window.Capacitor.Plugins;
      if(plugins?.Filesystem){
        try{
          await plugins.Filesystem.writeFile({
            path:nomeArquivo,
            data:btoa(unescape(encodeURIComponent(conteudo))),
            directory:"DOCUMENTS",
            recursive:true,
          });
          localStorage.setItem("ultimoBackup",agora);
          setUltimoBackup(agora);
          window.alert(`✅ Backup salvo!\n\nArquivo: ${nomeArquivo}\nLocal: Documentos do dispositivo`);
          return;
        }catch(e){console.warn("Filesystem falhou:",e);}
      }
      if(plugins?.Share){
        try{
          await plugins.Share.share({
            title:"Backup Mapa Financeiro 5G",
            text:conteudo,
            dialogTitle:"Salvar ou compartilhar backup",
          });
          localStorage.setItem("ultimoBackup",agora);
          setUltimoBackup(agora);
          return;
        }catch(e){console.warn("Share falhou:",e);}
      }
    }
    // Web / desktop — funciona normalmente com link
    const blob=new Blob([conteudo],{type:"application/json"});
    const url=URL.createObjectURL(blob);const a=document.createElement("a");
    a.href=url;a.download=nomeArquivo;
    document.body.appendChild(a);a.click();a.remove();URL.revokeObjectURL(url);
    localStorage.setItem("ultimoBackup",agora);
    setUltimoBackup(agora);
  }
    function importBackup(){
    const input=document.createElement("input");input.type="file";input.accept="application/json";
    input.onchange=async(ev)=>{
      const file=ev.target.files?.[0];if(!file)return;
      try{const text=await file.text();const norm=ensurePaymentsShape(JSON.parse(text));
        if(!window.confirm("Importar vai substituir os dados atuais. Continuar?"))return;
        setData(norm);latestDataRef.current=norm;
      }catch{window.alert("Arquivo inválido.");}
    };input.click();
  }

  // Correção automática de dados (roda sozinha, só altera algo se encontrar o problema):
  // 1) Remove entradas "Rendimento: ..." criadas pelo botão + Rendimento — rendimento
  //    é valorização do investimento, não dinheiro que entrou no caixa.
  // 2) Vendas e resgates que foram salvos como "Dividendos" passam para "Resgate de Investimentos".
  // 3) Aplica as mesmas correções nos meses já fechados (snapshot congelado).
  React.useEffect(()=>{
    const ehRendimentoIndevido=e=>String(e?.name||"").startsWith("Rendimento: ")&&e?.category==="Dividendos";
    const ehVendaOuResgate=e=>/^(Venda|Resgate): /.test(String(e?.name||""))&&e?.category==="Dividendos";
    const entries=data.entries||[];
    const fechamentos=data.profile?.fechamentos||{};
    const precisaEntries=entries.some(e=>ehRendimentoIndevido(e)||ehVendaOuResgate(e));
    const precisaFech=Object.values(fechamentos).some(f=>(f?.entradas||[]).some(e=>ehRendimentoIndevido(e)||ehVendaOuResgate(e)));
    if(!precisaEntries&&!precisaFech) return;
    setData(prev=>{
      const novasEntries=(prev.entries||[])
        .filter(e=>!ehRendimentoIndevido(e))
        .map(e=>ehVendaOuResgate(e)?{...e,category:"Resgate de Investimentos"}:e);
      const fechPrev=prev.profile?.fechamentos||{};
      const novosFech={};
      for(const [k,f] of Object.entries(fechPrev)){
        if(!f||!Array.isArray(f.entradas)){novosFech[k]=f;continue;}
        const removidas=f.entradas.filter(ehRendimentoIndevido);
        const valorRemovido=round2(removidas.reduce((s,e)=>s+Number(e.amount||0),0));
        const entradas=f.entradas
          .filter(e=>!ehRendimentoIndevido(e))
          .map(e=>ehVendaOuResgate(e)?{...e,category:"Resgate de Investimentos"}:e);
        const metrics=f.metrics&&valorRemovido>0
          ?{...f.metrics,entradas:round2((f.metrics.entradas||0)-valorRemovido),saldoMes:round2((f.metrics.saldoMes||0)-valorRemovido)}
          :f.metrics;
        novosFech[k]={...f,entradas,metrics};
      }
      return {...prev,entries:novasEntries,profile:{...prev.profile,fechamentos:novosFech}};
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[data.entries, data.profile?.fechamentos]);

  // Gerar (e manter atualizada) a fatura de cada cartão
  // Regra: a fatura do mês X = soma das compras no cartão que aparecem no mês X
  // (mesma lista que a aba Cartões mostra). Ela vence no mês seguinte.
  // Enquanto NÃO foi paga nem editada à mão, a fatura é recalculada sozinha
  // sempre que uma compra é adicionada, editada ou excluída.
  React.useEffect(()=>{
    const cartoes=data.profile?.cartoes||[];
    const bills=data.bills||[];
    const hoje = new Date();
    const diaHoje = hoje.getDate();
    const mesHoje = hoje.getMonth();
    const anoHoje = hoje.getFullYear();
    const adicionar=[];
    const atualizar={}; // id -> campos a alterar

    // Correção de dados antigos: faturas editadas perdiam a marca isFaturaGerada.
    // Recupera a marca e trata o valor como editado à mão (não recalcula por cima).
    bills.forEach(b=>{
      if(String(b.id).startsWith("fatura-auto-")&&!b.isFaturaGerada){
        atualizar[b.id]={isFaturaGerada:true,valorManual:true};
      }
    });

    const mesesVerificar = [
      mesHoje===0?{ano:anoHoje-1,mes:11}:{ano:anoHoje,mes:mesHoje-1},
      {ano:anoHoje,mes:mesHoje},
    ];

    cartoes.forEach(cartao=>{
      if(!cartao.fechamento) return;
      mesesVerificar.forEach(({ano:anoFatura,mes:mesFatura})=>{
        const ehMesAtual = anoFatura===anoHoje && mesFatura===mesHoje;
        if(ehMesAtual && diaHoje < cartao.fechamento) return;

        const mesKeyFatura = monthKey(anoFatura,mesFatura);
        const faturaId = `fatura-auto-${cartao.id}-${mesKeyFatura}`;
        const totalFatura = calcularTotalFaturaCartao(bills,cartao.id,anoFatura,mesFatura);
        const existente = bills.find(b=>b.id===faturaId);

        if(existente){
          const temPagamento=Object.values(existente.payments||{}).some(r=>(r.entries||[]).length>0);
          if(temPagamento||existente.valorManual||atualizar[faturaId]?.valorManual) return;
          if(totalFatura>0&&round2(existente.amount)!==totalFatura){
            atualizar[faturaId]={...(atualizar[faturaId]||{}),amount:totalFatura};
          }
          return;
        }
        if(totalFatura <= 0) return;

        // A fatura aparece no mês do VENCIMENTO (mês seguinte às compras)
        const mesVenc = mesFatura===11?0:mesFatura+1;
        const anoVenc = mesFatura===11?anoFatura+1:anoFatura;
        adicionar.push({
          id: faturaId,
          name: `Fatura ${cartao.nome}`,
          amount: totalFatura,
          dueDay: cartao.vencimento,
          installments: 1,
          frequency: "parcelado",
          startMonth: mesVenc,
          startYear: anoVenc,
          paymentMethod: "Débito/Pix",
          category: "Fatura Cartão",
          notes: `Fatura gerada automaticamente com as compras de ${MONTHS_PT[mesFatura]} — conferir valores na fatura do banco antes de pagar. Cartão: ${cartao.nome} · Fechamento: dia ${cartao.fechamento}`,
          cartaoId: "",
          isFaturaGerada: true,
          cartaoNome: cartao.nome,
          cartaoCor: cartao.cor,
          payments: {}
        });
      });
    });

    if(adicionar.length===0&&Object.keys(atualizar).length===0) return;
    setData(prev=>{
      const ids=new Set(prev.bills.map(b=>b.id));
      return {
        ...prev,
        bills:[
          ...prev.bills.map(b=>atualizar[b.id]?{...b,...atualizar[b.id]}:b),
          ...adicionar.filter(f=>!ids.has(f.id)),
        ],
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[data.profile?.cartoes, data.bills, currentMonth, currentYear]);

  const generatedOccurrences=useMemo(()=>{
    const base=[],overdue=[],atrasadosPagos=[];
    for(const bill of data.bills){
      if(shouldGenerateForMonth(bill,currentYear,currentMonth)){
        base.push({occurrenceId:getOccurrenceId(bill.id,currentKey),billId:bill.id,bill,refMonthKey:currentKey,refYear:currentYear,refMonth:currentMonth,dueDay:bill.dueDay,isOverdueSection:false});
      }
      const startKey=monthKey(bill.startYear,bill.startMonth);let rk=startKey;
      while(isBeforeMonth(rk,currentKey)){
        const p=parseMonthKey(rk);
        if(shouldGenerateForMonth(bill,p.year,p.month)&&getRecordStatus(bill,rk)!=="paid"){overdue.push({occurrenceId:getOccurrenceId(bill.id,rk),billId:bill.id,bill,refMonthKey:rk,refYear:p.year,refMonth:p.month,dueDay:bill.dueDay,isOverdueSection:true});}
        const nx=addMonth(p.year,p.month,1);rk=monthKey(nx.year,nx.month);
      }
      // Conta de mês anterior paga DENTRO deste mês (data real do pagamento) —
      // entra na relação de "Pagas" do mês em que o dinheiro realmente saiu.
      for(const[refKey,record]of Object.entries(bill.payments||{})){
        if(refKey===currentKey)continue;
        const entries=record.entries||[];
        if(!entries.some(e=>isCreatedInMonth(e,currentYear,currentMonth)))continue;
        const p=parseMonthKey(refKey);
        atrasadosPagos.push({occurrenceId:getOccurrenceId(bill.id,refKey)+"::atrasado::"+currentKey,billId:bill.id,bill,refMonthKey:refKey,refYear:p.year,refMonth:p.month,dueDay:bill.dueDay,isOverdueSection:false,isAtrasadoPago:true});
      }
    }
    overdue.sort((a,b)=>compareMonthKey(a.refMonthKey,b.refMonthKey)||sortBills(a.bill,b.bill));
    base.sort((a,b)=>{const aS=getOccurrenceVisualState(a,currentKey,isViewingCurrentMonth,today.day),bS=getOccurrenceVisualState(b,currentKey,isViewingCurrentMonth,today.day);if(aS.isLate!==bS.isLate)return aS.isLate?-1:1;return sortBills(a.bill,b.bill);});
    return{base,overdue,atrasadosPagos};
  },[data.bills,currentYear,currentMonth,currentKey,isViewingCurrentMonth,today.day]);

  const monthEntries=useMemo(()=>data.entries.filter((e)=>e.month===currentMonth&&e.year===currentYear),[data.entries,currentMonth,currentYear]);

  const monthMetrics=useMemo(()=>{
    let previsto=0,parcial=0,pendente=0,paidCount=0,totalCount=0;
    for(const occ of generatedOccurrences.base){
      const bill=occ.bill;const status=getRecordStatus(bill,occ.refMonthKey);const paidRef=getAmountPaidForMonthReference(bill,occ.refMonthKey);
      const valorMes=getBillAmountForMonth(bill,occ.refMonthKey);
      previsto+=Number(valorMes||0);totalCount+=1;
      if(status==="paid")paidCount+=1;
      if(status==="partial"){parcial+=paidRef;pendente+=Math.max(0,valorMes-paidRef);}
      else if(status==="pending"){pendente+=valorMes;}
    }
    // Caixa real: agrupa por DATA REAL do pagamento (createdAt), não pela aba em que foi pago.
    // "Pago" = contas deste mês pagas dentro do próprio mês.
    // "Atrasados" = contas de meses anteriores pagas agora, dentro deste mês — dinheiro que
    // realmente saiu do caixa este mês, mesmo pertencendo a uma competência passada.
    let pagoDesteMes=0,atrasados=0,aportesDesteMes=0;const atrasadosDetalhe=[];
    for(const bill of data.bills){
      for(const[refKey,record]of Object.entries(bill.payments||{})){
        for(const entry of(record.entries||[])){
          if(!isCreatedInMonth(entry,currentYear,currentMonth))continue;
          const ehCartao=bill.paymentMethod==="Cartão de Crédito"&&!bill.isFaturaGerada;
          const trocouForma=entry.paymentMethod&&entry.paymentMethod!=="Cartão de Crédito";
          if(!(bill.isFaturaGerada||!ehCartao||trocouForma))continue;
          const valor=Number(entry.amount||0);
          if(refKey===currentKey){
            pagoDesteMes+=valor;
            if(bill.category==="Investimentos"||bill.category==="Reserva de Emergência")aportesDesteMes+=valor;
          }else{
            atrasados+=valor;
            atrasadosDetalhe.push({nome:bill.name,categoria:bill.category,valor:round2(valor),mesOrigem:refKey,data:entry.createdAt});
          }
        }
      }
    }
    const pago=round2(pagoDesteMes+atrasados);
    const entradas=round2(monthEntries.reduce((sum,e)=>sum+Number(e.amount||0),0));
    return{previsto:round2(previsto),pago,pagoDesteMes:round2(pagoDesteMes),atrasados:round2(atrasados),atrasadosDetalhe,aportesDesteMes:round2(aportesDesteMes),parcial:round2(parcial),pendente:round2(pendente),entradas,saldoMes:round2(entradas-pago),progress:totalCount?round2((paidCount/totalCount)*100):0,paidCount,totalCount};
  },[generatedOccurrences,currentKey,currentYear,currentMonth,monthEntries,data.bills]);

  const saldoAcumulado=useMemo(()=>{
    // Determinar o ano inicial com base nos dados reais (não começa do ano 0)
    const anosEntradas=data.entries.map(e=>e.year).filter(Boolean);
    const anosBills=data.bills.flatMap(b=>Object.values(b.payments||{}).flatMap(r=>(r.entries||[]).map(e=>e.paidMonthKey?parseInt(e.paidMonthKey.split("-")[0]):0)));
    const anoMinDados=Math.min(...[...anosEntradas,...anosBills.filter(a=>a>0)],currentYear);
    const anoInicio=Math.max(2020,isFinite(anoMinDados)?anoMinDados:currentYear);
    const iInicio=anoInicio*12;
    const cIdx=currentYear*12+currentMonth;
    let total=0;
    for(let i=iInicio;i<=cIdx;i++){
      const year=Math.floor(i/12),month=i%12,key=monthKey(year,month);
      const ent=data.entries.filter((e)=>e.year===year&&e.month===month).reduce((s,e)=>s+Number(e.amount||0),0);
      // Usar a mesma lógica do monthMetrics — só contar o que saiu do caixa (excluir cartão automático)
      let pago=0;
      for(const bill of data.bills){
        for(const r of Object.values(bill.payments||{})){
          for(const e of(r.entries||[])){
            if(e.paidMonthKey!==key) continue;
            const ehCartao=bill.paymentMethod==="Cartão de Crédito"&&!bill.isFaturaGerada;
            const trocouForma=e.paymentMethod&&e.paymentMethod!=="Cartão de Crédito";
            if(bill.isFaturaGerada||!ehCartao||trocouForma) pago+=Number(e.amount||0);
          }
        }
      }
      total+=ent-pago;
    }
    return round2(total);
  },[data.bills,data.entries,currentMonth,currentYear]);

  const chartData=useMemo(()=>{
    // Usar generatedOccurrences.base: todas as despesas do mês (parcelas cartão, débito, etc.)
    // excluindo Fatura Cartão (isFaturaGerada) — mostra ONDE o dinheiro foi gasto, não COMO foi pago
    const catTotals={};
    for(const occ of generatedOccurrences.base){
      const bill=occ.bill;
      if(bill.isFaturaGerada) continue; // ignorar fatura gerada (veículo de pagamento, não gasto)
      if(bill.category==="Fatura Cartão") continue; // ignorar faturas manuais também
      const status=getRecordStatus(bill,occ.refMonthKey);
      if(status!=="paid"&&status!=="partial") continue; // só contar o que foi pago/parcial
      const pago=getGastoCategoriaMes(bill,occ.refMonthKey);
      if(pago<=0) continue;
      const cat=bill.category||"Outros";
      catTotals[cat]=(catTotals[cat]||0)+pago;
    }
    const byCategory=EXPENSE_CATEGORIES.filter(l=>l!=="Fatura Cartão"&&(catTotals[l]||0)>0).map(l=>({label:l,value:round2(catTotals[l]||0)}));
    for(const [cat,val] of Object.entries(catTotals)){
      if(!EXPENSE_CATEGORIES.includes(cat)&&val>0) byCategory.push({label:cat,value:round2(val)});
    }
    byCategory.sort((a,b)=>b.value-a.value);
    const totalPaid=round2(byCategory.reduce((s,i)=>s+i.value,0));
    return{byCategory,totalPaid};
  },[generatedOccurrences]);

  const spentByCategory=useMemo(()=>{
    const result={};
    for(const bill of data.bills){
      if(!shouldGenerateForMonth(bill,currentYear,currentMonth))continue;
      // Usar valor previsto (pago + pendente) para refletir o orçamento comprometido
      const previsto=getBillAmountForMonth(bill,currentKey);
      result[bill.category]=(result[bill.category]||0)+previsto;
    }
    return result;
  },[data.bills,currentYear,currentMonth,currentKey]);

  const budgetsWithSpent=useMemo(()=>data.budgets.map((b)=>({...b,_gasto:spentByCategory[b.category]||0})),[data.budgets,spentByCategory]);



  const evolutionData=useMemo(()=>{
    const ml=[];for(let o=5;o>=0;o--){const ref=addMonth(currentYear,currentMonth,-o);ml.push({key:monthKey(ref.year,ref.month),year:ref.year,month:ref.month,label:MONTHS_PT[ref.month].slice(0,3)});}
    // Mesma lógica do chartData ("Total pago por categoria"): soma o gasto real da categoria
    // no mês (débito, Pix ou cartão), ignorando a fatura gerada (veículo de pagamento, não gasto).
    return EXPENSE_CATEGORIES.filter((cat)=>cat!=="Fatura Cartão").map((cat)=>{
      const points=ml.map((mi)=>{
        let v=0;
        for(const bill of data.bills){
          if(bill.category!==cat)continue;
          if(bill.isFaturaGerada)continue;
          if(!shouldGenerateForMonth(bill,mi.year,mi.month))continue;
          const status=getRecordStatus(bill,mi.key);
          if(status!=="paid"&&status!=="partial")continue;
          v+=getGastoCategoriaMes(bill,mi.key);
        }
        return{label:mi.label,value:round2(v)};
      });
      return{category:cat,points};
    });
  },[data.bills,currentMonth,currentYear]);

  // Média mensal por categoria: usa os meses FECHADOS (todos menos o mês selecionado, que está incompleto)
  // e só conta a partir do primeiro mês com alguma despesa, para meses vazios antes do uso do app não puxarem a média para baixo.
  const mediaCategorias=useMemo(()=>{
    const total=evolutionData[0]?.points.length||0;
    const fechados=total-1;
    if(fechados<1)return{meses:0,itens:[]};
    const totalMes=(i)=>evolutionData.reduce((s,c)=>s+c.points[i].value,0);
    let inicio=-1;
    for(let i=0;i<fechados;i++){if(totalMes(i)>0){inicio=i;break;}}
    if(inicio<0)return{meses:0,itens:[]};
    const n=fechados-inicio;
    const itens=evolutionData.map((c)=>{
      const soma=c.points.slice(inicio,fechados).reduce((s,pt)=>s+pt.value,0);
      return{category:c.category,media:round2(soma/n),atual:c.points[fechados].value};
    }).filter((i)=>i.media>0||i.atual>0).sort((x,y)=>y.media-x.media);
    return{meses:n,itens};
  },[evolutionData]);

  const futureData=useMemo(()=>{
    const pts=[];
    // Patrimônio atual total (ativos + renda fixa + cofrinhos)
    const investimentos=data.profile?.investimentos||[];
    const rendaFixa=data.profile?.rendaFixa||[];
    const desafios=data.profile?.desafios||[];
    const totalAtivos=investimentos.reduce((s,i)=>s+Number(i.quantidade||0)*Number(i.precoMedio||0),0);
    const totalRF=rendaFixa.reduce((s,r)=>s+Number(r.valorInvestido||0)+Number(r.rendimentoAcumulado||0),0);
    const totalCofrinhos=desafios.filter(d=>d.ativo).reduce((s,d)=>s+Number(d.economiaTotal||0),0);
    const patrimonioAtual=round2(totalAtivos+totalRF+totalCofrinhos);
    for(let o=5;o>=0;o--){
      const ref=addMonth(currentYear,currentMonth,-o);const key=monthKey(ref.year,ref.month);
      // Aportes registrados como despesas de Investimentos/Reserva pagos neste mês
      let inv=0;for(const bill of data.bills){if(!isFutureCategoryExpense(bill.category))continue;for(const r of Object.values(bill.payments||{})){for(const e of(r.entries||[])){if(e.paidMonthKey===key)inv+=Number(e.amount||0);}}}
      // Rendimentos/Dividendos registrados como entradas neste mês
      let ret=0;for(const e of data.entries){if(!isFutureCategoryEntry(e.category))continue;if(e.year===ref.year&&e.month===ref.month)ret+=Number(e.amount||0);}
      // No mês atual, usar o patrimônio real total; nos anteriores, usar aportes acumulados como proxy
      const valor=o===0?patrimonioAtual:round2(inv+ret);
      pts.push({label:MONTHS_PT[ref.month].slice(0,3),value:valor});
    }
    return pts;
  },[data.bills,data.entries,data.profile,currentMonth,currentYear]);

  const editingBill=editingBillId?data.bills.find((b)=>b.id===editingBillId):null;
  const editingEntry=editingEntryId?data.entries.find((e)=>e.id===editingEntryId):null;
  const paymentContext=useMemo(()=>{
    const bill=data.bills.find((b)=>b.id===paymentModal.billId);if(!bill)return null;
    const record=getBillMonthRecord(bill,paymentModal.refMonthKey);
    const totalPaid=getEntriesTotal(record.entries);
    const valorMes=getBillAmountForMonth(bill,paymentModal.refMonthKey);
    const remaining=round2(Math.max(0,valorMes-totalPaid));
    return{bill,record,totalPaid,remaining,valorMes,diff:round2(round2(numberFromInput(totalPayAmount))-remaining)};
  },[data.bills,paymentModal.billId,paymentModal.refMonthKey,totalPayAmount]);

  function changeMonth(offset){const n=addMonth(currentYear,currentMonth,offset);setCurrentYear(n.year);setCurrentMonth(n.month);}
  function closeExpenseForm(){setEditingBillId(null);setExpenseFormOpen(false);}
  function openNewExpenseForm(){setEditingBillId(null);setExpenseFormOpen(true);}

  async function interpretarVoz(texto){
    if(!texto.trim()) return;
    setVoiceLoading(true);
    setVoiceErro("");
    try{
      const hoje = new Date();
      const mesAtualKey = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,"0")}`;

      // Passar despesas dos últimos 2 meses (atual + mês anterior para atrasadas)
      const mesAnterior=addMonth(hoje.getFullYear(),hoje.getMonth(),-1);
      const mesAnteriorKey=`${mesAnterior.year}-${String(mesAnterior.month+1).padStart(2,"0")}`;
      const despesasCadastradas = data.bills
        .filter(b=>!b.isFaturaGerada)
        .filter(b=>shouldGenerateForMonth(b,hoje.getFullYear(),hoje.getMonth())||shouldGenerateForMonth(b,mesAnterior.year,mesAnterior.month))
        .map(b=>{
          const noMesAtual=shouldGenerateForMonth(b,hoje.getFullYear(),hoje.getMonth());
          const mesKey=noMesAtual?mesAtualKey:mesAnteriorKey;
          const jaPago=(b.payments?.[mesKey]?.entries||[]).length>0;
          return jaPago?null:{id:b.id,mesKey,nome:b.name,valor:b.amount,mes:noMesAtual?"atual":"anterior"};
        })
        .filter(Boolean);

      const resp = await fetch("https://authentic-trust-production-0edc.up.railway.app/interpretar-voz",{
        method:"POST",
        headers:{
          "Content-Type":"application/json",
          "Authorization":`Bearer ${session.access_token}`
        },
        body:JSON.stringify({texto,despesasCadastradas,hoje:hoje.toISOString().slice(0,10)})
      });
      if(resp.status===429){
        const j=await resp.json();
        setVoiceErro(`🔒 ${j.error||"Limite de uso atingido."}`);
        setVoiceLoading(false);
        return;
      }
      if(resp.status===401){
        setVoiceErro("Sua sessão expirou. Saia e entre novamente para continuar usando o assistente.");
        setVoiceLoading(false);
        return;
      }
      if(!resp.ok){
        const j=await resp.json().catch(()=>({}));
        throw new Error(j.error||"Erro ao interpretar");
      }
      const{txt}=await resp.json();
      const json=JSON.parse(txt.replace(/```json|```/g,"").trim());
      if(json.tipo==="multiplas"){
        setVoiceMultiplas(json);
        setVoiceResultado(null);
      } else {
        setVoiceResultado(json);
        setVoiceMultiplas(null);
      }
    }catch(e){
      console.error("Erro voz:",e);
      setVoiceErro("Não entendi. Tente falar de forma mais clara. Ex: 'paguei 750 de aluguel'");
    }finally{
      setVoiceLoading(false);
    }
  }


  function confirmarVoz(){
    if(!voiceResultado) return;
    const agora = new Date();
    const mesKey = `${agora.getFullYear()}-${String(agora.getMonth()+1).padStart(2,"0")}`;

    // Verificar limite do plano antes de criar nova despesa ou entrada
    if(voiceResultado.tipo==="despesa"){
      const limGratis=15,limBasico=50; // alinhado com verificarLimiteDespesas() do cadastro manual
      const total=data.bills.length;
      if(!isPremium&&!isBasico&&total>=limGratis){
        setVoiceErro(`🔒 Limite do plano Grátis atingido (${limGratis} despesas). Acesse Planos para fazer upgrade.`);
        return;
      }
      if(isBasico&&total>=limBasico){
        setVoiceErro(`🔒 Limite do plano Básico atingido (${limBasico} despesas). Faça upgrade para o Premium.`);
        return;
      }
    }

    // Registrar pagamento de despesa existente
    if(voiceResultado.tipo==="pagamento"){
      const valorPago = voiceResultado.valor||0;
      // Usar o mesKey específico da despesa (atual ou anterior/atrasada)
      const chaveDoMes = voiceResultado.mesKey||mesKey;
      setData(prev=>{
        const bills = prev.bills.map(b=>{
          if(b.id!==voiceResultado.id) return b;
          const entry={id:uid(),amount:valorPago||b.amount,note:"Pago por voz 🎙️",paidMonthKey:chaveDoMes,createdAt:agora.toISOString(),paymentMethod:voiceResultado.pagamento||"Débito/Pix"};
          const existing=b.payments?.[chaveDoMes]?.entries||[];
          return{...b,payments:{...b.payments,[chaveDoMes]:{...(b.payments?.[chaveDoMes]||{}),entries:[...existing,entry]}}};
        });
        return{...prev,bills};
      });
      setVoiceOpen(false);setVoiceTexto("");setVoiceResultado(null);
      return;
    }

    if(voiceResultado.tipo==="despesa"){
      const nova={
        id:uid(),
        name:voiceResultado.nome,
        amount:voiceResultado.valor,
        dueDay:voiceResultado.diaVencimento||agora.getDate(),
        installments:voiceResultado.parcelas>1?voiceResultado.parcelas:1,
        frequency:voiceResultado.parcelas>1?"parcelado":"parcelado",
        startMonth:agora.getMonth(),
        startYear:agora.getFullYear(),
        paymentMethod:voiceResultado.pagamento||"Débito/Pix",
        category:voiceResultado.categoria||"Outros",
        notes:"Registrado por voz 🎙️",
        cartaoId:"",
        // Só registrar pagamento automático se não for cartão de crédito
        payments:(voiceResultado.pagamento||"Débito/Pix")!=="Cartão de Crédito"?{[mesKey]:{entries:[{id:uid(),amount:voiceResultado.valor,note:"Registrado por voz 🎙️",paidMonthKey:mesKey,createdAt:agora.toISOString(),paymentMethod:voiceResultado.pagamento||"Débito/Pix"}]}}:{}
      };
      setData(prev=>{
        const novas=[...prev.bills,nova];
        return{...prev,bills:novas};
      });
    } else if(voiceResultado.tipo==="entrada"){
      const nova={
        id:uid(),
        name:voiceResultado.nome,
        amount:voiceResultado.valor,
        year:agora.getFullYear(),
        month:agora.getMonth(),
        category:voiceResultado.categoria||"Outros",
        paymentMethod:voiceResultado.pagamento||"Débito/Pix",
        notes:"Registrado por voz 🎙️",
        createdAt:agora.toISOString()
      };
      setData(prev=>{
        const novas=[...prev.entries,nova];
        return{...prev,entries:novas};
      });
    }
    // Fechar modal após pequeno delay para garantir que setData foi processado
    setTimeout(()=>{
      setVoiceOpen(false);
      setVoiceTexto("");
      setVoiceResultado(null);
      setVoiceMultiplas(null);
    },100);
  }

  function iniciarGravacao(){
    // Navegador (site) — usa a API de voz do próprio navegador
    const SR = window.SpeechRecognition||window.webkitSpeechRecognition;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)||
      (navigator.platform==="MacIntel"&&navigator.maxTouchPoints>1);

    if(!SR){
      if(isIOS){
        setVoiceErro("No iPhone/iPad, use o campo de texto abaixo ou ative o ditado do teclado (ícone 🎤 no teclado) e cole o texto.");
      } else {
        setVoiceErro("Seu navegador não suporta reconhecimento de voz. Use o Chrome.");
      }
      return;
    }

    const rec = new SR();
    rec.lang="pt-BR";
    rec.continuous=false;
    rec.interimResults=false;
    setVoiceEscutando(true);
    setVoiceErro("");
    setVoiceResultado(null);

    rec.onresult=(e)=>{
      const texto=e.results[0][0].transcript;
      setVoiceTexto(texto);
      setVoiceEscutando(false);
      interpretarVoz(texto);
    };
    rec.onerror=(e)=>{
      setVoiceEscutando(false);
      if(e.error==="not-allowed"){
        setVoiceErro("Permissão de microfone negada. Vá em Configurações → Safari → Microfone → Permitir.");
      } else if(e.error==="no-speech"){
        setVoiceErro("Não ouvi nada. Tente falar mais perto do microfone.");
      } else {
        setVoiceErro("Não consegui ouvir. Tente novamente ou use o campo de texto.");
      }
    };
    rec.onend=()=>setVoiceEscutando(false);

    try{
      rec.start();
    }catch(e){
      setVoiceEscutando(false);
      setVoiceErro("Erro ao iniciar microfone. Use o campo de texto abaixo.");
    }
  }

  function salvarGastoRapido(){
    const valor=numberFromInput(gastoRapidoForm.valor);
    if(!gastoRapidoForm.nome.trim()||valor<=0) return;
    const agora=new Date();
    const mesKey=`${agora.getFullYear()}-${String(agora.getMonth()+1).padStart(2,"0")}`;
    const novaDespesa={
      id:uid(),
      name:gastoRapidoForm.nome.trim(),
      amount:valor,
      dueDay:agora.getDate(),
      installments:1,
      frequency:"parcelado",
      startMonth:agora.getMonth(),
      startYear:agora.getFullYear(),
      paymentMethod:gastoRapidoForm.pagamento,
      category:gastoRapidoForm.categoria,
      notes:"Gasto rápido",
      cartaoId:"",
      payments:{[mesKey]:{entries:[{id:uid(),amount:valor,note:"Gasto rápido",paidMonthKey:mesKey,createdAt:agora.toISOString(),paymentMethod:gastoRapidoForm.pagamento}]}}
    };
    setData(prev=>({...prev,bills:[...prev.bills,novaDespesa]}));
    setGastoRapidoOpen(false);
    setGastoRapidoForm({nome:"",valor:"",categoria:"Supermercado",pagamento:"Débito/Pix"});
  }
  function closeEntryForm(){setEditingEntryId(null);setEntryFormOpen(false);}
  function openNewEntryForm(){setEditingEntryId(null);setEntryFormOpen(true);}

  // Verificar plano do usuário
  // DEV: adicione ?plano=basico ou ?plano=premium na URL para testar sem ativar plano real
  const _planoUrl = new URLSearchParams(window.location.search).get("plano");
  const plano = (_planoUrl === "basico" || _planoUrl === "premium") ? _planoUrl : (data.profile?.plano || "gratis");
  const isPremium = plano === "premium";
  const isBasico = plano === "basico";
  const totalDespesas = data.bills?.length || 0;
  const limiteGratis = 15;
  const limiteBasico = 50;
  const pctLimite = !isPremium&&!isBasico ? Math.round((totalDespesas/limiteGratis)*100) : 0;

  function verificarLimiteDespesas() {
    if(isPremium) return true;
    const totalDespesas = data.bills.length;
    if(!isBasico && !isPremium && totalDespesas >= limiteGratis) {
      window.alert(`🎉 Que bom que você está gostando do Mapa Financeiro 5G!\n\n🔒 Você atingiu o limite do plano Grátis (${limiteGratis} despesas).\n\nFaça upgrade para continuar cadastrando e aproveitar todos os recursos!`);
      return false;
    }
    if(isBasico && totalDespesas >= limiteBasico) {
      window.alert(`🎉 Que bom que você está gostando do Mapa Financeiro 5G!\n\n🔒 Você atingiu o limite do plano Básico (${limiteBasico} despesas).\n\nFaça upgrade para o Premium e tenha despesas ilimitadas!`);
      return false;
    }
    return true;
  }

  function saveBill(payload){
    try{
      if(!payload.id && !verificarLimiteDespesas()) return;
      const pagarAgora=!!payload._pagarAgora;
      const oldBill=editingBillId?data.bills.find((b)=>b.id===editingBillId):null;
      const frequency=["mensal","semestral","anual","unico","parcelado"].includes(payload.frequency)?payload.frequency:"mensal";
      // Trava de segurança: início só pode mudar em despesas novas ou "Único".
      // Em edição de Mensal/Semestral/Anual/Parcelado, sempre mantém o início original
      // (mesmo que o payload chegue alterado), pois é o que gate a existência da
      // despesa nos meses anteriores (shouldGenerateForMonth).
      const startMonth=(oldBill&&frequency!=="unico")?oldBill.startMonth:clamp(Number(payload.startMonth||0),0,11);
      const startYear=(oldBill&&frequency!=="unico")?oldBill.startYear:Math.max(2000,Number(payload.startYear||new Date().getFullYear()));
      const n={...(oldBill||{}),id:editingBillId||uid(),name:String(payload.name||"").trim(),amount:round2(Math.max(0,Number(payload.amount||0))),dueDay:clamp(Number(payload.dueDay||1),1,31),installments:Math.max(0,Number(payload.installments||0)),frequency,startMonth,startYear,paymentMethod:PAYMENT_METHODS.includes(payload.paymentMethod)?payload.paymentMethod:PAYMENT_METHODS[0],category:EXPENSE_CATEGORIES.includes(payload.category)?payload.category:"Outros",notes:payload.notes||"",cartaoId:payload.paymentMethod==="Cartão de Crédito"?String(payload.cartaoId||""):"",taxaJuros:payload.taxaJuros&&payload.taxaJuros!=="0"?Math.round((parseFloat(String(payload.taxaJuros).replace(",","."))||0)*1000)/1000:0,payments:oldBill?oldBill.payments||{}:{}};
      // Fatura gerada editada à mão (ex.: valor conferido no app do banco): o app para de recalcular
      if(oldBill?.isFaturaGerada&&round2(oldBill.amount)!==n.amount) n.valorManual=true;
      // Se "Salvar e pagar hoje", registra pagamento automático no mês atual
      if(pagarAgora&&!editingBillId){
        const today=new Date();
        const rk=monthKey(today.getFullYear(),today.getMonth());
        n.payments={[rk]:{entries:[{id:uid(),amount:n.amount,note:"Pago no cadastro",createdAt:new Date().toISOString(),paidMonthKey:rk,paymentMethod:n.paymentMethod}]}};
      }
      // Escopo da alteração de valor em despesa Mensal (perguntado no formulário)
      if(oldBill&&frequency==="mensal"&&oldBill.amount!==n.amount&&payload._escopoValor){
        const refKey=payload._mesReferenciaEdicao||monthKey(new Date().getFullYear(),new Date().getMonth());
        if(payload._escopoValor==="somente_mes"){
          // Não altera o valor padrão da despesa — só grava um ajuste pontual no mês de referência.
          n.amount=oldBill.amount;
          const rec=n.payments[refKey]||{entries:[]};
          n.payments={...n.payments,[refKey]:{...rec,valorAjustado:round2(Number(payload.amount))}};
        }else{
          // "Este mês e os próximos": o novo valor vira o padrão dali pra frente, mas
          // congela o valor antigo (valorAjustado) em todo mês anterior ao de referência
          // que já tenha registro e ainda não tenha um ajuste próprio — histórico intocado.
          const novosPayments={...n.payments};
          for(const key of Object.keys(novosPayments)){
            if(isBeforeMonth(key,refKey)&&novosPayments[key].valorAjustado==null){
              novosPayments[key]={...novosPayments[key],valorAjustado:oldBill.amount};
            }
          }
          n.payments=novosPayments;
        }
      }
      setData((prev)=>{const sb=Array.isArray(prev.bills)?prev.bills:[];const ex=sb.some((b)=>b.id===n.id);return{...prev,bills:ex?sb.map((b)=>(b.id===n.id?n:b)):[...sb,n]};});
      closeExpenseForm();
    }catch(err){console.error(err);window.alert("Não foi possível salvar.");}
  }
  function saveEntry(payload){
    const n={id:editingEntryId||uid(),name:String(payload.name||"").trim(),amount:round2(Math.max(0,Number(payload.amount||0))),day:payload.day&&Number(payload.day)>0?clamp(Number(payload.day),1,31):undefined,month:clamp(Number(payload.month||0),0,11),year:Math.max(2000,Number(payload.year||new Date().getFullYear())),paymentMethod:PAYMENT_METHODS.includes(payload.paymentMethod)?payload.paymentMethod:PAYMENT_METHODS[0],category:ENTRY_CATEGORIES.includes(payload.category)?payload.category:"Outros",notes:payload.notes||"",createdAt:editingEntryId?data.entries.find((e)=>e.id===editingEntryId)?.createdAt||new Date().toISOString():new Date().toISOString()};
    setData((prev)=>{const ex=prev.entries.some((e)=>e.id===n.id);return{...prev,entries:ex?prev.entries.map((e)=>(e.id===n.id?n:e)):[...prev.entries,n]};});
    closeEntryForm();
  }
  function saveResgate(){
    const valor=numberFromInput(resgateValor);
    if(valor<=0)return;
    const now=new Date();
    const novaEntrada={
      id:uid(),
      name:resgateDesc.trim()||"Resgate de investimento",
      amount:valor,
      category:"Resgate de Investimentos",
      paymentMethod:"Débito/Pix",
      month:now.getMonth(),
      year:now.getFullYear(),
      notes:"Resgate registrado pelo Guardar 5G",
      createdAt:now.toISOString(),
    };
    // Desconta dos aportes acumulados (bills de Investimentos/Reserva), do mais recente para o mais antigo
    let restante=valor;
    const billsAtualizados=data.bills.map(b=>{
      if(restante<=0)return b;
      if(b.category!=="Investimentos"&&b.category!=="Reserva de Emergência")return b;
      const totalPago=Object.values(b.payments||{}).reduce((ps,rec)=>ps+(rec.entries||[]).reduce((es,e)=>es+Number(e.amount||0),0),0);
      const valorBill=totalPago>0?totalPago:Number(b.amount||0);
      if(valorBill<=0)return b;
      const desconto=Math.min(restante,valorBill);
      restante=round2(restante-desconto);
      // Registra o resgate como pagamento negativo usando campo resgates
      const resgates=[...(b.resgates||[]),{id:uid(),valor:desconto,data:now.toISOString(),desc:resgateDesc.trim()||"Resgate"}];
      return {...b,resgates};
    });
    setData(prev=>({
      ...prev,
      bills:billsAtualizados,
      entries:[...prev.entries,novaEntrada],
    }));
    const origemFinal=resgateOrigem||"";
    setResgateModal(false);
    setResgateValor("");
    setResgateDesc("");
    setResgateOrigem("");
    // Melhoria 3: mostrar confirmação pós-resgate com checklist
    const guiasPost={
      "Ativos (cripto/ações/FIIs)":{aba:"invest",label:"Investimentos",instrucao:"Atualize a quantidade do ativo vendido"},
      "Meta (poupança específica)":{aba:"metas",label:"Metas",instrucao:"Confirme o saldo atualizado da meta"},
      "Reserva de Emergência":{aba:"contas",label:"Despesas",instrucao:"Ajuste o lançamento de Reserva de Emergência"},
      "Desafio financeiro":{aba:"desafios",label:"Desafios",instrucao:"Considere encerrar o desafio se não for continuar"},
      "Investimento/Aporte (lançamento manual)":{aba:"contas",label:"Despesas",instrucao:"Ajuste o lançamento de Investimentos"},
    };
    if(origemFinal&&guiasPost[origemFinal]){
      setResgateConfirmacao({valor,desc:resgateDesc,origem:origemFinal,...guiasPost[origemFinal]});
    }
  }

  // Executa resgate direto (usado por Metas e Ativos que já fizeram a dedução na fonte)
  function registrarSaidaInvestimento(valor, descricao){
    if(valor<=0) return;
    const now=new Date();
    const rk=monthKey(now.getFullYear(),now.getMonth());
    const novaDesc={
      id:uid(),
      name:descricao||"Investimento",
      amount:round2(valor),
      dueDay:now.getDate(),
      installments:0,
      frequency:"unico",
      startMonth:now.getMonth(),
      startYear:now.getFullYear(),
      paymentMethod:"Débito/Pix",
      category:"Investimentos",
      notes:"Registrado automaticamente ao cadastrar ativo",
      cartaoId:"",
      taxaJuros:0,
      payments:{[rk]:{entries:[{id:uid(),amount:round2(valor),note:"Aporte registrado automaticamente",createdAt:now.toISOString(),paidMonthKey:rk,paymentMethod:"Débito/Pix"}]}},
    };
    setData(prev=>({...prev,bills:[...prev.bills,novaDesc]}));
  }

  function registrarDividendo(valor, descricao, categoria="Dividendos"){
    if(valor<=0) return;
    const now=new Date();
    const novaEntrada={
      id:uid(),
      name:descricao||"Dividendo",
      amount:round2(valor),
      category:ENTRY_CATEGORIES.includes(categoria)?categoria:"Dividendos",
      paymentMethod:"Débito/Pix",
      month:now.getMonth(),
      year:now.getFullYear(),
      notes:"Registrado automaticamente via carteira de investimentos",
      createdAt:now.toISOString(),
    };
    setData(prev=>({...prev,entries:[...prev.entries,novaEntrada]}));
  }

  function executarResgateDirecto(valor,desc,origem){
    if(valor<=0)return;
    const now=new Date();
    const novaEntrada={
      id:uid(),
      name:desc||"Resgate de investimento",
      amount:valor,
      category:"Resgate de Investimentos",
      paymentMethod:"Débito/Pix",
      month:now.getMonth(),
      year:now.getFullYear(),
      notes:origem?`Origem: ${origem}`:"Resgate registrado automaticamente",
      createdAt:now.toISOString(),
    };
    setData(prev=>({...prev,entries:[...prev.entries,novaEntrada]}));
    const guiasPost={
      "Ativos (cripto/ações/FIIs)":{aba:"invest",label:"Investimentos",instrucao:"A quantidade do ativo já foi atualizada automaticamente"},
      "Meta (poupança específica)":{aba:"metas",label:"Metas",instrucao:"O saldo da meta já foi deduzido automaticamente"},
    };
    if(origem&&guiasPost[origem]){
      setResgateConfirmacao({valor,desc,origem,...guiasPost[origem]});
    }
  }
  function editBill(id){setEditingBillId(id);setExpenseFormOpen(true);}
  function editEntry(id){setEditingEntryId(id);setEntryFormOpen(true);}
  function deleteBill(id){if(!window.confirm("Excluir esta despesa e todo o histórico?"))return;setData((p)=>({...p,bills:p.bills.filter((b)=>b.id!==id)}));}
  function deleteEntry(id){if(!window.confirm("Excluir esta entrada?"))return;setData((p)=>({...p,entries:p.entries.filter((e)=>e.id!==id)}));}

  function saveGoal(goal){setData((p)=>{const ex=p.goals.some((g)=>g.id===goal.id);return{...p,goals:ex?p.goals.map((g)=>(g.id===goal.id?goal:g)):[...p.goals,goal]};});}
  function deleteGoal(id){if(!window.confirm("Excluir esta meta?"))return;setData((p)=>({...p,goals:p.goals.filter((g)=>g.id!==id)}));}
  function addGoalValue(id,value){
    const now = new Date();
    const mesKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    // Registro gráfico independente — apenas atualiza o acumulado e o histórico mensal
    setData((p)=>({
      ...p,
      goals: p.goals.map((g)=>{
        if(g.id!==id) return g;
        const registros=[...(g.registros||[]),{data:now.toISOString(),mes:mesKey,valor:round2(value)}];
        return {...g,currentAmount:round2(g.currentAmount+value),registros};
      }),
    }));
  }

  function saveBudget(budget){setData((p)=>{const ex=p.budgets.some((b)=>b.id===budget.id);return{...p,budgets:ex?p.budgets.map((b)=>(b.id===budget.id?budget:b)):[...p.budgets,budget]};});}
  function deleteBudget(id){setData((p)=>({...p,budgets:p.budgets.filter((b)=>b.id!==id)}));}

  function saveProfile(profile){setData((p)=>({...p,profile}));}

  function openPaymentForOccurrence(occ){
    const valorMesAdj=getBillAmountForMonth(occ.bill,occ.refMonthKey);
    const rec=getBillMonthRecord(occ.bill,occ.refMonthKey);
    const totalPago=(rec.entries||[]).reduce((s,e)=>s+Number(e.amount||0),0);
    const rem=Math.max(0,round2(valorMesAdj-totalPago));
    setPaymentTab(getRecordStatus(occ.bill,occ.refMonthKey)==="partial"?"partial":"total");
    setTotalPayAmount(String(rem.toFixed(2)).replace(".",","));setTotalPayNote("");setTotalPayMethod(occ.bill.paymentMethod);
    setPartialAmount("");setPartialNote("");setPartialMethod(occ.bill.paymentMethod);
    setPaymentModal({open:true,billId:occ.billId,refMonthKey:occ.refMonthKey});
  }
  function openUndoForOccurrence(occ){setUndoModal({open:true,billId:occ.billId,refMonthKey:occ.refMonthKey});}
  function salvarAjusteValor(billId, refMonthKey, novoValor){
    const v=round2(Math.max(0,Number(novoValor||0)));
    if(v<=0) return;
    setData(prev=>({
      ...prev,
      bills:prev.bills.map(bill=>{
        if(bill.id!==billId) return bill;
        const rec=getBillMonthRecord(bill,refMonthKey);
        return{...bill,payments:{...bill.payments,[refMonthKey]:{...rec,valorAjustado:v}}};
      })
    }));
    setAjusteModal(null);
  }

  function addPaymentEntry(billId,refMonthKey,amount,note,paymentMethod){
    const value=round2(Math.max(0,Number(amount||0)));if(value<=0)return false;
    setData((prev)=>{
      const bill=prev.bills.find(b=>b.id===billId);
      const rec=getBillMonthRecord(bill,refMonthKey);
      const newEntry={id:uid(),amount:value,note:note||"",createdAt:new Date().toISOString(),paidMonthKey:currentKey,paymentMethod:PAYMENT_METHODS.includes(paymentMethod)?paymentMethod:bill?.paymentMethod};
      const newBills=prev.bills.map(b=>{
        if(b.id!==billId)return b;
        return{...b,payments:{...b.payments,[refMonthKey]:{...rec,entries:[...(rec.entries||[]),newEntry]}}};
      });
      let newDesafios=prev.profile?.desafios||[];
      let newInvestimentos=prev.profile?.investimentos||[];
      if(bill?.desafioId){
        const desafioId=bill.desafioId;
        const agora=new Date().toISOString();
        newDesafios=newDesafios.map(d=>{
          if(d.id!==desafioId)return d;
          const registros=[...(d.registros||[]),{data:agora,valor:value}];
          const economiaTotal=Math.round(registros.reduce((s,r)=>s+r.valor,0)*100)/100;
          return{...d,registros,economiaTotal};
        });
        const cofrinhoId="cofrinho_"+desafioId;
        const desafio=newDesafios.find(d=>d.id===desafioId);
        const existente=newInvestimentos.find(i=>i.id===cofrinhoId);
        if(existente){
          newInvestimentos=newInvestimentos.map(i=>i.id===cofrinhoId?{...i,quantidade:round2(i.quantidade+value),precoMedio:1}:i);
        } else {
          newInvestimentos=[...newInvestimentos,{id:cofrinhoId,nome:"Cofrinho: "+(desafio?.nome||desafioId),simbolo:"COF",tipo:"cofrinho",quantidade:value,precoMedio:1,emoji:"🐷"}];
        }
      }
      return{...prev,bills:newBills,profile:{...prev.profile,desafios:newDesafios,investimentos:newInvestimentos}};
    });
    return true;
  }
  function handleConfirmTotalPayment(){const a=numberFromInput(totalPayAmount);if(a<=0)return;const ok=addPaymentEntry(paymentModal.billId,paymentModal.refMonthKey,a,totalPayNote,totalPayMethod);if(ok)setPaymentModal({open:false,billId:null,refMonthKey:null});}
  function handleConfirmPartialPayment(){const a=numberFromInput(partialAmount);if(a<=0)return;const ok=addPaymentEntry(paymentModal.billId,paymentModal.refMonthKey,a,partialNote,partialMethod);if(!ok)return;setPartialAmount("");setPartialNote("");setPartialMethod(paymentContext?.bill?.paymentMethod||PAYMENT_METHODS[0]);}
  function undoPaymentsForOccurrence(billId,refMonthKey){setData((p)=>({...p,bills:p.bills.map((bill)=>bill.id!==billId?bill:{...bill,payments:{...bill.payments,[refMonthKey]:{...(bill.payments?.[refMonthKey]||{}),entries:[]}}})}));setUndoModal({open:false,billId:null,refMonthKey:null});}

  // Alertas de orçamento
  const orcamentoAlertas=budgetsWithSpent.filter((b)=>b.limitAmount>0&&b._gasto>b.limitAmount*0.9);

  if(authLoading)return<div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#09090f"}}><div className="spinner"/></div>;
  // Tela de redefinição de senha — quando usuário clicou no link do e-mail
  if(showResetPassword)return<ResetPasswordScreen onDone={()=>setShowResetPassword(false)}/>;
  // Onboarding público — só na primeira visita, antes do login
  // Tela de jornada — primeiro login
  if(session&&showBoasVindas)return(
    <BoasVindasModal
      nomeUsuario={boasVindasNome||data?.profile?.nome||""}
      onClose={()=>{setShowBoasVindas(false);setShowJornadaScreen(true);}}
    />
  );

  if(session&&showJornadaScreen)return(
    <JornadaScreen
      nomeUsuario={data?.profile?.nome||boasVindasNome||""}
      onStart={()=>{setShowJornadaScreen(false);setTutorialOpen(true);}}
      onSkip={()=>setShowJornadaScreen(false)}
    />
  );



  if(!session&&showPublicOnboarding)return(
    <PublicOnboarding
      onCriarConta={()=>{try{localStorage.setItem("mf5g_onboarding_visto","1");}catch{}setShowPublicOnboarding(false);}}
      onLogin={()=>{try{localStorage.setItem("mf5g_onboarding_visto","1");}catch{}setShowPublicOnboarding(false);}}
    />
  );
  if(!session)return(
    <Login onCadastroSucesso={(nome)=>{setBoasVindasNome(nome);}}/>
  );

  if(erroCarregamento)return(
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16,padding:24,textAlign:"center",background:"var(--bg-main,#0f0f1a)",color:"#fff"}}>
      <div style={{fontSize:48}}>⚠️</div>
      <h2 style={{margin:0,fontSize:18}}>Não foi possível carregar seus dados</h2>
      <p style={{margin:0,fontSize:14,color:"#9ca3af",maxWidth:320}}>Verifique sua conexão com a internet e tente novamente. Seus dados continuam salvos com segurança na nuvem.</p>
      <button className="primary-btn" onClick={loadFromSupabase} style={{marginTop:8}}>🔄 Tentar novamente</button>
    </div>
  );

  if(loading)return(
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,background:"var(--bg-main,#0f0f1a)",color:"#fff"}}>
      <div style={{fontSize:40}}>⏳</div>
      <p style={{margin:0,fontSize:14,color:"#9ca3af"}}>Carregando seus dados...</p>
    </div>
  );


  const dm=darkMode;

  return(
    <div className={`app-root ${dm?"dark":"light"}`}>
      <style>{`
        :root{
          --bg-main:${dm?"#09090f":"#f0f2f8"};
          --bg-card:${dm?"linear-gradient(180deg,rgba(18,18,29,.98),rgba(16,16,26,.98))":"linear-gradient(180deg,#fff,#f8f9ff)"};
          --bg-input:${dm?"#12121d":"#f0f2f8"};
          --bg-section:${dm?"#10101a":"#fff"};
          --bg-topbar:${dm?"rgba(9,9,15,.92)":"rgba(255,255,255,.95)"};
          --border:${dm?"#242438":"#e2e4f0"};
          --border-input:${dm?"#2b2b3d":"#d0d4e8"};
          --text-primary:${dm?"#f8fafc":"#1a1a2e"};
          --text-muted:${dm?"#a1a1aa":"#6b7280"};
          --text-sub:${dm?"#8f8fa4":"#9ca3af"};
        }
        *{box-sizing:border-box;}
        body{margin:0;background:var(--bg-main);color:var(--text-primary);font-family:Inter,ui-sans-serif,system-ui,-apple-system,sans-serif;}
        button,input,select,textarea{font:inherit;}
        .app-root{min-height:100vh;background:${dm?"radial-gradient(circle at top left,rgba(124,131,255,.18),transparent 28%),radial-gradient(circle at top right,rgba(168,85,247,.14),transparent 24%),#09090f":"#f0f2f8"};}

        .topbar{position:sticky;top:0;z-index:60;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 18px;border-bottom:1px solid var(--border);backdrop-filter:blur(14px);background:var(--bg-topbar);box-shadow:0 4px 20px rgba(0,0,0,.1);flex-wrap:wrap;}
        .brand{display:flex;align-items:center;gap:12px;font-weight:700;}
        .brand-badge{width:40px;height:40px;display:flex;align-items:center;justify-content:center;border-radius:12px;background:linear-gradient(135deg,#7c83ff,#9b6bff);border:1px solid rgba(255,255,255,.1);box-shadow:0 6px 16px rgba(124,131,255,.3);font-size:18px;}
        .brand-title{font-size:17px;font-weight:800;letter-spacing:-0.02em;color:var(--text-primary);}
        .brand-subtitle{font-size:11px;color:var(--text-muted);margin-top:1px;}

        .nav-tabs{display:flex;gap:5px;flex-wrap:wrap;align-items:center;}
        .nav-btn,.ghost-btn,.secondary-btn,.action-btn{border:1px solid var(--border-input);background:var(--bg-input);color:var(--text-primary);border-radius:10px;cursor:pointer;transition:.15s ease;}
        .primary-btn{border:none;background:linear-gradient(135deg,#6f6ff7,#9b6bff);color:#fff;border-radius:10px;cursor:pointer;transition:.15s ease;font-weight:700;box-shadow:0 6px 16px rgba(111,111,247,.25);}
        .nav-btn:hover,.ghost-btn:hover,.secondary-btn:hover,.action-btn:hover,.primary-btn:hover{transform:translateY(-1px);filter:brightness(1.1);}
        .nav-btn{padding:8px 12px;font-size:13px;}
        .nav-btn.active{background:linear-gradient(135deg,rgba(124,131,255,.2),rgba(168,85,247,.15));border-color:#6f6ff7;color:var(--text-primary);}
        .nav-btn.painel-btn{padding:9px 14px;font-size:14px;font-weight:700;}
        .nav-btn.painel-btn.active{background:linear-gradient(135deg,#6f6ff7,#9b6bff);border-color:transparent;color:#fff;box-shadow:0 6px 18px rgba(111,111,247,.3);}
        .ghost-btn,.secondary-btn{padding:8px 12px;}
        .primary-btn{padding:8px 14px;}
        .action-btn{padding:8px 12px;background:linear-gradient(135deg,rgba(124,131,255,.12),rgba(34,197,94,.1));border-color:rgba(124,131,255,.4);font-weight:600;}
        .icon-btn{width:34px;height:34px;display:flex;align-items:center;justify-content:center;border:1px solid var(--border-input);background:var(--bg-input);border-radius:10px;cursor:pointer;transition:.15s;}
        .icon-btn:hover{transform:translateY(-1px);border-color:#6f6ff7;}
        .logout-btn{padding:8px 12px;border-radius:10px;cursor:pointer;transition:.15s;border:1px solid #3d1a1a;background:#1a0a0a;color:#fca5a5;font:inherit;}
        .logout-btn:hover{border-color:#dc2626;}
        .theme-btn{padding:8px 12px;border-radius:10px;cursor:pointer;border:1px solid var(--border-input);background:var(--bg-input);color:var(--text-primary);font:inherit;transition:.15s;}
        .status-pill{font-size:11px;padding:6px 9px;border-radius:999px;background:var(--bg-input);border:1px solid var(--border);white-space:nowrap;color:var(--text-muted);}
        .status-saving{color:#fbbf24;}.status-saved{color:#22c55e;}.status-error{color:#fca5a5;}
        .header-actions{display:flex;gap:7px;align-items:center;flex-wrap:wrap;}

        .alerta-banner{max-width:1280px;margin:10px auto 0;padding:0 20px;display:flex;flex-direction:column;gap:7px;}
        .alerta-item{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:12px;font-size:13px;}
        .alerta-urgente{background:${dm?"#4c1118":"#fee2e2"};border:1px solid #dc2626;color:${dm?"#fecaca":"#991b1b"};}
        .alerta-aviso{background:${dm?"#35280f":"#fef9c3"};border:1px solid #a16207;color:${dm?"#fde68a":"#854d0e"};}

        .orcamento-alerta{max-width:1280px;margin:6px auto 0;padding:0 20px;}
        .orcamento-alerta-item{display:flex;align-items:center;gap:8px;padding:8px 14px;border-radius:10px;font-size:13px;background:${dm?"#2a1a00":"#fffbeb"};border:1px solid #f59e0b;color:${dm?"#fde68a":"#92400e"};margin-bottom:5px;}

        .container{max-width:1280px;margin:0 auto;padding:20px;}
        .sticky-month{position:sticky;top:70px;z-index:40;background:${dm?"rgba(9,9,15,.97)":"rgba(240,242,248,.97)"};padding-bottom:6px;backdrop-filter:blur(12px);}
        .cards-scroll{padding-bottom:10px;margin-bottom:6px;}
        .month-nav{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px;flex-wrap:wrap;padding-top:8px;}
        .month-title{font-size:26px;font-weight:900;letter-spacing:-0.03em;color:var(--text-primary);}

        .cards-grid{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:10px;margin-bottom:10px;}
        .stat-card{background:var(--bg-card);border:1px solid var(--border);border-radius:16px;padding:13px;box-shadow:0 4px 16px rgba(0,0,0,.1);min-width:0;}
        .stat-card.success{border-color:#1c5e33;}.stat-card.warn{border-color:#7a5a16;}.stat-card.error{border-color:#7d2029;}
        .stat-title{color:var(--text-muted);font-size:11px;margin-bottom:6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .stat-value-wrap{min-width:0;}
        .stat-value{font-size:clamp(12px,1.3vw,20px);font-weight:800;color:var(--text-primary);line-height:1.2;word-break:break-all;}
        .stat-sub{color:var(--text-sub);margin-top:4px;font-size:11px;}

        .score-card{display:flex;align-items:center;gap:16px;padding:14px 18px;border-radius:16px;background:var(--bg-card);border:1px solid var(--border);margin-bottom:10px;}
        .score-circle{width:56px;height:56px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:900;flex-shrink:0;}

        .progress-shell{padding:12px 14px;background:var(--bg-section);border:1px solid var(--border);border-radius:14px;margin-bottom:10px;}
        .progress-row{display:flex;justify-content:space-between;gap:10px;margin-bottom:8px;font-size:12px;color:var(--text-muted);}
        .progress-track{height:10px;border-radius:999px;background:var(--bg-input);overflow:hidden;}
        .progress-fill{height:100%;border-radius:999px;background:linear-gradient(90deg,#6f6ff7,#22c55e);}

        .section-card{background:var(--bg-section);border:1px solid var(--border);border-radius:18px;padding:16px;margin-bottom:14px;}
        .section-title{font-size:19px;font-weight:800;margin:0 0 12px;color:var(--text-primary);}
        .bills-list,.entries-list{display:flex;flex-direction:column;gap:10px;}

        .bill-card,.entry-card{border:1px solid var(--border-input);border-radius:14px;padding:13px;background:var(--bg-input);display:flex;gap:12px;justify-content:space-between;align-items:flex-start;transition:.15s;}
        .bill-card.paid{border-color:${dm?"#1e7b40":"#bbf7d0"};opacity:${dm?".75":"1"};background:${dm?"var(--bg-input)":"#f0fdf4"};}.bill-card.partial{border-color:${dm?"#b4871d":"#fde68a"};}
        .bill-card.late{border-color:#dc2626;box-shadow:0 0 0 1px rgba(220,38,38,.2);}
        .bill-left,.entry-left{display:flex;gap:10px;min-width:0;flex:1;}
        .bill-icon,.entry-icon{width:40px;height:40px;border-radius:11px;display:flex;align-items:center;justify-content:center;background:var(--bg-section);border:1px solid var(--border);font-size:17px;flex:0 0 auto;}
        .bill-main,.entry-main{min-width:0;flex:1;}
        .bill-name,.entry-name{font-size:15px;font-weight:700;display:flex;align-items:center;gap:7px;flex-wrap:wrap;color:var(--text-primary);}
        .bill-name.strike{text-decoration:line-through;}
        .bill-meta,.entry-meta{margin-top:4px;color:var(--text-muted);font-size:12px;display:flex;gap:7px;flex-wrap:wrap;}
        .bill-right,.entry-right{display:flex;flex-direction:column;align-items:flex-end;gap:7px;flex-shrink:0;}
        .bill-amount,.entry-amount{font-size:16px;font-weight:800;color:var(--text-primary);white-space:nowrap;}
        .bill-amount small{color:var(--text-muted);text-decoration:line-through;margin-right:5px;}
        .bill-actions{display:flex;gap:5px;flex-wrap:wrap;justify-content:flex-end;}

        .badge{font-size:11px;font-weight:700;padding:3px 7px;border-radius:999px;border:1px solid transparent;white-space:nowrap;}
        .badge.red{background:${dm?"#4c1118":"#fee2e2"};border-color:#dc2626;color:${dm?"#fecaca":"#991b1b"};}
        .badge.green{background:${dm?"#0f2f1b":"#dcfce7"};border-color:#166534;color:${dm?"#bbf7d0":"#166534"};}
        .badge.yellow{background:${dm?"#35280f":"#fef9c3"};border-color:#a16207;color:${dm?"#fde68a":"#854d0e"};}
        .badge.gray{background:var(--bg-input);border-color:var(--border);color:var(--text-muted);}
        .mini-progress{width:100px;height:6px;border-radius:999px;background:var(--border);overflow:hidden;}
        .mini-progress > span{display:block;height:100%;background:linear-gradient(90deg,#f59e0b,#fde047);}
        .empty{padding:22px;text-align:center;color:var(--text-muted);border:1px dashed var(--border);border-radius:12px;}

        .table-list{display:flex;flex-direction:column;gap:8px;}
        .table-row{display:grid;grid-template-columns:1.4fr .7fr .5fr .8fr .9fr auto;gap:8px;align-items:center;padding:11px;border-radius:12px;background:var(--bg-input);border:1px solid var(--border);color:var(--text-primary);font-size:13px;}
        .muted{color:var(--text-muted);}.row-actions{display:flex;gap:5px;justify-content:flex-end;}

        .goals-list{display:flex;flex-direction:column;gap:12px;}
        .goal-card{background:var(--bg-input);border:1px solid var(--border-input);border-radius:14px;padding:14px;transition:.15s;}
        .goal-card.goal-done{border-color:#166534;background:${dm?"rgba(15,47,27,.5)":"rgba(220,252,231,.5)"};}
        .budget-card{background:var(--bg-input);border:1px solid var(--border-input);border-radius:14px;padding:14px;}

        .chart-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
        .chart-card{background:var(--bg-section);border:1px solid var(--border);border-radius:18px;padding:14px;}
        .chart-title{font-size:16px;font-weight:800;margin-bottom:10px;color:var(--text-primary);}
        .chart-svg{width:100%;height:auto;display:block;}
        .axis{stroke:${dm?"#52526a":"#d1d5db"};stroke-width:1.4;}
        .bar-value{fill:${dm?"#f3f4f6":"#374151"};font-size:10px;font-weight:700;}.bar-value.inside{fill:${dm?"#0b1020":"#fff"};}
        .bar-label{fill:${dm?"#d4d4d8":"#6b7280"};font-size:10px;}
        .donut-track{fill:none;stroke:var(--border);stroke-width:34;}.donut-hole{fill:var(--bg-section);}
        .donut-center-title{fill:var(--text-muted);font-size:14px;font-weight:600;}.donut-center-value{fill:var(--text-primary);font-size:15px;font-weight:800;}
        .donut-wrap{display:grid;grid-template-columns:minmax(0,360px) 1fr;gap:14px;align-items:center;}
        .legend{display:flex;flex-direction:column;gap:7px;}
        .legend-row{display:grid;grid-template-columns:12px 1fr auto;gap:8px;align-items:center;}
        .legend-swatch{width:12px;height:12px;border-radius:999px;display:inline-block;}
        .legend-name{color:var(--text-primary);font-size:12px;}.legend-value{color:var(--text-muted);font-size:12px;}

        .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);display:flex;justify-content:center;align-items:center;z-index:80;padding:16px;}
        .modal-shell{width:100%;background:${dm?"#0f0f18":"#fff"};border:1px solid var(--border);border-radius:20px;box-shadow:0 20px 60px rgba(0,0,0,.3);overflow:hidden;max-height:92vh;}
        .modal-header{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px 10px;border-bottom:1px solid var(--border);position:sticky;top:0;background:${dm?"#0f0f18":"#fff"};z-index:2;}
        .modal-header h3{margin:0;font-size:18px;color:var(--text-primary);}.modal-body{padding:16px;}.scrollable-modal{overflow-y:auto;max-height:calc(92vh - 65px);}
        .form-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:11px;}
        .field{display:flex;flex-direction:column;gap:6px;}.field.full{grid-column:1 / -1;}.label{color:var(--text-muted);font-size:13px;}
        .input,.select,.textarea{background:var(--bg-input);color:var(--text-primary);border:1px solid var(--border-input);border-radius:11px;padding:10px 12px;outline:none;}
        .textarea{min-height:80px;resize:vertical;}
        .input:focus,.select:focus,.textarea:focus{border-color:#6f6ff7;box-shadow:0 0 0 3px rgba(111,111,247,.12);}
        .actions{display:flex;justify-content:flex-end;gap:9px;margin-top:14px;flex-wrap:wrap;}
        .tab-strip{display:flex;gap:7px;margin-bottom:12px;}
        .payment-history{display:flex;flex-direction:column;gap:8px;margin-top:12px;}
        .history-item{background:var(--bg-input);border:1px solid var(--border);border-radius:11px;padding:10px;display:flex;justify-content:space-between;gap:10px;font-size:13px;}
        .info-row{display:flex;justify-content:space-between;gap:10px;padding:10px 0;border-bottom:1px solid var(--border);font-size:13px;}
        .loading-screen{min-height:100vh;display:flex;align-items:center;justify-content:center;}
        .spinner{width:46px;height:46px;border-radius:999px;border:4px solid var(--border);border-top-color:#7c83ff;animation:spin 1s linear infinite;}
        .helper-error{margin-top:7px;color:#fca5a5;font-size:12px;}
        .two-col{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
        @keyframes spin{to{transform:rotate(360deg);}}

        @media(max-width:1100px){.cards-grid{grid-template-columns:repeat(4,minmax(0,1fr));}}
        @media(max-width:768px){
          .cards-grid{grid-template-columns:repeat(2,minmax(0,1fr));}
          .chart-grid,.donut-wrap,.form-grid,.two-col{grid-template-columns:1fr;}
          .table-row{grid-template-columns:1fr;}
          .bill-card,.entry-card{flex-direction:column;align-items:stretch;}
          .bill-right,.entry-right{flex-direction:row;align-items:center;justify-content:space-between;width:100%;margin-top:8px;}
          .bill-actions{flex-direction:row;}
          .topbar{flex-direction:column;align-items:flex-start;gap:8px;}
          .sticky-month{top:0;}
          .month-title{font-size:22px;}
          .nav-btn{padding:7px 9px;font-size:12px;}
          .nav-btn.painel-btn{padding:8px 11px;font-size:13px;}
        }
        .desktop-only-text{display:none;}
        @media(min-width:481px){.desktop-only-text{display:inline;}}
        @media(max-width:480px){
          .cards-grid{grid-template-columns:1fr 1fr;}
          .stat-value{font-size:13px;}
          .nav-tabs{display:none !important;}
          .hamburger-btn{display:flex !important;}
          .topbar{flex-wrap:nowrap;gap:6px;}
          .header-actions{gap:4px;}
          .header-actions .secondary-btn, .header-actions .primary-btn{font-size:12px;padding:6px 8px;}
        }
        @media(min-width:481px){
          .hamburger-btn{display:none !important;}
        }
      `}</style>

      {loading?<div className="loading-screen"><div className="spinner"/></div>:(
        <>
          {/* Drawer menu mobile */}
          {menuOpen&&(
            <div onClick={()=>setMenuOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:8000}}>
              <div onClick={e=>e.stopPropagation()} style={{position:"fixed",top:0,left:0,bottom:0,width:"75%",maxWidth:280,background:"var(--bg-main)",borderRight:"1px solid var(--border)",zIndex:8001,overflowY:"auto",padding:"16px 0"}}>
                {/* Header do drawer */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"0 16px 16px",borderBottom:"1px solid var(--border)",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{background:"#7C83FF",borderRadius:8,padding:"6px 8px",fontSize:16}}>💳</div>
                    <div>
                      <div style={{fontWeight:800,fontSize:14,color:"var(--text-primary)"}}>Mapa 5G</div>
                      <div style={{fontSize:11,color:"var(--text-muted)"}}>{data.profile?.nome||""}</div>
                    </div>
                  </div>
                  <button onClick={()=>setMenuOpen(false)} style={{background:"none",border:"none",fontSize:20,color:"var(--text-muted)",cursor:"pointer"}}>✕</button>
                </div>
                {/* Itens do menu */}
                {[
                  ["painel","🏠","Painel"],
                  ["contas","💸","Despesas"],
                  ["entradas","💰","Entradas"],
                  ["dividas","💳","Dívidas"],
                  ["metas","🎯","Metas"],
                  ["orcamento","📊","Orçamento"],
                  ["graficos","📈","Gráficos"],
                  ["evolucao","📉","Evolução"],
                  ["invest","📊","Investimentos"],
                  ["desafios","🏆","Desafios"],
                  ["noticias","📰","Notícias"],
                  ["cartoes","💳","Cartões"],
                  ["planos","💎","Planos"],
                  ["perfil","👤","Perfil"],
                  ["5g","🌟","5G"],
                ].map(([tab,icon,label])=>(
                  <button key={tab} onClick={()=>{setActiveTab(tab);setMenuOpen(false);}}
                    style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"12px 20px",background:activeTab===tab?"#7C83FF22":"none",border:"none",borderLeft:activeTab===tab?"3px solid #7C83FF":"3px solid transparent",cursor:"pointer",textAlign:"left"}}>
                    <span style={{fontSize:18,width:24,textAlign:"center"}}>{icon}</span>
                    <span style={{fontSize:14,fontWeight:activeTab===tab?700:400,color:activeTab===tab?"#7C83FF":"var(--text-primary)"}}>{label}</span>
                    {activeTab===tab&&<span style={{marginLeft:"auto",color:"#7C83FF",fontSize:12}}>●</span>}
                  </button>
                ))}
                {/* Ações rápidas */}
                <div style={{borderTop:"1px solid var(--border)",marginTop:8,padding:"8px 16px"}}>
                  <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:8,fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>Ações</div>
                  <button onClick={()=>{exportBackup();setMenuOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 4px",background:"none",border:"none",cursor:"pointer",color:"var(--text-primary)",fontSize:13}}>
                    <span>💾</span> Fazer backup
                  </button>
                  <button onClick={()=>{importBackup();setMenuOpen(false);}} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 4px",background:"none",border:"none",cursor:"pointer",color:"var(--text-primary)",fontSize:13}}>
                    <span>📥</span> Importar backup
                  </button>
                  <button onClick={handleLogout} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 4px",background:"none",border:"none",cursor:"pointer",color:"#EF4444",fontSize:13,marginTop:4}}>
                    <span>🚪</span> Sair
                  </button>
                </div>
              </div>
            </div>
          )}

          <header className="topbar">
            <div className="brand">
              <div className="brand-badge">💳</div>
              <div>
                <div className="brand-title">Mapa Financeiro 5G</div>
                <div className="brand-subtitle">Olá, {data.profile?.nome||session.user.email}</div>
              </div>
            </div>
            {/* Botão hamburguer — só mobile */}
            <button className="hamburger-btn" onClick={()=>setMenuOpen(!menuOpen)}
              style={{display:"none",alignItems:"center",justifyContent:"center",background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:10,padding:"8px 12px",cursor:"pointer",gap:6,fontSize:13,fontWeight:700,color:"var(--text-primary)",flexShrink:0}}>
              <span style={{fontSize:18,lineHeight:1}}>{menuOpen?"✕":"☰"}</span>
              <span style={{fontSize:11,color:"var(--text-muted)"}}>{menuOpen?"Fechar":"Menu"}</span>
            </button>
            <nav className="nav-tabs">
              <button className={`nav-btn painel-btn ${activeTab==="painel"?"active":""}`} onClick={()=>setActiveTab("painel")}>🏠 Painel</button>
              {[["contas","Despesas"],["entradas","Entradas"],["dividas","💳 Dívidas"],["metas","Metas 🎯"],["orcamento","Orçamento"],["graficos","Gráficos"],["evolucao","Evolução"],["invest","📊 Investimentos"],["desafios","🏆 Desafios"],["noticias","📰 Notícias"],["cartoes","Cartões"],["planos","💎 Planos"],["perfil","Perfil"],["5g","5G 🌟"]].map(([tab,label])=>(
                <button key={tab} className={`nav-btn ${activeTab===tab?"active":""}`} onClick={()=>setActiveTab(tab)}>{label}</button>
              ))}
            </nav>
            <div className="header-actions">
              <div className={`status-pill ${saveStatus==="saving"?"status-saving":saveStatus==="saved"?"status-saved":"status-error"}`}>
                {saveStatus==="saving"?"💾 salvando...":saveStatus==="saved"?"✓ salvo":"⚠ erro"}
              </div>
              <button className="theme-btn" onClick={()=>setDarkMode(!dm)} title="Alternar tema">{dm?"☀️":"🌙"}</button>
              <button className="secondary-btn" onClick={openNewEntryForm}>+ Entrada</button>
              <button className="primary-btn" onClick={openNewExpenseForm}>+ Despesa</button>
              {/* Menu ⚙️ Configurações */}
              <div style={{position:"relative"}}>
                <button className="secondary-btn" onClick={()=>setToolsOpen(!toolsOpen)} title="Ferramentas" style={{padding:"8px 10px",fontSize:16}}>⚙️</button>
                {toolsOpen&&(
                  <div style={{position:"fixed",right:8,top:"auto",marginTop:4,background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:12,padding:8,zIndex:9999,minWidth:170,boxShadow:"0 8px 32px rgba(0,0,0,0.35)"}}>
                    <button className="secondary-btn" onClick={()=>{importBackup();setToolsOpen(false);}} style={{width:"100%",textAlign:"left",marginBottom:4}}>📥 Importar backup</button>
                    <button className="secondary-btn" onClick={()=>{exportBackup();setToolsOpen(false);}} style={{width:"100%",textAlign:"left",marginBottom:4,background:(!ultimoBackup||((new Date()-new Date(ultimoBackup))/(1000*60*60*24))>7)?"#EF444420":"",border:(!ultimoBackup||((new Date()-new Date(ultimoBackup))/(1000*60*60*24))>7)?"1px solid #EF444444":""}}>
                      {(!ultimoBackup||((new Date()-new Date(ultimoBackup))/(1000*60*60*24))>7)?"⚠️":"💾"} Backup{ultimoBackup?` · ${new Date(ultimoBackup).toLocaleDateString("pt-BR")}`:" · nunca feito"}
                    </button>
                    <button className="secondary-btn" onClick={()=>{exportToExcel(data,currentYear,currentMonth);setToolsOpen(false);}} style={{width:"100%",textAlign:"left",marginBottom:4}}>📊 Excel</button>
                    <button className="secondary-btn" onClick={async()=>{
                      setToolsOpen(false);
                      const invs=data.profile?.investimentos||[];
                      // Buscar cotações em tempo real antes de gerar o PDF
                      const cotacoes=invs.length>0?await buscarCotacoesParaPDF(invs):(window.__mf5g_cotacoes||{});
                      const invComCotacao=invs.map(i=>({...i,precoAtual:cotacoes[i.id]||null}));
                      exportToPDF(data,currentYear,currentMonth,monthMetrics,saldoAcumulado,invComCotacao,data.profile?.rendaFixa||[],data.profile?.desafios||[]);}} style={{width:"100%",textAlign:"left"}}>📄 PDF</button>
                  </div>
                )}
              </div>
              <button className="logout-btn" onClick={handleLogout}>Sair</button>
            </div>
          </header>

          {(activeTab==="painel"||activeTab==="contas")&&<AlertaBanner bills={data.bills} currentYear={currentYear} currentMonth={currentMonth}/>}

          {/* Alertas de orçamento estourado — só no painel e despesas */}
          {(activeTab==="painel"||activeTab==="contas")&&orcamentoAlertas.length>0&&(
            <div className="orcamento-alerta">
              {orcamentoAlertas.map((b)=>(
                <div key={b.id} className="orcamento-alerta-item">
                  <span>⚠️</span>
                  <span><strong>{b.category}</strong>: gasto {currency(b._gasto)} de {currency(b.limitAmount)} ({b._gasto>b.limitAmount?"estourou o limite!":"quase no limite"})</span>
                </div>
              ))}
            </div>
          )}
          {(activeTab==="painel"||activeTab==="contas")&&(()=>{
            // Alerta: há itens 💳 pagos automaticamente mas sem fatura registrada este mês
            return null;
          })()}

          {/* Mês fixo */}
          <div className="container sticky-month">
            <div className="month-nav">
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <button className="icon-btn" onClick={()=>changeMonth(-1)}>←</button>
                <div className="month-title">{getMonthLabel(currentYear,currentMonth)}</div>
                <button className="icon-btn" onClick={()=>changeMonth(1)}>→</button>
                {activeTab==="painel"&&(
                  <button onClick={()=>setValoresVisiveis(v=>!v)}
                    title={valoresVisiveis?"Ocultar valores":"Mostrar valores"}
                    style={{background:"none",border:"none",cursor:"pointer",fontSize:20,padding:"4px",lineHeight:1,color:"var(--text-muted)",transition:".15s"}}
                  >{valoresVisiveis?"👁️":"🙈"}</button>
                )}
              </div>
              {/* Centro: frase ou porquinho no mobile */}
              {activeTab==="painel"&&!voiceOpen?(
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  {/* No desktop mostra a frase + porquinho pequeno */}
                  <span className="muted desktop-only-text" style={{fontSize:12}}>Seu mapa financeiro do mês</span>
                  <div onClick={()=>{setVoiceOpen(true);setVoiceTexto("");setVoiceResultado(null);setVoiceErro("");setVoiceMultiplas(null);}}
                    style={{cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:1,WebkitTapHighlightColor:"transparent",touchAction:"manipulation"}}>
                    <img src={porquinho} alt="Assistente 5G" style={{width:56,height:56,objectFit:"contain",filter:"drop-shadow(0 3px 8px rgba(124,131,255,0.5))"}}/>
                    <div style={{fontSize:9,fontWeight:700,color:"#7C83FF",background:"var(--bg-card)",border:"1px solid #7C83FF44",borderRadius:20,padding:"2px 7px",whiteSpace:"nowrap"}}>
                      Falar com o porquinho
                    </div>
                  </div>
                </div>
              ):(
                <div className="muted" style={{fontSize:12}}>{["graficos","evolucao"].includes(activeTab)?"Visualização mensal":"Seu mapa financeiro do mês"}</div>
              )}
            </div>
          </div>

          {/* Cards rolam com o conteúdo — só no Painel */}
          {activeTab==="painel"&&<div className="container cards-scroll">
            {/* Helper local para mascarar valores */}
            {(()=>{
              const mv=v=>valoresVisiveis?v:"••••";
              // Saldo operacional = Entradas − Pago sem aportes
              // = quanto sobrou para gastar livremente (alimentação, transporte, imprevistos)
              const pagoSemAportes=round2(monthMetrics.pagoDesteMes-monthMetrics.aportesDesteMes);
              const saldoOperacional=round2(monthMetrics.entradas-pagoSemAportes-monthMetrics.atrasados);
              const subOp=data.budgets.length>0?"Orçamento livre — lembre-se do orçamento":"Orçamento livre para gastar";
              const subPago=monthMetrics.atrasados>0?`Deste mês: ${currency(monthMetrics.pagoDesteMes)} + atrasados: ${currency(monthMetrics.atrasados)}`:"Feitos neste mês";
              return(
            <div className="cards-grid">
              <AppCard title="Previsto" value={mv(currency(monthMetrics.previsto))}/>
              <AppCard title="Pago" value={mv(currency(monthMetrics.pago))} tone="success" sub={subPago}/>
              <AppCard title="Parcial" value={mv(currency(monthMetrics.parcial))} tone="warn"/>
              <AppCard title="Pendente" value={mv(currency(monthMetrics.pendente))} tone="error"/>
              <AppCard title="Entradas" value={mv(currency(monthMetrics.entradas))}/>
              <AppCard title="Saldo mês" value={mv(currency(monthMetrics.saldoMes))} tone={monthMetrics.saldoMes>=0?"success":"error"}/>
              <AppCard title="Saldo acumulado" value={mv(currency(saldoAcumulado))} tone={saldoAcumulado>=0?"success":"error"}/>
              <AppCard title="Saldo operacional" value={mv(currency(saldoOperacional))} tone={saldoOperacional>=0?"success":"error"} sub={subOp}/>
            </div>
              );
            })()}

            {/* Banner de upgrade — grátis com 70%+ do limite */}
      {!isPremium&&!isBasico&&pctLimite>=70&&(
        <div style={{background:"linear-gradient(135deg,#7C83FF22,#a855f722)",border:"1px solid #7C83FF44",borderRadius:12,padding:"12px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
          <div>
            <div style={{fontWeight:700,fontSize:13,color:"#7C83FF",marginBottom:2}}>
              {pctLimite>=100?"🔒 Limite atingido!":"⚠️ Quase no limite!"}
            </div>
            <div style={{fontSize:12,color:"var(--text-muted)"}}>
              {totalDespesas}/15 despesas usadas · Básico a partir de R$ 8,90/mês
            </div>
          </div>
          <button onClick={()=>setActiveTab("planos")} style={{background:"linear-gradient(135deg,#7C83FF,#a855f7)",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
            Ver planos →
          </button>
        </div>
      )}

            <div className="progress-shell">
              <div className="progress-row"><span>Progresso do mês</span><span><strong>{monthMetrics.paidCount} de {monthMetrics.totalCount}</strong> quitadas · {percent(monthMetrics.progress)}</span></div>
              <div className="progress-track"><div className="progress-fill" style={{width:`${monthMetrics.progress}%`}}/></div>
            </div>
          </div>}

          <main className="container">
            {activeTab==="painel"&&(
              <>
                {/* Banner — Jornada 5G com Porquinho animado */}
                <BannerJornada onOpen={()=>{localStorage.setItem("mf5g_jornada_viu","true");setTutorialOpen(true);}}/>

                {generatedOccurrences.overdue.length>0&&(
                  <section className="section-card">
                    <h2 className="section-title">🔴 Atrasadas</h2>
                    <div className="bills-list">
                      {generatedOccurrences.overdue.map((occ)=>{
                        const status=getRecordStatus(occ.bill,occ.refMonthKey);const paidOff=status==="paid";
                        const state={status,isLate:true,percent:getPercentPaid(occ.bill,occ.refMonthKey),paid:getAmountPaidForMonthReference(occ.bill,occ.refMonthKey),remaining:getRemainingForMonthReference(occ.bill,occ.refMonthKey)};
                        return(
                          <div key={occ.occurrenceId} className={`bill-card ${paidOff?"paid":state.status==="partial"?"partial":"late"}`}>
                            <div className="bill-left">
                              <div className="bill-icon">{CATEGORY_ICONS[occ.bill.category]||"📦"}</div>
                              <div className="bill-main">
                                <div className={`bill-name ${paidOff?"strike":""}`}>{occ.bill.name}<span className="badge red">{formatMonthKeyLabel(occ.refMonthKey)}</span>{paidOff?<span className="badge green">Paga</span>:state.status==="partial"?<span className="badge yellow">{percent(state.percent)} pago</span>:<span className="badge red">Vencida</span>}</div>
                                <div className="bill-meta"><span>Dia {occ.bill.dueDay}</span><span>{occ.bill.paymentMethod}</span><span>{occ.bill.category}</span></div>
                                {state.status==="partial"&&<div style={{marginTop:7}}><div className="mini-progress"><span style={{width:`${state.percent}%`}}/></div></div>}
                              </div>
                            </div>
                            <div className="bill-right">
                              {(()=>{
                                const valorMes=getBillAmountForMonth(occ.bill,occ.refMonthKey);
                                const ajustado=valorMes!==occ.bill.amount;
                                return(
                                  <>
                                    <div className="bill-amount">
                                      {ajustado&&<small style={{textDecoration:"line-through",color:"var(--text-muted)",fontSize:11}}>{currency(occ.bill.amount)}</small>}
                                      {currency(paidOff?state.paid:valorMes)}
                                    </div>
                                    <div className="bill-actions">
                                      {paidOff?(
                                        <button className="secondary-btn" onClick={()=>openUndoForOccurrence(occ)}>Desfazer</button>
                                      ):(
                                        <>
                                          {!ajustado&&<button className="secondary-btn" style={{fontSize:11,padding:"3px 8px"}} onClick={()=>setAjusteModal({billId:occ.bill.id,refMonthKey:occ.refMonthKey,nome:occ.bill.name,valorAtual:getBillAmountForMonth(occ.bill,occ.refMonthKey)})}>📝</button>}
                                          <button className="action-btn" onClick={()=>openPaymentForOccurrence(occ)}>{state.status==="partial"?"Continuar":"Registrar pagamento"}</button>
                                        </>
                                      )}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}
                <section className="section-card">
                  <h2 className="section-title">Despesas do mês</h2>
                  {generatedOccurrences.base.length>1&&<input className="input" placeholder="🔍 Buscar despesa do mês..." value={buscaPainel} onChange={e=>setBuscaPainel(e.target.value)} style={{marginBottom:10}}/>}
                  {generatedOccurrences.base.length===0?<div className="empty">Nenhuma despesa cadastrada.<br/>Clique em <strong>+ Despesa</strong> para começar.</div>:(()=>{
                    // Separar em grupos: vencidas, vence hoje, a vencer, pagas
                    const occsComState = generatedOccurrences.base.filter(occ=>!buscaPainel||occ.bill.name.toLowerCase().includes(buscaPainel.toLowerCase())||occ.bill.category.toLowerCase().includes(buscaPainel.toLowerCase())).map(occ => ({
                      occ,
                      state: getOccurrenceVisualState(occ,currentKey,isViewingCurrentMonth,today.day),
                    }));
                    const diaRef = isViewingCurrentMonth ? today.day : 0;
                    const vencidas = occsComState.filter(({state}) => state.isLate && state.status!=="paid").sort((a,b)=>a.occ.bill.dueDay-b.occ.bill.dueDay);
                    const venceHoje = occsComState.filter(({state,occ}) => !state.isLate && state.status!=="paid" && isViewingCurrentMonth && occ.bill.dueDay===diaRef).sort((a,b)=>a.occ.bill.name.localeCompare(b.occ.bill.name));
                    const aVencer = occsComState.filter(({state,occ}) => !state.isLate && state.status!=="paid" && (!isViewingCurrentMonth || occ.bill.dueDay!==diaRef)).sort((a,b)=>a.occ.bill.dueDay-b.occ.bill.dueDay);
                    const pagas = occsComState.filter(({state}) => state.status==="paid").concat(
                      (generatedOccurrences.atrasadosPagos||[])
                        .filter(occ=>!buscaPainel||occ.bill.name.toLowerCase().includes(buscaPainel.toLowerCase())||occ.bill.category.toLowerCase().includes(buscaPainel.toLowerCase()))
                        .map(occ=>({occ,state:getOccurrenceVisualState(occ,currentKey,isViewingCurrentMonth,today.day)}))
                    ).sort((a,b)=>{
                      // Mais recente primeiro: pega o createdAt da última entrada de pagamento
                      const getLastPaid = (occ) => {
                        const entries = getBillMonthRecord(occ.bill, occ.refMonthKey).entries || [];
                        if(!entries.length) return "";
                        return entries.reduce((latest, e) => e.createdAt > latest ? e.createdAt : latest, "");
                      };
                      const dateA = getLastPaid(a.occ);
                      const dateB = getLastPaid(b.occ);
                      if(dateA && dateB) return dateB.localeCompare(dateA); // mais recente primeiro
                      return a.occ.bill.dueDay - b.occ.bill.dueDay;
                    });
                    // Separar "A vencer" em urgentes (≤7 dias) e demais
                    const hoje7=isViewingCurrentMonth?today.day+7:7;
                    const urgentes=isViewingCurrentMonth?aVencer.filter(({occ})=>occ.bill.dueDay<=hoje7&&occ.bill.dueDay>today.day):aVencer.filter(({occ})=>occ.bill.dueDay<=hoje7);
                    const demais=isViewingCurrentMonth?aVencer.filter(({occ})=>occ.bill.dueDay>hoje7):aVencer.filter(({occ})=>occ.bill.dueDay>hoje7);
                    const totalDemais=demais.reduce((s,{occ})=>s+(occ.bill.amount||0),0);
                    const grupos = [
                      {label:"⚠️ Vencidas", items: vencidas, show: vencidas.length>0, collapsible: false},
                      {label:"🔴 Vence hoje", items: venceHoje, show: venceHoje.length>0, collapsible: false, isHoje: true},
                      {label:"🔔 Próximos 7 dias", items: urgentes, show: urgentes.length>0, collapsible: false},
                      {label:`📅 Restante do mês${demais.length>0?" ("+demais.length+")":""}`, items: demais, show: demais.length>0, collapsible: true, totalDemais},
                      {label:"🙏 Pagas", items: pagas, show: pagas.length>0, collapsible: true},
                    ];
                    const renderCard = ({occ, state}) => {
                      const paidOff=state.status==="paid";
                      const budget=data.budgets.find((b)=>b.category===occ.bill.category);
                      const spent=spentByCategory[occ.bill.category]||0;
                      const budgetWarning=budget&&spent>budget.limitAmount;
                      return(
                        <div key={occ.occurrenceId} className={`bill-card ${paidOff?"paid":state.status==="partial"?"partial":state.isLate?"late":""}`}>
                          <div className="bill-left">
                            <div className="bill-icon">{CATEGORY_ICONS[occ.bill.category]||"📦"}</div>
                            <div className="bill-main">
                              <div className={`bill-name ${paidOff?"strike":""}`}>
                                {occ.bill.name}
                                {state.isLate&&!paidOff?<span className="badge red">⚠️ Vencida</span>:null}
                                {paidOff?<span className="badge green">🙏 Paga</span>:null}
                                {paidOff&&occ.refMonthKey!==currentKey?<span className="badge yellow" title="Conta de outro mês, paga dentro deste mês">📅 ref. {formatMonthKeyLabel(occ.refMonthKey)}</span>:null}
                                {state.status==="partial"?<span className="badge yellow">{percent(state.percent)} pago</span>:null}
                                {budgetWarning?<span className="badge red">Orçamento estourado</span>:null}
                              </div>
                              <div className="bill-meta">
                                <span>Dia {occ.bill.dueDay}</span><span>{occ.bill.paymentMethod}</span><span>{occ.bill.category}</span>
                                {occ.bill.isFaturaGerada?(
                                  <>
                                  <span style={{fontSize:10,background:"#7C83FF22",color:"#7C83FF",padding:"1px 6px",borderRadius:4,fontWeight:600}}>💳 Compras de {MONTHS_PT[occ.bill.startMonth===0?11:occ.bill.startMonth-1]}{occ.bill.valorManual?" · valor conferido":""}</span>
                                  {occ.bill.valorManual&&!Object.values(occ.bill.payments||{}).some(r=>(r.entries||[]).length>0)&&(
                                    <button type="button" onClick={(ev)=>{ev.stopPropagation();if(!window.confirm("Recalcular esta fatura pela soma das compras cadastradas no cartão?"))return;setData(prev=>({...prev,bills:prev.bills.map(b=>b.id===occ.bill.id?{...b,valorManual:false}:b)}));}}
                                      style={{fontSize:10,background:"#22c55e22",color:"#22c55e",border:"1px solid #22c55e55",padding:"1px 8px",borderRadius:4,fontWeight:700,cursor:"pointer"}}>🔄 Recalcular pelas compras</button>
                                  )}
                                  </>
                                ):(<>
                                <span>{getInstallmentCountLabel(occ.bill)}{occ.bill.installments>0?` • parcela ${getInstallmentNumberForMonth(occ.bill,occ.refYear,occ.refMonth)}`:""}</span>
                                {occ.bill.frequency==="mensal"&&occ.bill.installments===0&&<span style={{fontSize:10,background:"#7C83FF22",color:"#7C83FF",padding:"1px 6px",borderRadius:4,fontWeight:600}}>Fixo</span>}
                                {occ.bill.frequency==="anual"&&<span style={{fontSize:10,background:"#14B8A622",color:"#14B8A6",padding:"1px 6px",borderRadius:4,fontWeight:600}}>Anual</span>}
                                {occ.bill.frequency==="semestral"&&<span style={{fontSize:10,background:"#F59E0B22",color:"#F59E0B",padding:"1px 6px",borderRadius:4,fontWeight:600}}>Semestral</span>}
                                {occ.bill.installments>0&&<span style={{fontSize:10,background:"#EF444422",color:"#EF4444",padding:"1px 6px",borderRadius:4,fontWeight:600}}>Parcelado</span>}
                                </>)}
                              </div>
                              {state.status==="partial"&&<div style={{marginTop:7}}><div className="mini-progress"><span style={{width:`${state.percent}%`}}/></div></div>}
                            </div>
                          </div>
                          <div className="bill-right">
                            {(()=>{
                              const valorMes2=getBillAmountForMonth(occ.bill,occ.refMonthKey);
                              const ajustado2=valorMes2!==occ.bill.amount;
                              return(
                                <>
                                  <div className="bill-amount">
                                    {ajustado2&&<small style={{textDecoration:"line-through",color:"var(--text-muted)",fontSize:11}}>{currency(occ.bill.amount)}</small>}
                                    {currency(paidOff?state.paid:valorMes2)}
                                    {paidOff&&occ.bill.paymentMethod==="Cartão de Crédito"&&state.paid===0&&(
                                      <div style={{fontSize:10,color:"#7C83FF",fontWeight:600,marginTop:3}}>💳 Fatura do cartão</div>
                                    )}
                                  </div>
                                  <div className="bill-actions">
                                    {paidOff?(
                                      <button className="secondary-btn" onClick={()=>openUndoForOccurrence(occ)}>Desfazer</button>
                                    ):(
                                      <>
                                        {!ajustado2&&<button className="secondary-btn" style={{fontSize:11,padding:"3px 8px"}} onClick={()=>setAjusteModal({billId:occ.bill.id,refMonthKey:occ.refMonthKey,nome:occ.bill.name,valorAtual:getBillAmountForMonth(occ.bill,occ.refMonthKey)})}>📝</button>}
                                        <button className="action-btn" onClick={()=>openPaymentForOccurrence(occ)}>{state.status==="partial"?"Continuar pagamento":"Registrar pagamento"}</button>
                                      </>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    };
                    return grupos.map(({label, items, show, collapsible, totalDemais, isHoje}) => {
                      if(!show) return null;
                      const estaAberto = collapsible ? (gruposAbertos[label]===undefined ? false : gruposAbertos[label]) : true;
                      const total=items.reduce((s,{occ,state})=>s+(state?.status==="paid"?state.paid:(occ.bill.amount||0)),0);
                      return(
                        <div key={label} style={{marginBottom:12}}>
                          <div
                            style={{fontSize:12,fontWeight:700,color:isHoje?"#f97316":"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:estaAberto?8:0,paddingLeft:4,display:"flex",alignItems:"center",gap:8,cursor:collapsible?"pointer":"default",userSelect:"none",background:isHoje?"#f9731612":undefined,borderRadius:isHoje?8:undefined,padding:isHoje?"6px 10px":undefined,border:isHoje?"1px solid #f9731633":undefined}}
                            onClick={collapsible?()=>toggleGrupo(label):undefined}
                          >
                            <span>{label}</span>
                            <span style={{fontWeight:400,color:isHoje?"#f97316":"var(--text-muted)",fontSize:11}}>— {currency(total)}</span>
                            {collapsible&&<span style={{marginLeft:"auto",fontSize:11}}>{estaAberto?"▲ ocultar":"▼ ver "+items.length}</span>}
                          </div>
                          {estaAberto&&<div className="bills-list">{items.map(renderCard)}</div>}
                        </div>
                      );
                    });
                  })()}
                </section>
              </>
            )}

            {activeTab==="dividas"&&<DividasTab bills={data.bills} currentYear={currentYear} currentMonth={currentMonth}/>}

            {activeTab==="contas"&&(
              <section className="section-card">
                <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"center",flexWrap:"wrap",marginBottom:12}}>
                  <h2 className="section-title" style={{margin:0}}>Despesas do mês</h2>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <button className="secondary-btn" onClick={()=>setVerTodasCadastradas(true)}>📂 Ver todas cadastradas</button>
                    <button className="secondary-btn" onClick={()=>setGastoRapidoOpen(true)} style={{background:"#F59E0B22",border:"1px solid #F59E0B66",color:"#F59E0B",fontWeight:700}}>⚡ Rápido</button>
                    <button className="primary-btn" onClick={openNewExpenseForm}>+ Nova despesa</button>
                  </div>
                </div>
                <div className="table-list">
                  {data.bills.filter(b=>shouldGenerateForMonth(b,currentYear,currentMonth)).length>1&&(
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
                      <input className="input" placeholder="🔍 Buscar despesa..." value={buscaContas} onChange={e=>setBuscaContas(e.target.value)} style={{flex:2,minWidth:160}}/>
                      <select className="select" value={categoriaFiltroContas} onChange={e=>setCategoriaFiltroContas(e.target.value)} style={{flex:1,minWidth:150}}>
                        <option value="">Todas as categorias</option>
                        {EXPENSE_CATEGORIES.filter(c=>data.bills.some(b=>shouldGenerateForMonth(b,currentYear,currentMonth)&&b.category===c)).map(c=><option key={c} value={c}>{CATEGORY_ICONS[c]||"📦"} {c}</option>)}
                      </select>
                    </div>
                  )}
                  {data.bills.filter(b=>shouldGenerateForMonth(b,currentYear,currentMonth)).length===0?<div className="empty">Nenhuma despesa neste mês.</div>:(
                    data.bills.filter(b=>shouldGenerateForMonth(b,currentYear,currentMonth)&&(b.name.toLowerCase().includes(buscaContas.toLowerCase())||b.category.toLowerCase().includes(buscaContas.toLowerCase()))&&(!categoriaFiltroContas||b.category===categoriaFiltroContas)).slice().sort(sortBills).length===0?<div className="empty">Nenhuma despesa encontrada com esse filtro.</div>:
                    data.bills.filter(b=>shouldGenerateForMonth(b,currentYear,currentMonth)&&(b.name.toLowerCase().includes(buscaContas.toLowerCase())||b.category.toLowerCase().includes(buscaContas.toLowerCase()))&&(!categoriaFiltroContas||b.category===categoriaFiltroContas)).slice().sort(sortBills).map((bill)=>{
                      // Destaque especial para faturas geradas automaticamente
                      if(bill.isFaturaGerada){
                        const isPaga = Object.values(bill.payments||{}).some(p=>p.entries?.length>0);
                        return(
                          <div key={bill.id} style={{background:`linear-gradient(135deg,${bill.cartaoCor||"#7C83FF"}18,${bill.cartaoCor||"#7C83FF"}08)`,border:`2px solid ${bill.cartaoCor||"#7C83FF"}44`,borderRadius:12,padding:"12px 16px",display:"flex",flexDirection:"column",gap:8}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                              <div>
                                <div style={{fontWeight:800,fontSize:14,color:"var(--text-primary)",display:"flex",alignItems:"center",gap:6}}>
                                  💳 {bill.name}
                                  <span style={{background:bill.cartaoCor+"33",color:bill.cartaoCor,fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10}}>Fatura</span>
                                  {isPaga&&<span style={{background:"#22c55e22",color:"#22c55e",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10}}>✅ Paga</span>}
                                </div>
                                <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>Vence dia {bill.dueDay} · Gerada automaticamente</div>
                                <div style={{fontSize:10,color:"#F59E0B",marginTop:4,background:"#F59E0B18",borderRadius:6,padding:"4px 8px",display:"inline-block"}}>
                                  ⚠️ Confira no app do banco antes de pagar — pode haver cobranças não cadastradas
                                </div>
                              </div>
                              <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                                <div style={{fontWeight:900,fontSize:16,color:bill.cartaoCor||"#7C83FF"}}>{currency(bill.amount)}</div>
                              </div>
                            </div>
                            <div className="row-actions">
                              <button className="icon-btn" onClick={()=>editBill(bill.id)} title="Editar valor">✏️</button>
                              <button className="icon-btn" onClick={()=>deleteBill(bill.id)} title="Excluir">🗑️</button>
                            </div>
                          </div>
                        );
                      }
                      // Renderização normal para demais despesas
                      // isParcelado: frequency parcelado OU qualquer bill com installments > 0 (compatibilidade com dados antigos)
const isParcelado=bill.installments>0;
                      const now=new Date();
                      const pagas=isParcelado?Math.min(bill.installments,(now.getFullYear()*12+now.getMonth())-(bill.startYear*12+bill.startMonth)+1):0;
                      const pct=isParcelado&&bill.installments>0?Math.round((pagas/bill.installments)*100):0;
                      return(
                        <div className="table-row" key={bill.id}>
                          <div>
                            <strong>{CATEGORY_ICONS[bill.category]||"📦"} {bill.name}</strong>
                            <div className="muted" style={{fontSize:11,marginTop:2}}>{bill.category}</div>
                            {bill.endYear!=null&&bill.endMonth!=null&&(
                              <div style={{fontSize:10,color:"#ef4444",marginTop:4,background:"#ef444418",borderRadius:6,padding:"2px 8px",display:"inline-block"}}>
                                🔒 Encerrada em {MONTHS_PT[bill.endMonth]}/{bill.endYear}
                              </div>
                            )}
                            {isParcelado&&bill.installments>0&&(
                              <div style={{marginTop:4}}>
                                <div style={{height:4,background:"var(--border)",borderRadius:2,overflow:"hidden",width:120}}>
                                  <div style={{height:"100%",width:`${pct}%`,background:pct>=100?"#22c55e":"#7C83FF",borderRadius:2}}/>
                                </div>
                                <div style={{fontSize:10,color:"var(--text-muted)",marginTop:2}}>{pagas}/{bill.installments} parcelas · {pct}%</div>
                              </div>
                            )}
                          </div>
                          <div>{currency(bill.amount)}</div>
                          <div>Dia {bill.dueDay}</div>
                          <div>{getInstallmentCountLabel(bill)}</div>
                          <div><div>{bill.paymentMethod}</div><div className="muted" style={{fontSize:11,marginTop:2}}>{MONTHS_PT[bill.startMonth]}/{bill.startYear}</div></div>
                          <div className="row-actions">
                            {isParcelado&&bill.installments>0&&(
                              <button className="icon-btn" onClick={()=>setDebtModal(bill)} title="Ver progresso">📊</button>
                            )}
                            <button className="icon-btn" onClick={()=>editBill(bill.id)} title="Editar">✏️</button>
                            {bill.endYear!=null&&bill.endMonth!=null?(
                              <button className="icon-btn" onClick={()=>reativarBill(bill.id)} title="Reativar despesa">↩️</button>
                            ):(
                              <button className="icon-btn" onClick={()=>abrirEncerrarBill(bill)} title="Encerrar despesa (mantém o histórico já pago)">🔒</button>
                            )}
                            <button className="icon-btn" onClick={()=>deleteBill(bill.id)} title="Excluir">🗑️</button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </section>
            )}

            {/* Modal: despesas cadastradas, com foco no mês em navegação — mostra quanto se gasta
                por categoria naquele mês, com opção de alternar para ver todos os meses/recorrências */}
            <Modal open={verTodasCadastradas} onClose={()=>{setVerTodasCadastradas(false);setCategoriaFiltroTodas("");setBuscaTodasCadastradas("");}} title={`📂 Despesas por categoria — ${modoCadastro==="mes"?`${MONTHS_PT[currentMonth]}/${currentYear}`:"todos os meses"}`} width={640}>
              {(()=>{
                const refKey=monthKey(currentYear,currentMonth);
                const base=data.bills.filter(b=>!b.isFaturaGerada);
                // "mes": só as despesas que existem no mês em navegação, com o valor real daquele mês
                // (respeita ajuste pontual/valorAjustado). "todos": todas as despesas já cadastradas.
                const cadastradas=modoCadastro==="mes"?base.filter(b=>shouldGenerateForMonth(b,currentYear,currentMonth)):base;
                const valorExibido=(bill)=>modoCadastro==="mes"?getBillAmountForMonth(bill,refKey):bill.amount;
                const filtradas=cadastradas.filter(b=>(!categoriaFiltroTodas||b.category===categoriaFiltroTodas)&&b.name.toLowerCase().includes(buscaTodasCadastradas.toLowerCase()));
                const porCategoria={};
                for(const b of filtradas){(porCategoria[b.category]=porCategoria[b.category]||[]).push(b);}
                const categoriasOrdenadas=Object.keys(porCategoria).sort((a,b)=>a.localeCompare(b));
                const totalGeral=round2(filtradas.reduce((s,b)=>s+valorExibido(b),0));
                return(<>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
                    <button type="button" className={`nav-btn ${modoCadastro==="mes"?"active":""}`} onClick={()=>setModoCadastro("mes")} style={{flex:1}}>📅 {MONTHS_PT[currentMonth]}/{currentYear}</button>
                    <button type="button" className={`nav-btn ${modoCadastro==="todos"?"active":""}`} onClick={()=>setModoCadastro("todos")} style={{flex:1}}>🗂️ Todos os meses</button>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                    <input className="input" placeholder="🔍 Buscar por nome..." value={buscaTodasCadastradas} onChange={e=>setBuscaTodasCadastradas(e.target.value)} style={{flex:2,minWidth:160}}/>
                    <select className="select" value={categoriaFiltroTodas} onChange={e=>setCategoriaFiltroTodas(e.target.value)} style={{flex:1,minWidth:150}}>
                      <option value="">Todas as categorias</option>
                      {EXPENSE_CATEGORIES.map(c=><option key={c} value={c}>{CATEGORY_ICONS[c]||"📦"} {c}</option>)}
                    </select>
                  </div>
                  {categoriaFiltroTodas&&(
                    <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:14}}>
                      Total em <strong style={{color:"var(--text-primary)"}}>{categoriaFiltroTodas}</strong>{modoCadastro==="mes"?` em ${MONTHS_PT[currentMonth]}/${currentYear}`:""}: <strong style={{color:"var(--text-primary)"}}>{currency(totalGeral)}</strong>
                    </div>
                  )}
                  {filtradas.length===0?(
                    <div className="empty">
                      {categoriaFiltroTodas
                        ?`Nenhuma despesa cadastrada em ${categoriaFiltroTodas}${modoCadastro==="mes"?` em ${MONTHS_PT[currentMonth]}/${currentYear}`:""}.`
                        :`Nenhuma despesa cadastrada${modoCadastro==="mes"?` em ${MONTHS_PT[currentMonth]}/${currentYear}`:""}.`}
                    </div>
                  ):categoriasOrdenadas.map(cat=>{
                    const itens=porCategoria[cat].slice().sort((a,b)=>a.name.localeCompare(b.name));
                    const total=round2(itens.reduce((s,b)=>s+valorExibido(b),0));
                    return(
                      <div key={cat} style={{marginBottom:16}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                          <div style={{fontWeight:800,fontSize:13,color:"var(--text-primary)"}}>{CATEGORY_ICONS[cat]||"📦"} {cat} <span style={{color:"var(--text-muted)",fontWeight:400}}>({itens.length})</span></div>
                          <div style={{fontSize:12,color:"var(--text-muted)"}}>{currency(total)}{modoCadastro==="mes"?"":"/mês (base)"}</div>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {itens.map(bill=>(
                            <div key={bill.id} onClick={()=>{setVerTodasCadastradas(false);editBill(bill.id);}}
                              style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",borderRadius:10,border:"1px solid var(--border)",background:"var(--bg-card)",cursor:"pointer"}}>
                              <div>
                                <div style={{fontWeight:700,fontSize:13,color:"var(--text-primary)"}}>{bill.name}</div>
                                <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>{getInstallmentCountLabel(bill)} · dia {bill.dueDay} · {bill.paymentMethod}</div>
                                {modoCadastro==="todos"&&<div style={{fontSize:11,color:"#F59E0B",marginTop:2,fontWeight:600}}>📅 {getBillPeriodoLabel(bill)}</div>}
                              </div>
                              <div style={{fontWeight:700,fontSize:13,color:"var(--text-primary)"}}>{currency(valorExibido(bill))}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>);
              })()}
            </Modal>

            {activeTab==="entradas"&&(
              <section className="section-card">
                <div style={{display:"flex",justifyContent:"space-between",gap:12,alignItems:"center",flexWrap:"wrap",marginBottom:12}}>
                  <h2 className="section-title" style={{margin:0}}>Registro de entradas</h2>
                  <div style={{display:"flex",gap:8}}>
                    <button className="secondary-btn" style={{fontSize:13,background:"#14B8A622",border:"1px solid #14B8A644",color:"#14B8A6"}} onClick={()=>setResgateModal(true)}>💸 Registrar resgate</button>
                    <button className="primary-btn" onClick={openNewEntryForm}>+ Nova entrada</button>
                  </div>
                </div>
                <div className="entries-list">
                  {monthEntries.length>1&&<input className="input" placeholder="🔍 Buscar entrada..." value={buscaEntradas} onChange={e=>setBuscaEntradas(e.target.value)} style={{marginBottom:10}}/>}
                  {monthEntries.length===0?<div className="empty">Nenhuma entrada neste mês.</div>:(
                    monthEntries.filter(e=>e.name.toLowerCase().includes(buscaEntradas.toLowerCase())||e.category.toLowerCase().includes(buscaEntradas.toLowerCase())).slice().sort((a,b)=>(a.day||0)-(b.day||0)||a.name.localeCompare(b.name)).map((entry)=>(
                      <div className="entry-card" key={entry.id}>
                        <div className="entry-left">
                          <div className="entry-icon">{CATEGORY_ICONS[entry.category]||"💰"}</div>
                          <div className="entry-main">
                            <div className="entry-name">{entry.name}</div>
                            <div className="entry-meta"><span>{entry.category}</span><span>{entry.paymentMethod}</span><span>{entry.day?`Dia ${entry.day} · `:""}{MONTHS_PT[entry.month]}/{entry.year}</span></div>
                          </div>
                        </div>
                        <div className="entry-right">
                          <div className="entry-amount">{currency(entry.amount)}</div>
                          <div style={{display:"flex",gap:5}}><button className="icon-btn" onClick={()=>editEntry(entry.id)}>✏️</button><button className="icon-btn" onClick={()=>deleteEntry(entry.id)}>🗑️</button></div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>
            )}

            {activeTab==="metas"&&<MetasTab goals={data.goals} onSave={saveGoal} onDelete={deleteGoal} onAddValue={addGoalValue} onResgate={executarResgateDirecto}/>}

            {activeTab==="orcamento"&&<OrcamentoTab budgets={budgetsWithSpent} onSave={saveBudget} onDelete={deleteBudget} spentByCategory={spentByCategory} mediaCategorias={mediaCategorias}/>}

            {activeTab==="graficos"&&(
              <div className="chart-grid">
                <section className="chart-card"><div className="chart-title">Total pago por categoria</div>{chartData.byCategory.length>0?<DonutChart data={chartData.byCategory}/>:<div className="empty">Sem pagamentos neste mês.</div>}</section>
              </div>
            )}

            {activeTab==="evolucao"&&<EvolucaoTab evolutionData={evolutionData}/>}



            {activeTab==="perfil"&&<PerfilTab session={session} profile={data.profile||{}} onSave={saveProfile} onDeleteAccount={handleDeleteAccount}/>}

            {activeTab==="5g"&&<MetodoG5Tab bills={data.bills} entries={data.entries} goals={data.goals} monthMetrics={monthMetrics} currentYear={currentYear} currentMonth={currentMonth} desafios={data.profile?.desafios||[]} investimentos={data.profile?.investimentos||[]} rendaFixa={data.profile?.rendaFixa||[]} onResgate={()=>setResgateModal(true)}/>}

            {activeTab==="cartoes"&&<CartoesTab
              cartoes={data.profile?.cartoes||[]}
              bills={data.bills}
              currentYear={currentYear}
              currentMonth={currentMonth}
              plano={plano}
              onSave={(c)=>setData(d=>({...d,profile:{...d.profile,cartoes:c}}))}
              onGoToPlanos={()=>setActiveTab("planos")}
            />}

            {activeTab==="planos"&&<PlanosTab plano={plano} totalDespesas={data.bills.length} userId={session.user.id} userEmail={session.user.email} trialExpira={data.profile?.trial_expira}/>}

            {activeTab==="desafios"&&<DesafiosTab desafios={data.profile?.desafios||[]} onSave={(d)=>setData(prev=>({...prev,profile:{...prev.profile,desafios:d}}))} bills={data.bills} onSaveBills={(b)=>setData(prev=>({...prev,bills:b}))} investimentos={data.profile?.investimentos||[]} onSaveInvestimentos={(inv)=>setData(prev=>({...prev,profile:{...prev.profile,investimentos:inv}}))} onRegistrarDividendo={registrarDividendo} currentYear={currentYear} currentMonth={currentMonth}/>}

            {activeTab==="noticias"&&<NoticiasTab/>}

            {activeTab==="invest"&&(!isPremium&&!isBasico?(
              <section className="section-card">
                {/* Banner de upgrade */}
                <div style={{background:"linear-gradient(135deg,#7C83FF22,#a855f722)",border:"2px solid #7C83FF44",borderRadius:14,padding:"16px 20px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
                  <div>
                    <div style={{fontWeight:800,fontSize:15,color:"#7C83FF",marginBottom:4}}>🔒 Recurso do plano Básico ou Premium</div>
                    <div style={{fontSize:13,color:"var(--text-muted)"}}>Veja como funciona e faça upgrade para usar!</div>
                  </div>
                  <button className="primary-btn" onClick={()=>setActiveTab("planos")}>Ver planos →</button>
                </div>
                {/* Preview bloqueado */}
                <div style={{opacity:0.4,pointerEvents:"none",userSelect:"none"}}>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:20}}>
                    {[{label:"💰 Patrimônio total",valor:"R$ 2.450,00"},{label:"📥 Total investido",valor:"R$ 1.800,00"},{label:"📈 Resultado",valor:"+R$ 650,00"},{label:"🐷 Cofrinhos",valor:"R$ 580,00"}].map((c,i)=>(
                      <div key={i} style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:"14px 12px",textAlign:"center"}}>
                        <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:4}}>{c.label}</div>
                        <div style={{fontSize:16,fontWeight:900,color:"var(--text-primary)"}}>{c.valor}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:16,marginBottom:12}}>
                    <div style={{fontWeight:700,fontSize:15,marginBottom:12}}>📈 Meus Investimentos</div>
                    {[{emoji:"₿",nome:"Bitcoin",simbolo:"BTC",qtd:"0,005",valor:"R$ 1.950,00",result:"+38%"},{emoji:"⟠",nome:"Ethereum",simbolo:"ETH",qtd:"0,12",valor:"R$ 1.380,00",result:"+22%"}].map((inv,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid var(--border)"}}>
                        <div><div style={{fontWeight:700}}>{getEmojiAtivo(inv)} {inv.nome}</div><div style={{fontSize:11,color:"var(--text-muted)"}}>{inv.qtd} {inv.simbolo}</div></div>
                        <div style={{textAlign:"right"}}><div style={{fontWeight:800}}>{inv.valor}</div><div style={{fontSize:11,color:"#22c55e"}}>{inv.result}</div></div>
                      </div>
                    ))}
                  </div>
                  <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:14,padding:16}}>
                    <div style={{fontWeight:700,fontSize:15,marginBottom:12}}>🏦 Renda Fixa</div>
                    {[{nome:"CDB Banco XP",tipo:"CDB",invest:"R$ 1.000,00",rend:"+R$ 85,50",total:"R$ 1.085,50"}].map((r,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div><div style={{fontWeight:700}}>{r.nome}</div><div style={{fontSize:11,color:"var(--text-muted)"}}>{r.tipo}</div></div>
                        <div style={{textAlign:"right"}}><div style={{fontWeight:800,color:"#14B8A6"}}>{r.total}</div><div style={{fontSize:11,color:"#22c55e"}}>{r.rend}</div></div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            ):<CarteiraInvestTab profile={data.profile||{}} onSave={(inv)=>setData(d=>({...d,profile:{...d.profile,investimentos:inv}}))} investimentos={data.profile?.investimentos||[]} onResgate={executarResgateDirecto} onRegistrarSaida={registrarSaidaInvestimento} onRegistrarDividendo={registrarDividendo} desafios={data.profile?.desafios||[]} rendaFixa={data.profile?.rendaFixa||[]} onSaveRendaFixa={(rf)=>setData(d=>({...d,profile:{...d.profile,rendaFixa:rf}}))} futureData={futureData} entries={data.entries} bills={data.bills} plano={plano} />)}
          </main>

          <ExpenseFormModal open={expenseFormOpen} onClose={closeExpenseForm} onSave={saveBill} editingBill={editingBill} cartoes={data.profile?.cartoes||[]} onGastoRapido={()=>setGastoRapidoOpen(true)} currentKey={currentKey}/>
          <EntryFormModal open={entryFormOpen} onClose={closeEntryForm} onSave={saveEntry} editingEntry={editingEntry}/>

          {resgateModal&&(()=>{
            const GUIAS_RESGATE={
              "Ativos (cripto/ações/FIIs)":{"icon":"📊","passos":["Confirme o resgate abaixo para criar a entrada no caixa","Acesse a aba Investimentos","Clique em ✏️ no ativo vendido e atualize a quantidade restante (ou exclua se vendeu tudo)"]},
              "Reserva de Emergência":{"icon":"🆘","passos":["Confirme o resgate abaixo para criar a entrada no caixa","Acesse a aba Despesas","Encontre o lançamento de Reserva de Emergência e registre o valor resgatado como pago parcialmente, ou reduza o valor cadastrado"]},
              "Meta (poupança específica)":{"icon":"🎯","passos":["Confirme o resgate abaixo para criar a entrada no caixa","Acesse a aba Metas","Clique em ✏️ na meta e reduza o valor acumulado (campo Já tenho)"]},
              "Desafio financeiro":{"icon":"🏆","passos":["Confirme o resgate abaixo para criar a entrada no caixa","Acesse a aba Desafios","O valor economizado do desafio será mantido no histórico — considere encerrar o desafio se não for continuar"]},
              "Investimento/Aporte (lançamento manual)":{"icon":"💰","passos":["Confirme o resgate abaixo para criar a entrada no caixa","Acesse a aba Despesas","Encontre o lançamento de Investimentos correspondente e ajuste o valor ou exclua se foi resgate total"]},
            };
            const valorResgate=numberFromInput(resgateValor);
            const totalGuardar=(()=>{
              const billsG=data.bills.filter(b=>b.category==="Investimentos"||b.category==="Reserva de Emergência");
              return billsG.reduce((s,b)=>{
                const totalPago=Object.values(b.payments||{}).reduce((ps,rec)=>ps+(rec.entries||[]).reduce((es,e)=>es+Number(e.amount||0),0),0);
                const totalResgatado=(b.resgates||[]).reduce((rs,r)=>rs+Number(r.valor||0),0);
                const vb=totalPago>0?totalPago:Number(b.amount||0);
                return s+Math.max(0,vb-totalResgatado);
              },0)
              +(data.goals||[]).reduce((s,g)=>s+Number(g.currentAmount||0),0)
              +(data.profile?.desafios||[]).reduce((s,d)=>s+Number(d.economiaTotal||0),0)
              +(data.profile?.investimentos||[]).reduce((s,i)=>s+Number(i.quantidade||0)*Number(i.precoMedio||0),0);
            })();
            const aposResgate=Math.max(0,totalGuardar-valorResgate);
            const impacto=valorResgate>0&&valorResgate<=totalGuardar;
            const semSaldo=valorResgate>totalGuardar&&valorResgate>0;
            return(
              <div onClick={()=>setResgateModal(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg-main)",border:"1px solid var(--border)",borderRadius:18,width:"100%",maxWidth:420,padding:24}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:28}}>💸</span>
                      <div>
                        <div style={{fontWeight:800,fontSize:16,color:"var(--text-primary)"}}>Registrar resgate</div>
                        <div style={{fontSize:12,color:"var(--text-muted)"}}>Retira do Guardar e entra no caixa</div>
                      </div>
                    </div>
                    <button onClick={()=>setResgateModal(false)} style={{background:"none",border:"none",fontSize:20,color:"var(--text-muted)",cursor:"pointer"}}>✕</button>
                  </div>

                  <div style={{marginBottom:14}}>
                    <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>Valor do resgate (R$)</label>
                    <input
                      className="input"
                      type="text"
                      inputMode="decimal"
                      placeholder="Ex.: 500,00"
                      value={resgateValor}
                      onChange={e=>setResgateValor(e.target.value)}
                      autoFocus
                      style={{width:"100%",fontSize:18,fontWeight:700}}
                    />
                  </div>

                  <div style={{marginBottom:14}}>
                    <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>De onde está saindo?</label>
                    <select
                      className="input"
                      value={resgateOrigem}
                      onChange={e=>setResgateOrigem(e.target.value)}
                      style={{width:"100%"}}
                    >
                      <option value="">Selecione a origem...</option>
                      {Object.keys(GUIAS_RESGATE).map(k=>(
                        <option key={k} value={k}>{GUIAS_RESGATE[k].icon} {k}</option>
                      ))}
                    </select>
                  </div>

                  {/* Guia de ações por origem */}
                  {resgateOrigem&&(
                    <div style={{borderRadius:10,padding:"12px 14px",marginBottom:14,background:"#7C83FF18",border:"1px solid #7C83FF44"}}>
                      <div style={{fontSize:12,fontWeight:700,color:"#7C83FF",marginBottom:8}}>📋 O que fazer após confirmar</div>
                      {GUIAS_RESGATE[resgateOrigem].passos.map((passo,i)=>(
                        <div key={i} style={{display:"flex",gap:8,marginBottom:6,alignItems:"flex-start"}}>
                          <div style={{minWidth:18,height:18,borderRadius:"50%",background:"#7C83FF",color:"#fff",fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>{i+1}</div>
                          <div style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.5}}>{passo}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{marginBottom:16}}>
                    <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>Descrição (opcional)</label>
                    <input
                      className="input"
                      type="text"
                      placeholder="Ex.: Resgate reserva emergência"
                      value={resgateDesc}
                      onChange={e=>setResgateDesc(e.target.value)}
                      style={{width:"100%"}}
                    />
                  </div>

                  {/* Aviso de impacto */}
                  {valorResgate>0&&(
                    <div style={{borderRadius:10,padding:"12px 14px",marginBottom:20,background:semSaldo?"#EF444422":impacto?"#F59E0B22":"var(--bg-card)",border:`1px solid ${semSaldo?"#EF4444":impacto?"#F59E0B":"var(--border)"}`}}>
                      <div style={{fontSize:12,fontWeight:700,color:semSaldo?"#EF4444":impacto?"#F59E0B":"var(--text-muted)",marginBottom:8}}>
                        {semSaldo?"⚠️ Valor maior que o saldo do Guardar":"📊 Impacto no patrimônio"}
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:4}}>
                        <span style={{color:"var(--text-muted)"}}>Guardar atual</span>
                        <span style={{fontWeight:700,color:"#14B8A6"}}>{currency(totalGuardar)}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:4}}>
                        <span style={{color:"var(--text-muted)"}}>Resgate</span>
                        <span style={{fontWeight:700,color:"#EF4444"}}>− {currency(valorResgate)}</span>
                      </div>
                      <div style={{borderTop:"1px solid var(--border)",paddingTop:8,marginTop:4,display:"flex",justifyContent:"space-between",fontSize:14}}>
                        <span style={{color:"var(--text-muted)",fontWeight:700}}>Guardar após resgate</span>
                        <span style={{fontWeight:800,color:semSaldo?"#EF4444":"#14B8A6"}}>{currency(aposResgate)}</span>
                      </div>
                      {!semSaldo&&(
                        <div style={{marginTop:10,fontSize:11,color:"var(--text-muted)",lineHeight:1.5}}>
                          ✅ Uma entrada de <strong style={{color:"#22C55E"}}>{currency(valorResgate)}</strong> será criada automaticamente com a categoria <strong>Resgate de Investimentos</strong>.
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{display:"flex",gap:10}}>
                    <button className="ghost-btn" style={{flex:1}} onClick={()=>setResgateModal(false)}>Cancelar</button>
                    <button
                      className="primary-btn"
                      style={{flex:2,background:semSaldo?"#EF4444":undefined,opacity:valorResgate<=0?0.5:1}}
                      disabled={valorResgate<=0}
                      onClick={saveResgate}
                    >
                      {semSaldo?"⚠️ Resgatar mesmo assim":"Confirmar resgate"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Modal de progresso de dívida */}
          {debtModal&&(()=>{
            const bill=debtModal;
            const now=new Date();
            const pagas=Math.min(bill.installments,Math.max(0,(now.getFullYear()*12+now.getMonth())-(bill.startYear*12+bill.startMonth)+1));
            const restantes=bill.installments-pagas;
            const totalGeral=round2(bill.amount*bill.installments);
            const totalPago=round2(bill.amount*pagas);
            const totalFalta=round2(bill.amount*restantes);
            const pct=bill.installments>0?Math.round((pagas/bill.installments)*100):0;
            const termino=new Date(bill.startYear,bill.startMonth+bill.installments-1);
            const cur=v=>new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(v);
            // Simulação de antecipação
            const extra=parseFloat(extraMensal.replace(",","."))||0;
            const taxa=parseFloat(taxaJuros.replace(",","."))||0;
            const taxaMensal=taxa/100;

            // Simulação com juros compostos (Price/SAC simplificado)
            // Calcula saldo devedor atual e impacto do pagamento extra
            let novoTermino=null,economiaJuros=0;
            if(restantes>0){
              if(taxaMensal>0){
                // Saldo devedor atual pelo sistema Price
                // P = PMT * (1-(1+i)^-n) / i
                const saldoAtual=round2(bill.amount*(1-Math.pow(1+taxaMensal,-restantes))/taxaMensal);
                // Com pagamento extra: reduz o saldo devedor
                if(extra>0){
                  const saldoAposExtra=Math.max(0,saldoAtual-extra);
                  if(saldoAposExtra>0){
                    // Quantas parcelas restam após o extra
                    const novasParc=Math.ceil(-Math.log(1-saldoAposExtra*taxaMensal/bill.amount)/Math.log(1+taxaMensal));
                    const parcelasEconomizadas=Math.max(0,restantes-novasParc);
                    const totalSemExtra=bill.amount*restantes;
                    const totalComExtra=extra+bill.amount*novasParc;
                    economiaJuros=round2(totalSemExtra-totalComExtra);
                    const dataNovoTermino=new Date(termino);
                    dataNovoTermino.setMonth(dataNovoTermino.getMonth()-parcelasEconomizadas);
                    novoTermino={data:dataNovoTermino,economizadas:parcelasEconomizadas};
                  } else {
                    // Pagamento extra quita tudo
                    economiaJuros=round2(bill.amount*restantes-extra);
                    novoTermino={data:new Date(),economizadas:restantes,quitado:true};
                  }
                }
              } else if(extra>0){
                // Sem juros — lógica simples
                const parcelasMenos=Math.min(Math.floor(extra/bill.amount),restantes);
                economiaJuros=round2(parcelasMenos*bill.amount);
                const dataNovoTermino=new Date(termino);
                dataNovoTermino.setMonth(dataNovoTermino.getMonth()-parcelasMenos);
                if(parcelasMenos>0) novoTermino={data:dataNovoTermino,economizadas:parcelasMenos};
              }
            }

            return(
              <div onClick={()=>setDebtModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg-main)",border:"1px solid var(--border)",borderRadius:18,width:"100%",maxWidth:460,maxHeight:"85vh",overflowY:"auto",padding:24}}>
                  {/* Header */}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
                    <div>
                      <div style={{fontWeight:800,fontSize:16,color:"var(--text-primary)"}}>{CATEGORY_ICONS[bill.category]||"📦"} {bill.name}</div>
                      <div style={{fontSize:12,color:"var(--text-muted)",marginTop:2}}>{bill.category} · {bill.paymentMethod}</div>
                    </div>
                    <button onClick={()=>{setDebtModal(null);setExtraMensal("");setTaxaJuros("");}} style={{background:"none",border:"none",fontSize:20,color:"var(--text-muted)",cursor:"pointer"}}>✕</button>
                  </div>

                  {/* Barra de progresso grande */}
                  <div style={{marginBottom:20}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"var(--text-muted)",marginBottom:6}}>
                      <span>Progresso do pagamento</span>
                      <span style={{fontWeight:700,color:pct>=100?"#22c55e":"#7C83FF"}}>{pct}%</span>
                    </div>
                    <div style={{height:14,background:"var(--border)",borderRadius:7,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pct}%`,background:pct>=100?"linear-gradient(90deg,#22c55e,#16a34a)":"linear-gradient(90deg,#7C83FF,#6366f1)",borderRadius:7,transition:"width .5s"}}/>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--text-muted)",marginTop:4}}>
                      <span>{pagas} pagas</span>
                      <span>{restantes} restantes</span>
                    </div>
                  </div>

                  {/* Cards de resumo */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:20}}>
                    {[
                      {label:"Total da dívida",valor:totalGeral,cor:"var(--text-primary)"},
                      {label:"✅ Já pago",valor:totalPago,cor:"#22c55e"},
                      {label:"⏳ Falta pagar",valor:totalFalta,cor:"#EF4444"},
                    ].map((c,i)=>(
                      <div key={i} style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 8px",textAlign:"center"}}>
                        <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:4,lineHeight:1.3}}>{c.label}</div>
                        <div style={{fontSize:13,fontWeight:800,color:c.cor}}>{cur(c.valor)}</div>
                      </div>
                    ))}
                  </div>

                  {/* Detalhes */}
                  <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:10,padding:"12px 14px",marginBottom:20,fontSize:13}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                      <span style={{color:"var(--text-muted)"}}>Parcela mensal</span>
                      <span style={{fontWeight:700}}>{cur(bill.amount)}</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                      <span style={{color:"var(--text-muted)"}}>Início</span>
                      <span>{MONTHS_PT[bill.startMonth]}/{bill.startYear}</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <span style={{color:"var(--text-muted)"}}>📅 Previsão de término</span>
                      <span style={{fontWeight:700,color:"#7C83FF"}}>{MONTHS_PT[termino.getMonth()]}/{termino.getFullYear()}</span>
                    </div>
                  </div>

                  {/* Simulação de antecipação com juros */}
                  {restantes>0&&(
                    <div style={{background:"#F59E0B18",border:"1px solid #F59E0B44",borderRadius:10,padding:"12px 14px"}}>
                      <div style={{fontWeight:700,fontSize:13,color:"#F59E0B",marginBottom:4}}>💡 Simulação de antecipação</div>
                      <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:10,lineHeight:1.5}}>
                        ⚠️ <em>Esta é uma simulação estimada. O valor real pode variar conforme as regras do seu banco ou financeira.</em>
                      </div>

                      {/* Campo de juros */}
                      <div style={{marginBottom:10}}>
                        <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>
                          Taxa de juros mensal (%) — opcional
                        </label>
                        <input
                          className="input"
                          type="text"
                          inputMode="decimal"
                          placeholder="Ex.: 2,5 (deixe vazio se sem juros)"
                          value={taxaJuros}
                          onChange={e=>setTaxaJuros(e.target.value)}
                          style={{width:"100%"}}
                        />
                        {taxa>0&&(
                          <div style={{fontSize:11,color:"var(--text-muted)",marginTop:3}}>
                            {taxa}% a.m. = ~{(round2(Math.pow(1+taxaMensal,12)-1)*100).toFixed(1)}% ao ano
                          </div>
                        )}
                      </div>

                      {/* Campo de valor extra */}
                      <div style={{marginBottom:10}}>
                        <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>
                          Valor extra para antecipar (R$)
                        </label>
                        <input
                          className="input"
                          type="text"
                          inputMode="decimal"
                          placeholder={`Ex.: ${cur(bill.amount)} (1 parcela)`}
                          value={extraMensal}
                          onChange={e=>setExtraMensal(e.target.value)}
                          style={{width:"100%"}}
                        />
                      </div>

                      {/* Resultado */}
                      {extra>0&&novoTermino&&(
                        <div style={{background:"var(--bg-card)",borderRadius:8,padding:"10px 12px",fontSize:13,lineHeight:1.9}}>
                          {novoTermino.quitado?(
                            <div style={{color:"#22c55e",fontWeight:700,textAlign:"center",fontSize:14}}>🎉 Dívida quitada com este pagamento!</div>
                          ):(
                            <>
                              <div>✅ Elimina <strong style={{color:"#22c55e"}}>{novoTermino.economizadas} parcela(s)</strong></div>
                              {economiaJuros>0&&<div>💰 Economia estimada: <strong style={{color:"#22c55e"}}>{cur(economiaJuros)}</strong>{taxa>0?" (incluindo juros)":""}</div>}
                              <div>📅 Novo término estimado: <strong style={{color:"#7C83FF"}}>{MONTHS_PT[novoTermino.data.getMonth()]}/{novoTermino.data.getFullYear()}</strong></div>
                            </>
                          )}
                        </div>
                      )}
                      {extra>0&&!novoTermino&&taxa===0&&(
                        <div style={{fontSize:12,color:"var(--text-muted)"}}>⚠️ Valor menor que uma parcela — informe a taxa de juros para ver o impacto no saldo devedor.</div>
                      )}

                      {/* Aviso legal */}
                      <div style={{marginTop:10,fontSize:10,color:"var(--text-muted)",lineHeight:1.5,borderTop:"1px solid var(--border)",paddingTop:8}}>
                        📌 Simulação baseada no sistema Price (juros compostos). Cada banco ou financeira pode usar métodos diferentes (SAC, SACRE, etc.). Consulte sempre o extrato oficial da sua dívida para valores exatos.
                      </div>
                    </div>
                  )}

                  <button className="ghost-btn" style={{width:"100%",marginTop:16}} onClick={()=>{setDebtModal(null);setExtraMensal("");setTaxaJuros("");}}>Fechar</button>
                </div>
              </div>
            );
          })()}

          {/* Modal: Encerrar despesa (mantém histórico já pago) */}
          {encerrarModal&&(()=>{
            const{bill,mes,ano}=encerrarModal;
            const anoAtual=new Date().getFullYear();
            const anos=[anoAtual-1,anoAtual,anoAtual+1,anoAtual+2];
            return(
              <div onClick={()=>setEncerrarModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg-main)",border:"1px solid var(--border)",borderRadius:18,width:"100%",maxWidth:420,padding:24}}>
                  <h3 style={{margin:"0 0 8px",fontSize:16}}>🔒 Encerrar "{bill.name}"</h3>
                  <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:16}}>
                    A despesa para de ser cobrada a partir do mês seguinte ao escolhido abaixo. Os meses já lançados e pagos <strong>não são apagados</strong> — todo o histórico continua no app.
                  </div>
                  <div className="field">
                    <label className="label">Último mês em que ela ainda conta</label>
                    <div style={{display:"flex",gap:8}}>
                      <select className="select" value={mes} onChange={e=>setEncerrarModal({...encerrarModal,mes:e.target.value})} style={{flex:1}}>
                        {MONTHS_PT.map((m,i)=><option key={i} value={i}>{m}</option>)}
                      </select>
                      <select className="select" value={ano} onChange={e=>setEncerrarModal({...encerrarModal,ano:e.target.value})} style={{flex:1}}>
                        {anos.map(a=><option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="actions" style={{marginTop:16}}>
                    <button className="ghost-btn" onClick={()=>setEncerrarModal(null)}>Cancelar</button>
                    <button className="primary-btn" onClick={confirmarEncerrarBill}>🔒 Encerrar despesa</button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Banner aviso backup semanal */}
          {activeTab==="painel"&&(!ultimoBackup||((new Date()-new Date(ultimoBackup))/(1000*60*60*24))>7)&&(
            <div style={{background:"#EF444418",border:"1px solid #EF444440",borderRadius:10,padding:"10px 16px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <div style={{fontSize:12,color:"#EF4444",fontWeight:600}}>
                💾 {!ultimoBackup?"Você nunca fez um backup dos seus dados!":`Último backup há ${Math.floor((new Date()-new Date(ultimoBackup))/(1000*60*60*24))} dias — faça um novo!`}
              </div>
              <button onClick={()=>{exportBackup();}} style={{background:"#EF4444",color:"#fff",border:"none",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                💾 Fazer backup
              </button>
            </div>
          )}





          {/* Modal Gasto Rápido */}
          {gastoRapidoOpen&&(
            <div onClick={()=>setGastoRapidoOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"flex-end",justifyContent:"center",padding:16}}>
              <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg-main)",border:"1px solid var(--border)",borderRadius:18,width:"100%",maxWidth:480,padding:24,marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:24}}>⚡</span>
                    <div>
                      <div style={{fontWeight:800,fontSize:16,color:"#F59E0B"}}>Gasto rápido</div>
                      <div style={{fontSize:11,color:"var(--text-muted)"}}>Registrado como pago agora · {new Date().toLocaleDateString("pt-BR")}</div>
                    </div>
                  </div>
                  <button onClick={()=>setGastoRapidoOpen(false)} style={{background:"none",border:"none",fontSize:20,color:"var(--text-muted)",cursor:"pointer"}}>✕</button>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>O que foi?</label>
                  <input className="input" type="text" placeholder="Ex.: Almoço, Uber, Farmácia..." value={gastoRapidoForm.nome} onChange={e=>setGastoRapidoForm(f=>({...f,nome:e.target.value}))} autoFocus style={{width:"100%"}}/>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>Quanto gastou? (R$)</label>
                  <input className="input" type="text" inputMode="decimal" placeholder="Ex.: 35,90" value={gastoRapidoForm.valor} onChange={e=>setGastoRapidoForm(f=>({...f,valor:e.target.value}))} style={{width:"100%",fontSize:20,fontWeight:700}}/>
                </div>
                <div style={{marginBottom:12}}>
                  <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:6}}>Categoria</label>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {["Alimentação","Supermercado","Restaurantes e Delivery","Transporte","Acessórios do Veículo","Saúde","Moradia","Lazer","Comunicação","Educação","Serviços","Outros"].map(cat=>(
                      <button key={cat} type="button" onClick={()=>setGastoRapidoForm(f=>({...f,categoria:cat}))}
                        style={{padding:"5px 12px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${gastoRapidoForm.categoria===cat?"#F59E0B":"var(--border)"}`,background:gastoRapidoForm.categoria===cat?"#F59E0B22":"var(--bg-card)",color:gastoRapidoForm.categoria===cat?"#F59E0B":"var(--text-muted)"}}>
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{marginBottom:20}}>
                  <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:6}}>Forma de pagamento</label>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {[["Débito/Pix","💳"],["Dinheiro","💵"],["Cartão de Crédito","💳"],["Empréstimo","🤝"]].map(([pg,ic])=>(
                      <button key={pg} type="button" onClick={()=>setGastoRapidoForm(f=>({...f,pagamento:pg}))}
                        style={{padding:"5px 12px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${gastoRapidoForm.pagamento===pg?"#F59E0B":"var(--border)"}`,background:gastoRapidoForm.pagamento===pg?"#F59E0B22":"var(--bg-card)",color:gastoRapidoForm.pagamento===pg?"#F59E0B":"var(--text-muted)"}}>
                        {ic} {pg}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={salvarGastoRapido} disabled={!gastoRapidoForm.nome.trim()||!gastoRapidoForm.valor}
                  style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,#F59E0B,#D97706)",color:"#fff",border:"none",borderRadius:12,fontSize:15,fontWeight:800,cursor:"pointer",opacity:(!gastoRapidoForm.nome.trim()||!gastoRapidoForm.valor)?0.5:1}}>
                  ⚡ Registrar {gastoRapidoForm.valor?new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(numberFromInput(gastoRapidoForm.valor)||0):""}
                </button>
                <div style={{fontSize:10,color:"var(--text-muted)",textAlign:"center",marginTop:8}}>
                  Registrado como pago · só este mês · {gastoRapidoForm.pagamento}
                </div>
              </div>
            </div>
          )}

          {/* Modal Assistente de Voz — Porquinho 5G */}
          {voiceOpen&&(
            <div onClick={()=>setVoiceOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:2000,display:"flex",alignItems:"flex-end",justifyContent:"center",padding:16}}>
              <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg-main)",border:"1px solid var(--border)",borderRadius:18,width:"100%",maxWidth:480,padding:24,marginBottom:8}}>

                {/* Header com porquinho */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <img src={porquinho} alt="Porquinho 5G" style={{width:56,height:56,objectFit:"contain"}}/>
                    <div>
                      <div style={{fontWeight:800,fontSize:16,color:"#7C83FF"}}>Assistente Finanças 5G</div>
                      <div style={{fontSize:11,color:"var(--text-muted)"}}>Fale ou digite o que aconteceu</div>
                    </div>
                  </div>
                  <button onClick={()=>setVoiceOpen(false)} style={{background:"none",border:"none",fontSize:20,color:"var(--text-muted)",cursor:"pointer"}}>✕</button>
                </div>

                {!voiceTexto&&!voiceResultado&&!voiceMultiplas&&(
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:8}}>Experimente dizer:</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {["paguei 45 reais de almoço no débito","recebi 3000 de salário","paguei 750 de aluguel"].map(ex=>(
                        <button key={ex} onClick={()=>{setVoiceTexto(ex);interpretarVoz(ex);}}
                          style={{textAlign:"left",background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:8,padding:"8px 12px",fontSize:12,color:"var(--text-primary)",cursor:"pointer"}}>
                          "{ex}"
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{display:"flex",gap:8,marginBottom:12}}>
                  <input className="input" style={{flex:1}} placeholder="Ou digite aqui..."
                    value={voiceTexto} onChange={e=>setVoiceTexto(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&interpretarVoz(voiceTexto)}
                  />
                  {voiceTexto&&(
                    <button onClick={()=>interpretarVoz(voiceTexto)} className="primary-btn" disabled={voiceLoading}>➡️</button>
                  )}
                </div>

                <button onClick={iniciarGravacao} disabled={voiceEscutando||voiceLoading}
                  style={{width:"100%",padding:"14px",background:voiceEscutando?"linear-gradient(135deg,#EF4444,#dc2626)":"linear-gradient(135deg,#7C83FF,#a855f7)",color:"#fff",border:"none",borderRadius:12,fontSize:15,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:12,opacity:(voiceEscutando||voiceLoading)?0.8:1}}>
                  <span style={{fontSize:22}}>{voiceEscutando?"🔴":"🎙️"}</span>
                  {voiceEscutando?"Ouvindo... fale agora!":voiceLoading?"⏳ Interpretando...":"Toque para falar"}
                </button>

                <div style={{fontSize:10,color:"var(--text-muted)",textAlign:"center",marginBottom:12}}>
                  Grátis: até 15 mensagens no total · Básico: 10/dia · Premium: 30/dia
                </div>

                {voiceErro&&<div style={{background:"#EF444418",border:"1px solid #EF444440",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#EF4444",marginBottom:12}}>{voiceErro}</div>}

                {voiceMultiplas&&(
                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#F59E0B",marginBottom:4}}>⚠️ Encontrei mais de um "{voiceMultiplas.nome}"</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {voiceMultiplas.opcoes.map((op,oi)=>(
                        <button key={oi} onClick={()=>{
                          setVoiceResultado({tipo:"pagamento",id:op.id,mesKey:op.mesKey,nome:op.nome,valor:op.valor,pagamento:voiceMultiplas.pagamento||"Débito/Pix"});
                          setVoiceMultiplas(null);
                        }} style={{textAlign:"left",background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:8,padding:"8px 12px",fontSize:12,cursor:"pointer"}}>
                          {op.nome} · {new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(op.valor)} · {op.mes==="atual"?"deste mês":"mês anterior"} {op.status==="ATRASADA"?"(atrasada)":""}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {voiceResultado&&voiceResultado.tipo!=="erro"&&(
                  <div style={{marginBottom:12}}>
                    {voiceResultado.tipo==="pagamento"&&(
                      <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:10,padding:12,marginBottom:12}}>
                        <div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)",marginBottom:4}}>💳 {voiceResultado.nome}</div>
                        <div style={{fontSize:13,color:"var(--text-muted)"}}>
                          Valor: <strong>{new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(voiceResultado.valor)}</strong>
                          {" · "}{voiceResultado.pagamento}
                        </div>
                      </div>
                    )}
                    {voiceResultado.tipo!=="pagamento"&&(
                      <div style={{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:10,padding:12,marginBottom:12,display:"flex",flexDirection:"column",gap:4}}>
                        {[
                          {label:"Tipo",valor:voiceResultado.tipo==="despesa"?"💸 Despesa":"💰 Entrada"},
                          {label:"Nome",valor:voiceResultado.nome},
                          {label:"Valor",valor:new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(voiceResultado.valor)},
                          {label:"Categoria",valor:voiceResultado.categoria},
                          {label:"Pagamento",valor:voiceResultado.pagamento},
                          voiceResultado.parcelas>1&&{label:"Parcelas",valor:`${voiceResultado.parcelas}x`},
                        ].filter(Boolean).map(item=>(
                          <div key={item.label} style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
                            <span className="muted">{item.label}</span><span style={{fontWeight:600}}>{item.valor}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>{setVoiceResultado(null);setVoiceTexto("");}} className="ghost-btn" style={{flex:1}}>Cancelar</button>
                      <button onClick={confirmarVoz} className="primary-btn" style={{flex:2}}>
                        {voiceResultado.tipo==="pagamento"?"✅ Registrar pagamento":"✅ Confirmar e salvar"}
                      </button>
                    </div>
                  </div>
                )}

                {voiceResultado?.tipo==="erro"&&(
                  <div style={{background:"#F59E0B18",border:"1px solid #F59E0B44",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#F59E0B",marginBottom:12}}>
                    ⚠️ {voiceResultado.mensagem} — tente falar de outra forma.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Modal confirmação pós-resgate — Melhoria 3 */}
          {resgateConfirmacao&&(
            <div onClick={()=>setResgateConfirmacao(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
              <div onClick={e=>e.stopPropagation()} style={{background:"var(--bg-main)",border:"1px solid #22c55e44",borderRadius:18,width:"100%",maxWidth:400,padding:24}}>
                <div style={{textAlign:"center",marginBottom:20}}>
                  <div style={{fontSize:40,marginBottom:8}}>✅</div>
                  <div style={{fontWeight:800,fontSize:16,color:"var(--text-primary)"}}>Resgate registrado!</div>
                  <div style={{fontSize:13,color:"var(--text-muted)",marginTop:4}}>
                    Uma entrada de <strong style={{color:"#22c55e"}}>{currency(resgateConfirmacao.valor)}</strong> foi criada no caixa.
                  </div>
                </div>
                <div style={{background:"#14B8A618",border:"1px solid #14B8A630",borderRadius:10,padding:"12px 14px",marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#14B8A6",marginBottom:10}}>📋 Próximo passo</div>
                  <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                    <div style={{minWidth:20,height:20,borderRadius:"50%",background:"#14B8A6",color:"#fff",fontSize:11,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>1</div>
                    <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.5}}>{resgateConfirmacao.instrucao}</div>
                  </div>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button className="ghost-btn" style={{flex:1}} onClick={()=>setResgateConfirmacao(null)}>Fechar</button>
                  <button className="primary-btn" style={{flex:2,background:"#14B8A6"}} onClick={()=>{setActiveTab(resgateConfirmacao.aba);setResgateConfirmacao(null);}}>
                    Ir para {resgateConfirmacao.label} →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal ajuste de valor do mês */}
      {ajusteModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setAjusteModal(null)}>
          <div style={{background:"var(--bg-section)",border:"1px solid var(--border)",borderRadius:18,padding:24,width:"100%",maxWidth:380}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:"0 0 8px",fontSize:16,color:"var(--text-primary)"}}>📝 Ajustar valor do mês</h3>
            <p style={{fontSize:12,color:"var(--text-muted)",margin:"0 0 16px"}}>{ajusteModal.nome}</p>
            <div className="field" style={{marginBottom:16}}>
              <label className="label">Valor real do boleto (R$)</label>
              <input className="input" type="number" step="any"
                placeholder={`Estimado: ${new Intl.NumberFormat("pt-BR",{style:"currency",currency:"BRL"}).format(ajusteModal.valorAtual)}`}
                defaultValue={ajusteModal.valorAtual}
                autoFocus
                id="ajuste-input"
              />
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>
                Só altera o valor deste mês. Os meses seguintes continuam com o valor padrão.
              </div>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="ghost-btn" onClick={()=>setAjusteModal(null)}>Cancelar</button>
              <button className="primary-btn" onClick={()=>{
                const v=parseFloat(document.getElementById("ajuste-input").value)||0;
                salvarAjusteValor(ajusteModal.billId,ajusteModal.refMonthKey,v);
              }}>✅ Salvar valor</button>
            </div>
          </div>
        </div>
      )}

      <Modal open={paymentModal.open} onClose={()=>setPaymentModal({open:false,billId:null,refMonthKey:null})} title={paymentContext?`Pagamento • ${paymentContext.bill.name}`:"Pagamento"}>
            {paymentContext&&(
              <>
                <div className="info-row"><span className="muted">Referência</span><strong>{formatMonthKeyLabel(paymentModal.refMonthKey)}</strong></div>
                <div className="info-row"><span className="muted">Valor {paymentContext.valorMes!==paymentContext.bill.amount?"ajustado":"cadastrado"}</span><strong>{currency(paymentContext.valorMes)}</strong></div>
                <div className="info-row"><span className="muted">Já pago</span><strong>{currency(paymentContext.totalPaid)}</strong></div>
                <div className="info-row"><span className="muted">Restante</span><strong>{currency(paymentContext.remaining)}</strong></div>
                <div className="tab-strip">
                  <button className={`nav-btn ${paymentTab==="total"?"active":""}`} onClick={()=>setPaymentTab("total")}>💯 Total</button>
                  <button className={`nav-btn ${paymentTab==="partial"?"active":""}`} onClick={()=>setPaymentTab("partial")}>✂️ Parcial</button>
                  <button className={`nav-btn ${paymentTab==="quitar"?"active":""}`} onClick={()=>setPaymentTab("quitar")} title="Conta veio num valor diferente do previsto">✅ Quitar</button>
                </div>
                {paymentTab==="total"?(
                  <>
                    <div className="form-grid">
                      <div className="field"><label className="label">Valor pago</label><input className="input" type="text" inputMode="decimal" value={totalPayAmount} onChange={(e)=>setTotalPayAmount(e.target.value)} placeholder="Ex.: 20,90"/></div>
                      <div className="field"><label className="label">Forma de pagamento</label><select className="select" value={totalPayMethod} onChange={(e)=>setTotalPayMethod(e.target.value)}>{PAYMENT_METHODS.map((l)=><option key={l} value={l}>{l}</option>)}</select></div>
                      <div className="field full"><label className="label">Observação</label><input className="input" value={totalPayNote} onChange={(e)=>setTotalPayNote(e.target.value)} placeholder="Ex.: juros, desconto..."/></div>
                    </div>
                    {numberFromInput(totalPayAmount)<=0?<div className="helper-error">Informe um valor maior que zero.</div>:null}
                    {Math.abs(paymentContext.diff)>=0.01&&numberFromInput(totalPayAmount)>0?<div style={{marginTop:10}} className={`badge ${paymentContext.diff>0?"yellow":"green"}`}>{paymentContext.diff>0?`⬆ Juros ${currency(Math.abs(paymentContext.diff))}`:`⬇ Desconto ${currency(Math.abs(paymentContext.diff))}`}</div>:null}
                    <div className="actions"><button className="ghost-btn" onClick={()=>setPaymentModal({open:false,billId:null,refMonthKey:null})}>Cancelar</button><button className="primary-btn" onClick={handleConfirmTotalPayment}>Confirmar pagamento</button></div>
                  </>
                ):paymentTab==="quitar"?(
                  <>
                    <div style={{background:"#22c55e11",border:"1px solid #22c55e33",borderRadius:10,padding:"12px 14px",marginBottom:14,fontSize:13,lineHeight:1.6}}>
                      <div style={{fontWeight:700,color:"#22c55e",marginBottom:4}}>✅ Quitar pelo valor real pago</div>
                      <div style={{color:"var(--text-muted)"}}>Use quando a conta veio num valor diferente do previsto (ex: luz, água, telefone). O app marca como <strong style={{color:"var(--text-primary)"}}>paga</strong> pelo valor que você informar, independente do valor cadastrado.</div>
                    </div>
                    <div className="form-grid">
                      <div className="field"><label className="label">Valor real pago (R$)</label><input className="input" type="text" inputMode="decimal" value={totalPayAmount} onChange={(e)=>setTotalPayAmount(e.target.value)} placeholder={`Previsto: ${currency(paymentContext.valorMes)}`}/></div>
                      <div className="field"><label className="label">Forma de pagamento</label><select className="select" value={totalPayMethod} onChange={(e)=>setTotalPayMethod(e.target.value)}>{PAYMENT_METHODS.map((l)=><option key={l} value={l}>{l}</option>)}</select></div>
                      <div className="field full"><label className="label">Observação</label><input className="input" value={totalPayNote} onChange={(e)=>setTotalPayNote(e.target.value)} placeholder="Ex.: conta veio menor este mês"/></div>
                    </div>
                    {numberFromInput(totalPayAmount)>0&&(
                      <div style={{marginTop:8,fontSize:12,color:"var(--text-muted)"}}>
                        Previsto: <strong>{currency(paymentContext.valorMes)}</strong> → Pago: <strong style={{color:"#22c55e"}}>{currency(numberFromInput(totalPayAmount))}</strong>
                        {round2(numberFromInput(totalPayAmount))<round2(paymentContext.valorMes)&&<span style={{color:"#22c55e",marginLeft:6}}>⬇ Economia de {currency(round2(paymentContext.valorMes-numberFromInput(totalPayAmount)))}</span>}
                        {round2(numberFromInput(totalPayAmount))>round2(paymentContext.valorMes)&&<span style={{color:"#F59E0B",marginLeft:6}}>⬆ Veio {currency(round2(numberFromInput(totalPayAmount)-paymentContext.valorMes))} a mais</span>}
                      </div>
                    )}
                    {numberFromInput(totalPayAmount)<=0?<div className="helper-error">Informe o valor real pago.</div>:null}
                    <div className="actions">
                      <button className="ghost-btn" onClick={()=>setPaymentModal({open:false,billId:null,refMonthKey:null})}>Cancelar</button>
                      <button className="primary-btn" style={{background:"linear-gradient(135deg,#22c55e,#16a34a)"}} onClick={()=>{
                        const valorReal=numberFromInput(totalPayAmount);
                        if(valorReal<=0)return;
                        // Limpar pagamentos anteriores e registrar o valor real como quitado
                        const billId=paymentModal.billId;
                        const refKey=paymentModal.refMonthKey;
                        setData(prev=>({...prev,bills:prev.bills.map(b=>{
                          if(b.id!==billId)return b;
                          return{...b,payments:{...b.payments,[refKey]:{...(b.payments?.[refKey]||{}),valorAjustado:valorReal,entries:[{id:uid(),amount:valorReal,note:totalPayNote||"Valor real da conta",createdAt:new Date().toISOString(),paidMonthKey:refKey,paymentMethod:totalPayMethod,_quitado:true}]}}};
                        })}));
                        // Atualizar o valor previsto da despesa para o valor real pago
                        setData(prev=>({...prev,bills:prev.bills.map(b=>b.id===billId?{...b,amount:valorReal}:b)}));
                        setPaymentModal({open:false,billId:null,refMonthKey:null});
                      }}>✅ Quitar e atualizar valor</button>
                    </div>
                  </>
                ):(
                  <>
                    <div className="form-grid">
                      <div className="field"><label className="label">Valor desta entrada</label><input className="input" type="text" inputMode="decimal" value={partialAmount} onChange={(e)=>setPartialAmount(e.target.value)} placeholder="Ex.: 80,00"/></div>
                      <div className="field"><label className="label">Forma de pagamento</label><select className="select" value={partialMethod} onChange={(e)=>setPartialMethod(e.target.value)}>{PAYMENT_METHODS.map((l)=><option key={l} value={l}>{l}</option>)}</select></div>
                      <div className="field full"><label className="label">Observação</label><input className="input" value={partialNote} onChange={(e)=>setPartialNote(e.target.value)} placeholder="Ex.: parcela, ajuste"/></div>
                    </div>
                    {numberFromInput(partialAmount)<=0?<div className="helper-error">Informe um valor maior que zero.</div>:null}
                    <div className="actions"><button className="primary-btn" onClick={handleConfirmPartialPayment}>Adicionar entrada</button></div>
                    <div className="payment-history">
                      {(paymentContext.record.entries||[]).length===0?<div className="empty">Nenhuma entrada parcial ainda.</div>:(
                        paymentContext.record.entries.map((entry)=>(
                          <div className="history-item" key={entry.id}>
                            <div><strong>{currency(entry.amount)}</strong><div className="muted" style={{marginTop:2}}>{entry.note||"Sem observação"}</div></div>
                            <div className="muted">{entry.paymentMethod} • {formatMonthKeyLabel(entry.paidMonthKey)}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </Modal>

          <Modal open={undoModal.open} onClose={()=>setUndoModal({open:false,billId:null,refMonthKey:null})} title="Desfazer pagamento" width={440}>
            <p style={{marginTop:0,color:"var(--text-muted)"}}>Tem certeza? Todo o histórico desta referência será removido.</p>
            <div className="actions"><button className="ghost-btn" onClick={()=>setUndoModal({open:false,billId:null,refMonthKey:null})}>Cancelar</button><button className="primary-btn" onClick={()=>undoPaymentsForOccurrence(undoModal.billId,undoModal.refMonthKey)}>Desfazer</button></div>
          </Modal>

          {/* Modal — Tutorial do app */}
          <Modal open={tutorialOpen} onClose={()=>{localStorage.setItem("mf5g_jornada_viu","true");setTutorialOpen(false);}} title={<span style={{display:"flex",alignItems:"center",gap:10}}><img src={porquinhoCarro} alt="" style={{width:32,height:32,objectFit:"contain"}}/> Sua jornada rumo à liberdade financeira</span>} width={520}>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {[
                {emoji:"🏠",titulo:"Painel",desc:"Visão geral do mês com 3 grupos colapsáveis: 🔔 Próximos 7 dias (sempre visível), 📅 Restante do mês e 🙏 Pagas. Contas vencidas aparecem sempre no topo com destaque vermelho. Use 👁️ para ocultar valores em locais públicos. Use 🔍 para buscar por nome ou categoria. Use 📝 para ajustar o valor de uma conta variável (água, luz) só naquele mês, sem alterar os demais."},
                {emoji:"🐷",titulo:"Assistente de Voz (Porquinho)",desc:"Toque no porquinho na tela principal para registrar gastos e entradas por voz! Diga por exemplo: 'paguei 45 reais de almoço no cartão' ou 'recebi 3000 de salário'. O app interpreta e cadastra automaticamente."},
                {emoji:"⚡",titulo:"Gasto Rápido (+ Despesa)",desc:"Para gastos do dia a dia não programados — alimentação, transporte, cafezinho — use o botão + Despesa no topo. Registra o gasto como já pago e desconta direto do saldo. Ideal para pequenos gastos diários sem burocracia."},
                {emoji:"💸",titulo:"Despesas",desc:"Cadastre contas a pagar. Frequências: Único, Mensal, Semestral, Anual e Parcelado. Para parcelado, informe quantas vezes e como vai pagar (Cartão, Débito, Boleto, Pix parcelado, Consignado, etc.). Para cartão de crédito, selecione qual cartão. Toque em qualquer despesa para editar direto (edição inline). Ao mudar o valor de uma despesa Mensal, o app pergunta se vale só para o mês atual ou também para os próximos — o histórico de meses já pagos nunca é alterado. Por segurança, a data de início não pode ser editada depois do cadastro (exceto em despesas Único). Use 🔒 Encerrar despesa quando cancelar algo recorrente (ex: um seguro) — você escolhe até qual mês ela ainda conta, e os meses anteriores já pagos continuam no histórico normalmente; use ↩️ para reativar se precisar. Use 🔍 para buscar por nome ou o seletor de categoria para filtrar as despesas do mês. Toque em 📂 Ver todas cadastradas para ver quanto você gasta por categoria no mês em navegação (ex: Agosto/2026), com botão para alternar e ver todos os meses juntos. O seletor sempre lista as 33 categorias disponíveis, mesmo as sem despesa no momento, mostrando 'vazio' quando não há nada cadastrado."},
                {emoji:"✅",titulo:"Registrar pagamento",desc:"Ao pagar uma conta use: 💯 Total (valor exato), ✂️ Parcial (entrada parcial) ou ✅ Quitar (para contas variáveis como luz e água onde o valor real difere do previsto). O app alerta via WhatsApp e notificação push 7 dias e 1 dia antes do vencimento (alertas chegam inclusive nos fins de semana)."},
                {emoji:"💰",titulo:"Entradas",desc:"Registre sua renda: salário, freelances, rendimentos e extras. Informe o dia do recebimento para melhor controle. Toque em qualquer entrada para editar direto. Use 🔍 para buscar. Esses valores formam o saldo disponível do mês."},
                {emoji:"💳",titulo:"Dívidas",desc:"Painel exclusivo para financiamentos e parcelamentos. Veja o total da dívida, quanto já pagou e quanto falta. Informe a taxa de juros — o app calcula quanto você economizaria quitando hoje. Toque para editar inline. Use 🔍 para buscar."},
                {emoji:"🎯",titulo:"Metas",desc:"Defina objetivos financeiros (viagem, reserva, carro) com valor-alvo e prazo. Acompanhe o progresso com barra visual. Adicione aportes com + Guardar. Toque para editar inline. Use 🔍 para buscar."},
                {emoji:"📊",titulo:"Orçamento",desc:"Defina limites de gasto por categoria. O app alerta quando você está se aproximando ou ultrapassando o limite. Use 🔍 para buscar categorias."},
                {emoji:"📈",titulo:"Gráficos",desc:"Visualize onde o dinheiro foi gasto no mês por categoria — inclui gastos no débito e no cartão de crédito pela categoria real (Alimentação, Transporte, etc.), sem duplicidade. Navegue pelos meses com as setas."},
                {emoji:"📅",titulo:"Evolução",desc:"Acompanhe a evolução dos gastos por categoria ao longo dos últimos 6 meses — inclui débito, Pix e cartão de crédito na categoria real, sem duplicidade. Toque no gráfico para ampliar em tela cheia. Identifique tendências e padrões de consumo."},
                {emoji:"💼",titulo:"Investimentos",desc:"Gerencie sua carteira completa, agrupada por tipo: FIIs, Ações BR, Cripto, ETFs, Internacional, Moedas e Cofrinhos. RENDA VARIÁVEL: + Ativo para cadastrar, + Aporte para novas compras (informe valor + preço por unidade, o app calcula preço médio), + Dividendo para proventos, - Vender para registrar venda (cria entrada automática no caixa). RENDA FIXA: + Aporte soma ao investido, + Rendimento soma ao acumulado (não entra no caixa), - Resgatar cria entrada e reduz o saldo. COFRINHOS: criados automaticamente ao pagar Desafios. Cotações automáticas: Brapi (FIIs/ações), CoinGecko (cripto), AwesomeAPI (moedas)."},
                {emoji:"🏆",titulo:"Desafios",desc:"Transforme hábitos em investimentos! Escolha duração: 3x, 6x, 12x, 24x ou ∞. Ao aceitar, o app cria despesa mensal automática no Painel. Ao pagar, o valor vai direto para o cofrinho em Investimentos. Acompanhe a barra de progresso por meses pagos. Use + Rendimento para registrar juros. Para cancelar, o app exibe mensagem motivacional antes de confirmar."},
                {emoji:"📰",titulo:"Notícias",desc:"Últimas notícias de economia e finanças do Brasil, atualizadas automaticamente. Disponível para todos os planos."},
                {emoji:"💳",titulo:"Cartões",desc:"Cadastre seus cartões de crédito informando limite, dia de vencimento e dia de fechamento. Veja o total gasto somando todos os cartões no topo da tela, e a fatura aberta (gastos acumulando) e a fatura fechada a pagar de cada um. As despesas cadastradas no cartão aparecem agrupadas por categoria. Disponível nos planos Básico e Premium."},
                {emoji:"💎",titulo:"Planos",desc:"Plano Grátis com funcionalidades básicas. Básico (R$9,90/mês) e Premium (R$17,90/mês) desbloqueiam alertas WhatsApp, cartões de crédito e carteira ilimitada. Novos usuários ganham 30 dias de Premium grátis."},
                {emoji:"🌟",titulo:"5G — Os 5 Pilares",desc:"Veja sua vida financeira pelos 5 pilares do Método Finanças 5G com valores do mês atual: 💼 Ganhar (entradas recebidas), 🙏 Gratidão (contas pagas), 🤝 Generosidade (doações realizadas), 🏠 Gastar (despesas pagas) e 📈 Guardar (aportes realizados). O painel reflete apenas o mês visualizado."},
                {emoji:"👤",titulo:"Perfil",desc:"Configure seu nome e WhatsApp para receber alertas de contas (planos Básico e Premium). Plano Premium inclui 2 números. Para parar os alertas, apague o número e salve. Para encerrar a conta, use 🗑️ Excluir minha conta — todos os dados são apagados permanentemente."},
                {emoji:"🔐",titulo:"Login",desc:"Use e-mail e senha ou entre com Google. Toque no 👁️ para mostrar/ocultar a senha. No Android, após o primeiro login a biometria é ativada automaticamente — nas próximas vezes use apenas a digital. Você também receberá notificações push sobre contas a vencer."},
                {emoji:"⚙️",titulo:"Menu de ferramentas",desc:"Toque na engrenagem (⚙️) para acessar: Backup (salva seus dados na nuvem), Excel, PDF (relatório completo do mês) e Importar. Faça backup regularmente para proteger seus dados!"},
              ].map((item,i,arr)=>(
                <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"10px 0",borderBottom:i<arr.length-1?"1px solid var(--border)":"none"}}>
                  <div style={{fontSize:24,flexShrink:0,width:36,textAlign:"center"}}>{item.emoji}</div>
                  <div>
                    <div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)",marginBottom:2}}>{item.titulo}</div>
                    <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.5}}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{marginTop:16}} className="actions">
              <button className="primary-btn" onClick={()=>setTutorialOpen(false)}>Vamos lá! 🚀</button>
            </div>
          </Modal>

          {/* Bônus de 30 dias Premium — revelado após o 1º registro, não no cadastro */}
          {showPremiumBonus && (
            <PremiumBonusModal onClose={()=>setShowPremiumBonus(false)}/>
          )}

          {/* Rodapé profissional */}
          <footer style={{marginTop:40,padding:"24px 20px",borderTop:"1px solid var(--border)",background:"var(--bg-card)",textAlign:"center"}}>
            <div style={{display:"flex",flexWrap:"wrap",justifyContent:"center",alignItems:"center",gap:"6px 20px",marginBottom:12}}>
              <span style={{fontSize:16,fontWeight:800,color:"var(--text-primary)"}}>💳 Mapa Financeiro 5G</span>
              <a href="mailto:metodofinancas5g@gmail.com" style={{fontSize:12,color:"var(--text-muted)",textDecoration:"none"}}>✉️ metodofinancas5g@gmail.com</a>
            </div>
            <div style={{display:"flex",flexWrap:"wrap",justifyContent:"center",gap:"8px 16px",marginBottom:12}}>
              <a href="https://financas5g.com.br" target="_blank" rel="noreferrer" style={{fontSize:12,color:"#F59E0B",textDecoration:"none",fontWeight:600}}>🌟 Conheça o Método 5G</a>
              <span onClick={()=>setTutorialOpen(true)} style={{fontSize:12,color:"#F59E0B",cursor:"pointer",textDecoration:"none",fontWeight:600}}>📖 Como usar o app</span>
              <a href="https://instagram.com/financas5g" target="_blank" rel="noreferrer" style={{fontSize:12,color:"#7C83FF",textDecoration:"none",fontWeight:600}}>📸 Instagram</a>
              <a href="https://www.youtube.com/channel/UCpWZFFcnkt4pZA4WTtVo7AQ?sub_confirmation=1" target="_blank" rel="noreferrer" style={{fontSize:12,color:"#EF4444",textDecoration:"none",fontWeight:600}}>▶️ YouTube</a>
              <a href="https://www.tiktok.com/@financas5g" target="_blank" rel="noreferrer" style={{fontSize:12,color:"var(--text-primary)",textDecoration:"none",fontWeight:600}}>🎵 TikTok</a>
              <a href="https://wa.me/5541995964572" target="_blank" rel="noreferrer" style={{fontSize:12,color:"#22C55E",textDecoration:"none",fontWeight:600}}>💬 WhatsApp</a>
              <span style={{color:"var(--text-muted)"}}>·</span>
              <a href="https://wa.me/5541995964572?text=Olá!%20Tenho%20um%20feedback%20sobre%20o%20Mapa%20Financeiro%205G:%20" target="_blank" rel="noreferrer" style={{fontSize:12,color:"#22c55e",textDecoration:"none",fontWeight:600}}>💡 Sugestões e Feedback</a>
            </div>
            <p style={{fontSize:11,color:"var(--text-muted)",margin:0}}>
              © {new Date().getFullYear()} Mapa Financeiro 5G · Todos os direitos reservados · Feito com 💜 para você organizar sua vida financeira
            </p>
          </footer>
        </>
      )}

    </div>
  );
}
